import type { ServicePageConfig } from "../types";
import {
  getPackagePricingRows,
  PACKAGE_PRICE_HINT_SV,
  PACKAGE_PRICE_HINT_EN,
} from "../package-pricing";

export const hemleveransPages: ServicePageConfig[] = [
  {
    // Hemleverans Butik — config för /hemleverans-butik
    // Vinkel: Generell hemleverans från butiker som inte erbjuder egen leverans,
    // eller där butikens leverans är dyr/långsam. Vi hämtar i butiken och kör hem.
    slug: "hemleverans-butik",
    serviceCategory: "hemleverans",

    meta: {
      title:
        "Hemleverans Butik – Vi Hämtar I Butiken Och Kör Hem Till Dig | Ungdomskraft",
      description:
        "Köpt något i butik som inte får plats i bilen? Vi hämtar ditt köp och levererar hem samma dag. Skåpbil, bärhjälp och inbärning ingår. Boka hemleverans idag.",
      h1: "Hemleverans butik",
    },

    hero: {
      heading: "Vi hämtar i butiken och kör hem till dig – Hemleverans",
      intro:
        "Hittat drömköpet i butiken men det får inte plats i bilen? Eller har du beställt online med hämta-i-butik men saknar transport hem? Vi löser det. Ungdomskraft hämtar ditt köp direkt i butiken, lastar skåpbilen och levererar hem till dig – med inbärning och uppställning på plats. Fungerar med alla butiker: Elgiganten, IKEA, JYSK, Mio, Bauhaus, Rusta, Jula, Power, Byggmax, K-Rauta, Biltema och alla andra.",
      heroImage: "/hero-hemleverans.png",
    },

    featureCards: {
      sinceText: "Hemleverans sedan 2014",
      storyText:
        "Sedan 2014 har Ungdomskraft hjälpt tusentals kunder att få hem sina butiksköp – från tunga vitvaror och flatpack-möbler till byggmaterial och elektronik. Våra ungdomar hämtar i butiken, lastar skåpbilen och bär in hemma hos dig.",
      bookYourText: "Boka din hemleverans",
      bookDescText:
        "Ange butikens adress och din hemadress, beskriv vad som ska hämtas och välj en tid. Du ser priset direkt.",
      vehicleTitleText: "Skåpbilar perfekta för hemleverans",
      vehicleDescText:
        "Vi kommer med rätt storlek på skåpbil – oavsett om du hämtar en TV eller ett helt kök. Du slipper hyra släp, köra själv eller slita ryggen.",
    },

    itemsSection: {
      heading: "Saker vi hämtar och levererar hem",
      items: [
        {
          name: "Möbler",
          description:
            "Soffor, matbord, bokhyllor, sängar, garderober och flatpack-paket från alla möbelbutiker",
          image: "/sofa.webp",
        },
        {
          name: "Vitvaror",
          description:
            "Kylskåp, tvättmaskiner, torktumlare, diskmaskiner, spisar och frysar",
          image: "/washer.webp",
        },
        {
          name: "Elektronik",
          description:
            "TV-apparater, datorer, högtalare, projektorer och annan hemelektronik",
          image: "/tv.webp",
        },
        {
          name: "Byggmaterial",
          description:
            "Trä, gipsskivor, kakel, färg, verktyg och tyngre byggvaror",
          image: "/patio-furniture.webp",
        },
        {
          name: "Trädgård & utemöbler",
          description:
            "Utemöbler, grillar, krukor, planteringslådor och trädgårdsredskap",
          image: "/painting.webp",
        },
        {
          name: "Inredning & övrigt",
          description:
            "Mattor, lampor, speglar, hyllsystem, förvaringslösningar och skrymmande kartonger",
          image: "/box.webp",
        },
      ],
    },

    seoContent: {
      mainHeading:
        "Hemleverans från butik – vi hämtar, kör och bär in åt dig",
      mainText:
        "Du har hittat precis vad du letade efter i butiken – en ny soffa, ett kylskåp, en stor TV eller ett helt flatpack-kök. Men sen kommer den jobbiga frågan: hur får jag hem det här? Butikens egen hemleverans kostar ofta 500–1 500 kr, tar dagar att boka och inkluderar sällan inbärning förbi ytterdörren. Att hyra släp kräver dragkrok och en ledig förmiddag. Att be kompisar om hjälp fungerar en gång – inte varje gång. Ungdomskraft erbjuder hemleverans från alla butiker i Stockholm och Göteborg. Vi hämtar ditt köp direkt i butiken, lastar skåpbilen och kör hem till dig. Vi bär in genom trapphus, hiss eller trångt garage och ställer på plats där du vill ha det. Ingen extra kostnad för inbärning, inga dolda avgifter, inga veckors väntan. Du ser priset direkt när du bokar. Vi fungerar med alla butiker – Elgiganten, Power, IKEA, JYSK, Mio, Rusta, Jula, Bauhaus, K-Rauta, Byggmax, Biltema och alla andra fysiska butiker. Oavsett om du handlat i en stor kedja eller en liten lokal butik hämtar vi ditt köp och levererar hem. Vi kör även paket som du beställt online med hämta-i-butik men som är för stora eller tunga att ta hem själv. Varje vecka kör vi hundratals hemleveranser i Stockholms- och Göteborgsområdet. Våra ungdomar är vana vid att hantera tunga och skrymmande varor – från 80-kilos kylskåp och tvättmaskiner till 3-meterspaket med gipsskivor och reglar. Vi vet hur man skyddar ömtåliga ytor, navigerar trånga trapphus och manövrerar genom smala dörröppningar. Vi tar med oss alla verktyg som behövs: möbelfiltar, bärremmar, spännband och skydd för golv och väggar. Om en vara inte får plats genom dörren löser vi det – antingen genom att demontera förpackningen eller ta en alternativ väg. Du behöver inte förbereda något extra, vi sköter allt från lastning till uppställning.",
      preparationHeading: "Förbered inför din hemleverans",
      preparationText:
        "Se till att varan är betald och redo att hämtas i butiken. Om du beställt online med hämta-i-butik – kolla att du fått bekräftelse om att varan finns på plats. Ha din hemadress och eventuell portkod redo. Mät gärna dörröppningar och trapphus om det rör sig om tunga eller skrymmande varor som kylskåp, tvättmaskiner eller stora möbler. Kolla att skåpbilen kan parkera nära porten hemma hos dig. Ju mer information du anger vid bokning, desto smidigare går leveransen.",
      additionalSections: [
        {
          heading: "Hemleverans av möbler från butik",
          content:
            "Möbler är vår vanligaste hemleverans. Du har hittat den perfekta soffan på IKEA, ett massivt matbord på Mio eller en garderob på JYSK – men den får inte plats i bilen. Med Ungdomskrafts hemleverans hämtar vi dina möbler direkt i butiken. Vi lastar flatpack-paket eller färdigmonterade möbler i skåpbilen, skyddar allt med filtar och spännband och kör hem till dig. Där bär vi in genom trapphus och dörrar och ställer paketen eller möblerna där du vill ha dem. Många av våra kunder berättar att de nu handlar helt annorlunda i möbelbutiker – de vågar köpa stora, tunga möbler som de aldrig hade kunnat få hem på egen hand. Vi hanterar allt från en enstaka bokhylla till ett helt rum med flatpack-paket. Behöver du hjälp med montering? Beskriv det vid bokning så löser vi det också.",
        },
        {
          heading: "Hemleverans av vitvaror och elektronik",
          content:
            "Vitvaror och stor elektronik är tunga, ömtåliga och kräver ofta specialhantering. Ett kylskåp väger 70–90 kg, en tvättmaskin runt 80 kg och en 65-tums TV behöver varsam hantering för att inte skadas. Butikernas egna leveransalternativ inkluderar sällan inbärning uppför trappor – eller kostar extra för varje våning. Med Ungdomskraft ingår alltid inbärning till rätt plats i hemmet, oavsett våning. Vi hämtar din vitvara eller TV i butiken, skyddar den under transport och bär in på plats. Vi hanterar Elgiganten, Power och alla andra elektronik- och vitvarubutiker. Tänk på att vi ansvarar för transporten och inbärningen – inkoppling av vatten, el och avlopp behöver du ordna separat om det behövs.",
        },
        {
          heading: "Hemleverans av byggmaterial",
          content:
            "Byggprojekt kräver material som sällan får plats i en personbil – gipsskivor, reglar, trä, kakel, färghinkar och tunga verktyg. Att göra flera turer till Bauhaus, Byggmax eller K-Rauta slösar tid och bensin. Med vår hemleverans hämtar vi allt material i en enda tur. Vi lastar skåpbilen i butiken och levererar direkt till ditt byggprojekt – hem, stuga eller arbetsplats. Vi bär in materialet dit du behöver det, oavsett om det är upp till tredje våningen eller in i ett garage. Perfekt för renoveringsprojekt där du behöver stora mängder material snabbt och smidigt.",
        },
        {
          heading: "Så fungerar hemleverans med Ungdomskraft",
          listItems: [
            {
              label: "Boka online",
              text: "Ange butikens adress och din hemadress. Beskriv vad som ska hämtas och välj en tid. Du ser priset direkt.",
            },
            {
              label: "Vi hämtar i butiken",
              text: "Vårt team kommer med skåpbil och rätt utrustning. Vi hämtar ditt köp, skyddar med filtar och spännband och lastar allt.",
            },
            {
              label: "Vi levererar hem",
              text: "Vi kör hem till dig, bär in genom trapphus och dörrar och ställer ditt köp på plats.",
            },
            {
              label: "Klart",
              text: "Ditt köp står hemma hos dig – på rätt plats, utan att du lyfte ett finger.",
            },
          ],
          afterContent:
            "Hela processen tar normalt 1–3 timmar beroende på avstånd och mängd. Du kan följa statusen i realtid.",
        },
        {
          heading: "Vilka butiker hämtar ni från?",
          content:
            "Vi hämtar från alla butiker i Stockholm och Göteborg. De vanligaste är Elgiganten, Power, IKEA, JYSK, Mio, Rusta, Jula, Bauhaus, K-Rauta, Byggmax och Biltema – men vi kör från vilken butik som helst. Ange bara butikens adress vid bokning. Vi hämtar även varor beställda online med hämta-i-butik, returnerade varor som ska tillbaka till butiken och varor från flera butiker i samma tur om de ligger i rimligt avstånd.",
        },
        {
          heading: "Varför boka hemleverans med Ungdomskraft?",
          content:
            "Det som gör Ungdomskraft unikt är att vi anställer unga människor och ger dem riktig arbetslivserfarenhet. När du bokar hos oss får du inte bara professionell hjälp med din hemleverans – du bidrar också till att unga människor kommer in på arbetsmarknaden och bygger sin framtid. Varje uppdrag bemannas av motiverade ungdomar som lär sig allt från kundservice och logistik till fysiskt arbete och ansvarsfullt miljötänk. Vi har funnits sedan 2014 och har genomfört tiotusentals transporter i Stockholm och Göteborg. Våra kunder kommer tillbaka gång efter gång – inte bara för att tjänsten fungerar, utan för att de vet att deras pengar gör skillnad. Jämfört med butikernas egna leveransalternativ erbjuder vi ofta snabbare leverans, inbärning på plats och ett konkurrenskraftigt pris – utan dolda avgifter. Allt är försäkrat, vi har egna skåpbilar i flera storlekar och du ser alltid priset innan du bokar.\n\nHemleverans från butik ger inte rätt till RUT-avdrag, men flera av våra andra tjänster gör det – exempelvis flytt, bärhjälp och möbelmontering med 50 % avdrag på arbetskostnaden. Kombinera gärna tjänster i en och samma bokning.",
        },
      ],
    },

    faq: [
      {
        question: "Vad kostar hemleverans från butik?",
        answer:
          "Priset beror på mängden och avståndet. En mindre hämtning kostar från 595 kr, medan större leveranser med vitvaror eller många möbelpaket kostar från 995–2 495 kr. Du ser det exakta priset när du bokar online.",
      },
      {
        question: "Kan jag få RUT-avdrag på hemleverans från butik?",
        answer:
          "Hemleverans från butik ger i sig inte rätt till RUT-avdrag. RUT gäller hushållsnära tjänster som utförs i din bostad, exempelvis flytt, bärhjälp och möbelmontering. Ungdomskraft erbjuder flera RUT-berättigade tjänster med 50 % avdrag på arbetskostnaden – kombinera enkelt i en bokning via vår hemsida.",
      },
      {
        question: "Ingår skåpbil i hemleveransen?",
        answer:
          "Ja, skåpbil ingår alltid. Vi väljer rätt storlek utifrån vad som ska transporteras – du behöver inte hyra bil, köra eller tanka.",
      },
      {
        question: "Ingår inbärning i priset?",
        answer:
          "Ja, inbärning ingår alltid. Vi bär ditt köp genom trapphus, hiss och dörrar och ställer på plats hemma hos dig – oavsett våning.",
      },
      {
        question: "Vilka butiker hämtar ni från?",
        answer:
          "Vi hämtar från alla butiker i Stockholm och Göteborg – Elgiganten, Power, IKEA, JYSK, Mio, Rusta, Jula, Bauhaus, K-Rauta, Byggmax, Biltema och alla andra. Ange bara butikens adress vid bokning.",
      },
      {
        question: "Kan ni hämta från flera butiker i samma tur?",
        answer:
          "Ja, vi kan hämta från flera butiker under samma körning om de ligger i rimligt avstånd. Beskriv alla hämtpunkter när du bokar.",
      },
      {
        question: "Kan ni bära tunga vitvaror i trappor?",
        answer:
          "Ja, vi bär från vilken våning som helst – med eller utan hiss. Våra ungdomar är vana vid tunga lyft. Vi hanterar kylskåp, tvättmaskiner, torktumlare och alla andra tunga varor.",
      },
      {
        question: "Kan ni montera möbler?",
        answer:
          "Vi fokuserar på transport och inbärning, men kan ofta hjälpa till med enklare montering om du beskriver det vid bokning. Beskriv vad som behöver monteras så ser vi vad vi kan göra.",
      },
      {
        question: "Kan ni koppla in vitvaror?",
        answer:
          "Nej, vi ansvarar för transport och inbärning – inte för inkoppling av vatten, el eller avlopp. Vi ställer vitvaran på plats så att en elektriker eller rörmokare enkelt kan koppla in den.",
      },
      {
        question: "Skyddar ni sakerna under transporten?",
        answer:
          "Ja, vi har möbelfiltar, bärremmar och spännband. Vi skyddar alla varor under lastning och transport. Allt är försäkrat – om något skadas ersätter vi det.",
      },
      {
        question: "Hur snabbt kan ni leverera?",
        answer:
          "Vi erbjuder ofta hemleverans med kort varsel – ofta redan samma dag – inom några timmar. Boka online och se tillgängliga tider direkt.",
      },
      {
        question: "Erbjuder ni hemleverans på helger?",
        answer:
          "Ja, vi erbjuder hemleverans på helger och vissa kvällar. Kontrollera att butiken har öppet den tid du bokar – vi kan inte hämta om butiken är stängd.",
      },
      {
        question: "Behöver jag vara med i butiken?",
        answer:
          "Nej, du behöver inte vara med i butiken. Se till att varan är betald och redo att hämtas – vi sköter resten. Du behöver bara vara hemma för att ta emot leveransen.",
      },
      {
        question: "Är hemleveransen försäkrad?",
        answer:
          "Ja, alla våra transporter är helförsäkrade. Om något skadas under lastning, transport eller inbärning ersätter vi det. Du kan boka med trygghet.",
      },
      {
        question:
          "I vilka kommuner erbjuder ni hemleverans från butik?",
        answer:
          "Vi erbjuder hemleverans i hela Stockholmsområdet: Botkyrka, Danderyd, Ekerö, Haninge, Huddinge, Järfälla, Lidingö, Nacka, Norrtälje, Nykvarn, Nynäshamn, Salem, Sigtuna, Sollentuna, Solna, Stockholm, Sundbyberg, Södertälje, Tyresö, Täby, Upplands Väsby, Upplands-Bro, Vallentuna, Vaxholm, Värmdö och Österåker. Vi finns även i Göteborgsområdet: Ale, Alingsås, Göteborg, Härryda, Kungälv, Kungsbacka, Lerum, Lilla Edet, Mölndal, Partille, Stenungsund, Tjörn och Öckerö.",
      },
      {
        question: "Varför välja Ungdomskraft för hemleverans från butik?",
        answer:
          "Hos oss ingår alltid skåpbil, bärhjälp och inbärning till rätt plats – och du ser priset direkt vid bokning. Vi kan ofta köra samma dag, allt är försäkrat och du bidrar till att unga får jobb.",
      },
    ],

    priceHint: PACKAGE_PRICE_HINT_SV.bud,

    pricingRows: getPackagePricingRows("bud", "sv"),

    dynamicHeadings: {
      threeSteps: "Hemleverans i 3 enkla steg",
      step2Text:
        "Våra ungdomar hämtar ditt köp i butiken, skyddar med filtar och lastar skåpbilen. Sedan kör vi hem till dig.",
      step3Title: "Allt på plats",
      step3Desc:
        "Ditt köp står hemma hos dig – inburet och uppställt. Klart.",
      ctaHeading: "Boka hemleverans idag",
      ctaText:
        "Ange butikens adress och din hemadress, beskriv vad som ska hämtas och välj en tid. Du ser priset direkt.",
    },

    en: {
      slug: "store-delivery",
      meta: {
        title:
          "Store Delivery – We Pick Up At The Store And Deliver Home | Ungdomskraft",
        description:
          "Bought something in store that won't fit in your car? We pick up and deliver home same day – van, carrying and placement included. Book today!",
        h1: "Store delivery",
      },
      hero: {
        heading: "We pick up at the store and deliver home – Store-Delivery",
        intro:
          "Found the perfect purchase in store but it won't fit in your car? Or ordered online with click-and-collect but lack transport home? We've got it. Ungdomskraft picks up your purchase directly from the store, loads the van and delivers to your home — with carrying and placement included. Works with all stores: Elgiganten, IKEA, JYSK, Mio, Bauhaus, Rusta, Jula, Power, Byggmax, K-Rauta, Biltema and every other store.",
      },
      featureCards: {
        sinceText: "Store delivery since 2014",
        storyText:
          "Since 2014, Ungdomskraft has helped thousands of customers get their store purchases home — from heavy appliances and flatpack furniture to building materials and electronics. Our young crew pick up at the store, load the van and carry inside at your home.",
        bookYourText: "Book your store delivery",
        bookDescText:
          "Enter the store address and your home address, describe what needs picking up and choose a time. You see the price instantly.",
        vehicleTitleText: "Vans perfect for store delivery",
        vehicleDescText:
          "We arrive with the right size van — whether you're picking up a TV or an entire kitchen. No need to rent a trailer, drive yourself or strain your back.",
      },
      itemsSection: {
        heading: "Items we pick up and deliver home",
        items: [
          {
            name: "Furniture",
            description:
              "Sofas, dining tables, bookshelves, beds, wardrobes and flatpack packages from all furniture stores",
            image: "/sofa.webp",
          },
          {
            name: "Appliances",
            description:
              "Fridges, washing machines, dryers, dishwashers, ovens and freezers",
            image: "/washer.webp",
          },
          {
            name: "Electronics",
            description:
              "TVs, computers, speakers, projectors and other home electronics",
            image: "/tv.webp",
          },
          {
            name: "Building materials",
            description:
              "Timber, plasterboard, tiles, paint, tools and heavy building supplies",
            image: "/patio-furniture.webp",
          },
          {
            name: "Garden & outdoor",
            description:
              "Outdoor furniture, grills, planters, raised beds and garden tools",
            image: "/painting.webp",
          },
          {
            name: "Home decor & other",
            description:
              "Rugs, lamps, mirrors, shelving systems, storage solutions and bulky boxes",
            image: "/box.webp",
          },
        ],
      },
      seoContent: {
        mainHeading:
          "Store delivery — we pick up, drive and carry inside for you",
        mainText:
          "You've found exactly what you were looking for in the store — a new sofa, a fridge, a big TV or an entire flatpack kitchen. But then comes the tricky question: how do I get this home? The store's own delivery often costs 500–1 500 SEK, takes days to book and rarely includes carrying past the front door. Renting a trailer requires a tow bar and a free morning. Asking friends for help works once — not every time. Ungdomskraft offers store delivery from all stores in Stockholm and Gothenburg. We pick up your purchase directly from the store, load the van and drive to your home. We carry inside through stairwells, lifts or tight garages and place it where you want it. No extra charge for carrying, no hidden fees, no weeks of waiting. You see the price instantly when you book. We work with all stores — Elgiganten, Power, IKEA, JYSK, Mio, Rusta, Jula, Bauhaus, K-Rauta, Byggmax, Biltema and every other physical store. Whether you've shopped at a big chain or a small local store, we pick up your purchase and deliver home. We also transport packages you've ordered online with click-and-collect that are too large or heavy to take home yourself. Every week we make hundreds of store deliveries across the Stockholm and Gothenburg regions. Our crew are used to handling heavy and bulky goods — from 80-kilo fridges and washing machines to 3-metre packages of plasterboard and timber. We know how to protect delicate surfaces, navigate narrow stairwells and manoeuvre through tight doorways. We bring all the tools needed: furniture blankets, carrying straps, ratchet straps and protection for floors and walls. If an item won't fit through the door we solve it — either by dismantling the packaging or finding an alternative route. You don't need to prepare anything extra, we handle everything from loading to placement.",
        preparationHeading: "Prepare for your store delivery",
        preparationText:
          "Make sure the item is paid for and ready to collect at the store. If you ordered online with click-and-collect, check that you've received confirmation that the item is available. Have your home address and any door codes ready. Measure doorways and stairwells if it involves heavy or bulky items like fridges, washing machines or large furniture. Check that the van can park near the entrance at your home. The more information you provide when booking, the smoother the delivery will be.",
        additionalSections: [
          {
            heading: "Furniture delivery from store",
            content:
              "Furniture is our most common store delivery. You've found the perfect sofa at IKEA, a solid dining table at Mio or a wardrobe at JYSK — but it won't fit in your car. With Ungdomskraft's store delivery we pick up your furniture directly from the store. We load flatpack packages or ready-assembled furniture into the van, protect everything with blankets and straps and drive to your home. There we carry inside through stairwells and doorways and place the packages or furniture where you want them. Many of our customers tell us they now shop completely differently at furniture stores — they dare to buy large, heavy pieces they could never have taken home on their own. We handle everything from a single bookshelf to an entire room of flatpack packages. Need help with assembly? Describe it when booking and we can arrange that too.",
          },
          {
            heading: "Appliance and electronics delivery",
            content:
              "Appliances and large electronics are heavy, fragile and often require special handling. A fridge weighs 70–90 kg, a washing machine around 80 kg and a 65-inch TV needs careful handling to avoid damage. Stores' own delivery options rarely include carrying upstairs — or charge extra per floor. With Ungdomskraft, carrying to the right spot in your home is always included, regardless of floor. We pick up your appliance or TV from the store, protect it during transport and carry inside to its place. We handle Elgiganten, Power and all other electronics and appliance stores. Note that we are responsible for transport and carrying — connecting water, electricity and plumbing needs to be arranged separately if required.",
          },
          {
            heading: "Building materials delivery",
            content:
              "Building projects require materials that rarely fit in a car — plasterboard, timber, tiles, paint tins and heavy tools. Making multiple trips to Bauhaus, Byggmax or K-Rauta wastes time and fuel. With our store delivery we pick up all materials in a single trip. We load the van at the store and deliver directly to your project — home, cabin or workplace. We carry the materials where you need them, whether that's up to the third floor or into a garage. Perfect for renovation projects where you need large quantities of materials quickly and conveniently.",
          },
          {
            heading: "How store delivery works with Ungdomskraft",
            listItems: [
              {
                label: "Book online",
                text: "Enter the store address and your home address. Describe what needs picking up and choose a time. You see the price instantly.",
              },
              {
                label: "We pick up at the store",
                text: "Our team arrive with a van and the right equipment. We collect your purchase, protect with blankets and straps and load everything.",
              },
              {
                label: "We deliver home",
                text: "We drive to your home, carry inside through stairwells and doorways and place your purchase where you want it.",
              },
              {
                label: "Done",
                text: "Your purchase is at home — in the right place, without lifting a finger.",
              },
            ],
            afterContent:
              "The whole process typically takes 1–3 hours depending on distance and amount. You can follow the status in real time.",
          },
          {
            heading: "Which stores do you pick up from?",
            content:
              "We pick up from all stores in Stockholm and Gothenburg. The most common are Elgiganten, Power, IKEA, JYSK, Mio, Rusta, Jula, Bauhaus, K-Rauta, Byggmax and Biltema — but we collect from any store. Just enter the store address when booking. We also pick up items ordered online with click-and-collect, returned goods going back to the store and items from multiple stores in the same trip if they're within a reasonable distance.",
          },
          {
            heading: "Why book store delivery with Ungdomskraft?",
            content:
              "What makes Ungdomskraft unique is that we employ young people and give them real work experience. When you book with us, you don't just get professional help with your store delivery — you also contribute to getting young people into the workforce and building their future. Every job is staffed by motivated young workers who learn everything from customer service and logistics to physical work and responsible environmental practices. We've been around since 2014 and have completed tens of thousands of transports across Stockholm and Gothenburg. Our customers come back time and again — not just because the service works, but because they know their money makes a difference. Compared to stores' own delivery options, we often offer faster delivery, carrying and placement included and a competitive price — without hidden fees. Everything is insured, we have our own vans in multiple sizes and you always see the price before you book.\n\nStore delivery does not qualify for the Swedish RUT tax deduction, but several of our other services do – including moving help, carrying help and furniture assembly with a 50% deduction on the labour cost. Feel free to combine services in a single booking.",
          },
        ],
      },
      faq: [
        {
          question: "How much does store delivery cost?",
          answer:
            "The price depends on the amount and distance. A smaller pickup starts at 595 SEK, while larger deliveries with appliances or many furniture packages cost 749–2 495 SEK. You see the exact price when you book online.",
        },
        {
          question: "Can I get a RUT tax deduction on store delivery?",
          answer:
            "Store delivery does not in itself qualify for the Swedish RUT tax deduction. RUT applies to household services carried out in your home, such as moving help, carrying help and furniture assembly. Ungdomskraft offers several RUT-eligible services with a 50% deduction on the labour cost – easily combine them in one booking via our website.",
        },
        {
          question: "Is a van included?",
          answer:
            "Yes, a van is always included. We choose the right size based on what needs transporting — you don't need to rent a vehicle.",
        },
        {
          question: "Is carrying inside included?",
          answer:
            "Yes, carrying inside is always included. We carry your purchase through stairwells, lifts and doorways and place it at your home — regardless of floor.",
        },
        {
          question: "Which stores do you pick up from?",
          answer:
            "We pick up from all stores in Stockholm and Gothenburg — Elgiganten, Power, IKEA, JYSK, Mio, Rusta, Jula, Bauhaus, K-Rauta, Byggmax, Biltema and every other store. Just enter the store address when booking.",
        },
        {
          question: "Can you pick up from multiple stores in one trip?",
          answer:
            "Yes, we can pick up from several stores during the same run if they're within a reasonable distance. Describe all pickup points when you book.",
        },
        {
          question: "Can you carry heavy appliances up stairs?",
          answer:
            "Yes, we carry from any floor — with or without a lift. Our crew are used to heavy items. We handle fridges, washing machines, dryers and all other heavy goods.",
        },
        {
          question: "Can you assemble furniture?",
          answer:
            "We focus on transport and carrying, but can often help with simple assembly if you describe it when booking. Let us know what needs assembling and we'll see what we can do.",
        },
        {
          question: "Can you connect appliances?",
          answer:
            "No, we are responsible for transport and carrying — not for connecting water, electricity or plumbing. We place the appliance in position so an electrician or plumber can easily connect it.",
        },
        {
          question: "Do you protect items during transport?",
          answer:
            "Yes, we use furniture blankets, carrying straps and ratchet straps. We protect all goods during loading and transport. Everything is insured — if something is damaged we replace it.",
        },
        {
          question: "How quickly can you deliver?",
          answer:
            "We often offer store delivery at short notice — often the same day — within a few hours. Book online and see available times instantly.",
        },
        {
          question: "Is store delivery available on weekends?",
          answer:
            "Yes, we offer delivery on weekends and some evenings. Check that the store is open at your chosen time — we can't pick up if the store is closed.",
        },
        {
          question: "Do I need to be at the store?",
          answer:
            "No, you don't need to be at the store. Make sure the item is paid for and ready to collect — we handle the rest. You just need to be home to receive the delivery.",
        },
        {
          question: "Is the delivery insured?",
          answer:
            "Yes, all our transports are fully insured. If anything is damaged during loading, transport or carrying we replace it. You can book with confidence.",
        },
        {
          question: "In which areas do you offer store delivery?",
          answer:
            "We offer store delivery across the entire Stockholm region: Botkyrka, Danderyd, Ekerö, Haninge, Huddinge, Järfälla, Lidingö, Nacka, Norrtälje, Nykvarn, Nynäshamn, Salem, Sigtuna, Sollentuna, Solna, Stockholm, Sundbyberg, Södertälje, Tyresö, Täby, Upplands Väsby, Upplands-Bro, Vallentuna, Vaxholm, Värmdö and Österåker. We're also in the Gothenburg region: Ale, Alingsås, Göteborg, Härryda, Kungälv, Kungsbacka, Lerum, Lilla Edet, Mölndal, Partille, Stenungsund, Tjörn and Öckerö.",
        },
        {
          question: "Why choose Ungdomskraft for store delivery?",
          answer:
            "We always include a van, carrying help and placement in the right spot — and you see the price instantly when booking. We can often deliver the same day, everything is insured and you're helping young people get into work.",
        },
      ],
      priceHint: PACKAGE_PRICE_HINT_EN.bud,
      pricingRows: getPackagePricingRows("bud", "en"),
      dynamicHeadings: {
        threeSteps: "Store delivery in 3 easy steps",
        step2Text:
          "Our crew pick up your purchase at the store, protect with blankets and load the van. Then we drive home to you.",
        step3Title: "All in place",
        step3Desc:
          "Your purchase is at home — carried in and placed. Done.",
        ctaHeading: "Book store delivery today",
        ctaText:
          "Enter the store address and your home address, describe what needs picking up and choose a time. You see the price instantly.",
      },
    },
  },
  {
    // Hemleverans Elgiganten — config för /hemleverans-elgiganten
    // Vinkel: Hämta köp från Elgiganten som inte får plats i bilen – TV, vitvaror, datorer, gaming.
    // Unik vinkel: Elgigantens produktmix (tung elektronik + vitvaror) kräver varsam hantering.
    slug: "hemleverans-elgiganten",
    serviceCategory: "hemleverans",

    meta: {
      title:
        "Hemleverans Elgiganten – Vi Hämtar Ditt Köp Och Kör Hem | Ungdomskraft",
      description:
        "Köpt TV, tvättmaskin eller kylskåp på Elgiganten som inte får plats i bilen? Vi hämtar i butiken och levererar hem med inbärning. Boka hemleverans Elgiganten idag.",
      h1: "Hemleverans Elgiganten",
    },

    hero: {
      heading: "Vi hämtar ditt köp på Elgiganten",
      intro:
        "Köpt en 75-tums TV, ett nytt kylskåp eller en tvättmaskin på Elgiganten – men den får inte plats i bilen? Vi löser det snabbt och smidigt. Ungdomskraft hämtar ditt köp direkt på Elgiganten, lastar skåpbilen och levererar hem till dig – med inbärning och uppställning på plats. Vi kör från alla Elgiganten-butiker i Stockholm och Göteborg.",
      heroImage: "/hero-transport-elektronik.png",
    },

    featureCards: {
      sinceText: "Elgiganten-leverans sedan 2014",
      storyText:
        "Sedan 2014 har Ungdomskraft hjälpt tusentals kunder att få hem sina Elgiganten-köp – från stora TV-apparater och tunga vitvaror till gaming-skärmar och soundbars. Våra ungdomar vet hur man hanterar ömtålig elektronik och tunga vitvaror i trånga trapphus.",
      bookYourText: "Boka din Elgiganten-leverans",
      bookDescText:
        "Ange Elgiganten-butikens adress och din hemadress, beskriv vad som ska hämtas och välj en tid. Du ser priset direkt.",
      vehicleTitleText: "Skåpbilar perfekta för Elgiganten-leveranser",
      vehicleDescText:
        "Vi kommer med rätt storlek på skåpbil – oavsett om du hämtar en laptop eller ett komplett kök med vitvaror. Du slipper hyra släp, köra själv eller slita ryggen.",
    },

    itemsSection: {
      heading: "Saker vi hämtar på Elgiganten",
      items: [
        {
          name: "TV-apparater",
          description:
            "55-, 65-, 75- och 85-tums TV-apparater – varsam hantering och säker transport hem",
          image: "/tv.webp",
        },
        {
          name: "Vitvaror",
          description:
            "Kylskåp, frysar, tvättmaskiner, torktumlare, diskmaskiner och spisar från Elgiganten",
          image: "/washer.webp",
        },
        {
          name: "Datorer & gaming",
          description:
            "Stationära datorer, gaming-datorer, bildskärmar och kringutrustning",
          image: "/patio-furniture.webp",
        },
        {
          name: "Ljud & bild",
          description:
            "Soundbars, surroundsystem, projektorer och hemmabio-utrustning",
          image: "/painting.webp",
        },
        {
          name: "Hushållsmaskiner",
          description:
            "Robotdammsugare, luftrenare, kaffemaskiner och andra större hushållsprodukter",
          image: "/box.webp",
        },
        {
          name: "Övrigt skrymmande",
          description:
            "Elcyklar, löpband, crosstrainers och annan skrymmande elektronik från Elgiganten",
          image: "/sofa.webp",
        },
      ],
    },

    seoContent: {
      mainHeading:
        "Hemleverans från Elgiganten – vi hämtar, kör och bär in åt dig",
      mainText:
        "Elgiganten är Sveriges största elektronikkedja med butiker i hela Stockholms- och Göteborgsområdet. Sortimentet sträcker sig från små tillbehör till stora vitvaror och TV-apparater som kräver skåpbil för att ta hem. Problemet många kunder känner igen: du hittar den perfekta TV:n, det nya kylskåpet eller tvättmaskinen i butiken – men den får inte plats i bilen. Elgigantens egen hemleverans kostar ofta 400–1 000 kr extra, kan ta upp till en vecka att boka och inkluderar sällan inbärning förbi ytterdörren. Om du bor på tredje våningen utan hiss tillkommer ytterligare avgifter per våningsplan. Det är här Ungdomskraft blir ett smartare alternativ. Vi hämtar ditt köp direkt i Elgiganten-butiken, lastar skåpbilen varsamt och kör hem till dig. Inbärning till rätt plats i hemmet ingår alltid – oavsett våning, trångt trapphus eller avsaknad av hiss. Du betalar ett pris, du ser det direkt vid bokning och det tillkommer inga dolda avgifter. Vi kör från alla Elgiganten-butiker i Stockholm och Göteborg: Kungens Kurva, Barkarby, Nacka Forum, Täby, Solna, Farsta, Södermalm, Nordstan, Frölunda Torg, Sisjön, Bäckebol och alla övriga. Våra ungdomar hanterar elektronik och vitvaror med samma omsorg som butikens egna leveransteam – men ofta snabbare och med mer flexibla tider. Vi vet att en 75-tums TV måste stå upprätt under transporten, att ett kylskåp inte ska lutas mer än nödvändigt och att en tvättmaskin behöver transportspärrar. Allt skyddas med filtar och spännband, och vi bär med den försiktighet som dyr elektronik kräver. Många av våra kunder kombinerar Elgiganten-köp med hämtning från andra butiker i samma tur. Ska du hämta en TV på Elgiganten och en möbel på IKEA? Vi gör det i en och samma körning.",
      preparationHeading: "Förbered inför din Elgiganten-leverans",
      preparationText:
        "Se till att köpet är betalt och redo att hämtas i butiken. Om du beställt online med hämta-i-butik – kontrollera att du fått sms eller mejl om att varan finns på plats. Ha din hemadress och eventuell portkod redo. Mät dörröppningar och trapphus om det rör sig om stora vitvaror som kylskåp, tvättmaskiner eller mycket stora TV-apparater. Kolla att skåpbilen kan parkera nära porten hemma hos dig. Om du vill att vi tar bort emballage efter inbärning – ange det vid bokning.",
      additionalSections: [
        {
          heading: "Hemleverans av TV från Elgiganten",
          content:
            "TV-apparater är ett av de vanligaste köpen vi hämtar på Elgiganten. En 65-tums TV i originalförpackning är cirka 160 cm bred och väger 25–40 kg – den får inte plats i de flesta personbilar, och att balansera den på baksätet riskerar att skada skärmen. Med Ungdomskrafts hemleverans hämtar vi din TV direkt på Elgiganten. Vi lastar den stående i skåpbilen (aldrig liggande – det skadar panelen), säkrar med spännband och filtar och kör hem till dig. Vi bär in genom trapphus och dörrar och ställer TV:n på plats. Behöver du hjälp att lyfta upp den på väggen eller TV-bänken? Beskriv det vid bokning. Vi hämtar alla storlekar – från 55-tums till 85-tums och större. Oavsett om du köpt en Samsung, LG, Sony eller annan modell hanterar vi den med den försiktighet som dyr elektronik kräver.",
        },
        {
          heading: "Hemleverans av vitvaror från Elgiganten",
          content:
            "Vitvaror från Elgiganten – kylskåp, frysar, tvättmaskiner, torktumlare, diskmaskiner och spisar – väger ofta 60–90 kg och kräver minst två personer för att bära. Elgigantens egen leverans tar ibland upp till en vecka och kostar extra för varje våningsplan utan hiss. Med Ungdomskraft hämtar vi din vitvara i butiken, skyddar den under transporten och bär in till exakt rätt plats i hemmet – oavsett våning. Vi har erfarenhet av att bära kylskåp genom smala trapphus, tvättmaskiner genom låga källardörrar och torktumlare upp tre trappor utan hiss. Vi ser till att transportspärrarna sitter kvar under körningen och att vitvaran står stabilt under lastning. Tänk på att vi ansvarar för transport och inbärning – inkoppling av vatten, el och avlopp behöver du ordna separat, till exempel med en elektriker eller rörmokare.",
        },
        {
          heading: "Hemleverans av datorer och gaming-utrustning",
          content:
            "Gaming-datorer, stora bildskärmar och hemmabio-utrustning från Elgiganten kan vara både tunga och skrymmande. En stationär gaming-dator med tillbehör väger ofta 20–30 kg i originalförpackning, och en 34-tums ultrawide-skärm behöver varsam hantering. Vi hämtar allt på Elgiganten, paketerar säkert i skåpbilen och levererar hem till dig. Vi bär in och ställer på plats – du fokuserar på att packa upp och plugga in. Även större köp som löpband, elcyklar och crosstrainers från Elgiganten hämtar vi utan problem. De väger ofta 40–80 kg och kräver skåpbil och minst två bärare – perfekt uppdrag för våra erfarna ungdomar.",
        },
        {
          heading: "Så fungerar Elgiganten-leverans med Ungdomskraft",
          listItems: [
            {
              label: "Boka online",
              text: "Ange Elgiganten-butikens adress och din hemadress. Beskriv vad du köpt och välj en tid. Du ser priset direkt.",
            },
            {
              label: "Vi hämtar på Elgiganten",
              text: "Vårt team kommer med skåpbil. Vi hämtar ditt köp i butiken, skyddar med filtar och spännband och lastar varsamt.",
            },
            {
              label: "Vi levererar hem",
              text: "Vi kör hem till dig, bär in genom trapphus och dörrar och ställer ditt köp på plats.",
            },
            {
              label: "Klart",
              text: "Din nya TV, vitvara eller dator står hemma hos dig – inburen och uppställd. Utan att du lyfte ett finger.",
            },
          ],
          afterContent:
            "Hela processen tar normalt 1–3 timmar beroende på avstånd. Du kan följa statusen i realtid.",
        },
        {
          heading: "Vi kör från alla Elgiganten-butiker",
          content:
            "Vi hämtar från alla Elgiganten-butiker i Stockholm och Göteborg. I Stockholm: Kungens Kurva, Barkarby, Nacka Forum, Täby, Solna, Farsta, Södermalm, Fridhemsplan och alla övriga. I Göteborg: Nordstan, Frölunda Torg, Sisjön, Bäckebol och alla övriga butiker. Ange bara butikens adress vid bokning så sköter vi resten. Vi hämtar även varor beställda online med hämta-i-butik, samt varor från Elgigantens lagerförsäljning och kampanjer.",
        },
        {
          heading: "Varför boka Elgiganten-leverans med Ungdomskraft?",
          content:
            "Jämfört med Elgigantens egen hemleverans erbjuder vi ofta snabbare leverans, alltid inbärning till rätt plats och ett transparent pris utan dolda tillägg per våning. Dessutom gör du dubbel nytta: du får hem ditt köp smidigt och du bidrar till att unga människor kommer in på arbetsmarknaden. Det som gör Ungdomskraft unikt är att vi anställer unga människor och ger dem riktig arbetslivserfarenhet. Varje uppdrag bemannas av motiverade ungdomar som lär sig allt från kundservice och logistik till varsam hantering av dyr elektronik. Vi har funnits sedan 2014 och har genomfört tiotusentals transporter i Stockholm och Göteborg. Allt är försäkrat, vi har egna skåpbilar i flera storlekar och du ser alltid priset innan du bokar. Inga dolda avgifter, inga överraskningar.\n\nHemleverans från Elgiganten ger inte rätt till RUT-avdrag, men flera av våra andra tjänster gör det – exempelvis flytt, bärhjälp och möbelmontering med 50 % avdrag på arbetskostnaden. Kombinera gärna tjänster i en och samma bokning.",
        },
      ],
    },

    faq: [
      {
        question: "Vad kostar hemleverans från Elgiganten?",
        answer:
          "Priset beror på vad du köpt och avståndet. En TV eller mindre elektronik kostar från 595 kr. Vitvaror som kylskåp eller tvättmaskin kostar från 995–1 495 kr. Du ser det exakta priset när du bokar online.",
      },
      {
        question: "Kan jag få RUT-avdrag på hemleverans från Elgiganten?",
        answer:
          "Hemleverans från Elgiganten ger i sig inte rätt till RUT-avdrag. RUT gäller hushållsnära tjänster som utförs i din bostad, exempelvis flytt, bärhjälp och möbelmontering. Ungdomskraft erbjuder flera RUT-berättigade tjänster med 50 % avdrag på arbetskostnaden – kombinera enkelt i en bokning via vår hemsida.",
      },
      {
        question: "Ingår skåpbil i Elgiganten-leveransen?",
        answer:
          "Ja, skåpbil ingår alltid. Vi väljer rätt storlek utifrån ditt köp – du behöver inte hyra bil, köra eller tanka.",
      },
      {
        question: "Ingår inbärning i priset?",
        answer:
          "Ja, inbärning ingår alltid. Vi bär ditt köp genom trapphus, hiss och dörrar och ställer på plats hemma hos dig – oavsett våning. Inga extra avgifter per våningsplan.",
      },
      {
        question: "Vilka Elgiganten-butiker hämtar ni från?",
        answer:
          "Vi hämtar från alla Elgiganten-butiker i Stockholm och Göteborg: Kungens Kurva, Barkarby, Nacka Forum, Täby, Solna, Farsta, Södermalm, Nordstan, Frölunda Torg, Sisjön, Bäckebol och alla övriga. Ange bara butikens adress vid bokning.",
      },
      {
        question: "Kan ni hämta varor jag beställt online med hämta-i-butik?",
        answer:
          "Ja, vi hämtar allt som finns redo att hämtas i butiken – oavsett om du köpt på plats eller beställt online. Se till att du fått bekräftelse om att varan finns på plats innan du bokar.",
      },
      {
        question: "Hur transporterar ni TV-apparater?",
        answer:
          "Vi transporterar alltid TV-apparater stående i originalförpackningen – aldrig liggande. Vi säkrar med spännband och skyddar med filtar. Det gäller alla storlekar från 55 till 85 tum och större.",
      },
      {
        question: "Kan ni bära ett kylskåp upp i trappor?",
        answer:
          "Ja, vi bär vitvaror till vilken våning som helst – med eller utan hiss. Våra ungdomar är vana vid att bära 70–90 kg genom trånga trapphus. Vi hanterar kylskåp, tvättmaskiner, torktumlare och alla andra tunga vitvaror.",
      },
      {
        question: "Kan ni koppla in vitvaror från Elgiganten?",
        answer:
          "Nej, vi ansvarar för transport och inbärning – inte för inkoppling av vatten, el eller avlopp. Vi ställer vitvaran på exakt rätt plats så att en elektriker eller rörmokare enkelt kan koppla in den.",
      },
      {
        question: "Skyddar ni elektroniken under transporten?",
        answer:
          "Ja, vi använder möbelfiltar, spännband och extra skydd för ömtålig elektronik. TV-apparater transporteras alltid stående. Allt är försäkrat – om något skadas ersätter vi det.",
      },
      {
        question: "Hur snabbt kan ni leverera från Elgiganten?",
        answer:
          "Vi erbjuder ofta hemleverans med kort varsel – ofta redan samma dag – inom några timmar. Boka online och se tillgängliga tider direkt.",
      },
      {
        question: "Erbjuder ni Elgiganten-leverans på helger?",
        answer:
          "Ja, vi erbjuder hemleverans på helger och vissa kvällar. Kontrollera att Elgiganten-butiken har öppet den tid du bokar – vi kan inte hämta om butiken är stängd.",
      },
      {
        question: "Kan ni hämta från Elgiganten och en annan butik i samma tur?",
        answer:
          "Ja, vi kan hämta från flera butiker under samma körning om de ligger i rimligt avstånd. Beskriv alla hämtpunkter när du bokar – till exempel Elgiganten och IKEA i samma tur.",
      },
      {
        question: "Behöver jag vara med på Elgiganten?",
        answer:
          "Nej, du behöver inte vara med i butiken. Se till att varan är betald och redo att hämtas – vi sköter resten. Du behöver bara vara hemma för att ta emot leveransen.",
      },
      {
        question: "Är hemleveransen försäkrad?",
        answer:
          "Ja, alla våra transporter är helförsäkrade. Om något skadas under lastning, transport eller inbärning ersätter vi det. Du kan boka med trygghet.",
      },
      {
        question:
          "I vilka kommuner erbjuder ni Elgiganten-leverans?",
        answer:
          "Vi erbjuder Elgiganten-leverans i hela Stockholmsområdet: Botkyrka, Danderyd, Ekerö, Haninge, Huddinge, Järfälla, Lidingö, Nacka, Norrtälje, Nykvarn, Nynäshamn, Salem, Sigtuna, Sollentuna, Solna, Stockholm, Sundbyberg, Södertälje, Tyresö, Täby, Upplands Väsby, Upplands-Bro, Vallentuna, Vaxholm, Värmdö och Österåker. Vi finns även i Göteborgsområdet: Ale, Alingsås, Göteborg, Härryda, Kungälv, Kungsbacka, Lerum, Lilla Edet, Mölndal, Partille, Stenungsund, Tjörn och Öckerö.",
      },
      {
        question: "Varför välja Ungdomskraft för Elgiganten-leverans?",
        answer:
          "Hos oss ingår alltid skåpbil, bärhjälp och inbärning till rätt plats – oavsett våning. Du ser priset direkt vid bokning, vi kan ofta köra samma dag och allt är försäkrat.",
      },
    ],

    priceHint: PACKAGE_PRICE_HINT_SV.bud,

    pricingRows: getPackagePricingRows("bud", "sv"),

    dynamicHeadings: {
      threeSteps: "Elgiganten-leverans i 3 enkla steg",
      step2Text:
        "Våra ungdomar hämtar ditt köp på Elgiganten, skyddar med filtar och lastar skåpbilen. Sedan kör vi hem till dig.",
      step3Title: "Allt på plats",
      step3Desc:
        "Din nya TV, vitvara eller dator står hemma hos dig – inburen och uppställd. Klart.",
      ctaHeading: "Boka Elgiganten-leverans idag",
      ctaText:
        "Ange Elgiganten-butikens adress och din hemadress, beskriv vad du köpt och välj en tid. Du ser priset direkt.",
    },

    en: {
      slug: "elgiganten-delivery",
      meta: {
        title:
          "Elgiganten Delivery – We Pick Up Your Purchase And Deliver Home | Ungdomskraft",
        description:
          "Bought a TV, washing machine or fridge at Elgiganten that won't fit in your car? We pick up at the store and deliver home with carrying included. Book Elgiganten delivery today.",
        h1: "Elgiganten delivery",
      },
      hero: {
        heading: "We pick up your purchase at Elgiganten",
        intro:
          "Bought a 75-inch TV, a new fridge or a washing machine at Elgiganten — but it won't fit in your car? We solve it quickly and smoothly. Ungdomskraft picks up your purchase directly at Elgiganten, loads the van and delivers to your home — with carrying and placement included. We collect from all Elgiganten stores in Stockholm and Gothenburg.",
      },
      featureCards: {
        sinceText: "Elgiganten delivery since 2014",
        bookYourText: "Book your Elgiganten delivery",
        bookDescText:
          "Enter the Elgiganten store address and your home address, describe what you bought and choose a time. You see the price instantly.",
        vehicleTitleText: "Vans perfect for Elgiganten deliveries",
        vehicleDescText:
          "We arrive with the right size van — whether you're picking up a laptop or a complete kitchen with appliances. No need to rent a trailer, drive yourself or strain your back.",
      },
      itemsSection: {
        heading: "Items we pick up at Elgiganten",
        items: [
          {
            name: "TVs",
            description:
              "55-, 65-, 75- and 85-inch TVs — careful handling and safe transport home",
            image: "/tv.webp",
          },
          {
            name: "Appliances",
            description:
              "Fridges, freezers, washing machines, dryers, dishwashers and ovens from Elgiganten",
            image: "/washer.webp",
          },
          {
            name: "Computers & gaming",
            description:
              "Desktop computers, gaming PCs, monitors and peripherals",
            image: "/patio-furniture.webp",
          },
          {
            name: "Audio & video",
            description:
              "Soundbars, surround systems, projectors and home cinema equipment",
            image: "/painting.webp",
          },
          {
            name: "Home appliances",
            description:
              "Robot vacuums, air purifiers, coffee machines and other large household products",
            image: "/box.webp",
          },
          {
            name: "Other bulky items",
            description:
              "E-bikes, treadmills, cross trainers and other bulky electronics from Elgiganten",
            image: "/sofa.webp",
          },
        ],
      },
      seoContent: {
        mainHeading:
          "Delivery from Elgiganten — we pick up, drive and carry inside for you",
        mainText:
          "Elgiganten is Sweden's largest electronics retailer with stores across the Stockholm and Gothenburg regions. The range extends from small accessories to large appliances and TVs that require a van to get home. The problem many customers recognise: you find the perfect TV, the new fridge or washing machine in the store — but it won't fit in your car. Elgiganten's own delivery often costs 400–1 000 SEK extra, can take up to a week to book and rarely includes carrying past the front door. If you live on the third floor without a lift, there are additional charges per floor. That's where Ungdomskraft becomes a smarter alternative. We pick up your purchase directly from the Elgiganten store, load the van carefully and drive to your home. Carrying to the right spot in your home is always included — regardless of floor, narrow stairwells or lack of lift. You pay one price, you see it instantly when booking and there are no hidden fees. We collect from all Elgiganten stores in Stockholm and Gothenburg: Kungens Kurva, Barkarby, Nacka Forum, Täby, Solna, Farsta, Södermalm, Nordstan, Frölunda Torg, Sisjön, Bäckebol and all others. Our crew handle electronics and appliances with the same care as the store's own delivery team — but often faster and with more flexible times. We know that a 75-inch TV must stand upright during transport, that a fridge shouldn't be tilted more than necessary and that a washing machine needs transit bolts. Everything is protected with blankets and straps, and we carry with the care that expensive electronics demand. Many of our customers combine Elgiganten purchases with pickups from other stores in the same trip. Need to pick up a TV at Elgiganten and a piece of furniture at IKEA? We do it in a single run.",
        preparationHeading: "Prepare for your Elgiganten delivery",
        preparationText:
          "Make sure the purchase is paid for and ready to collect at the store. If you ordered online with click-and-collect, check that you've received an SMS or email confirming the item is available. Have your home address and any door codes ready. Measure doorways and stairwells if it involves large appliances like fridges, washing machines or very large TVs. Check that the van can park near the entrance at your home. If you'd like us to remove packaging after carrying in, mention it when booking.",
        additionalSections: [
          {
            heading: "TV delivery from Elgiganten",
            content:
              "TVs are one of the most common purchases we pick up at Elgiganten. A 65-inch TV in its original packaging is approximately 160 cm wide and weighs 25–40 kg — it won't fit in most cars, and balancing it on the back seat risks damaging the screen. With Ungdomskraft's delivery we pick up your TV directly at Elgiganten. We load it upright in the van (never flat — that damages the panel), secure with straps and blankets and drive to your home. We carry inside through stairwells and doorways and place the TV where you want it. Need help lifting it onto the wall mount or TV stand? Describe it when booking. We pick up all sizes — from 55-inch to 85-inch and larger. Whether you bought a Samsung, LG, Sony or any other brand, we handle it with the care that expensive electronics demand.",
          },
          {
            heading: "Appliance delivery from Elgiganten",
            content:
              "Appliances from Elgiganten — fridges, freezers, washing machines, dryers, dishwashers and ovens — often weigh 60–90 kg and require at least two people to carry. Elgiganten's own delivery sometimes takes up to a week and charges extra per floor without a lift. With Ungdomskraft we pick up your appliance at the store, protect it during transport and carry inside to the exact right place in your home — regardless of floor. We have experience carrying fridges through narrow stairwells, washing machines through low basement doors and dryers up three flights without a lift. We make sure transit bolts stay in place during the drive and that the appliance stands stable during loading. Note that we are responsible for transport and carrying — connecting water, electricity and plumbing needs to be arranged separately, for example with an electrician or plumber.",
          },
          {
            heading: "Computer and gaming equipment delivery",
            content:
              "Gaming PCs, large monitors and home cinema equipment from Elgiganten can be both heavy and bulky. A desktop gaming PC with accessories often weighs 20–30 kg in its original packaging, and a 34-inch ultrawide monitor needs careful handling. We pick up everything at Elgiganten, package securely in the van and deliver to your home. We carry inside and place — you focus on unpacking and plugging in. We also handle larger purchases like treadmills, e-bikes and cross trainers from Elgiganten without issue. These often weigh 40–80 kg and require a van and at least two carriers — a perfect job for our experienced crew.",
          },
          {
            heading: "How Elgiganten delivery works with Ungdomskraft",
            listItems: [
              {
                label: "Book online",
                text: "Enter the Elgiganten store address and your home address. Describe what you bought and choose a time. You see the price instantly.",
              },
              {
                label: "We pick up at Elgiganten",
                text: "Our team arrive with a van. We collect your purchase at the store, protect with blankets and straps and load carefully.",
              },
              {
                label: "We deliver home",
                text: "We drive to your home, carry inside through stairwells and doorways and place your purchase where you want it.",
              },
              {
                label: "Done",
                text: "Your new TV, appliance or computer is at home — carried in and placed. Without lifting a finger.",
              },
            ],
            afterContent:
              "The whole process typically takes 1–3 hours depending on distance. You can follow the status in real time.",
          },
          {
            heading: "We collect from all Elgiganten stores",
            content:
              "We pick up from all Elgiganten stores in Stockholm and Gothenburg. In Stockholm: Kungens Kurva, Barkarby, Nacka Forum, Täby, Solna, Farsta, Södermalm, Fridhemsplan and all others. In Gothenburg: Nordstan, Frölunda Torg, Sisjön, Bäckebol and all other locations. Just enter the store address when booking and we handle the rest. We also pick up items ordered online with click-and-collect, as well as items from Elgiganten's clearance sales and campaigns.",
          },
          {
            heading: "Why book Elgiganten delivery with Ungdomskraft?",
            content:
              "Compared to Elgiganten's own delivery, we often offer faster service, always include carrying to the right spot and provide a transparent price with no hidden per-floor surcharges. Plus you make a double impact: you get your purchase home smoothly and you help young people enter the workforce. What makes Ungdomskraft unique is that we employ young people and give them real work experience. Every job is staffed by motivated young workers who learn everything from customer service and logistics to careful handling of expensive electronics. We've been around since 2014 and have completed tens of thousands of transports across Stockholm and Gothenburg. Everything is insured, we have our own vans in multiple sizes and you always see the price before you book. No hidden fees, no surprises.\n\nElgiganten delivery does not qualify for the Swedish RUT tax deduction, but several of our other services do – including moving help, carrying help and furniture assembly with a 50% deduction on the labour cost. Feel free to combine services in a single booking.",
          },
        ],
      },
      faq: [
        {
          question: "How much does Elgiganten delivery cost?",
          answer:
            "The price depends on what you bought and the distance. A TV or smaller electronics starts at 595 SEK. Appliances like fridges or washing machines cost from 749–1 495 SEK. You see the exact price when you book online.",
        },
        {
          question: "Can I get a RUT tax deduction on Elgiganten delivery?",
          answer:
            "Elgiganten delivery does not in itself qualify for the Swedish RUT tax deduction. RUT applies to household services carried out in your home, such as moving help, carrying help and furniture assembly. Ungdomskraft offers several RUT-eligible services with a 50% deduction on the labour cost – easily combine them in one booking via our website.",
        },
        {
          question: "Is a van included in the Elgiganten delivery?",
          answer:
            "Yes, a van is always included. We choose the right size based on your purchase — you don't need to rent a vehicle.",
        },
        {
          question: "Is carrying inside included?",
          answer:
            "Yes, carrying inside is always included. We carry your purchase through stairwells, lifts and doorways and place it at your home — regardless of floor. No extra charges per floor.",
        },
        {
          question: "Which Elgiganten stores do you pick up from?",
          answer:
            "We pick up from all Elgiganten stores in Stockholm and Gothenburg: Kungens Kurva, Barkarby, Nacka Forum, Täby, Solna, Farsta, Södermalm, Nordstan, Frölunda Torg, Sisjön, Bäckebol and all others. Just enter the store address when booking.",
        },
        {
          question: "Can you pick up items I ordered online with click-and-collect?",
          answer:
            "Yes, we pick up everything that's ready to collect at the store — whether you bought in store or ordered online. Make sure you've received confirmation that the item is available before booking.",
        },
        {
          question: "How do you transport TVs?",
          answer:
            "We always transport TVs standing upright in their original packaging — never flat. We secure with straps and protect with blankets. This applies to all sizes from 55 to 85 inches and larger.",
        },
        {
          question: "Can you carry a fridge up stairs?",
          answer:
            "Yes, we carry appliances to any floor — with or without a lift. Our crew are used to carrying 70–90 kg through narrow stairwells. We handle fridges, washing machines, dryers and all other heavy appliances.",
        },
        {
          question: "Can you connect appliances from Elgiganten?",
          answer:
            "No, we are responsible for transport and carrying — not for connecting water, electricity or plumbing. We place the appliance in the exact right spot so an electrician or plumber can easily connect it.",
        },
        {
          question: "Do you protect electronics during transport?",
          answer:
            "Yes, we use furniture blankets, straps and extra protection for fragile electronics. TVs are always transported upright. Everything is insured — if something is damaged we replace it.",
        },
        {
          question: "How quickly can you deliver from Elgiganten?",
          answer:
            "We often offer delivery at short notice — often the same day — within a few hours. Book online and see available times instantly.",
        },
        {
          question: "Is Elgiganten delivery available on weekends?",
          answer:
            "Yes, we offer delivery on weekends and some evenings. Check that the Elgiganten store is open at your chosen time — we can't pick up if the store is closed.",
        },
        {
          question: "Can you pick up from Elgiganten and another store in the same trip?",
          answer:
            "Yes, we can pick up from several stores during the same run if they're within a reasonable distance. Describe all pickup points when you book — for example Elgiganten and IKEA in the same trip.",
        },
        {
          question: "Do I need to be at Elgiganten?",
          answer:
            "No, you don't need to be at the store. Make sure the item is paid for and ready to collect — we handle the rest. You just need to be home to receive the delivery.",
        },
        {
          question: "Is the delivery insured?",
          answer:
            "Yes, all our transports are fully insured. If anything is damaged during loading, transport or carrying we replace it. You can book with confidence.",
        },
        {
          question: "In which areas do you offer Elgiganten delivery?",
          answer:
            "We offer Elgiganten delivery across the entire Stockholm region: Botkyrka, Danderyd, Ekerö, Haninge, Huddinge, Järfälla, Lidingö, Nacka, Norrtälje, Nykvarn, Nynäshamn, Salem, Sigtuna, Sollentuna, Solna, Stockholm, Sundbyberg, Södertälje, Tyresö, Täby, Upplands Väsby, Upplands-Bro, Vallentuna, Vaxholm, Värmdö and Österåker. We're also in the Gothenburg region: Ale, Alingsås, Göteborg, Härryda, Kungälv, Kungsbacka, Lerum, Lilla Edet, Mölndal, Partille, Stenungsund, Tjörn and Öckerö.",
        },
        {
          question: "Why choose Ungdomskraft for Elgiganten delivery?",
          answer:
            "We always include a van, carrying help and placement in the right spot — regardless of floor. You see the price instantly when booking, we can often deliver the same day and everything is insured.",
        },
      ],
      priceHint: PACKAGE_PRICE_HINT_EN.bud,
      pricingRows: getPackagePricingRows("bud", "en"),
      dynamicHeadings: {
        threeSteps: "Elgiganten delivery in 3 easy steps",
        step2Text:
          "Our crew pick up your purchase at Elgiganten, protect with blankets and load the van. Then we drive home to you.",
        step3Title: "All in place",
        step3Desc:
          "Your new TV, appliance or computer is at home — carried in and placed. Done.",
        ctaHeading: "Book Elgiganten delivery today",
        ctaText:
          "Enter the Elgiganten store address and your home address, describe what you bought and choose a time. You see the price instantly.",
      },
    },
  },
  {
    // Hemleverans Power — config för /hemleverans-power
    // Vinkel: Hämta köp från Power som inte får plats i bilen – TV, vitvaror, datorer, hushållsmaskiner.
    // Unik vinkel vs Elgiganten: Power har ofta lägre priser och aggressiva kampanjer –
    // kunderna vill ha hem sina fynd snabbt och billigt.
    slug: "hemleverans-power",
    serviceCategory: "hemleverans",

    meta: {
      title:
        "Hemleverans Power – Vi Hämtar Ditt Köp Och Kör Hem | Ungdomskraft",
      description:
        "Köpt TV, tvättmaskin eller kylskåp på Power som inte får plats i bilen? Vi hämtar i butiken och levererar hem med inbärning. Försäkrat. Boka Power-leverans idag.",
      h1: "Hemleverans Power",
    },

    hero: {
      heading: "Vi hämtar ditt köp på Power",
      intro:
        "Hittat ett bra pris på en ny TV, tvättmaskin eller kylskåp på Power – men den får inte plats i bilen? Vi löser det snabbt och smidigt. Ungdomskraft hämtar ditt köp direkt på Power, lastar skåpbilen och levererar hem till dig – med inbärning och uppställning på plats. Vi kör från alla Power-butiker i Stockholm och Göteborg.",
      heroImage: "/hero-transport-elektronik.png",
    },

    featureCards: {
      sinceText: "Power-leverans sedan 2014",
      storyText:
        "Sedan 2014 har Ungdomskraft hjälpt tusentals kunder att få hem sina Power-köp – från stora TV-apparater och tunga vitvaror till datorer och hushållsmaskiner. Våra ungdomar vet hur man hanterar ömtålig elektronik och tunga vitvaror i trånga trapphus.",
      bookYourText: "Boka din Power-leverans",
      bookDescText:
        "Ange Power-butikens adress och din hemadress, beskriv vad som ska hämtas och välj en tid. Du ser priset direkt.",
      vehicleTitleText: "Skåpbilar perfekta för Power-leveranser",
      vehicleDescText:
        "Vi kommer med rätt storlek på skåpbil – oavsett om du hämtar en laptop eller ett komplett kök med vitvaror. Du slipper hyra släp, köra själv eller slita ryggen.",
    },

    itemsSection: {
      heading: "Saker vi hämtar på Power",
      items: [
        {
          name: "TV-apparater",
          description:
            "55-, 65-, 75- och 85-tums TV-apparater – varsam hantering och säker transport hem",
          image: "/tv.webp",
        },
        {
          name: "Vitvaror",
          description:
            "Kylskåp, frysar, tvättmaskiner, torktumlare, diskmaskiner och spisar från Power",
          image: "/washer.webp",
        },
        {
          name: "Datorer & gaming",
          description:
            "Stationära datorer, gaming-datorer, bildskärmar och kringutrustning",
          image: "/patio-furniture.webp",
        },
        {
          name: "Ljud & bild",
          description:
            "Soundbars, surroundsystem, projektorer och hemmabio-utrustning",
          image: "/painting.webp",
        },
        {
          name: "Hushållsmaskiner",
          description:
            "Robotdammsugare, luftrenare, kaffemaskiner och andra större hushållsprodukter",
          image: "/box.webp",
        },
        {
          name: "Övrigt skrymmande",
          description:
            "Elcyklar, löpband, crosstrainers och annan skrymmande elektronik från Power",
          image: "/sofa.webp",
        },
      ],
    },

    seoContent: {
      mainHeading:
        "Hemleverans från Power – vi hämtar, kör och bär in åt dig",
      mainText:
        "Power är en av Sveriges största elektronikkedjor och har byggt sitt rykte på aggressiva priser och breda kampanjer. Butikerna i Stockholm och Göteborg erbjuder allt från TV-apparater och vitvaror till datorer, gaming-utrustning och hushållsmaskiner. Problemet som många Power-kunder känner igen: du slår till på ett kampanjpris för ett nytt kylskåp, en 75-tums TV eller en tvättmaskin – men den får inte plats i bilen. Powers egen hemleverans kostar ofta flera hundra kronor extra, kan ta upp till en vecka att boka och inkluderar sällan inbärning förbi ytterdörren. Bor du på tredje våningen utan hiss tillkommer ytterligare avgifter per våningsplan. Ungdomskraft erbjuder ett snabbare och smartare alternativ. Vi hämtar ditt köp direkt i Power-butiken, lastar skåpbilen varsamt och kör hem till dig. Inbärning till rätt plats i hemmet ingår alltid – oavsett våning, trångt trapphus eller avsaknad av hiss. Du betalar ett pris, du ser det direkt vid bokning och det tillkommer inga dolda avgifter. Vi kör från alla Power-butiker i Stockholm och Göteborg: Barkarby, Kungens Kurva, Nacka, Täby, Kista, Farsta, Nordstan, Frölunda, Bäckebol, Sisjön och alla övriga. Våra ungdomar hanterar elektronik och vitvaror med samma omsorg som butikens egna leveransteam – men ofta snabbare och med mer flexibla tider. Vi vet att en stor TV måste stå upprätt under transporten, att ett kylskåp inte ska lutas mer än nödvändigt och att en tvättmaskin behöver transportspärrar. Allt skyddas med filtar och spännband, och vi bär med den försiktighet som dyr elektronik kräver. Många av våra kunder passar på att kombinera Power-köp med hämtning från andra butiker i samma tur. Ska du hämta en TV på Power och möbler på JYSK? Vi gör det i en och samma körning – du sparar både tid och pengar.",
      preparationHeading: "Förbered inför din Power-leverans",
      preparationText:
        "Se till att köpet är betalt och redo att hämtas i butiken. Om du beställt online med hämta-i-butik – kontrollera att du fått sms eller mejl om att varan finns på plats. Ha din hemadress och eventuell portkod redo. Mät dörröppningar och trapphus om det rör sig om stora vitvaror som kylskåp, tvättmaskiner eller mycket stora TV-apparater. Kolla att skåpbilen kan parkera nära porten hemma hos dig. Om du vill att vi tar bort emballage efter inbärning – ange det vid bokning.",
      additionalSections: [
        {
          heading: "Hemleverans av TV från Power",
          content:
            "TV-apparater är ett av de vanligaste köpen vi hämtar på Power. Power har ofta kampanjpriser på stora TV-apparater, vilket gör att många kunder slår till på en 65- eller 75-tums TV – för att sen inse att den inte får plats i bilen. En 65-tums TV i originalförpackning är cirka 160 cm bred och väger 25–40 kg. Med Ungdomskrafts hemleverans hämtar vi din TV direkt på Power. Vi lastar den stående i skåpbilen (aldrig liggande – det skadar panelen), säkrar med spännband och filtar och kör hem till dig. Vi bär in genom trapphus och dörrar och ställer TV:n på plats. Vi hämtar alla storlekar och märken – Samsung, LG, Sony, Philips, TCL, Hisense och alla andra. Oavsett om du köpt på kampanj, i lagerförsäljning eller till ordinarie pris hanterar vi din TV med den försiktighet som dyr elektronik kräver.",
        },
        {
          heading: "Hemleverans av vitvaror från Power",
          content:
            "Vitvaror från Power – kylskåp, frysar, tvättmaskiner, torktumlare, diskmaskiner och spisar – väger ofta 60–90 kg och kräver minst två personer för att bära. Powers egen leverans tar ibland upp till en vecka och kostar extra för varje våningsplan utan hiss. Med Ungdomskraft hämtar vi din vitvara i butiken, skyddar den under transporten och bär in till exakt rätt plats i hemmet – oavsett våning. Vi har erfarenhet av att bära kylskåp genom smala trapphus, tvättmaskiner genom låga källardörrar och torktumlare upp tre trappor utan hiss. Vi ser till att transportspärrarna sitter kvar under körningen och att vitvaran står stabilt under lastning. Tänk på att vi ansvarar för transport och inbärning – inkoppling av vatten, el och avlopp behöver du ordna separat.",
        },
        {
          heading: "Hemleverans av datorer och gaming-utrustning från Power",
          content:
            "Power har ett brett sortiment av datorer, gaming-utrustning och tillbehör. Gaming-datorer, stora bildskärmar och hemmabio-utrustning kan vara både tunga och skrymmande. En stationär gaming-dator med tillbehör väger ofta 20–30 kg i originalförpackning, och en ultrawide-skärm behöver varsam hantering. Vi hämtar allt på Power, paketerar säkert i skåpbilen och levererar hem till dig. Vi bär in och ställer på plats – du fokuserar på att packa upp och plugga in. Även större köp som löpband, elcyklar och crosstrainers från Power hämtar vi utan problem.",
        },
        {
          heading: "Så fungerar Power-leverans med Ungdomskraft",
          listItems: [
            {
              label: "Boka online",
              text: "Ange Power-butikens adress och din hemadress. Beskriv vad du köpt och välj en tid. Du ser priset direkt.",
            },
            {
              label: "Vi hämtar på Power",
              text: "Vårt team kommer med skåpbil. Vi hämtar ditt köp i butiken, skyddar med filtar och spännband och lastar varsamt.",
            },
            {
              label: "Vi levererar hem",
              text: "Vi kör hem till dig, bär in genom trapphus och dörrar och ställer ditt köp på plats.",
            },
            {
              label: "Klart",
              text: "Din nya TV, vitvara eller dator står hemma hos dig – inburen och uppställd. Utan att du lyfte ett finger.",
            },
          ],
          afterContent:
            "Hela processen tar normalt 1–3 timmar beroende på avstånd. Du kan följa statusen i realtid.",
        },
        {
          heading: "Vi kör från alla Power-butiker",
          content:
            "Vi hämtar från alla Power-butiker i Stockholm och Göteborg. I Stockholm: Barkarby, Kungens Kurva, Nacka, Täby, Kista, Farsta och alla övriga. I Göteborg: Nordstan, Frölunda, Bäckebol, Sisjön och alla övriga butiker. Ange bara butikens adress vid bokning så sköter vi resten. Vi hämtar även varor beställda online med hämta-i-butik, samt varor från Powers kampanjer och reaperioder.",
        },
        {
          heading: "Varför boka Power-leverans med Ungdomskraft?",
          content:
            "Jämfört med Powers egen hemleverans erbjuder vi ofta snabbare leverans, alltid inbärning till rätt plats och ett transparent pris utan dolda tillägg per våning. Dessutom gör du dubbel nytta: du får hem ditt köp smidigt och du bidrar till att unga människor kommer in på arbetsmarknaden. Det som gör Ungdomskraft unikt är att vi anställer unga människor och ger dem riktig arbetslivserfarenhet. Varje uppdrag bemannas av motiverade ungdomar som lär sig allt från kundservice och logistik till varsam hantering av dyr elektronik. Vi har funnits sedan 2014 och har genomfört tiotusentals transporter i Stockholm och Göteborg. Allt är försäkrat, vi har egna skåpbilar i flera storlekar och du ser alltid priset innan du bokar. Inga dolda avgifter, inga överraskningar.\n\nHemleverans från Power ger inte rätt till RUT-avdrag, men flera av våra andra tjänster gör det – exempelvis flytt, bärhjälp och möbelmontering med 50 % avdrag på arbetskostnaden. Kombinera gärna tjänster i en och samma bokning.",
        },
      ],
    },

    faq: [
      {
        question: "Vad kostar hemleverans från Power?",
        answer:
          "Priset beror på vad du köpt och avståndet. En TV eller mindre elektronik kostar från 595 kr. Vitvaror som kylskåp eller tvättmaskin kostar från 995–1 495 kr. Du ser det exakta priset när du bokar online.",
      },
      {
        question: "Kan jag få RUT-avdrag på hemleverans från Power?",
        answer:
          "Hemleverans från Power ger i sig inte rätt till RUT-avdrag. RUT gäller hushållsnära tjänster som utförs i din bostad, exempelvis flytt, bärhjälp och möbelmontering. Ungdomskraft erbjuder flera RUT-berättigade tjänster med 50 % avdrag på arbetskostnaden – kombinera enkelt i en bokning via vår hemsida.",
      },
      {
        question: "Ingår skåpbil i Power-leveransen?",
        answer:
          "Ja, skåpbil ingår alltid. Vi väljer rätt storlek utifrån ditt köp – du behöver inte hyra bil, köra eller tanka.",
      },
      {
        question: "Ingår inbärning i priset?",
        answer:
          "Ja, inbärning ingår alltid. Vi bär ditt köp genom trapphus, hiss och dörrar och ställer på plats hemma hos dig – oavsett våning. Inga extra avgifter per våningsplan.",
      },
      {
        question: "Vilka Power-butiker hämtar ni från?",
        answer:
          "Vi hämtar från alla Power-butiker i Stockholm och Göteborg: Barkarby, Kungens Kurva, Nacka, Täby, Kista, Farsta, Nordstan, Frölunda, Bäckebol, Sisjön och alla övriga. Ange bara butikens adress vid bokning.",
      },
      {
        question: "Kan ni hämta varor jag beställt online med hämta-i-butik?",
        answer:
          "Ja, vi hämtar allt som finns redo att hämtas i butiken – oavsett om du köpt på plats eller beställt online. Se till att du fått bekräftelse om att varan finns på plats innan du bokar.",
      },
      {
        question: "Hur transporterar ni TV-apparater från Power?",
        answer:
          "Vi transporterar alltid TV-apparater stående i originalförpackningen – aldrig liggande. Vi säkrar med spännband och skyddar med filtar. Det gäller alla storlekar från 55 till 85 tum och större.",
      },
      {
        question: "Kan ni bära ett kylskåp upp i trappor?",
        answer:
          "Ja, vi bär vitvaror till vilken våning som helst – med eller utan hiss. Våra ungdomar är vana vid att bära 70–90 kg genom trånga trapphus. Vi hanterar kylskåp, tvättmaskiner, torktumlare och alla andra tunga vitvaror.",
      },
      {
        question: "Kan ni koppla in vitvaror från Power?",
        answer:
          "Nej, vi ansvarar för transport och inbärning – inte för inkoppling av vatten, el eller avlopp. Vi ställer vitvaran på exakt rätt plats så att en elektriker eller rörmokare enkelt kan koppla in den.",
      },
      {
        question: "Skyddar ni elektroniken under transporten?",
        answer:
          "Ja, vi använder möbelfiltar, spännband och extra skydd för ömtålig elektronik. TV-apparater transporteras alltid stående. Allt är försäkrat – om något skadas ersätter vi det.",
      },
      {
        question: "Hur snabbt kan ni leverera från Power?",
        answer:
          "Vi erbjuder ofta hemleverans med kort varsel – ofta redan samma dag – inom några timmar. Boka online och se tillgängliga tider direkt.",
      },
      {
        question: "Erbjuder ni Power-leverans på helger?",
        answer:
          "Ja, vi erbjuder hemleverans på helger och vissa kvällar. Kontrollera att Power-butiken har öppet den tid du bokar – vi kan inte hämta om butiken är stängd.",
      },
      {
        question: "Kan ni hämta från Power och en annan butik i samma tur?",
        answer:
          "Ja, vi kan hämta från flera butiker under samma körning om de ligger i rimligt avstånd. Beskriv alla hämtpunkter när du bokar – till exempel Power och IKEA i samma tur.",
      },
      {
        question: "Behöver jag vara med på Power?",
        answer:
          "Nej, du behöver inte vara med i butiken. Se till att varan är betald och redo att hämtas – vi sköter resten. Du behöver bara vara hemma för att ta emot leveransen.",
      },
      {
        question: "Är hemleveransen försäkrad?",
        answer:
          "Ja, alla våra transporter är helförsäkrade. Om något skadas under lastning, transport eller inbärning ersätter vi det. Du kan boka med trygghet.",
      },
      {
        question:
          "I vilka kommuner erbjuder ni Power-leverans?",
        answer:
          "Vi erbjuder Power-leverans i hela Stockholmsområdet: Botkyrka, Danderyd, Ekerö, Haninge, Huddinge, Järfälla, Lidingö, Nacka, Norrtälje, Nykvarn, Nynäshamn, Salem, Sigtuna, Sollentuna, Solna, Stockholm, Sundbyberg, Södertälje, Tyresö, Täby, Upplands Väsby, Upplands-Bro, Vallentuna, Vaxholm, Värmdö och Österåker. Vi finns även i Göteborgsområdet: Ale, Alingsås, Göteborg, Härryda, Kungälv, Kungsbacka, Lerum, Lilla Edet, Mölndal, Partille, Stenungsund, Tjörn och Öckerö.",
      },
      {
        question: "Varför välja Ungdomskraft för Power-leverans?",
        answer:
          "Hos oss ingår alltid skåpbil, bärhjälp och inbärning till rätt plats – oavsett våning. Du ser priset direkt vid bokning, vi kan ofta köra samma dag och allt är försäkrat.",
      },
    ],

    priceHint: PACKAGE_PRICE_HINT_SV.bud,

    pricingRows: getPackagePricingRows("bud", "sv"),

    dynamicHeadings: {
      threeSteps: "Power-leverans i 3 enkla steg",
      step2Text:
        "Våra ungdomar hämtar ditt köp på Power, skyddar med filtar och lastar skåpbilen. Sedan kör vi hem till dig.",
      step3Title: "Allt på plats",
      step3Desc:
        "Din nya TV, vitvara eller dator står hemma hos dig – inburen och uppställd. Klart.",
      ctaHeading: "Boka Power-leverans idag",
      ctaText:
        "Ange Power-butikens adress och din hemadress, beskriv vad du köpt och välj en tid. Du ser priset direkt.",
    },

    en: {
      slug: "power-delivery",
      meta: {
        title:
          "Power Delivery – We Pick Up Your Purchase And Deliver Home | Ungdomskraft",
        description:
          "Bought a TV, washing machine or fridge at Power that won't fit in your car? We pick up at the store and deliver home with carrying included. Book Power delivery today.",
        h1: "Power delivery",
      },
      hero: {
        heading: "We pick up your purchase at Power",
        intro:
          "Found a great price on a new TV, washing machine or fridge at Power — but it won't fit in your car? We solve it quickly and smoothly. Ungdomskraft picks up your purchase directly at Power, loads the van and delivers to your home — with carrying and placement included. We collect from all Power stores in Stockholm and Gothenburg.",
      },
      featureCards: {
        sinceText: "Power delivery since 2014",
        bookYourText: "Book your Power delivery",
        bookDescText:
          "Enter the Power store address and your home address, describe what you bought and choose a time. You see the price instantly.",
        vehicleTitleText: "Vans perfect for Power deliveries",
        vehicleDescText:
          "We arrive with the right size van — whether you're picking up a laptop or a complete kitchen with appliances. No need to rent a trailer, drive yourself or strain your back.",
      },
      itemsSection: {
        heading: "Items we pick up at Power",
        items: [
          {
            name: "TVs",
            description:
              "55-, 65-, 75- and 85-inch TVs — careful handling and safe transport home",
            image: "/tv.webp",
          },
          {
            name: "Appliances",
            description:
              "Fridges, freezers, washing machines, dryers, dishwashers and ovens from Power",
            image: "/washer.webp",
          },
          {
            name: "Computers & gaming",
            description:
              "Desktop computers, gaming PCs, monitors and peripherals",
            image: "/patio-furniture.webp",
          },
          {
            name: "Audio & video",
            description:
              "Soundbars, surround systems, projectors and home cinema equipment",
            image: "/painting.webp",
          },
          {
            name: "Home appliances",
            description:
              "Robot vacuums, air purifiers, coffee machines and other large household products",
            image: "/box.webp",
          },
          {
            name: "Other bulky items",
            description:
              "E-bikes, treadmills, cross trainers and other bulky electronics from Power",
            image: "/sofa.webp",
          },
        ],
      },
      seoContent: {
        mainHeading:
          "Delivery from Power — we pick up, drive and carry inside for you",
        mainText:
          "Power is one of Sweden's largest electronics retailers, known for aggressive pricing and frequent campaigns. The stores in Stockholm and Gothenburg offer everything from TVs and appliances to computers, gaming equipment and household machines. The problem many Power customers recognise: you jump on a campaign price for a new fridge, a 75-inch TV or a washing machine — but it won't fit in your car. Power's own delivery often costs several hundred SEK extra, can take up to a week to book and rarely includes carrying past the front door. If you live on the third floor without a lift, there are additional charges per floor. Ungdomskraft offers a faster and smarter alternative. We pick up your purchase directly from the Power store, load the van carefully and drive to your home. Carrying to the right spot in your home is always included — regardless of floor, narrow stairwells or lack of lift. You pay one price, you see it instantly when booking and there are no hidden fees. We collect from all Power stores in Stockholm and Gothenburg: Barkarby, Kungens Kurva, Nacka, Täby, Kista, Farsta, Nordstan, Frölunda, Bäckebol, Sisjön and all others. Our crew handle electronics and appliances with the same care as the store's own delivery team — but often faster and with more flexible times. We know that a large TV must stand upright during transport, that a fridge shouldn't be tilted more than necessary and that a washing machine needs transit bolts. Everything is protected with blankets and straps, and we carry with the care that expensive electronics demand. Many of our customers combine Power purchases with pickups from other stores in the same trip. Need to pick up a TV at Power and furniture at JYSK? We do it in a single run — you save both time and money.",
        preparationHeading: "Prepare for your Power delivery",
        preparationText:
          "Make sure the purchase is paid for and ready to collect at the store. If you ordered online with click-and-collect, check that you've received an SMS or email confirming the item is available. Have your home address and any door codes ready. Measure doorways and stairwells if it involves large appliances like fridges, washing machines or very large TVs. Check that the van can park near the entrance at your home. If you'd like us to remove packaging after carrying in, mention it when booking.",
        additionalSections: [
          {
            heading: "TV delivery from Power",
            content:
              "TVs are one of the most common purchases we pick up at Power. Power often has campaign prices on large TVs, which means many customers jump on a 65- or 75-inch TV — only to realise it won't fit in the car. A 65-inch TV in its original packaging is approximately 160 cm wide and weighs 25–40 kg. With Ungdomskraft's delivery we pick up your TV directly at Power. We load it upright in the van (never flat — that damages the panel), secure with straps and blankets and drive to your home. We carry inside through stairwells and doorways and place the TV where you want it. We pick up all sizes and brands — Samsung, LG, Sony, Philips, TCL, Hisense and all others. Whether you bought on campaign, in a clearance sale or at regular price, we handle your TV with the care that expensive electronics demand.",
          },
          {
            heading: "Appliance delivery from Power",
            content:
              "Appliances from Power — fridges, freezers, washing machines, dryers, dishwashers and ovens — often weigh 60–90 kg and require at least two people to carry. Power's own delivery sometimes takes up to a week and charges extra per floor without a lift. With Ungdomskraft we pick up your appliance at the store, protect it during transport and carry inside to the exact right place in your home — regardless of floor. We have experience carrying fridges through narrow stairwells, washing machines through low basement doors and dryers up three flights without a lift. We make sure transit bolts stay in place during the drive and that the appliance stands stable during loading. Note that we are responsible for transport and carrying — connecting water, electricity and plumbing needs to be arranged separately.",
          },
          {
            heading: "Computer and gaming equipment delivery from Power",
            content:
              "Power has a wide range of computers, gaming equipment and accessories. Gaming PCs, large monitors and home cinema equipment can be both heavy and bulky. A desktop gaming PC with accessories often weighs 20–30 kg in its original packaging, and an ultrawide monitor needs careful handling. We pick up everything at Power, package securely in the van and deliver to your home. We carry inside and place — you focus on unpacking and plugging in. We also handle larger purchases like treadmills, e-bikes and cross trainers from Power without issue.",
          },
          {
            heading: "How Power delivery works with Ungdomskraft",
            listItems: [
              {
                label: "Book online",
                text: "Enter the Power store address and your home address. Describe what you bought and choose a time. You see the price instantly.",
              },
              {
                label: "We pick up at Power",
                text: "Our team arrive with a van. We collect your purchase at the store, protect with blankets and straps and load carefully.",
              },
              {
                label: "We deliver home",
                text: "We drive to your home, carry inside through stairwells and doorways and place your purchase where you want it.",
              },
              {
                label: "Done",
                text: "Your new TV, appliance or computer is at home — carried in and placed. Without lifting a finger.",
              },
            ],
            afterContent:
              "The whole process typically takes 1–3 hours depending on distance. You can follow the status in real time.",
          },
          {
            heading: "We collect from all Power stores",
            content:
              "We pick up from all Power stores in Stockholm and Gothenburg. In Stockholm: Barkarby, Kungens Kurva, Nacka, Täby, Kista, Farsta and all others. In Gothenburg: Nordstan, Frölunda, Bäckebol, Sisjön and all other locations. Just enter the store address when booking and we handle the rest. We also pick up items ordered online with click-and-collect, as well as items from Power's campaigns and sale periods.",
          },
          {
            heading: "Why book Power delivery with Ungdomskraft?",
            content:
              "Compared to Power's own delivery, we often offer faster service, always include carrying to the right spot and provide a transparent price with no hidden per-floor surcharges. Plus you make a double impact: you get your purchase home smoothly and you help young people enter the workforce. What makes Ungdomskraft unique is that we employ young people and give them real work experience. Every job is staffed by motivated young workers who learn everything from customer service and logistics to careful handling of expensive electronics. We've been around since 2014 and have completed tens of thousands of transports across Stockholm and Gothenburg. Everything is insured, we have our own vans in multiple sizes and you always see the price before you book. No hidden fees, no surprises.\n\nPower delivery does not qualify for the Swedish RUT tax deduction, but several of our other services do – including moving help, carrying help and furniture assembly with a 50% deduction on the labour cost. Feel free to combine services in a single booking.",
          },
        ],
      },
      faq: [
        {
          question: "How much does Power delivery cost?",
          answer:
            "The price depends on what you bought and the distance. A TV or smaller electronics starts at 595 SEK. Appliances like fridges or washing machines cost from 749–1 495 SEK. You see the exact price when you book online.",
        },
        {
          question: "Can I get a RUT tax deduction on Power delivery?",
          answer:
            "Power delivery does not in itself qualify for the Swedish RUT tax deduction. RUT applies to household services carried out in your home, such as moving help, carrying help and furniture assembly. Ungdomskraft offers several RUT-eligible services with a 50% deduction on the labour cost – easily combine them in one booking via our website.",
        },
        {
          question: "Is a van included in the Power delivery?",
          answer:
            "Yes, a van is always included. We choose the right size based on your purchase — you don't need to rent a vehicle.",
        },
        {
          question: "Is carrying inside included?",
          answer:
            "Yes, carrying inside is always included. We carry your purchase through stairwells, lifts and doorways and place it at your home — regardless of floor. No extra charges per floor.",
        },
        {
          question: "Which Power stores do you pick up from?",
          answer:
            "We pick up from all Power stores in Stockholm and Gothenburg: Barkarby, Kungens Kurva, Nacka, Täby, Kista, Farsta, Nordstan, Frölunda, Bäckebol, Sisjön and all others. Just enter the store address when booking.",
        },
        {
          question: "Can you pick up items I ordered online with click-and-collect?",
          answer:
            "Yes, we pick up everything that's ready to collect at the store — whether you bought in store or ordered online. Make sure you've received confirmation that the item is available before booking.",
        },
        {
          question: "How do you transport TVs from Power?",
          answer:
            "We always transport TVs standing upright in their original packaging — never flat. We secure with straps and protect with blankets. This applies to all sizes from 55 to 85 inches and larger.",
        },
        {
          question: "Can you carry a fridge up stairs?",
          answer:
            "Yes, we carry appliances to any floor — with or without a lift. Our crew are used to carrying 70–90 kg through narrow stairwells. We handle fridges, washing machines, dryers and all other heavy appliances.",
        },
        {
          question: "Can you connect appliances from Power?",
          answer:
            "No, we are responsible for transport and carrying — not for connecting water, electricity or plumbing. We place the appliance in the exact right spot so an electrician or plumber can easily connect it.",
        },
        {
          question: "Do you protect electronics during transport?",
          answer:
            "Yes, we use furniture blankets, straps and extra protection for fragile electronics. TVs are always transported upright. Everything is insured — if something is damaged we replace it.",
        },
        {
          question: "How quickly can you deliver from Power?",
          answer:
            "We often offer delivery at short notice — often the same day — within a few hours. Book online and see available times instantly.",
        },
        {
          question: "Is Power delivery available on weekends?",
          answer:
            "Yes, we offer delivery on weekends and some evenings. Check that the Power store is open at your chosen time — we can't pick up if the store is closed.",
        },
        {
          question: "Can you pick up from Power and another store in the same trip?",
          answer:
            "Yes, we can pick up from several stores during the same run if they're within a reasonable distance. Describe all pickup points when you book — for example Power and IKEA in the same trip.",
        },
        {
          question: "Do I need to be at Power?",
          answer:
            "No, you don't need to be at the store. Make sure the item is paid for and ready to collect — we handle the rest. You just need to be home to receive the delivery.",
        },
        {
          question: "Is the delivery insured?",
          answer:
            "Yes, all our transports are fully insured. If anything is damaged during loading, transport or carrying we replace it. You can book with confidence.",
        },
        {
          question: "In which areas do you offer Power delivery?",
          answer:
            "We offer Power delivery across the entire Stockholm region: Botkyrka, Danderyd, Ekerö, Haninge, Huddinge, Järfälla, Lidingö, Nacka, Norrtälje, Nykvarn, Nynäshamn, Salem, Sigtuna, Sollentuna, Solna, Stockholm, Sundbyberg, Södertälje, Tyresö, Täby, Upplands Väsby, Upplands-Bro, Vallentuna, Vaxholm, Värmdö and Österåker. We're also in the Gothenburg region: Ale, Alingsås, Göteborg, Härryda, Kungälv, Kungsbacka, Lerum, Lilla Edet, Mölndal, Partille, Stenungsund, Tjörn and Öckerö.",
        },
        {
          question: "Why choose Ungdomskraft for Power delivery?",
          answer:
            "We always include a van, carrying help and placement in the right spot — regardless of floor. You see the price instantly when booking, we can often deliver the same day and everything is insured.",
        },
      ],
      priceHint: PACKAGE_PRICE_HINT_EN.bud,
      pricingRows: getPackagePricingRows("bud", "en"),
      dynamicHeadings: {
        threeSteps: "Power delivery in 3 easy steps",
        step2Text:
          "Our crew pick up your purchase at Power, protect with blankets and load the van. Then we drive home to you.",
        step3Title: "All in place",
        step3Desc:
          "Your new TV, appliance or computer is at home — carried in and placed. Done.",
        ctaHeading: "Book Power delivery today",
        ctaText:
          "Enter the Power store address and your home address, describe what you bought and choose a time. You see the price instantly.",
      },
    },
  },
  {
    // Hemleverans IKEA — config för /hemleverans-ikea
    // Vinkel: Hämta köp från IKEA som inte får plats i bilen – flatpack-möbler, kök, garderober, soffor.
    // Unik vinkel: IKEA:s flatpack-paket är tunga och skrymmande, ofta flera kollin per möbel.
    // IKEAs egen leverans är dyr (från 59–399 kr + väntetid) och exkluderar ofta inbärning uppför trappor.
    slug: "hemleverans-ikea",
    serviceCategory: "hemleverans",

    meta: {
      title:
        "Hemleverans IKEA – Vi Hämtar Ditt Köp Och Kör Hem | Ungdomskraft",
      description:
        "Köpt möbler på IKEA som inte får plats i bilen? Vi hämtar dina IKEA-paket och levererar hem med inbärning samma dag. Fast pris, försäkrat. Boka idag!",
      h1: "Hemleverans IKEA",
    },

    hero: {
      heading: "Vi hämtar dina IKEA-paket och kör hem till dig – Hemleverans-IKEA",
      intro:
        "Handlat på IKEA men paketen får inte plats i bilen? En KALLAX-hylla, PAX-garderob eller KIVIK-soffa i flatpack kräver ofta skåpbil – och Vi löser det snabbt och smidigt. Ungdomskraft hämtar dina IKEA-paket direkt i varuhuset, lastar skåpbilen och levererar hem till dig – med inbärning och uppställning på plats. Vi kör från alla IKEA-varuhus i Stockholm och Göteborg.",
      heroImage: "/hero-hemleverans.png",
    },

    featureCards: {
      sinceText: "IKEA-leverans sedan 2014",
      storyText:
        "Sedan 2014 har Ungdomskraft hjälpt tusentals kunder att få hem sina IKEA-köp – från enstaka hyllor till kompletta kök och garderobssystem. Våra ungdomar vet hur man hanterar tunga flatpack-paket i trånga trapphus och lastar skåpbilar effektivt.",
      bookYourText: "Boka din IKEA-leverans",
      bookDescText:
        "Ange IKEA-varuhusets adress och din hemadress, beskriv vad du handlat och välj en tid. Du ser priset direkt.",
      vehicleTitleText: "Skåpbilar perfekta för IKEA-leveranser",
      vehicleDescText:
        "Vi kommer med rätt storlek på skåpbil – oavsett om du hämtar en bokhylla eller ett helt kök. Du slipper hyra släp, köra själv eller göra flera turer.",
    },

    itemsSection: {
      heading: "IKEA-produkter vi hämtar och levererar hem",
      items: [
        {
          name: "Garderober & förvaring",
          description:
            "PAX-garderober, BESTÅ-förvaringssystem, KALLAX-hyllor och BILLY-bokhyllor i flatpack",
          image: "/wardrobe.webp",
        },
        {
          name: "Soffor & fåtöljer",
          description:
            "KIVIK, FRIHETEN, EKTORP och andra IKEA-soffor – både flatpack och färdigmonterade",
          image: "/sofa.webp",
        },
        {
          name: "Sängar & madrasser",
          description:
            "MALM, HEMNES och BRIMNES sängramar, madrasser och sängtillbehör",
          image: "/bed.webp",
        },
        {
          name: "Matbord & stolar",
          description:
            "EKEDALEN, LISABO, SKOGSTA och andra matgrupper i flatpack eller monterat",
          image: "/patio-furniture.webp",
        },
        {
          name: "Kök",
          description:
            "METOD-köksstommar, luckor, bänkskivor, vitvaror och kökstillbehör",
          image: "/box.webp",
        },
        {
          name: "Övrigt",
          description:
            "Badrumsmöbler, barnmöbler, utemöbler, mattor, belysning och allt annat från IKEA",
          image: "/painting.webp",
        },
      ],
    },

    seoContent: {
      mainHeading:
        "Hemleverans från IKEA – vi hämtar, kör och bär in åt dig",
      mainText:
        "IKEA är Sveriges mest besökta möbelvaruhus, och varje helg strömmar tusentals kunder genom varuhusen i Stockholm och Göteborg. Du hittar den perfekta PAX-garderoben, ett komplett METOD-kök eller en KIVIK-soffa till ett bra pris – men sen kommer den jobbiga insikten: paketen får inte plats i bilen. En PAX-garderob består av 4–8 flatpack-kollin som väger totalt 80–150 kg. Ett komplett METOD-kök kan bli 20+ kollin. Även en enstaka KALLAX-hylla i 4×4-storlek väger över 50 kg och är 150 cm lång. IKEAs egen hemleverans kostar från 59 kr för mindre paket till 399 kr för större leveranser, men inkluderar sällan inbärning förbi ytterdörren. Leveranstiden kan vara 2–7 dagar, och under högsäsong ännu längre. Bor du på tredje våningen utan hiss måste du bära in själv – eller betala extra. Ungdomskraft erbjuder ett snabbare och komplett alternativ. Vi hämtar dina IKEA-paket direkt i varuhuset eller på IKEAs leveransutlämning, lastar skåpbilen och kör hem till dig. Inbärning till rätt plats i hemmet ingår alltid – oavsett våning, trångt trapphus eller avsaknad av hiss. Vi bär dina flatpack-paket genom smala trapphus och ställer dem i rätt rum så att du kan börja montera direkt. Du ser priset direkt vid bokning, inga dolda avgifter tillkommer. Vi kör från alla IKEA-varuhus i Stockholm och Göteborg: Kungens Kurva, Barkarby, Järfälla, Upplands Väsby (IKEA Kållered i Göteborg, IKEA Bäckebol och övriga). Varje vecka hämtar vi hundratals IKEA-leveranser – vi vet exakt hur IKEAs utlämning fungerar och hur man lastar flatpack-paket på effektivaste sätt. En vanlig IKEA-leverans med oss tar 1–3 timmar från bokning till dina paket står hemma hos dig.",
      preparationHeading: "Förbered inför din IKEA-leverans",
      preparationText:
        "Se till att köpet är betalt och klart för upphämtning. Om du handlat i varuhuset – ha kvitto eller ordernummer redo. Om du beställt online med hämta-i-varuhus – kontrollera att du fått bekräftelse. Ha din hemadress och eventuell portkod redo. Mät gärna dörröppningar och trapphus om paketen är stora – en PAX-garderobsstomme är 236 cm lång i paketet. Kolla att skåpbilen kan parkera nära porten hemma hos dig. Ange vid bokning hur många kollin det rör sig om och vilka möbler det gäller – det hjälper oss att välja rätt skåpbilsstorlek.",
      additionalSections: [
        {
          heading: "Hemleverans av IKEA-garderober och förvaringssystem",
          content:
            "PAX-garderober och BESTÅ-förvaringssystem är bland de populäraste produkterna på IKEA – och bland de svåraste att få hem. En standard PAX-garderob med 2–3 stommar, hyllplan, lådor och dörrar blir snabbt 5–8 kollin som väger totalt 80–150 kg. Paketen är långa (upp till 236 cm), tunga och svåra att hantera ensam. Med Ungdomskrafts IKEA-leverans hämtar vi alla kollin i varuhuset, lastar effektivt i skåpbilen och kör hem till dig. Vi bär in alla paket genom trapphus och dörrar och ställer dem i rätt rum – redo att monteras. Samma sak gäller BILLY-bokhyllor, KALLAX-hyllor, HEMNES-byråer och alla andra förvaringslösningar. Vi hanterar allt från ett enskilt kolli till en komplett garderobslösning med 15+ paket.",
        },
        {
          heading: "Hemleverans av IKEA-kök",
          content:
            "Att köpa ett nytt kök på IKEA är ett stort projekt – och transporten är ofta den mest underskattade delen. Ett komplett METOD-kök kan bestå av 20–40 kollin: stommar, luckor, lådinredningar, bänkskivor, skåp och tillbehör. Totala vikten kan överstiga 200 kg. Med Ungdomskraft hämtar vi hela ditt kök i en enda tur. Vi lastar alla kollin i skåpbilen, säkrar ordentligt och kör hem till dig. Vi bär in alla paket till köket och ställer dem sorterade – så att din köksinstallatör eller du själv enkelt kan börja monteringen. Vi hanterar även IKEA-vitvaror (kylskåp, diskmaskiner, fläktar) som ofta ingår i köksbeställningen.",
        },
        {
          heading: "Hemleverans av IKEA-soffor och sängar",
          content:
            "IKEA-soffor som KIVIK, FRIHETEN och EKTORP finns både i flatpack och som färdigmonterade visningsexemplar. Flatpack-varianter kan bestå av 3–5 kollin, medan färdigmonterade soffor kräver skåpbil på grund av storleken. IKEA-sängar som MALM, HEMNES och BRIMNES med tillhörande madrass blir ofta 3–4 tunga kollin. Vi hämtar allt på IKEA, skyddar med filtar under transporten och bär in på plats hemma hos dig. Madrasser transporteras helst platta – vi har utrymme i skåpbilen för att lasta dem korrekt. Behöver du hjälp med ihopsättning? Beskriv det vid bokning så ser vi vad vi kan göra.",
        },
        {
          heading: "Så fungerar IKEA-leverans med Ungdomskraft",
          listItems: [
            {
              label: "Boka online",
              text: "Ange IKEA-varuhusets adress och din hemadress. Beskriv vad du handlat (möbler, antal kollin) och välj en tid. Du ser priset direkt.",
            },
            {
              label: "Vi hämtar på IKEA",
              text: "Vårt team kommer med skåpbil. Vi hämtar dina paket i varuhuset eller vid utlämningen, lastar effektivt och säkrar allt.",
            },
            {
              label: "Vi levererar hem",
              text: "Vi kör hem till dig, bär in alla paket genom trapphus och dörrar och ställer i rätt rum.",
            },
            {
              label: "Klart",
              text: "Alla dina IKEA-paket står hemma hos dig – inburna och redo att monteras. Utan att du lyfte ett finger.",
            },
          ],
          afterContent:
            "Hela processen tar normalt 1–3 timmar beroende på avstånd och mängd. Du kan följa statusen i realtid.",
        },
        {
          heading: "Vi kör från alla IKEA-varuhus",
          content:
            "Vi hämtar från alla IKEA-varuhus i Stockholm och Göteborg. I Stockholm: IKEA Kungens Kurva, IKEA Barkarby och IKEA Järfälla/Upplands Väsby. I Göteborg: IKEA Kållered, IKEA Bäckebol och övriga. Vi hämtar även från IKEAs leveransutlämningar och upphämtningspunkter. Ange bara varuhusets adress vid bokning så sköter vi resten. Ska du handla på IKEA och en annan butik samma dag? Vi kan hämta från flera ställen i samma tur.",
        },
        {
          heading: "Varför boka IKEA-leverans med Ungdomskraft?",
          content:
            "Jämfört med IKEAs egen hemleverans erbjuder vi ofta snabbare leverans, alltid inbärning till rätt rum och ett transparent pris utan dolda tillägg. IKEAs leverans kostar 59–399 kr men exkluderar inbärning uppför trappor – med oss ingår det alltid. Dessutom gör du dubbel nytta: du får hem dina IKEA-möbler smidigt och du bidrar till att unga människor kommer in på arbetsmarknaden. Det som gör Ungdomskraft unikt är att vi anställer unga människor och ger dem riktig arbetslivserfarenhet. Varje uppdrag bemannas av motiverade ungdomar som lär sig allt från kundservice och logistik till fysiskt arbete och smart lastning. Vi har funnits sedan 2014 och har genomfört tiotusentals transporter i Stockholm och Göteborg. Allt är försäkrat, vi har egna skåpbilar i flera storlekar och du ser alltid priset innan du bokar. Inga dolda avgifter, inga överraskningar.\n\nHemleverans från IKEA ger inte rätt till RUT-avdrag, men flera av våra andra tjänster gör det – exempelvis flytt, bärhjälp och möbelmontering med 50 % avdrag på arbetskostnaden. Kombinera gärna tjänster i en och samma bokning.",
        },
      ],
    },

    faq: [
      {
        question: "Vad kostar hemleverans från IKEA?",
        answer:
          "Priset beror på antalet kollin och avståndet. En mindre IKEA-leverans (1–3 paket) kostar från 595 kr. Större leveranser med garderober eller kök kostar från 995–2 495 kr. Du ser det exakta priset när du bokar online.",
      },
      {
        question: "Kan jag få RUT-avdrag på hemleverans från IKEA?",
        answer:
          "Hemleverans från IKEA ger i sig inte rätt till RUT-avdrag. RUT gäller hushållsnära tjänster som utförs i din bostad, exempelvis flytt, bärhjälp och möbelmontering. Ungdomskraft erbjuder flera RUT-berättigade tjänster med 50 % avdrag på arbetskostnaden – kombinera enkelt i en bokning via vår hemsida.",
      },
      {
        question: "Ingår skåpbil i IKEA-leveransen?",
        answer:
          "Ja, skåpbil ingår alltid. Vi väljer rätt storlek utifrån ditt köp – du behöver inte hyra bil, släp eller tak-box.",
      },
      {
        question: "Ingår inbärning i priset?",
        answer:
          "Ja, inbärning ingår alltid. Vi bär dina IKEA-paket genom trapphus, hiss och dörrar och ställer i rätt rum hemma hos dig – oavsett våning. Inga extra avgifter per våningsplan.",
      },
      {
        question: "Vilka IKEA-varuhus hämtar ni från?",
        answer:
          "Vi hämtar från alla IKEA-varuhus i Stockholm och Göteborg: Kungens Kurva, Barkarby, Järfälla/Upplands Väsby, Kållered, Bäckebol och övriga. Vi hämtar även från IKEAs utlämningspunkter. Ange bara adressen vid bokning.",
      },
      {
        question: "Kan ni hämta varor jag beställt online med hämta-i-varuhus?",
        answer:
          "Ja, vi hämtar allt som finns redo att hämtas – oavsett om du handlat i varuhuset eller beställt online. Se till att du fått bekräftelse om att ordern finns på plats.",
      },
      {
        question: "Hur många IKEA-paket kan ni ta?",
        answer:
          "Vi tar allt från enstaka kollin till 30+ paket. Vi väljer rätt skåpbilsstorlek utifrån din beställning. Ange antal kollin och vilka möbler det gäller vid bokning så matchar vi rätt kapacitet.",
      },
      {
        question: "Kan ni montera IKEA-möbler?",
        answer:
          "Vi fokuserar på transport och inbärning, men kan ofta hjälpa till med enklare montering. Beskriv vad som behöver monteras vid bokning så ser vi vad vi kan göra.",
      },
      {
        question: "Kan ni bära tunga IKEA-paket i trappor?",
        answer:
          "Ja, vi bär till vilken våning som helst – med eller utan hiss. Våra ungdomar är vana vid tunga lyft. Vi hanterar PAX-garderobsstommar (236 cm, 40+ kg), köksstommar och alla andra tunga kollin.",
      },
      {
        question: "Skyddar ni paketen under transporten?",
        answer:
          "Ja, vi säkrar alla paket med spännband i skåpbilen så att inget glider eller skadas. Ömtåliga föremål skyddas extra med filtar. Allt är försäkrat – om något skadas ersätter vi det.",
      },
      {
        question: "Hur snabbt kan ni leverera från IKEA?",
        answer:
          "Vi erbjuder ofta IKEA-leverans med kort varsel – ofta redan samma dag – inom några timmar. Boka online och se tillgängliga tider direkt. Vi är snabba och flexibla.",
      },
      {
        question: "Erbjuder ni IKEA-leverans på helger?",
        answer:
          "Ja, vi erbjuder hemleverans på helger och vissa kvällar. Perfekt om du handlar på IKEA en lördagsförmiddag och vill ha paketen hemma samma dag.",
      },
      {
        question: "Kan ni hämta från IKEA och en annan butik i samma tur?",
        answer:
          "Ja, vi kan hämta från flera butiker under samma körning om de ligger i rimligt avstånd. Beskriv alla hämtpunkter vid bokning – till exempel IKEA och JYSK i samma tur.",
      },
      {
        question: "Behöver jag vara med i varuhuset?",
        answer:
          "Nej, du behöver inte vara med på IKEA. Se till att köpet är betalt och redo att hämtas – vi sköter resten. Du behöver bara vara hemma för att ta emot leveransen.",
      },
      {
        question: "Är IKEA-leveransen försäkrad?",
        answer:
          "Ja, alla våra transporter är helförsäkrade. Om något skadas under lastning, transport eller inbärning ersätter vi det. Du kan boka med trygghet.",
      },
      {
        question:
          "I vilka kommuner erbjuder ni IKEA-leverans?",
        answer:
          "Vi erbjuder IKEA-leverans i hela Stockholmsområdet: Botkyrka, Danderyd, Ekerö, Haninge, Huddinge, Järfälla, Lidingö, Nacka, Norrtälje, Nykvarn, Nynäshamn, Salem, Sigtuna, Sollentuna, Solna, Stockholm, Sundbyberg, Södertälje, Tyresö, Täby, Upplands Väsby, Upplands-Bro, Vallentuna, Vaxholm, Värmdö och Österåker. Vi finns även i Göteborgsområdet: Ale, Alingsås, Göteborg, Härryda, Kungälv, Kungsbacka, Lerum, Lilla Edet, Mölndal, Partille, Stenungsund, Tjörn och Öckerö.",
      },
      {
        question: "Varför välja Ungdomskraft för IKEA-leverans?",
        answer:
          "Hos oss ingår alltid skåpbil, bärhjälp och inbärning till rätt rum. Du ser priset direkt vid bokning, vi kan ofta köra samma dag och allt är försäkrat. Du bidrar dessutom till att unga får arbetslivserfarenhet.",
      },
    ],

    priceHint: PACKAGE_PRICE_HINT_SV.bud,

    pricingRows: getPackagePricingRows("bud", "sv"),

    dynamicHeadings: {
      threeSteps: "IKEA-leverans i 3 enkla steg",
      step2Text:
        "Våra ungdomar hämtar dina IKEA-paket i varuhuset, lastar och säkrar allt i skåpbilen. Sedan kör vi hem till dig.",
      step3Title: "Allt på plats",
      step3Desc:
        "Alla dina IKEA-paket står hemma hos dig – inburna och redo att monteras. Klart.",
      ctaHeading: "Boka IKEA-leverans idag",
      ctaText:
        "Ange IKEA-varuhusets adress och din hemadress, beskriv vad du handlat och välj en tid. Du ser priset direkt.",
    },

    en: {
      slug: "ikea-delivery",
      meta: {
        title:
          "IKEA Delivery – We Pick Up Your Purchase And Deliver Home | Ungdomskraft",
        description:
          "Bought furniture at IKEA that won't fit in your car? We pick up your IKEA packages and deliver home with carrying included. Fixed price. Book today!",
        h1: "IKEA delivery",
      },
      hero: {
        heading: "We pick up your IKEA packages and deliver home – IKEA-Delivery",
        intro:
          "Shopped at IKEA but the packages won't fit in your car? A KALLAX shelf, PAX wardrobe or KIVIK sofa in flatpack often requires a van — We solve it quickly and smoothly. Ungdomskraft picks up your IKEA packages directly at the store, loads the van and delivers to your home — with carrying and placement included. We collect from all IKEA stores in Stockholm and Gothenburg.",
      },
      featureCards: {
        sinceText: "IKEA delivery since 2014",
        bookYourText: "Book your IKEA delivery",
        bookDescText:
          "Enter the IKEA store address and your home address, describe what you bought and choose a time. You see the price instantly.",
        vehicleTitleText: "Vans perfect for IKEA deliveries",
        vehicleDescText:
          "We arrive with the right size van — whether you're picking up a bookshelf or an entire kitchen. No need to rent a trailer, drive yourself or make multiple trips.",
      },
      itemsSection: {
        heading: "IKEA products we pick up and deliver home",
        items: [
          {
            name: "Wardrobes & storage",
            description:
              "PAX wardrobes, BESTÅ storage systems, KALLAX shelves and BILLY bookcases in flatpack",
            image: "/wardrobe.webp",
          },
          {
            name: "Sofas & armchairs",
            description:
              "KIVIK, FRIHETEN, EKTORP and other IKEA sofas — both flatpack and ready-assembled",
            image: "/sofa.webp",
          },
          {
            name: "Beds & mattresses",
            description:
              "MALM, HEMNES and BRIMNES bed frames, mattresses and bed accessories",
            image: "/bed.webp",
          },
          {
            name: "Dining tables & chairs",
            description:
              "EKEDALEN, LISABO, SKOGSTA and other dining sets in flatpack or assembled",
            image: "/patio-furniture.webp",
          },
          {
            name: "Kitchens",
            description:
              "METOD kitchen cabinets, doors, worktops, appliances and kitchen accessories",
            image: "/box.webp",
          },
          {
            name: "Other",
            description:
              "Bathroom furniture, children's furniture, outdoor furniture, rugs, lighting and everything else from IKEA",
            image: "/painting.webp",
          },
        ],
      },
      seoContent: {
        mainHeading:
          "Delivery from IKEA — we pick up, drive and carry inside for you",
        mainText:
          "IKEA is Sweden's most visited furniture store, and every weekend thousands of customers stream through the stores in Stockholm and Gothenburg. You find the perfect PAX wardrobe, a complete METOD kitchen or a KIVIK sofa at a great price — but then comes the frustrating realisation: the packages won't fit in your car. A PAX wardrobe consists of 4–8 flatpack packages weighing 80–150 kg in total. A complete METOD kitchen can be 20+ packages. Even a single KALLAX shelf in the 4×4 size weighs over 50 kg and is 150 cm long. IKEA's own delivery costs from 59 SEK for smaller packages to 399 SEK for larger deliveries, but rarely includes carrying past the front door. Delivery time can be 2–7 days, and during peak season even longer. If you live on the third floor without a lift, you'll need to carry in yourself — or pay extra. Ungdomskraft offers a faster and complete alternative. We pick up your IKEA packages directly at the store or at IKEA's delivery collection point, load the van and drive to your home. Carrying to the right room in your home is always included — regardless of floor, narrow stairwells or lack of lift. We carry your flatpack packages through narrow stairwells and place them in the right room so you can start assembling straight away. You see the price instantly when booking, no hidden fees. We collect from all IKEA stores in Stockholm and Gothenburg: Kungens Kurva, Barkarby, Järfälla/Upplands Väsby, Kållered, Bäckebol and others. Every week we make hundreds of IKEA deliveries — we know exactly how IKEA's collection point works and how to load flatpack packages most efficiently. A typical IKEA delivery with us takes 1–3 hours from booking to your packages being at home.",
        preparationHeading: "Prepare for your IKEA delivery",
        preparationText:
          "Make sure the purchase is paid for and ready for collection. If you shopped in store, have your receipt or order number ready. If you ordered online with click-and-collect, check that you've received confirmation. Have your home address and any door codes ready. Measure doorways and stairwells if the packages are large — a PAX wardrobe frame is 236 cm long in the package. Check that the van can park near the entrance at your home. When booking, specify how many packages and which furniture items — it helps us choose the right van size.",
        additionalSections: [
          {
            heading: "IKEA wardrobe and storage system delivery",
            content:
              "PAX wardrobes and BESTÅ storage systems are among IKEA's most popular products — and among the hardest to get home. A standard PAX wardrobe with 2–3 frames, shelves, drawers and doors quickly becomes 5–8 packages weighing 80–150 kg in total. The packages are long (up to 236 cm), heavy and difficult to handle alone. With Ungdomskraft's IKEA delivery we pick up all packages at the store, load efficiently into the van and drive to your home. We carry all packages through stairwells and doorways and place them in the right room — ready to assemble. The same applies to BILLY bookcases, KALLAX shelves, HEMNES dressers and all other storage solutions. We handle everything from a single package to a complete wardrobe solution with 15+ packages.",
          },
          {
            heading: "IKEA kitchen delivery",
            content:
              "Buying a new kitchen at IKEA is a big project — and transport is often the most underestimated part. A complete METOD kitchen can consist of 20–40 packages: frames, doors, drawer inserts, worktops, cabinets and accessories. The total weight can exceed 200 kg. With Ungdomskraft we pick up your entire kitchen in a single trip. We load all packages into the van, secure properly and drive to your home. We carry all packages into the kitchen and place them sorted — so your kitchen installer or you can easily start assembling. We also handle IKEA appliances (fridges, dishwashers, extractor hoods) that are often part of the kitchen order.",
          },
          {
            heading: "IKEA sofa and bed delivery",
            content:
              "IKEA sofas like KIVIK, FRIHETEN and EKTORP come both in flatpack and as ready-assembled display models. Flatpack versions can consist of 3–5 packages, while ready-assembled sofas need a van due to their size. IKEA beds like MALM, HEMNES and BRIMNES with matching mattress often become 3–4 heavy packages. We pick up everything at IKEA, protect with blankets during transport and carry inside to the right spot in your home. Mattresses are preferably transported flat — we have space in the van to load them correctly. Need help with assembly? Describe it when booking and we'll see what we can do.",
          },
          {
            heading: "How IKEA delivery works with Ungdomskraft",
            listItems: [
              {
                label: "Book online",
                text: "Enter the IKEA store address and your home address. Describe what you bought (furniture, number of packages) and choose a time. You see the price instantly.",
              },
              {
                label: "We pick up at IKEA",
                text: "Our team arrive with a van. We collect your packages at the store or collection point, load efficiently and secure everything.",
              },
              {
                label: "We deliver home",
                text: "We drive to your home, carry all packages through stairwells and doorways and place them in the right room.",
              },
              {
                label: "Done",
                text: "All your IKEA packages are at home — carried in and ready to assemble. Without lifting a finger.",
              },
            ],
            afterContent:
              "The whole process typically takes 1–3 hours depending on distance and amount. You can follow the status in real time.",
          },
          {
            heading: "We collect from all IKEA stores",
            content:
              "We pick up from all IKEA stores in Stockholm and Gothenburg. In Stockholm: IKEA Kungens Kurva, IKEA Barkarby and IKEA Järfälla/Upplands Väsby. In Gothenburg: IKEA Kållered, IKEA Bäckebol and others. We also pick up from IKEA's delivery collection points. Just enter the store address when booking and we handle the rest. Shopping at IKEA and another store the same day? We can pick up from multiple locations in the same trip.",
          },
          {
            heading: "Why book IKEA delivery with Ungdomskraft?",
            content:
              "Compared to IKEA's own delivery, we often offer faster service, always include carrying to the right room and provide a transparent price with no hidden surcharges. IKEA's delivery costs 59–399 SEK but excludes carrying upstairs — with us it's always included. Plus you make a double impact: you get your IKEA furniture home smoothly and you help young people enter the workforce. What makes Ungdomskraft unique is that we employ young people and give them real work experience. Every job is staffed by motivated young workers who learn everything from customer service and logistics to physical work and smart loading. We've been around since 2014 and have completed tens of thousands of transports across Stockholm and Gothenburg. Everything is insured, we have our own vans in multiple sizes and you always see the price before you book. No hidden fees, no surprises.\n\nIKEA delivery does not qualify for the Swedish RUT tax deduction, but several of our other services do – including moving help, carrying help and furniture assembly with a 50% deduction on the labour cost. Feel free to combine services in a single booking.",
          },
        ],
      },
      faq: [
        {
          question: "How much does IKEA delivery cost?",
          answer:
            "The price depends on the number of packages and distance. A smaller IKEA delivery (1–3 packages) starts at 595 SEK. Larger deliveries with wardrobes or kitchens cost 749–2 495 SEK. You see the exact price when you book online.",
        },
        {
          question: "Can I get a RUT tax deduction on IKEA delivery?",
          answer:
            "IKEA delivery does not in itself qualify for the Swedish RUT tax deduction. RUT applies to household services carried out in your home, such as moving help, carrying help and furniture assembly. Ungdomskraft offers several RUT-eligible services with a 50% deduction on the labour cost – easily combine them in one booking via our website.",
        },
        {
          question: "Is a van included in the IKEA delivery?",
          answer:
            "Yes, a van is always included. We choose the right size based on your purchase — you don't need to rent a vehicle, trailer or roof box.",
        },
        {
          question: "Is carrying inside included?",
          answer:
            "Yes, carrying inside is always included. We carry your IKEA packages through stairwells, lifts and doorways and place them in the right room — regardless of floor. No extra charges per floor.",
        },
        {
          question: "Which IKEA stores do you pick up from?",
          answer:
            "We pick up from all IKEA stores in Stockholm and Gothenburg: Kungens Kurva, Barkarby, Järfälla/Upplands Väsby, Kållered, Bäckebol and others. We also pick up from IKEA's collection points. Just enter the address when booking.",
        },
        {
          question: "Can you pick up items I ordered online with click-and-collect?",
          answer:
            "Yes, we pick up everything that's ready to collect — whether you shopped in store or ordered online. Make sure you've received confirmation that the order is available.",
        },
        {
          question: "How many IKEA packages can you take?",
          answer:
            "We handle anything from individual packages to 30+. We choose the right van size based on your order. Specify the number of packages and which furniture when booking so we match the right capacity.",
        },
        {
          question: "Can you assemble IKEA furniture?",
          answer:
            "We focus on transport and carrying, but can often help with simple assembly. Describe what needs assembling when booking and we'll see what we can do.",
        },
        {
          question: "Can you carry heavy IKEA packages up stairs?",
          answer:
            "Yes, we carry to any floor — with or without a lift. Our crew are used to heavy items. We handle PAX wardrobe frames (236 cm, 40+ kg), kitchen cabinets and all other heavy packages.",
        },
        {
          question: "Do you protect packages during transport?",
          answer:
            "Yes, we secure all packages with straps in the van so nothing slides or gets damaged. Fragile items get extra blanket protection. Everything is insured — if something is damaged we replace it.",
        },
        {
          question: "How quickly can you deliver from IKEA?",
          answer:
            "We often offer IKEA delivery at short notice — often the same day — within a few hours. Book online and see available times instantly. We are fast and flexible.",
        },
        {
          question: "Is IKEA delivery available on weekends?",
          answer:
            "Yes, we offer delivery on weekends and some evenings. Perfect if you shop at IKEA on a Saturday morning and want the packages home the same day.",
        },
        {
          question: "Can you pick up from IKEA and another store in the same trip?",
          answer:
            "Yes, we can pick up from several stores during the same run if they're within a reasonable distance. Describe all pickup points when you book — for example IKEA and JYSK in the same trip.",
        },
        {
          question: "Do I need to be at the store?",
          answer:
            "No, you don't need to be at IKEA. Make sure the purchase is paid for and ready to collect — we handle the rest. You just need to be home to receive the delivery.",
        },
        {
          question: "Is the IKEA delivery insured?",
          answer:
            "Yes, all our transports are fully insured. If anything is damaged during loading, transport or carrying we replace it. You can book with confidence.",
        },
        {
          question: "In which areas do you offer IKEA delivery?",
          answer:
            "We offer IKEA delivery across the entire Stockholm region: Botkyrka, Danderyd, Ekerö, Haninge, Huddinge, Järfälla, Lidingö, Nacka, Norrtälje, Nykvarn, Nynäshamn, Salem, Sigtuna, Sollentuna, Solna, Stockholm, Sundbyberg, Södertälje, Tyresö, Täby, Upplands Väsby, Upplands-Bro, Vallentuna, Vaxholm, Värmdö and Österåker. We're also in the Gothenburg region: Ale, Alingsås, Göteborg, Härryda, Kungälv, Kungsbacka, Lerum, Lilla Edet, Mölndal, Partille, Stenungsund, Tjörn and Öckerö.",
        },
        {
          question: "Why choose Ungdomskraft for IKEA delivery?",
          answer:
            "We always include a van, carrying help and placement in the right room. You see the price instantly when booking, we can often deliver the same day and everything is insured. Plus you're helping young people gain work experience.",
        },
      ],
      priceHint: PACKAGE_PRICE_HINT_EN.bud,
      pricingRows: getPackagePricingRows("bud", "en"),
      dynamicHeadings: {
        threeSteps: "IKEA delivery in 3 easy steps",
        step2Text:
          "Our crew pick up your IKEA packages at the store, load and secure everything in the van. Then we drive home to you.",
        step3Title: "All in place",
        step3Desc:
          "All your IKEA packages are at home — carried in and ready to assemble. Done.",
        ctaHeading: "Book IKEA delivery today",
        ctaText:
          "Enter the IKEA store address and your home address, describe what you bought and choose a time. You see the price instantly.",
      },
    },
  },
  {
    // Hemleverans JYSK — config för /hemleverans-jysk
    // Vinkel: Hämta köp från JYSK som inte får plats i bilen – sängar, madrasser, garderober, trädgårdsmöbler.
    // Unik vinkel: JYSK är starka på sovrum och trädgård, ofta tunga paket (madrasser, kontinentalsängar).
    slug: "hemleverans-jysk",
    serviceCategory: "hemleverans",

    meta: {
      title:
        "Hemleverans JYSK – Vi Hämtar Ditt Köp Och Kör Hem | Ungdomskraft",
      description:
        "Köpt säng, madrass eller möbler på JYSK som inte får plats i bilen? Vi hämtar i butiken och levererar hem med inbärning. Fast pris, försäkrat. Boka idag!",
      h1: "Hemleverans JYSK",
    },

    hero: {
      heading: "Vi hämtar ditt köp på JYSK",
      intro:
        "Hittat en ny kontinentalsäng, madrass eller garderob på JYSK – men paketen får inte plats i bilen? En kontinentalsäng väger 50–80 kg och en garderob i flatpack kräver skåpbil. Vi löser det snabbt och smidigt. Ungdomskraft hämtar ditt köp direkt på JYSK, lastar skåpbilen och levererar hem till dig – med inbärning och uppställning på plats. Vi kör från alla JYSK-butiker i Stockholm och Göteborg.",
      heroImage: "/hero-hemleverans.png",
    },

    featureCards: {
      sinceText: "JYSK-leverans sedan 2014",
      storyText:
        "Sedan 2014 har Ungdomskraft hjälpt tusentals kunder att få hem sina JYSK-köp – från tunga kontinentalsängar och madrasser till garderober, soffor och trädgårdsmöbler. Våra ungdomar vet hur man hanterar tunga paket i trånga trapphus.",
      bookYourText: "Boka din JYSK-leverans",
      bookDescText:
        "Ange JYSK-butikens adress och din hemadress, beskriv vad du handlat och välj en tid. Du ser priset direkt.",
      vehicleTitleText: "Skåpbilar perfekta för JYSK-leveranser",
      vehicleDescText:
        "Vi kommer med rätt storlek på skåpbil – oavsett om du hämtar en kudde eller en komplett kontinentalsäng. Du slipper hyra släp, köra själv eller slita ryggen.",
    },

    itemsSection: {
      heading: "JYSK-produkter vi hämtar och levererar hem",
      items: [
        {
          name: "Sängar & madrasser",
          description:
            "Kontinentalsängar, ramsängar, resårmadrasser och bäddmadrasser – tunga och skrymmande",
          image: "/bed.webp",
        },
        {
          name: "Garderober & förvaring",
          description:
            "Garderober, byråer, kommoder och hyllsystem i flatpack från JYSK",
          image: "/wardrobe.webp",
        },
        {
          name: "Soffor & fåtöljer",
          description:
            "Soffor, bäddsoffor, fåtöljer och fotpallar – flatpack eller färdigmonterade",
          image: "/sofa.webp",
        },
        {
          name: "Matbord & stolar",
          description:
            "Matgrupper, barbord, sidobord och stolar i flatpack eller monterat",
          image: "/table.webp",
        },
        {
          name: "Trädgårdsmöbler",
          description:
            "Loungemöbler, parasoll, trädgårdsbord, stolar och förvaringslådor för utomhus",
          image: "/patio-furniture.webp",
        },
        {
          name: "Övrigt",
          description:
            "Badrumsmöbler, mattor, gardiner, sängkläder i storpack och övriga skrymmande JYSK-köp",
          image: "/box.webp",
        },
      ],
    },

    seoContent: {
      mainHeading:
        "Hemleverans från JYSK – vi hämtar, kör och bär in åt dig",
      mainText:
        "JYSK är en av Nordens största möbelkedjor med butiker i hela Stockholms- och Göteborgsområdet. Sortimentet är brett – från kontinentalsängar och madrasser till garderober, soffor, matgrupper och trädgårdsmöbler – och priserna är ofta svårslagena. Men det finns en utmaning som många JYSK-kunder känner igen: transporten. Du slår till på en ny kontinentalsäng till kampanjpris, hittar den perfekta garderoben eller fyller varukorgen med trädgårdsmöbler inför sommaren. Och sen står du vid kassan med paket som tillsammans väger över 100 kg och inte har en chans att få plats i personbilen. En kontinentalsäng från JYSK väger ofta 50–80 kg och är uppdelad i 2–3 kollin. En garderob i flatpack blir 3–5 tunga paket. Trädgårdsmöbler i kartong är skrymmande och ohanterliga. JYSKs egen hemleverans finns som alternativ men kostar ofta 299–699 kr beroende på storlek, tar 3–7 arbetsdagar och inkluderar vanligtvis inte inbärning uppför trappor. Bor du på andra eller tredje våningen utan hiss står du där med paketen vid ytterdörren. Ungdomskraft erbjuder ett snabbare och komplett alternativ. Vi hämtar ditt JYSK-köp direkt i butiken, lastar skåpbilen och kör hem till dig. Inbärning till rätt plats i hemmet ingår alltid – oavsett våning, trångt trapphus eller avsaknad av hiss. Du ser priset direkt vid bokning, och det tillkommer inga dolda avgifter. Vi kör från alla JYSK-butiker i Stockholm och Göteborg. Varje vecka hämtar vi hundratals leveranser från möbelbutiker – vi vet hur man lastar tunga sängpaket, skrymmande garderobskollin och ömtåliga glasskåp. Våra ungdomar bär med erfarenhet och omsorg, oavsett om det är upp tre trappor eller genom en smal villadörr. Du får dina JYSK-möbler på plats i hemmet utan att lyfta ett finger.",
      preparationHeading: "Förbered inför din JYSK-leverans",
      preparationText:
        "Se till att köpet är betalt och klart för upphämtning. Ha din hemadress och eventuell portkod redo. Mät gärna dörröppningar och trapphus om det rör sig om stora möbler som kontinentalsängar eller garderober – en sängbotten kan vara 180 cm lång och svår att manövrera i trånga hörn. Kolla att skåpbilen kan parkera nära porten hemma hos dig. Ange vid bokning vilka möbler det gäller och antal kollin – det hjälper oss att välja rätt skåpbilsstorlek.",
      additionalSections: [
        {
          heading: "Hemleverans av sängar och madrasser från JYSK",
          content:
            "JYSK är kända för sitt breda utbud av sängar och madrasser till bra priser. Kontinentalsängar, ramsängar, resårmadrasser och bäddmadrasser hör till de mest populära produkterna – och de mest problematiska att transportera. En kontinentalsäng i 180 cm-bredd består ofta av 2–3 kollin: undersäng, översäng och eventuellt sänggavel. Totala vikten landar på 50–80 kg. En kvalitetsmadrass väger 15–30 kg och är skrymmande även om den är rullförpackad. Med Ungdomskrafts hemleverans hämtar vi din säng direkt på JYSK. Vi lastar alla kollin i skåpbilen, kör hem till dig och bär in till sovrummet – oavsett våning. Vi ställer sängdelarna på plats så att du enkelt kan montera ihop dem. Har du köpt en komplett sänglösning med gavel, madrass och bäddmadrass? Vi tar allt i samma tur.",
        },
        {
          heading: "Hemleverans av garderober och förvaring från JYSK",
          content:
            "JYSK erbjuder garderober, byråer, kommoder och hyllsystem i flatpack till konkurrenskraftiga priser. En garderob med skjutdörrar kan bestå av 3–5 kollin som väger totalt 60–100 kg. Paketen är långa, tunga och ohanterliga – att bära dem uppför trappor på egen hand är en utmaning de flesta vill undvika. Vi hämtar alla kollin på JYSK, lastar effektivt i skåpbilen och kör hem till dig. Vi bär in alla paket genom trapphus och dörrar och ställer dem i rätt rum. Vill du att vi packar upp och tar med emballaget? Beskriv det vid bokning. Samma sak gäller byråer, kommoder, skohyllor och alla andra förvaringslösningar från JYSK.",
        },
        {
          heading: "Hemleverans av trädgårdsmöbler från JYSK",
          content:
            "JYSK har ett stort sortiment av trädgårdsmöbler – loungegrupper, parasoll, matgrupper för utomhus och förvaringslådor. Under våren och sommaren är dessa bland de populäraste produkterna, och de säljer ofta slut snabbt under kampanjer. Problemet: trädgårdsmöbler i kartong är skrymmande och svåra att få plats med i bilen. En loungegrupp kan vara 4–6 stora kollin. Med vår hemleverans hämtar vi allt på JYSK och levererar direkt till din trädgård, altan eller balkong. Vi bär ut genom huset om det behövs – du slipper släpa tunga kartonger genom hallen.",
        },
        {
          heading: "Så fungerar JYSK-leverans med Ungdomskraft",
          listItems: [
            {
              label: "Boka online",
              text: "Ange JYSK-butikens adress och din hemadress. Beskriv vad du handlat och välj en tid. Du ser priset direkt.",
            },
            {
              label: "Vi hämtar på JYSK",
              text: "Vårt team kommer med skåpbil. Vi hämtar ditt köp i butiken, lastar och säkrar allt med spännband.",
            },
            {
              label: "Vi levererar hem",
              text: "Vi kör hem till dig, bär in alla paket genom trapphus och dörrar och ställer i rätt rum.",
            },
            {
              label: "Klart",
              text: "Dina JYSK-möbler står hemma hos dig – inburna och redo. Utan att du lyfte ett finger.",
            },
          ],
          afterContent:
            "Hela processen tar normalt 1–3 timmar beroende på avstånd och mängd. Du kan följa statusen i realtid.",
        },
        {
          heading: "Vi kör från alla JYSK-butiker",
          content:
            "Vi hämtar från alla JYSK-butiker i Stockholm och Göteborg. I Stockholm: Kungens Kurva, Barkarby, Nacka, Täby, Kista, Farsta, Haninge, Sollentuna och alla övriga. I Göteborg: Frölunda, Bäckebol, Sisjön, Kungälv och alla övriga butiker. Ange bara butikens adress vid bokning så sköter vi resten. Ska du handla på JYSK och en annan butik samma dag? Vi kan hämta från flera ställen i samma tur.",
        },
        {
          heading: "Varför boka JYSK-leverans med Ungdomskraft?",
          content:
            "Jämfört med JYSKs egen hemleverans erbjuder vi ofta snabbare leverans, alltid inbärning till rätt rum och ett transparent pris utan dolda tillägg per våning. Dessutom gör du dubbel nytta: du får hem dina JYSK-möbler smidigt och du bidrar till att unga människor kommer in på arbetsmarknaden. Det som gör Ungdomskraft unikt är att vi anställer unga människor och ger dem riktig arbetslivserfarenhet. Varje uppdrag bemannas av motiverade ungdomar som lär sig allt från kundservice och logistik till fysiskt arbete och smart lastning. Vi har funnits sedan 2014 och har genomfört tiotusentals transporter i Stockholm och Göteborg. Allt är försäkrat, vi har egna skåpbilar i flera storlekar och du ser alltid priset innan du bokar. Inga dolda avgifter, inga överraskningar.\n\nHemleverans från JYSK ger inte rätt till RUT-avdrag, men flera av våra andra tjänster gör det – exempelvis flytt, bärhjälp och möbelmontering med 50 % avdrag på arbetskostnaden. Kombinera gärna tjänster i en och samma bokning.",
        },
      ],
    },

    faq: [
      {
        question: "Vad kostar hemleverans från JYSK?",
        answer:
          "Priset beror på mängden och avståndet. En mindre JYSK-leverans kostar från 595 kr. Större leveranser med kontinentalsängar eller garderober kostar från 995–2 495 kr. Du ser det exakta priset när du bokar online.",
      },
      {
        question: "Kan jag få RUT-avdrag på hemleverans från JYSK?",
        answer:
          "Hemleverans från JYSK ger i sig inte rätt till RUT-avdrag. RUT gäller hushållsnära tjänster som utförs i din bostad, exempelvis flytt, bärhjälp och möbelmontering. Ungdomskraft erbjuder flera RUT-berättigade tjänster med 50 % avdrag på arbetskostnaden – kombinera enkelt i en bokning via vår hemsida.",
      },
      {
        question: "Ingår skåpbil i JYSK-leveransen?",
        answer:
          "Ja, skåpbil ingår alltid. Vi väljer rätt storlek utifrån ditt köp – du behöver inte hyra bil eller släp.",
      },
      {
        question: "Ingår inbärning i priset?",
        answer:
          "Ja, inbärning ingår alltid. Vi bär ditt köp genom trapphus, hiss och dörrar och ställer på plats hemma hos dig – oavsett våning. Inga extra avgifter per våningsplan.",
      },
      {
        question: "Vilka JYSK-butiker hämtar ni från?",
        answer:
          "Vi hämtar från alla JYSK-butiker i Stockholm och Göteborg: Kungens Kurva, Barkarby, Nacka, Täby, Kista, Farsta, Haninge, Sollentuna, Frölunda, Bäckebol, Sisjön, Kungälv och alla övriga. Ange bara butikens adress vid bokning.",
      },
      {
        question: "Kan ni bära en tung kontinentalsäng uppför trappor?",
        answer:
          "Ja, vi bär sängar och madrasser till vilken våning som helst – med eller utan hiss. Våra ungdomar är vana vid att bära 50–80 kg genom trånga trapphus. Vi hanterar kontinentalsängar, ramsängar och alla storlekar.",
      },
      {
        question: "Kan ni montera JYSK-möbler?",
        answer:
          "Vi fokuserar på transport och inbärning, men kan ofta hjälpa med enklare montering. Beskriv vad som behöver monteras vid bokning så ser vi vad vi kan göra.",
      },
      {
        question: "Kan ni leverera trädgårdsmöbler till altanen?",
        answer:
          "Ja, vi levererar trädgårdsmöbler direkt till din trädgård, altan eller balkong. Vi bär ut genom huset om det behövs – du slipper släpa tunga kartonger själv.",
      },
      {
        question: "Skyddar ni sakerna under transporten?",
        answer:
          "Ja, vi säkrar alla paket med spännband i skåpbilen. Ömtåliga föremål skyddas extra med filtar. Allt är försäkrat – om något skadas ersätter vi det.",
      },
      {
        question: "Hur snabbt kan ni leverera från JYSK?",
        answer:
          "Vi erbjuder ofta hemleverans med kort varsel – ofta redan samma dag – inom några timmar. Boka online och se tillgängliga tider direkt. Vi är snabba och flexibla.",
      },
      {
        question: "Erbjuder ni JYSK-leverans på helger?",
        answer:
          "Ja, vi erbjuder hemleverans på helger och vissa kvällar. Kontrollera att JYSK-butiken har öppet den tid du bokar – vi kan inte hämta om butiken är stängd.",
      },
      {
        question: "Kan ni hämta från JYSK och en annan butik i samma tur?",
        answer:
          "Ja, vi kan hämta från flera butiker under samma körning om de ligger i rimligt avstånd. Beskriv alla hämtpunkter vid bokning – till exempel JYSK och IKEA i samma tur.",
      },
      {
        question: "Behöver jag vara med i butiken?",
        answer:
          "Nej, du behöver inte vara med på JYSK. Se till att köpet är betalt och redo att hämtas – vi sköter resten. Du behöver bara vara hemma för att ta emot leveransen.",
      },
      {
        question: "Är JYSK-leveransen försäkrad?",
        answer:
          "Ja, alla våra transporter är helförsäkrade. Om något skadas under lastning, transport eller inbärning ersätter vi det. Du kan boka med trygghet.",
      },
      {
        question:
          "I vilka kommuner erbjuder ni JYSK-leverans?",
        answer:
          "Vi erbjuder JYSK-leverans i hela Stockholmsområdet: Botkyrka, Danderyd, Ekerö, Haninge, Huddinge, Järfälla, Lidingö, Nacka, Norrtälje, Nykvarn, Nynäshamn, Salem, Sigtuna, Sollentuna, Solna, Stockholm, Sundbyberg, Södertälje, Tyresö, Täby, Upplands Väsby, Upplands-Bro, Vallentuna, Vaxholm, Värmdö och Österåker. Vi finns även i Göteborgsområdet: Ale, Alingsås, Göteborg, Härryda, Kungälv, Kungsbacka, Lerum, Lilla Edet, Mölndal, Partille, Stenungsund, Tjörn och Öckerö.",
      },
      {
        question: "Varför välja Ungdomskraft för JYSK-leverans?",
        answer:
          "Hos oss ingår alltid skåpbil, bärhjälp och inbärning till rätt rum. Du ser priset direkt vid bokning, vi kan ofta köra samma dag och allt är försäkrat.",
      },
    ],

    priceHint: PACKAGE_PRICE_HINT_SV.bud,

    pricingRows: getPackagePricingRows("bud", "sv"),

    dynamicHeadings: {
      threeSteps: "JYSK-leverans i 3 enkla steg",
      step2Text:
        "Våra ungdomar hämtar ditt köp på JYSK, lastar och säkrar allt i skåpbilen. Sedan kör vi hem till dig.",
      step3Title: "Allt på plats",
      step3Desc:
        "Dina JYSK-möbler står hemma hos dig – inburna och redo. Klart.",
      ctaHeading: "Boka JYSK-leverans idag",
      ctaText:
        "Ange JYSK-butikens adress och din hemadress, beskriv vad du handlat och välj en tid. Du ser priset direkt.",
    },

    en: {
      slug: "jysk-delivery",
      meta: {
        title:
          "JYSK Delivery – We Pick Up Your Purchase And Deliver Home | Ungdomskraft",
        description:
          "Bought a bed, mattress or furniture at JYSK that won't fit in your car? We pick up at the store and deliver home with carrying included. Book JYSK delivery today.",
        h1: "JYSK delivery",
      },
      hero: {
        heading: "We pick up your purchase at JYSK",
        intro:
          "Found a new continental bed, mattress or wardrobe at JYSK — but the packages won't fit in your car? A continental bed weighs 50–80 kg and a flatpack wardrobe requires a van. We solve it quickly and smoothly. Ungdomskraft picks up your purchase directly at JYSK, loads the van and delivers to your home — with carrying and placement included. We collect from all JYSK stores in Stockholm and Gothenburg.",
      },
      featureCards: {
        sinceText: "JYSK delivery since 2014",
        bookYourText: "Book your JYSK delivery",
        bookDescText:
          "Enter the JYSK store address and your home address, describe what you bought and choose a time. You see the price instantly.",
        vehicleTitleText: "Vans perfect for JYSK deliveries",
        vehicleDescText:
          "We arrive with the right size van — whether you're picking up a pillow or a complete continental bed. No need to rent a trailer, drive yourself or strain your back.",
      },
      itemsSection: {
        heading: "JYSK products we pick up and deliver home",
        items: [
          {
            name: "Beds & mattresses",
            description:
              "Continental beds, bed frames, spring mattresses and mattress toppers — heavy and bulky",
            image: "/bed.webp",
          },
          {
            name: "Wardrobes & storage",
            description:
              "Wardrobes, dressers, chests of drawers and shelving systems in flatpack from JYSK",
            image: "/wardrobe.webp",
          },
          {
            name: "Sofas & armchairs",
            description:
              "Sofas, sofa beds, armchairs and footstools — flatpack or ready-assembled",
            image: "/sofa.webp",
          },
          {
            name: "Dining tables & chairs",
            description:
              "Dining sets, bar tables, side tables and chairs in flatpack or assembled",
            image: "/table.webp",
          },
          {
            name: "Garden furniture",
            description:
              "Lounge sets, parasols, garden tables, chairs and outdoor storage boxes",
            image: "/patio-furniture.webp",
          },
          {
            name: "Other",
            description:
              "Bathroom furniture, rugs, curtains, bedding in bulk and other bulky JYSK purchases",
            image: "/box.webp",
          },
        ],
      },
      seoContent: {
        mainHeading:
          "Delivery from JYSK — we pick up, drive and carry inside for you",
        mainText:
          "JYSK is one of the Nordic region's largest furniture chains with stores across the Stockholm and Gothenburg areas. The range is wide — from continental beds and mattresses to wardrobes, sofas, dining sets and garden furniture — and the prices are often hard to beat. But there's a challenge many JYSK customers recognise: transport. You jump on a new continental bed at a campaign price, find the perfect wardrobe or fill your trolley with garden furniture for the summer. And then you're standing at the checkout with packages that together weigh over 100 kg and have no chance of fitting in your car. A continental bed from JYSK often weighs 50–80 kg and is split into 2–3 packages. A flatpack wardrobe becomes 3–5 heavy packages. Garden furniture in boxes is bulky and unwieldy. JYSK's own delivery is an option but often costs 299–699 SEK depending on size, takes 3–7 working days and usually doesn't include carrying upstairs. If you live on the second or third floor without a lift, you're left with the packages at the front door. Ungdomskraft offers a faster and complete alternative. We pick up your JYSK purchase directly from the store, load the van and drive to your home. Carrying to the right spot in your home is always included — regardless of floor, narrow stairwells or lack of lift. You see the price instantly when booking, and there are no hidden fees. We collect from all JYSK stores in Stockholm and Gothenburg. Every week we make hundreds of deliveries from furniture stores — we know how to load heavy bed packages, bulky wardrobe boxes and fragile glass cabinets. Our crew carry with experience and care, whether it's up three flights or through a narrow front door. You get your JYSK furniture in place at home without lifting a finger.",
        preparationHeading: "Prepare for your JYSK delivery",
        preparationText:
          "Make sure the purchase is paid for and ready for collection. Have your home address and any door codes ready. Measure doorways and stairwells if it involves large furniture like continental beds or wardrobes — a bed base can be 180 cm long and difficult to manoeuvre around tight corners. Check that the van can park near the entrance at your home. When booking, specify which furniture and number of packages — it helps us choose the right van size.",
        additionalSections: [
          {
            heading: "Bed and mattress delivery from JYSK",
            content:
              "JYSK is known for its wide range of beds and mattresses at great prices. Continental beds, bed frames, spring mattresses and mattress toppers are among the most popular products — and the most problematic to transport. A continental bed in 180 cm width often consists of 2–3 packages: lower base, upper mattress and optional headboard. Total weight lands at 50–80 kg. A quality mattress weighs 15–30 kg and is bulky even when roll-packed. With Ungdomskraft's delivery we pick up your bed directly at JYSK. We load all packages into the van, drive to your home and carry into the bedroom — regardless of floor. We place the bed parts so you can easily assemble them. Bought a complete bed solution with headboard, mattress and topper? We take everything in the same trip.",
          },
          {
            heading: "Wardrobe and storage delivery from JYSK",
            content:
              "JYSK offers wardrobes, dressers, chests of drawers and shelving systems in flatpack at competitive prices. A sliding-door wardrobe can consist of 3–5 packages weighing 60–100 kg in total. The packages are long, heavy and unwieldy — carrying them upstairs on your own is a challenge most people want to avoid. We pick up all packages at JYSK, load efficiently into the van and drive to your home. We carry all packages through stairwells and doorways and place them in the right room. Want us to unpack and take the packaging? Describe it when booking. The same applies to dressers, chests of drawers, shoe racks and all other storage solutions from JYSK.",
          },
          {
            heading: "Garden furniture delivery from JYSK",
            content:
              "JYSK has a large range of garden furniture — lounge sets, parasols, outdoor dining sets and storage boxes. During spring and summer these are among the most popular products, and they often sell out quickly during campaigns. The problem: garden furniture in boxes is bulky and hard to fit in a car. A lounge set can be 4–6 large packages. With our delivery we pick up everything at JYSK and deliver directly to your garden, patio or balcony. We carry through the house if needed — no dragging heavy boxes through the hallway yourself.",
          },
          {
            heading: "How JYSK delivery works with Ungdomskraft",
            listItems: [
              {
                label: "Book online",
                text: "Enter the JYSK store address and your home address. Describe what you bought and choose a time. You see the price instantly.",
              },
              {
                label: "We pick up at JYSK",
                text: "Our team arrive with a van. We collect your purchase at the store, load and secure everything with straps.",
              },
              {
                label: "We deliver home",
                text: "We drive to your home, carry all packages through stairwells and doorways and place them in the right room.",
              },
              {
                label: "Done",
                text: "Your JYSK furniture is at home — carried in and ready. Without lifting a finger.",
              },
            ],
            afterContent:
              "The whole process typically takes 1–3 hours depending on distance and amount. You can follow the status in real time.",
          },
          {
            heading: "We collect from all JYSK stores",
            content:
              "We pick up from all JYSK stores in Stockholm and Gothenburg. In Stockholm: Kungens Kurva, Barkarby, Nacka, Täby, Kista, Farsta, Haninge, Sollentuna and all others. In Gothenburg: Frölunda, Bäckebol, Sisjön, Kungälv and all other locations. Just enter the store address when booking and we handle the rest. Shopping at JYSK and another store the same day? We can pick up from multiple locations in the same trip.",
          },
          {
            heading: "Why book JYSK delivery with Ungdomskraft?",
            content:
              "Compared to JYSK's own delivery, we often offer faster service, always include carrying to the right room and provide a transparent price with no hidden surcharges. Plus you make a double impact: you get your JYSK furniture home smoothly and you help young people enter the workforce. What makes Ungdomskraft unique is that we employ young people and give them real work experience. Every job is staffed by motivated young workers who learn everything from customer service and logistics to physical work and smart loading. We've been around since 2014 and have completed tens of thousands of transports across Stockholm and Gothenburg. Everything is insured, we have our own vans in multiple sizes and you always see the price before you book. No hidden fees, no surprises.\n\nJYSK delivery does not qualify for the Swedish RUT tax deduction, but several of our other services do – including moving help, carrying help and furniture assembly with a 50% deduction on the labour cost. Feel free to combine services in a single booking.",
          },
        ],
      },
      faq: [
        {
          question: "How much does JYSK delivery cost?",
          answer:
            "The price depends on the amount and distance. A smaller JYSK delivery starts at 595 SEK. Larger deliveries with continental beds or wardrobes cost 749–2 495 SEK. You see the exact price when you book online.",
        },
        {
          question: "Can I get a RUT tax deduction on JYSK delivery?",
          answer:
            "JYSK delivery does not in itself qualify for the Swedish RUT tax deduction. RUT applies to household services carried out in your home, such as moving help, carrying help and furniture assembly. Ungdomskraft offers several RUT-eligible services with a 50% deduction on the labour cost – easily combine them in one booking via our website.",
        },
        {
          question: "Is a van included in the JYSK delivery?",
          answer:
            "Yes, a van is always included. We choose the right size based on your purchase — you don't need to rent a vehicle or trailer.",
        },
        {
          question: "Is carrying inside included?",
          answer:
            "Yes, carrying inside is always included. We carry your purchase through stairwells, lifts and doorways and place it at your home — regardless of floor. No extra charges per floor.",
        },
        {
          question: "Which JYSK stores do you pick up from?",
          answer:
            "We pick up from all JYSK stores in Stockholm and Gothenburg: Kungens Kurva, Barkarby, Nacka, Täby, Kista, Farsta, Haninge, Sollentuna, Frölunda, Bäckebol, Sisjön, Kungälv and all others. Just enter the store address when booking.",
        },
        {
          question: "Can you carry a heavy continental bed up stairs?",
          answer:
            "Yes, we carry beds and mattresses to any floor — with or without a lift. Our crew are used to carrying 50–80 kg through narrow stairwells. We handle continental beds, bed frames and all sizes.",
        },
        {
          question: "Can you assemble JYSK furniture?",
          answer:
            "We focus on transport and carrying, but can often help with simple assembly. Describe what needs assembling when booking and we'll see what we can do.",
        },
        {
          question: "Can you deliver garden furniture to the patio?",
          answer:
            "Yes, we deliver garden furniture directly to your garden, patio or balcony. We carry through the house if needed — no dragging heavy boxes yourself.",
        },
        {
          question: "Do you protect items during transport?",
          answer:
            "Yes, we secure all packages with straps in the van. Fragile items get extra blanket protection. Everything is insured — if something is damaged we replace it.",
        },
        {
          question: "How quickly can you deliver from JYSK?",
          answer:
            "We often offer delivery at short notice — often the same day — within a few hours. Book online and see available times instantly. We are fast and flexible.",
        },
        {
          question: "Is JYSK delivery available on weekends?",
          answer:
            "Yes, we offer delivery on weekends and some evenings. Check that the JYSK store is open at your chosen time — we can't pick up if the store is closed.",
        },
        {
          question: "Can you pick up from JYSK and another store in the same trip?",
          answer:
            "Yes, we can pick up from several stores during the same run if they're within a reasonable distance. Describe all pickup points when you book — for example JYSK and IKEA in the same trip.",
        },
        {
          question: "Do I need to be at the store?",
          answer:
            "No, you don't need to be at JYSK. Make sure the purchase is paid for and ready to collect — we handle the rest. You just need to be home to receive the delivery.",
        },
        {
          question: "Is the JYSK delivery insured?",
          answer:
            "Yes, all our transports are fully insured. If anything is damaged during loading, transport or carrying we replace it. You can book with confidence.",
        },
        {
          question: "In which areas do you offer JYSK delivery?",
          answer:
            "We offer JYSK delivery across the entire Stockholm region: Botkyrka, Danderyd, Ekerö, Haninge, Huddinge, Järfälla, Lidingö, Nacka, Norrtälje, Nykvarn, Nynäshamn, Salem, Sigtuna, Sollentuna, Solna, Stockholm, Sundbyberg, Södertälje, Tyresö, Täby, Upplands Väsby, Upplands-Bro, Vallentuna, Vaxholm, Värmdö and Österåker. We're also in the Gothenburg region: Ale, Alingsås, Göteborg, Härryda, Kungälv, Kungsbacka, Lerum, Lilla Edet, Mölndal, Partille, Stenungsund, Tjörn and Öckerö.",
        },
        {
          question: "Why choose Ungdomskraft for JYSK delivery?",
          answer:
            "We always include a van, carrying help and placement in the right room. You see the price instantly when booking, we can often deliver the same day and everything is insured.",
        },
      ],
      priceHint: PACKAGE_PRICE_HINT_EN.bud,
      pricingRows: getPackagePricingRows("bud", "en"),
      dynamicHeadings: {
        threeSteps: "JYSK delivery in 3 easy steps",
        step2Text:
          "Our crew pick up your purchase at JYSK, load and secure everything in the van. Then we drive home to you.",
        step3Title: "All in place",
        step3Desc:
          "Your JYSK furniture is at home — carried in and ready. Done.",
        ctaHeading: "Book JYSK delivery today",
        ctaText:
          "Enter the JYSK store address and your home address, describe what you bought and choose a time. You see the price instantly.",
      },
    },
  },
  {
    // Hemleverans Rusta — config för /hemleverans-rusta
    // Vinkel: Hämta köp från Rusta som inte får plats i bilen – inredning, trädgård, förråd, bygg, husgeråd.
    // Unik vinkel: Rusta är lågprisvaruhuset med extremt brett sortiment – allt från möbler och trädgård
    // till byggtillbehör, husgeråd och säsongsvaror. Ofta impulsköp som blir större än planerat.
    slug: "hemleverans-rusta",
    serviceCategory: "hemleverans",

    meta: {
      title:
        "Hemleverans Rusta – Vi Hämtar Ditt Köp Och Kör Hem | Ungdomskraft",
      description:
        "Köpt möbler, trädgårdsmöbler eller inredning på Rusta som inte får plats i bilen? Vi hämtar i butiken och levererar hem med inbärning. Boka hemleverans Rusta idag.",
      h1: "Hemleverans Rusta",
    },

    hero: {
      heading: "Vi hämtar ditt köp på Rusta",
      intro:
        "Handlat på Rusta och vagnen blev fullare än planerat? Trädgårdsmöbler, förvaringshyllor, krukor och säsongsdekorationer i stora kartonger – allt ryms sällan i bilen. Rustas lågpriser gör det lätt att handla stort, men transporten hem blir en utmaning. Vi löser det. Ungdomskraft hämtar ditt köp direkt på Rusta, lastar skåpbilen och levererar hem till dig – med inbärning och uppställning på plats. Vi kör från alla Rusta-butiker i Stockholm och Göteborg.",
      heroImage: "/hero-hemleverans.png",
    },

    featureCards: {
      sinceText: "Rusta-leverans sedan 2014",
      storyText:
        "Sedan 2014 har Ungdomskraft hjälpt tusentals kunder att få hem sina Rusta-köp – från trädgårdsmöbler och förvaringslösningar till säsongsvaror och inredning. Våra ungdomar lastar skåpbilen effektivt och bär in hemma hos dig.",
      bookYourText: "Boka din Rusta-leverans",
      bookDescText:
        "Ange Rusta-butikens adress och din hemadress, beskriv vad du handlat och välj en tid. Du ser priset direkt.",
      vehicleTitleText: "Skåpbilar perfekta för Rusta-leveranser",
      vehicleDescText:
        "Vi kommer med rätt storlek på skåpbil – oavsett om du hämtar en hylla eller hela trädgårdsinredningen. Du slipper hyra släp, köra själv eller göra flera turer.",
    },

    itemsSection: {
      heading: "Rusta-produkter vi hämtar och levererar hem",
      items: [
        {
          name: "Trädgård & utomhus",
          description:
            "Trädgårdsmöbler, parasoll, grillar, krukor, planteringslådor och staket",
          image: "/patio-furniture.webp",
        },
        {
          name: "Förvaring & hyllor",
          description:
            "Förvaringshyllor, garagehyllor, verktygsskåp och organisationslösningar",
          image: "/wardrobe.webp",
        },
        {
          name: "Möbler & inredning",
          description:
            "Soffbord, matgrupper, stolar, mattor, lampor och dekorationer",
          image: "/sofa.webp",
        },
        {
          name: "Bygg & renovering",
          description:
            "Färg, verktyg, belysning, el-tillbehör och tyngre byggvaror",
          image: "/box.webp",
        },
        {
          name: "Husgeråd & kök",
          description:
            "Köksredskap, porslin, stekpannor, förvaringsburkar och köksapparater",
          image: "/dishes.webp",
        },
        {
          name: "Säsongsvaror",
          description:
            "Juldekorationer, sommarartiklar, campingutrustning och övrigt skrymmande säsongsmaterial",
          image: "/painting.webp",
        },
      ],
    },

    seoContent: {
      mainHeading:
        "Hemleverans från Rusta – vi hämtar, kör och bär in åt dig",
      mainText:
        "Rusta är Sveriges populära lågprisvaruhus med ett enormt sortiment – allt från trädgårdsmöbler, inredning och förvaringslösningar till byggmaterial, husgeråd, textilier och säsongsvaror. Butikerna i Stockholm och Göteborg lockar hundratusentals kunder varje vecka med sina kampanjpriser och breda utbud. Problemet som många Rusta-kunder känner igen: du går in för att köpa en kruka och en lampa, och kommer ut med en full vagn – trädgårdsmöbler, förvaringshyllor, stora krukor, dekorationer och ett halvt trädgårdspaket. Allt till fantastiska priser, men totalt omöjligt att klämma in i personbilen. Trädgårdsmöbelset i kartong, garagehyllor i flatpack och stora krukor i plast tar enorm plats. Rusta erbjuder inte alltid egen hemleverans, och när de gör det är utbudet begränsat och väntetiderna långa. Att hyra släp kostar tid och pengar, och att göra flera turer i bilen är slöseri med hela eftermiddagen. Ungdomskraft erbjuder ett snabbt och smidigt alternativ. Vi hämtar ditt Rusta-köp direkt i butiken, lastar allt i skåpbilen och kör hem till dig. Inbärning till rätt plats ingår alltid – vi bär in till trädgården, altanen, garaget, förrådet eller vilken plats du vill. Du ser priset direkt vid bokning, inga dolda avgifter tillkommer. Vi kör från alla Rusta-butiker i Stockholm och Göteborg. Varje vecka hämtar vi hundratals leveranser från Rusta och liknande varuhus. Våra ungdomar är vana vid att hantera allt från tunga förvaringshyllor i flatpack till ömtåliga krukor och dekorationer. Vi lastar effektivt så att inget skadas under transporten och bär in med omsorg oavsett om det är genom ett trångt trapphus eller ut till altanen.",
      preparationHeading: "Förbered inför din Rusta-leverans",
      preparationText:
        "Se till att köpet är betalt och klart för upphämtning. Ha din hemadress och eventuell portkod redo. Om du har handlat mycket – gör gärna en snabb lista över de största föremålen så att vi kan välja rätt skåpbilsstorlek. Kolla att skåpbilen kan parkera nära porten hemma hos dig. Om varorna ska till trädgård eller altan – se till att grind eller passage är öppen. Ange vid bokning om det finns speciella önskemål, till exempel att vi ska bära in till garaget eller ställa på altanen.",
      additionalSections: [
        {
          heading: "Hemleverans av trädgårdsmöbler och utomhusprodukter från Rusta",
          content:
            "Rusta har ett av marknadens bredaste sortiment av trädgårdsmöbler till lågpris – loungegrupper, matgrupper för utomhus, parasoll, grillar, planteringslådor, stora krukor och staket. Under våren och sommaren är efterfrågan enorm, och kampanjvaror säljer slut snabbt. Problemet: en loungegrupp i kartong blir 3–5 stora kollin, parasoll och stolskuddar tar stor plats, och tunga krukor i keramik eller betong väger 20–40 kg styck. Allt ryms inte i bilen – särskilt inte om du passar på att handla flera kampanjvaror samtidigt. Med Ungdomskrafts hemleverans hämtar vi allt på Rusta och levererar direkt till din trädgård, altan eller balkong. Vi bär ut genom huset eller runt fasaden beroende på vad som fungerar bäst. Du slipper släpa tunga kartonger och riskera att repa golven.",
        },
        {
          heading: "Hemleverans av förvaringslösningar och hyllor från Rusta",
          content:
            "Rusta erbjuder prisvärda förvaringslösningar – garagehyllor, redskapsskåp, verktygsskåp, förvaringshyllor och organisationssystem. Dessa produkter är ofta i flatpack och kan vara förvånansvärt tunga – en garagehylla i metall väger 20–40 kg och ett verktygsskåp ännu mer. Med vår hemleverans hämtar vi allt på Rusta och levererar till rätt plats – garaget, källaren, förrådet eller vardagsrummet. Vi bär in oavsett var förvaringslösningen ska stå. Perfekt för dig som renoverar garaget, organiserar källaren eller bygger en ny förrådsvägg.",
        },
        {
          heading: "Hemleverans av möbler och inredning från Rusta",
          content:
            "Rustas sortiment av inredning och möbler växer ständigt – soffbord, stolar, matgrupper, hyllor, lampor och mattor till priser som ofta är betydligt lägre än hos möbelkedjorna. Möblerna säljs ofta i flatpack och kan vara skrymmande trots kompakt förpackning. En matgrupp blir ofta 3–4 kollin, ett soffbord 1–2 kollin och mattor i rulle är upp till 3 meter långa. Med Ungdomskraft hämtar vi allt på Rusta, skyddar under transporten och bär in hemma hos dig. Vi ställer paketen i rätt rum så att du enkelt kan montera.",
        },
        {
          heading: "Så fungerar Rusta-leverans med Ungdomskraft",
          listItems: [
            {
              label: "Boka online",
              text: "Ange Rusta-butikens adress och din hemadress. Beskriv vad du handlat och välj en tid. Du ser priset direkt.",
            },
            {
              label: "Vi hämtar på Rusta",
              text: "Vårt team kommer med skåpbil. Vi hämtar ditt köp i butiken, lastar och säkrar allt med spännband.",
            },
            {
              label: "Vi levererar hem",
              text: "Vi kör hem till dig, bär in till rätt plats – trädgård, garage, källare eller vardagsrum.",
            },
            {
              label: "Klart",
              text: "Ditt Rusta-köp står hemma hos dig – inburet och på plats. Utan att du lyfte ett finger.",
            },
          ],
          afterContent:
            "Hela processen tar normalt 1–3 timmar beroende på avstånd och mängd. Du kan följa statusen i realtid.",
        },
        {
          heading: "Vi kör från alla Rusta-butiker",
          content:
            "Vi hämtar från alla Rusta-butiker i Stockholm och Göteborg. I Stockholm: Kungens Kurva, Barkarby, Bromma, Nacka, Täby, Haninge, Sollentuna, Södertälje och alla övriga. I Göteborg: Sisjön, Bäckebol, Frölunda, Kungälv och alla övriga butiker. Ange bara butikens adress vid bokning så sköter vi resten. Ska du handla på Rusta och en annan butik samma dag? Vi kan hämta från flera ställen i samma tur.",
        },
        {
          heading: "Varför boka Rusta-leverans med Ungdomskraft?",
          content:
            "Rusta erbjuder inte alltid egen hemleverans, och när de gör det är väntetiderna ofta långa. Med Ungdomskraft får du snabb leverans med inbärning till rätt plats – oavsett om det är trädgården, garaget eller sovrummet. Dessutom gör du dubbel nytta: du får hem ditt köp smidigt och du bidrar till att unga människor kommer in på arbetsmarknaden. Det som gör Ungdomskraft unikt är att vi anställer unga människor och ger dem riktig arbetslivserfarenhet. Varje uppdrag bemannas av motiverade ungdomar som lär sig allt från kundservice och logistik till fysiskt arbete. Vi har funnits sedan 2014 och har genomfört tiotusentals transporter i Stockholm och Göteborg. Allt är försäkrat, vi har egna skåpbilar i flera storlekar och du ser alltid priset innan du bokar. Inga dolda avgifter, inga överraskningar.\n\nHemleverans från Rusta ger inte rätt till RUT-avdrag, men flera av våra andra tjänster gör det – exempelvis flytt, bärhjälp och möbelmontering med 50 % avdrag på arbetskostnaden. Kombinera gärna tjänster i en och samma bokning.",
        },
      ],
    },

    faq: [
      {
        question: "Vad kostar hemleverans från Rusta?",
        answer:
          "Priset beror på mängden och avståndet. En mindre Rusta-leverans kostar från 595 kr. Större leveranser med trädgårdsmöbler eller förvaringshyllor kostar från 995–2 495 kr. Du ser det exakta priset när du bokar online.",
      },
      {
        question: "Kan jag få RUT-avdrag på hemleverans från Rusta?",
        answer:
          "Hemleverans från Rusta ger i sig inte rätt till RUT-avdrag. RUT gäller hushållsnära tjänster som utförs i din bostad, exempelvis flytt, bärhjälp och möbelmontering. Ungdomskraft erbjuder flera RUT-berättigade tjänster med 50 % avdrag på arbetskostnaden – kombinera enkelt i en bokning via vår hemsida.",
      },
      {
        question: "Ingår skåpbil i Rusta-leveransen?",
        answer:
          "Ja, skåpbil ingår alltid. Vi väljer rätt storlek utifrån ditt köp – du behöver inte hyra bil eller släp.",
      },
      {
        question: "Ingår inbärning i priset?",
        answer:
          "Ja, inbärning ingår alltid. Vi bär ditt köp till rätt plats – trädgården, garaget, källaren eller vardagsrummet – oavsett våning. Inga extra avgifter.",
      },
      {
        question: "Vilka Rusta-butiker hämtar ni från?",
        answer:
          "Vi hämtar från alla Rusta-butiker i Stockholm och Göteborg: Kungens Kurva, Barkarby, Bromma, Nacka, Täby, Haninge, Sollentuna, Södertälje, Sisjön, Bäckebol, Frölunda, Kungälv och alla övriga. Ange bara butikens adress vid bokning.",
      },
      {
        question: "Kan ni leverera trädgårdsmöbler till altanen?",
        answer:
          "Ja, vi levererar direkt till din trädgård, altan eller balkong. Vi bär ut genom huset eller runt fasaden – du slipper släpa tunga kartonger själv.",
      },
      {
        question: "Kan ni leverera till garaget eller förrådet?",
        answer:
          "Ja, vi bär in till garaget, källaren, förrådet eller vilken plats du vill. Ange önskad avlämningsplats vid bokning.",
      },
      {
        question: "Kan ni bära tunga saker i trappor?",
        answer:
          "Ja, vi bär till vilken våning som helst – med eller utan hiss. Våra ungdomar är vana vid tunga lyft och trånga trapphus.",
      },
      {
        question: "Skyddar ni sakerna under transporten?",
        answer:
          "Ja, vi säkrar alla kollin med spännband i skåpbilen. Ömtåliga föremål som krukor och glasvaror skyddas extra med filtar. Allt är försäkrat – om något skadas ersätter vi det.",
      },
      {
        question: "Hur snabbt kan ni leverera från Rusta?",
        answer:
          "Vi erbjuder ofta hemleverans med kort varsel – ofta redan samma dag – inom några timmar. Boka online och se tillgängliga tider direkt.",
      },
      {
        question: "Erbjuder ni Rusta-leverans på helger?",
        answer:
          "Ja, vi erbjuder hemleverans på helger och vissa kvällar. Kontrollera att Rusta-butiken har öppet den tid du bokar – vi kan inte hämta om butiken är stängd.",
      },
      {
        question: "Kan ni hämta från Rusta och en annan butik i samma tur?",
        answer:
          "Ja, vi kan hämta från flera butiker under samma körning om de ligger i rimligt avstånd. Beskriv alla hämtpunkter vid bokning – till exempel Rusta och Jula i samma tur.",
      },
      {
        question: "Behöver jag vara med i butiken?",
        answer:
          "Nej, du behöver inte vara med på Rusta. Se till att köpet är betalt och redo att hämtas – vi sköter resten. Du behöver bara vara hemma för att ta emot leveransen.",
      },
      {
        question: "Är Rusta-leveransen försäkrad?",
        answer:
          "Ja, alla våra transporter är helförsäkrade. Om något skadas under lastning, transport eller inbärning ersätter vi det. Du kan boka med trygghet.",
      },
      {
        question:
          "I vilka kommuner erbjuder ni Rusta-leverans?",
        answer:
          "Vi erbjuder Rusta-leverans i hela Stockholmsområdet: Botkyrka, Danderyd, Ekerö, Haninge, Huddinge, Järfälla, Lidingö, Nacka, Norrtälje, Nykvarn, Nynäshamn, Salem, Sigtuna, Sollentuna, Solna, Stockholm, Sundbyberg, Södertälje, Tyresö, Täby, Upplands Väsby, Upplands-Bro, Vallentuna, Vaxholm, Värmdö och Österåker. Vi finns även i Göteborgsområdet: Ale, Alingsås, Göteborg, Härryda, Kungälv, Kungsbacka, Lerum, Lilla Edet, Mölndal, Partille, Stenungsund, Tjörn och Öckerö.",
      },
      {
        question: "Varför välja Ungdomskraft för Rusta-leverans?",
        answer:
          "Hos oss ingår alltid skåpbil, bärhjälp och inbärning till rätt plats. Du ser priset direkt vid bokning, vi kan ofta köra samma dag och allt är försäkrat.",
      },
    ],

    priceHint: PACKAGE_PRICE_HINT_SV.bud,

    pricingRows: getPackagePricingRows("bud", "sv"),

    dynamicHeadings: {
      threeSteps: "Rusta-leverans i 3 enkla steg",
      step2Text:
        "Våra ungdomar hämtar ditt köp på Rusta, lastar och säkrar allt i skåpbilen. Sedan kör vi hem till dig.",
      step3Title: "Allt på plats",
      step3Desc:
        "Ditt Rusta-köp står hemma hos dig – inburet och på plats. Klart.",
      ctaHeading: "Boka Rusta-leverans idag",
      ctaText:
        "Ange Rusta-butikens adress och din hemadress, beskriv vad du handlat och välj en tid. Du ser priset direkt.",
    },

    en: {
      slug: "rusta-delivery",
      meta: {
        title:
          "Rusta Delivery – We Pick Up Your Purchase And Deliver Home | Ungdomskraft",
        description:
          "Bought furniture or garden items at Rusta that won't fit in your car? We pick up at the store and deliver home with carrying included. Book today!",
        h1: "Rusta delivery",
      },
      hero: {
        heading: "We pick up your purchase at Rusta",
        intro:
          "Shopped at Rusta and the trolley got fuller than planned? Garden furniture, storage shelves, planters and seasonal decorations in large boxes — it rarely all fits in the car. Rusta's low prices make it easy to shop big, but getting it home becomes a challenge. We solve it. Ungdomskraft picks up your purchase directly at Rusta, loads the van and delivers to your home — with carrying and placement included. We collect from all Rusta stores in Stockholm and Gothenburg.",
      },
      featureCards: {
        sinceText: "Rusta delivery since 2014",
        bookYourText: "Book your Rusta delivery",
        bookDescText:
          "Enter the Rusta store address and your home address, describe what you bought and choose a time. You see the price instantly.",
        vehicleTitleText: "Vans perfect for Rusta deliveries",
        vehicleDescText:
          "We arrive with the right size van — whether you're picking up a shelf or an entire garden setup. No need to rent a trailer, drive yourself or make multiple trips.",
      },
      itemsSection: {
        heading: "Rusta products we pick up and deliver home",
        items: [
          {
            name: "Garden & outdoor",
            description:
              "Garden furniture, parasols, grills, planters, raised beds and fencing",
            image: "/patio-furniture.webp",
          },
          {
            name: "Storage & shelving",
            description:
              "Storage shelves, garage shelving, tool cabinets and organisation systems",
            image: "/wardrobe.webp",
          },
          {
            name: "Furniture & decor",
            description:
              "Coffee tables, dining sets, chairs, rugs, lamps and decorations",
            image: "/sofa.webp",
          },
          {
            name: "Building & renovation",
            description:
              "Paint, tools, lighting, electrical accessories and heavier building supplies",
            image: "/box.webp",
          },
          {
            name: "Kitchenware",
            description:
              "Kitchen tools, porcelain, frying pans, storage jars and kitchen appliances",
            image: "/dishes.webp",
          },
          {
            name: "Seasonal items",
            description:
              "Christmas decorations, summer items, camping gear and other bulky seasonal products",
            image: "/painting.webp",
          },
        ],
      },
      seoContent: {
        mainHeading:
          "Delivery from Rusta — we pick up, drive and carry inside for you",
        mainText:
          "Rusta is Sweden's popular budget department store with an enormous range — everything from garden furniture, home decor and storage solutions to building materials, kitchenware, textiles and seasonal products. The stores in Stockholm and Gothenburg attract hundreds of thousands of customers every week with their campaign prices and wide selection. The problem many Rusta customers recognise: you go in to buy a planter and a lamp, and come out with a full trolley — garden furniture, storage shelves, large planters, decorations and half a garden kit. All at fantastic prices, but completely impossible to fit in your car. Garden furniture sets in boxes, garage shelving in flatpack and large ceramic planters take up enormous space. Rusta doesn't always offer their own delivery, and when they do the options are limited and waiting times are long. Renting a trailer costs time and money, and making multiple car trips wastes the whole afternoon. Ungdomskraft offers a fast and smooth alternative. We pick up your Rusta purchase directly at the store, load everything into the van and drive to your home. Carrying to the right spot is always included — we carry to the garden, patio, garage, storage room or wherever you want it. You see the price instantly when booking, no hidden fees. We collect from all Rusta stores in Stockholm and Gothenburg. Every week we make hundreds of deliveries from Rusta and similar stores. Our crew are used to handling everything from heavy metal storage shelves to fragile planters and decorations. We load efficiently so nothing gets damaged during transport and carry inside with care, whether it's through a narrow stairwell or out to the patio.",
        preparationHeading: "Prepare for your Rusta delivery",
        preparationText:
          "Make sure the purchase is paid for and ready for collection. Have your home address and any door codes ready. If you've bought a lot, make a quick list of the largest items so we can choose the right van size. Check that the van can park near the entrance at your home. If items are going to the garden or patio, make sure the gate or passage is open. When booking, specify any special requests, such as carrying into the garage or placing on the patio.",
        additionalSections: [
          {
            heading: "Garden furniture and outdoor product delivery from Rusta",
            content:
              "Rusta has one of the market's widest ranges of garden furniture at budget prices — lounge sets, outdoor dining sets, parasols, grills, raised beds, large planters and fencing. During spring and summer demand is enormous, and campaign items sell out quickly. The problem: a lounge set in boxes becomes 3–5 large packages, parasols and seat cushions take up lots of space, and heavy ceramic or concrete planters weigh 20–40 kg each. It all won't fit in the car — especially if you take advantage of multiple campaign items at once. With Ungdomskraft's delivery we pick up everything at Rusta and deliver directly to your garden, patio or balcony. We carry through the house or around the building depending on what works best. No dragging heavy boxes and risking scratched floors.",
          },
          {
            heading: "Storage solution and shelving delivery from Rusta",
            content:
              "Rusta offers affordable storage solutions — garage shelving, tool sheds, tool cabinets, storage shelves and organisation systems. These products are often in flatpack and can be surprisingly heavy — a metal garage shelf weighs 20–40 kg and a tool cabinet even more. With our delivery we pick up everything at Rusta and deliver to the right spot — the garage, basement, storage room or living room. We carry inside regardless of where the storage solution needs to go. Perfect for anyone renovating their garage, organising their basement or building a new storage wall.",
          },
          {
            heading: "Furniture and home decor delivery from Rusta",
            content:
              "Rusta's range of home decor and furniture is constantly growing — coffee tables, chairs, dining sets, shelves, lamps and rugs at prices that are often significantly lower than at furniture chains. The furniture is often sold in flatpack and can be bulky despite compact packaging. A dining set often becomes 3–4 packages, a coffee table 1–2 packages and rugs in rolls can be up to 3 metres long. With Ungdomskraft we pick up everything at Rusta, protect during transport and carry inside at your home. We place the packages in the right room so you can easily assemble.",
          },
          {
            heading: "How Rusta delivery works with Ungdomskraft",
            listItems: [
              {
                label: "Book online",
                text: "Enter the Rusta store address and your home address. Describe what you bought and choose a time. You see the price instantly.",
              },
              {
                label: "We pick up at Rusta",
                text: "Our team arrive with a van. We collect your purchase at the store, load and secure everything with straps.",
              },
              {
                label: "We deliver home",
                text: "We drive to your home and carry to the right spot — garden, garage, basement or living room.",
              },
              {
                label: "Done",
                text: "Your Rusta purchase is at home — carried in and in place. Without lifting a finger.",
              },
            ],
            afterContent:
              "The whole process typically takes 1–3 hours depending on distance and amount. You can follow the status in real time.",
          },
          {
            heading: "We collect from all Rusta stores",
            content:
              "We pick up from all Rusta stores in Stockholm and Gothenburg. In Stockholm: Kungens Kurva, Barkarby, Bromma, Nacka, Täby, Haninge, Sollentuna, Södertälje and all others. In Gothenburg: Sisjön, Bäckebol, Frölunda, Kungälv and all other locations. Just enter the store address when booking and we handle the rest. Shopping at Rusta and another store the same day? We can pick up from multiple locations in the same trip.",
          },
          {
            heading: "Why book Rusta delivery with Ungdomskraft?",
            content:
              "Rusta doesn't always offer their own delivery, and when they do the waiting times are often long and carrying is rarely included. With Ungdomskraft you get fast delivery with carrying to the right spot — whether it's the garden, garage or bedroom. Plus you make a double impact: you get your purchase home smoothly and you help young people enter the workforce. What makes Ungdomskraft unique is that we employ young people and give them real work experience. Every job is staffed by motivated young workers who learn everything from customer service and logistics to physical work. We've been around since 2014 and have completed tens of thousands of transports across Stockholm and Gothenburg. Everything is insured, we have our own vans in multiple sizes and you always see the price before you book. No hidden fees, no surprises.\n\nRusta delivery does not qualify for the Swedish RUT tax deduction, but several of our other services do – including moving help, carrying help and furniture assembly with a 50% deduction on the labour cost. Feel free to combine services in a single booking.",
          },
        ],
      },
      faq: [
        {
          question: "How much does Rusta delivery cost?",
          answer:
            "The price depends on the amount and distance. A smaller Rusta delivery starts at 595 SEK. Larger deliveries with garden furniture or storage shelving cost 749–2 495 SEK. You see the exact price when you book online.",
        },
        {
          question: "Can I get a RUT tax deduction on Rusta delivery?",
          answer:
            "Rusta delivery does not in itself qualify for the Swedish RUT tax deduction. RUT applies to household services carried out in your home, such as moving help, carrying help and furniture assembly. Ungdomskraft offers several RUT-eligible services with a 50% deduction on the labour cost – easily combine them in one booking via our website.",
        },
        {
          question: "Is a van included in the Rusta delivery?",
          answer:
            "Yes, a van is always included. We choose the right size based on your purchase — you don't need to rent a vehicle or trailer.",
        },
        {
          question: "Is carrying inside included?",
          answer:
            "Yes, carrying is always included. We carry your purchase to the right spot — garden, garage, basement or living room — regardless of floor. No extra charges.",
        },
        {
          question: "Which Rusta stores do you pick up from?",
          answer:
            "We pick up from all Rusta stores in Stockholm and Gothenburg: Kungens Kurva, Barkarby, Bromma, Nacka, Täby, Haninge, Sollentuna, Södertälje, Sisjön, Bäckebol, Frölunda, Kungälv and all others. Just enter the store address when booking.",
        },
        {
          question: "Can you deliver garden furniture to the patio?",
          answer:
            "Yes, we deliver directly to your garden, patio or balcony. We carry through the house or around the building — no dragging heavy boxes yourself.",
        },
        {
          question: "Can you deliver to the garage or storage room?",
          answer:
            "Yes, we carry to the garage, basement, storage room or wherever you want. Specify the drop-off location when booking.",
        },
        {
          question: "Can you carry heavy items up stairs?",
          answer:
            "Yes, we carry to any floor — with or without a lift. Our crew are used to heavy items and narrow stairwells.",
        },
        {
          question: "Do you protect items during transport?",
          answer:
            "Yes, we secure all packages with straps in the van. Fragile items like planters and glassware get extra blanket protection. Everything is insured — if something is damaged we replace it.",
        },
        {
          question: "How quickly can you deliver from Rusta?",
          answer:
            "We often offer delivery at short notice — often the same day — within a few hours. Book online and see available times instantly.",
        },
        {
          question: "Is Rusta delivery available on weekends?",
          answer:
            "Yes, we offer delivery on weekends and some evenings. Check that the Rusta store is open at your chosen time — we can't pick up if the store is closed.",
        },
        {
          question: "Can you pick up from Rusta and another store in the same trip?",
          answer:
            "Yes, we can pick up from several stores during the same run if they're within a reasonable distance. Describe all pickup points when you book — for example Rusta and Jula in the same trip.",
        },
        {
          question: "Do I need to be at the store?",
          answer:
            "No, you don't need to be at Rusta. Make sure the purchase is paid for and ready to collect — we handle the rest. You just need to be home to receive the delivery.",
        },
        {
          question: "Is the Rusta delivery insured?",
          answer:
            "Yes, all our transports are fully insured. If anything is damaged during loading, transport or carrying we replace it. You can book with confidence.",
        },
        {
          question: "In which areas do you offer Rusta delivery?",
          answer:
            "We offer Rusta delivery across the entire Stockholm region: Botkyrka, Danderyd, Ekerö, Haninge, Huddinge, Järfälla, Lidingö, Nacka, Norrtälje, Nykvarn, Nynäshamn, Salem, Sigtuna, Sollentuna, Solna, Stockholm, Sundbyberg, Södertälje, Tyresö, Täby, Upplands Väsby, Upplands-Bro, Vallentuna, Vaxholm, Värmdö and Österåker. We're also in the Gothenburg region: Ale, Alingsås, Göteborg, Härryda, Kungälv, Kungsbacka, Lerum, Lilla Edet, Mölndal, Partille, Stenungsund, Tjörn and Öckerö.",
        },
        {
          question: "Why choose Ungdomskraft for Rusta delivery?",
          answer:
            "We always include a van, carrying help and placement in the right spot. You see the price instantly when booking, we can often deliver the same day and everything is insured.",
        },
      ],
      priceHint: PACKAGE_PRICE_HINT_EN.bud,
      pricingRows: getPackagePricingRows("bud", "en"),
      dynamicHeadings: {
        threeSteps: "Rusta delivery in 3 easy steps",
        step2Text:
          "Our crew pick up your purchase at Rusta, load and secure everything in the van. Then we drive home to you.",
        step3Title: "All in place",
        step3Desc:
          "Your Rusta purchase is at home — carried in and in place. Done.",
        ctaHeading: "Book Rusta delivery today",
        ctaText:
          "Enter the Rusta store address and your home address, describe what you bought and choose a time. You see the price instantly.",
      },
    },
  },

  {
    // Hemleverans Jula — config för /hemleverans-jula
    // Vinkel: Hemleverans av Jula-köp (verktyg, trädgård, bygg, bil, hem)
    // som inte får plats i bilen. Vi hämtar i butiken och kör hem.
    slug: "hemleverans-jula",
    serviceCategory: "hemleverans",

    meta: {
      title:
        "Hemleverans Jula – Vi Hämtar Ditt Köp Och Kör Hem | Ungdomskraft",
      description:
        "Köpt verktyg, trädgårdsmöbler eller byggmaterial på Jula som inte får plats i bilen? Vi hämtar i butiken och levererar hem med inbärning. Boka hemleverans Jula idag.",
      h1: "Hemleverans Jula",
    },

    hero: {
      heading: "Vi hämtar ditt köp på Jula",
      intro:
        "Handlat på Jula och bilen räcker inte till? Verktyg, trädgårdsmöbler, byggmaterial och tunga maskiner – det mesta som gör Jula populärt är också det som är svårast att få hem. Vi löser det. Ungdomskraft hämtar ditt köp direkt på Jula, lastar skåpbilen och levererar hem till dig – med inbärning och uppställning på plats. Vi kör från alla Jula-butiker i Stockholm och Göteborg.",
      heroImage: "/hero-hemleverans.png",
    },

    featureCards: {
      sinceText: "Jula-leverans sedan 2014",
      storyText:
        "Sedan 2014 har Ungdomskraft hjälpt tusentals kunder att få hem sina Jula-köp – från tunga verktygsmaskiner och byggmaterial till trädgårdsmöbler och bildelar. Våra ungdomar hämtar i butiken, lastar skåpbilen och bär in hemma hos dig.",
      bookYourText: "Boka din Jula-leverans",
      bookDescText:
        "Ange Jula-butikens adress och din hemadress, beskriv vad som ska hämtas och välj en tid. Du ser priset direkt.",
      vehicleTitleText: "Skåpbilar perfekta för Jula-leveranser",
      vehicleDescText:
        "Vi kommer med rätt storlek på skåpbil – oavsett om du hämtar en borrmaskin eller ett helt garagebygge. Du slipper hyra släp, köra själv eller göra flera turer.",
    },

    itemsSection: {
      heading: "Jula-produkter vi hämtar och levererar hem",
      items: [
        {
          name: "Verktyg & maskiner",
          description:
            "Borrmaskiner, svarvar, kompressorer, bänkslipar, svetsaggregat och verktygsvagnar",
          image: "/toolbox.webp",
        },
        {
          name: "Trädgård & utomhus",
          description:
            "Trädgårdsmöbler, grillar, studsmattor, robotgräsklippare, växthus och staket",
          image: "/patio-furniture.webp",
        },
        {
          name: "Bygg & renovering",
          description:
            "Virke, gipsskivor, isolering, golv, fönster, dörrar och VVS-material",
          image: "/wardrobe.webp",
        },
        {
          name: "Förvaring & garage",
          description:
            "Garagehyllor, verktygsskåp, arbetsbänkar, redskapshållare och organisationssystem",
          image: "/office-desk.webp",
        },
        {
          name: "Bil & motor",
          description:
            "Takboxar, dragkrokar, däck, motorolja och biltillbehör i större förpackningar",
          image: "/microwave.webp",
        },
        {
          name: "El & belysning",
          description:
            "Strålkastare, solcellspaneler, elcentraler, kabelvindor och tyngre installationsmaterial",
          image: "/tv.webp",
        },
      ],
    },

    seoContent: {
      mainHeading:
        "Hemleverans från Jula – vi hämtar, kör och bär in åt dig",
      mainText:
        "Jula är en av Sveriges mest populära butikskedjor för hem, trädgård, verkstad och fordon. Med tusentals produkter inom bygg, verktyg, trädgård, el, VVS, bil och fritid lockar Jula hundratusentals kunder varje vecka till sina butiker i Stockholm och Göteborg. Det som gör Jula unikt är bredden – under samma tak hittar du allt från en skruvdragare och en hink spackel till en komplett trädgårdsmöbelgrupp, ett växthus i glas och en robotgräsklippare. Problemet som väldigt många Jula-kunder känner igen: du går in för att köpa en borrmaskin och ett paket skruv, och kommer ut med en full vagn. Garagehyllor i flatpack, en verktygsvagn på 40 kg, trädgårdsmöbler i stora kartonger, ett parasoll och kanske ett par säckar jord. Allt till bra priser – men inte en chans att allt ryms i bilen. Jula-butikerna ligger dessutom ofta i externhandelsområden utanför stadskärnan, så att göra flera turer tar hela dagen. Att hyra släp löser problemet kortsiktigt men kostar både tid och pengar, och du måste fortfarande lyfta allt själv. Ungdomskraft erbjuder ett snabbt och smidigt alternativ. Vi hämtar ditt Jula-köp direkt i butiken, lastar allt i skåpbilen och kör hem till dig. Inbärning till rätt plats ingår alltid – vi bär in till garaget, verkstaden, trädgården, källaren eller vilken plats du vill. Du ser priset direkt vid bokning, inga dolda avgifter tillkommer. Vi kör från alla Jula-butiker i Stockholm och Göteborg. Varje vecka hämtar vi hundratals leveranser från Jula och liknande butiker. Våra ungdomar är vana vid att hantera allt från tunga verktygsmaskiner och byggsäckar till ömtåliga elektronikprodukter och skrymmande trädgårdsmöbler. Vi lastar effektivt och säkrar med spännband så att inget skadas under transporten.",
      preparationHeading: "Förbered inför din Jula-leverans",
      preparationText:
        "Se till att köpet är betalt och klart för upphämtning – meddela Julas personal att någon hämtar åt dig om det behövs. Ha din hemadress och eventuell portkod redo. Om du handlat tungt eller skrymmande – lista gärna de största föremålen så att vi kan välja rätt skåpbilsstorlek. Kontrollera att skåpbilen kan parkera nära porten eller garageinfarten hemma hos dig. Om varorna ska till verkstaden, garaget eller trädgården – se till att passage och dörrar är öppna. Ange vid bokning om det finns speciella önskemål, till exempel att allt ska in i garaget eller ner i källaren.",
      additionalSections: [
        {
          heading: "Hemleverans av verktyg och maskiner från Jula",
          content:
            "Jula är känt för sitt enorma utbud av verktyg och maskiner – allt från handhållna skruvdragare till tunga bänkslipar, kompressorer, svetsaggregat och verktygsvagnar. Många av dessa produkter väger 20–60 kg och levereras i stora, tunga kartonger. En verktygsvagn i stål väger ofta runt 40 kg tom, en kompressor 25–50 kg och ett svetsaggregat kan vara ännu tyngre. Att bära dessa produkter själv från butiken till bilen och sedan in i garaget eller verkstaden är både tungt och riskabelt – särskilt i trappor eller trånga utrymmen. Med Ungdomskrafts hemleverans hämtar vi allt på Jula och levererar direkt till din verkstad, garage eller förråd. Våra ungdomar lyfter och bär regelbundet – du slipper riskera ryggskott eller skador på produkterna.",
        },
        {
          heading: "Hemleverans av trädgårdsprodukter från Jula",
          content:
            "Jula har ett brett sortiment för trädgård och utomhus: trädgårdsmöbler, parasoll, grillar, studsmattor, robotgräsklippare, växthus, staket, markplattor och stora krukor. Under vår och sommar exploderar efterfrågan, och kampanjvaror säljer slut snabbt. Utmaningen är transporten hem – en loungegrupp i kartong blir 3–5 stora kollin, ett växthus är enormt skrymmande, och studsmattor i förpackning kan vara 2 meter långa. Tunga trädgårdsprodukter som markplattor, betongkrukor och grindstolpar väger dessutom ofta 30–80 kg styck. Med Ungdomskraft hämtar vi hela din trädgårdsbeställning på Jula och kör hem till dig. Vi bär ut till trädgården, altanen eller balkongen – genom huset eller runt fasaden. Perfekt för dig som planerar en trädgårdsrenovering men saknar lämpligt fordon.",
        },
        {
          heading: "Hemleverans av byggmaterial från Jula",
          content:
            "Jula erbjuder ett komplett sortiment av byggmaterial – virke, gipsskivor, isolering, skivmaterial, golv, fönster, dörrar, färg och VVS-tillbehör. Dessa produkter är nästan alltid för tunga eller skrymmande för en vanlig personbil. Gipsskivor är 2,4 meter långa, virkesbuntar väger tiotals kilo och isoleringsskivor tar enorm plats. Jula erbjuder ibland utkörning, men väntetiderna kan vara långa och tiderna begränsade. Med Ungdomskraft bokar du leverans när det passar dig. Vi hämtar på Jula, lastar byggmaterialet säkert i skåpbilen och kör hem till dig. Vi bär in till bygget, garaget eller förrådet – oavsett var renoveringen pågår. Perfekt komplement för hemmafixaren som inte har tillgång till pickup eller släpvagn.",
        },
        {
          heading: "Så fungerar Jula-leverans med Ungdomskraft",
          listItems: [
            {
              label: "Boka online",
              text: "Ange Jula-butikens adress och din hemadress. Beskriv vad du handlat och välj en tid. Du ser priset direkt.",
            },
            {
              label: "Vi hämtar på Jula",
              text: "Vårt team kommer med skåpbil. Vi hämtar ditt köp i butiken, lastar och säkrar allt med spännband.",
            },
            {
              label: "Vi levererar hem",
              text: "Vi kör hem till dig, bär in till rätt plats – garaget, verkstaden, trädgården eller källaren.",
            },
            {
              label: "Klart",
              text: "Ditt Jula-köp står hemma hos dig – inburet och på plats. Utan att du lyfte ett finger.",
            },
          ],
          afterContent:
            "Hela processen tar normalt 1–3 timmar beroende på avstånd och mängd. Du kan följa statusen i realtid.",
        },
        {
          heading: "Vi kör från alla Jula-butiker",
          content:
            "Vi hämtar från alla Jula-butiker i Stockholm och Göteborg. I Stockholm: Kungens Kurva, Barkarby, Bromma, Nacka, Täby, Haninge och alla övriga. I Göteborg: Sisjön, Bäckebol, Kungälv och alla övriga butiker. Ange bara butikens adress vid bokning så sköter vi resten. Ska du handla på Jula och en annan butik samma dag? Vi kan hämta från flera ställen i samma tur – till exempel Jula och Bauhaus eller Jula och Biltema i samma körning.",
        },
        {
          heading: "Varför boka Jula-leverans med Ungdomskraft?",
          content:
            "Jula erbjuder ibland egen hemleverans, men väntetiderna kan vara långa och inbärning till rätt plats sällan inkluderad. Med Ungdomskraft får du snabb leverans med inbärning – oavsett om varorna ska till garaget, verkstaden, trädgården eller sovrummet. Dessutom gör du dubbel nytta varje gång du bokar: du får hem ditt köp smidigt och du bidrar till att unga människor kommer in på arbetsmarknaden.\n\nDet som gör Ungdomskraft unikt är vår samhällsnytta. Vi anställer unga människor – ofta i åldern 18–25 – som annars har svårt att få sitt första jobb. Många saknar arbetslivserfarenhet, nätverk eller kontakter i branschen. Genom att arbeta hos oss får de riktig, praktisk erfarenhet som arbetsgivare värdesätter: kundkontakt ansikte mot ansikte, logistik och planering, fysiskt arbete under tidspress, samarbete i team och eget ansvar för att ett uppdrag slutförs ordentligt. De lär sig att kommunicera professionellt med kunder, att lösa problem på plats och att leverera kvalitet utan genvägar.\n\nVarje Jula-leverans bemannas av våra ungdomar. De hämtar ditt köp i butiken, lastar skåpbilen metodiskt, kör säkert till din adress och bär in till exakt den plats du önskar. De behandlar dina varor med samma omsorg som om det vore deras egna – för de vet att varje nöjd kund stärker deras CV och öppnar dörrar till framtida jobb. Den energi och det engagemang de visar märks i varje uppdrag.\n\nVi har funnits sedan 2014 och har genomfört tiotusentals transporter och leveranser i Stockholm och Göteborg. Under åren har hundratals ungdomar fått sin första arbetslivserfarenhet genom Ungdomskraft – och många har gått vidare till fasta tjänster inom logistik, bygg, kundservice och andra branscher. Vår modell fungerar för att den bygger på riktigt arbete med riktiga kunder, inte praktik utan ansvar.\n\nHållbarhet genomsyrar allt vi gör. Vi planerar körningar effektivt för att minimera tomkörning. Vi samlastar när det är möjligt. Vi återvinner förpackningsmaterial och ser till att wellpapp, frigolit och plast sorteras korrekt efter leverans. För företag som beställer via Jula-leverans erbjuder vi dokumentation som kan användas i CSR-rapportering – varje bokning stödjer ungdomsarbete och hållbar logistik.\n\nAllt är helförsäkrat. Vi har egna skåpbilar i flera storlekar – från liten skåpbil för verktyg och småsaker till stor skåpbil som rymmer byggmaterial och trädgårdsmöbler. Du ser alltid priset innan du bokar. Inga dolda avgifter, inga överraskningar. Och om något mot förmodan skadas under transporten ersätter vi det omedelbart.\n\nNär du bokar Jula-leverans med Ungdomskraft väljer du mer än en transporttjänst. Du väljer att ge en ung människa möjligheten att lära, växa och bygga sin framtid. Det är smidig leverans med mening.\n\nHemleverans från Jula ger inte rätt till RUT-avdrag, men flera av våra andra tjänster gör det – exempelvis flytt, bärhjälp och möbelmontering med 50 % avdrag på arbetskostnaden. Kombinera gärna tjänster i en och samma bokning.",
        },
      ],
    },

    faq: [
      {
        question: "Vad kostar hemleverans från Jula?",
        answer:
          "Priset beror på mängden och avståndet. En mindre Jula-leverans med verktyg eller tillbehör kostar från 595 kr. Större leveranser med trädgårdsmöbler eller byggmaterial kostar från 995–2 495 kr. Du ser det exakta priset när du bokar online.",
      },
      {
        question: "Kan jag få RUT-avdrag på hemleverans från Jula?",
        answer:
          "Hemleverans från Jula ger i sig inte rätt till RUT-avdrag. RUT gäller hushållsnära tjänster som utförs i din bostad, exempelvis flytt, bärhjälp och möbelmontering. Ungdomskraft erbjuder flera RUT-berättigade tjänster med 50 % avdrag på arbetskostnaden – kombinera enkelt i en bokning via vår hemsida.",
      },
      {
        question: "Ingår skåpbil i Jula-leveransen?",
        answer:
          "Ja, skåpbil ingår alltid. Vi väljer rätt storlek utifrån ditt köp – du behöver inte hyra bil eller släp.",
      },
      {
        question: "Ingår inbärning i priset för Jula-leveransen?",
        answer:
          "Ja, inbärning ingår alltid. Vi bär ditt köp till rätt plats – garaget, verkstaden, trädgården eller källaren – oavsett våning. Inga extra avgifter.",
      },
      {
        question: "Vilka Jula-butiker hämtar ni från?",
        answer:
          "Vi hämtar från alla Jula-butiker i Stockholm och Göteborg: Kungens Kurva, Barkarby, Bromma, Nacka, Täby, Haninge, Sisjön, Bäckebol, Kungälv och alla övriga. Ange bara butikens adress vid bokning.",
      },
      {
        question: "Kan ni leverera tunga verktyg och maskiner från Jula?",
        answer:
          "Ja, vi hanterar tunga produkter dagligen – verktygsvagnar, kompressorer, bänkslipar och svetsaggregat. Vi bär in till garaget, verkstaden eller källaren oavsett tyngd.",
      },
      {
        question: "Kan ni leverera trädgårdsmöbler från Jula till altanen?",
        answer:
          "Ja, vi levererar direkt till din trädgård, altan eller balkong. Vi bär ut genom huset eller runt fasaden – du slipper släpa tunga kartonger själv.",
      },
      {
        question: "Kan ni leverera byggmaterial från Jula?",
        answer:
          "Ja, vi transporterar virke, gipsskivor, isolering, golv och annat byggmaterial. Vi lastar säkert i skåpbilen och bär in till bygget, garaget eller förrådet.",
      },
      {
        question: "Kan ni bära tunga Jula-köp i trappor?",
        answer:
          "Ja, vi bär till vilken våning som helst – med eller utan hiss. Våra ungdomar är vana vid tunga lyft och trånga trapphus.",
      },
      {
        question: "Skyddar ni sakerna under Jula-transporten?",
        answer:
          "Ja, vi säkrar alla kollin med spännband i skåpbilen. Ömtåliga föremål skyddas extra med filtar. Allt är försäkrat – om något skadas ersätter vi det.",
      },
      {
        question: "Hur snabbt kan ni leverera från Jula?",
        answer:
          "Vi erbjuder ofta hemleverans med kort varsel – ofta redan samma dag – inom några timmar. Boka online och se tillgängliga tider direkt.",
      },
      {
        question: "Erbjuder ni Jula-leverans på helger?",
        answer:
          "Ja, vi erbjuder hemleverans på helger och vissa kvällar. Kontrollera att Jula-butiken har öppet den tid du bokar – vi kan inte hämta om butiken är stängd.",
      },
      {
        question: "Kan ni hämta från Jula och en annan butik i samma tur?",
        answer:
          "Ja, vi kan hämta från flera butiker under samma körning om de ligger i rimligt avstånd. Beskriv alla hämtpunkter vid bokning – till exempel Jula och Bauhaus i samma tur.",
      },
      {
        question: "Behöver jag vara med på Jula vid upphämtningen?",
        answer:
          "Nej, du behöver inte vara med på Jula. Se till att köpet är betalt och redo att hämtas – vi sköter resten. Du behöver bara vara hemma för att ta emot leveransen.",
      },
      {
        question:
          "I vilka kommuner erbjuder ni hemleverans från Jula?",
        answer:
          "Vi erbjuder Jula-leverans i hela Stockholmsområdet: Botkyrka, Danderyd, Ekerö, Haninge, Huddinge, Järfälla, Lidingö, Nacka, Norrtälje, Nykvarn, Nynäshamn, Salem, Sigtuna, Sollentuna, Solna, Stockholm, Sundbyberg, Södertälje, Tyresö, Täby, Upplands Väsby, Upplands-Bro, Vallentuna, Vaxholm, Värmdö och Österåker. Vi finns även i Göteborgsområdet: Ale, Alingsås, Göteborg, Härryda, Kungälv, Kungsbacka, Lerum, Lilla Edet, Mölndal, Partille, Stenungsund, Tjörn och Öckerö.",
      },
      {
        question: "Varför välja Ungdomskraft för Jula-leverans?",
        answer:
          "Hos oss ingår alltid skåpbil, bärhjälp och inbärning till rätt plats. Du ser priset direkt vid bokning, vi kan ofta köra samma dag och allt är försäkrat.",
      },
    ],

    priceHint: PACKAGE_PRICE_HINT_SV.bud,

    pricingRows: getPackagePricingRows("bud", "sv"),

    dynamicHeadings: {
      threeSteps: "Jula-leverans i 3 enkla steg",
      step2Text:
        "Våra ungdomar hämtar ditt köp på Jula, lastar och säkrar allt i skåpbilen. Sedan kör vi hem till dig.",
      step3Title: "Allt på plats",
      step3Desc:
        "Ditt Jula-köp står hemma hos dig – inburet och på plats. Klart.",
      ctaHeading: "Boka Jula-leverans idag",
      ctaText:
        "Ange Jula-butikens adress och din hemadress, beskriv vad du handlat och välj en tid. Du ser priset direkt.",
    },

    en: {
      slug: "jula-delivery",
      meta: {
        title:
          "Jula Delivery – We Pick Up Your Purchase And Deliver Home | Ungdomskraft",
        description:
          "Bought tools or building materials at Jula that won't fit in your car? We pick up at the store and deliver home with carrying included. Book today!",
        h1: "Jula delivery",
      },
      hero: {
        heading: "We pick up your purchase at Jula",
        intro:
          "Shopped at Jula and your car can't handle it all? Tools, garden furniture, building materials and heavy machinery — the things that make Jula popular are also the hardest to get home. We solve it. Ungdomskraft picks up your purchase directly at Jula, loads the van and delivers to your home — with carrying and placement included. We collect from all Jula stores in Stockholm and Gothenburg.",
      },
      featureCards: {
        sinceText: "Jula delivery since 2014",
        bookYourText: "Book your Jula delivery",
        bookDescText:
          "Enter the Jula store address and your home address, describe what you bought and choose a time. You see the price instantly.",
        vehicleTitleText: "Vans perfect for Jula deliveries",
        vehicleDescText:
          "We arrive with the right size van — whether you're picking up a drill or an entire garage project. No need to rent a trailer, drive yourself or make multiple trips.",
      },
      itemsSection: {
        heading: "Jula products we pick up and deliver home",
        items: [
          {
            name: "Tools & machinery",
            description:
              "Power drills, lathes, compressors, bench grinders, welding machines and tool trolleys",
            image: "/toolbox.webp",
          },
          {
            name: "Garden & outdoor",
            description:
              "Garden furniture, grills, trampolines, robotic mowers, greenhouses and fencing",
            image: "/patio-furniture.webp",
          },
          {
            name: "Building & renovation",
            description:
              "Timber, plasterboard, insulation, flooring, windows, doors and plumbing supplies",
            image: "/wardrobe.webp",
          },
          {
            name: "Storage & garage",
            description:
              "Garage shelving, tool cabinets, workbenches, tool holders and organisation systems",
            image: "/office-desk.webp",
          },
          {
            name: "Car & motor",
            description:
              "Roof boxes, tow bars, tyres, motor oil and car accessories in bulk",
            image: "/microwave.webp",
          },
          {
            name: "Electrical & lighting",
            description:
              "Floodlights, solar panels, consumer units, cable drums and heavy installation materials",
            image: "/tv.webp",
          },
        ],
      },
      seoContent: {
        mainHeading:
          "Delivery from Jula — we pick up, drive and carry inside for you",
        mainText:
          "Jula is one of Sweden's most popular retail chains for home, garden, workshop and automotive products. With thousands of products spanning building materials, tools, garden, electrical, plumbing, car and leisure, Jula attracts hundreds of thousands of customers every week to their stores in Stockholm and Gothenburg. What makes Jula unique is the range — under one roof you'll find everything from a cordless drill and a tub of filler to a complete garden furniture set, a glass greenhouse and a robotic lawn mower. The problem many Jula customers recognise: you go in to buy a drill and a pack of screws, and come out with a full trolley. Garage shelving in flatpack, a 40 kg tool trolley, garden furniture in large boxes, a parasol and maybe a couple of bags of soil. All at great prices — but no chance everything fits in the car. Jula stores are also often located in out-of-town retail parks, so making multiple trips takes the whole day. Renting a trailer solves the problem temporarily but costs both time and money, and you still have to do all the heavy lifting yourself. Ungdomskraft offers a fast and smooth alternative. We pick up your Jula purchase directly at the store, load everything into the van and drive to your home. Carrying to the right spot is always included — we carry to the garage, workshop, garden, basement or wherever you want. You see the price instantly when booking, no hidden fees. We collect from all Jula stores in Stockholm and Gothenburg. Every week we make hundreds of deliveries from Jula and similar stores. Our crew are used to handling everything from heavy tool machinery and building sacks to fragile electronics and bulky garden furniture. We load efficiently and secure with straps so nothing gets damaged during transport.",
        preparationHeading: "Prepare for your Jula delivery",
        preparationText:
          "Make sure the purchase is paid for and ready for collection — notify Jula staff if someone is picking up on your behalf. Have your home address and any door codes ready. If you've bought heavy or bulky items, list the largest ones so we can choose the right van size. Check that the van can park near the entrance or garage at your home. If items are going to the workshop, garage or garden, make sure the passage and doors are open. When booking, specify any special requests, such as carrying everything into the garage or down to the basement.",
        additionalSections: [
          {
            heading: "Tool and machinery delivery from Jula",
            content:
              "Jula is known for its enormous range of tools and machinery — everything from handheld drills to heavy bench grinders, compressors, welding machines and tool trolleys. Many of these products weigh 20–60 kg and come in large, heavy boxes. A steel tool trolley often weighs around 40 kg empty, a compressor 25–50 kg and a welding machine can be even heavier. Carrying these products yourself from the store to the car and then into the garage or workshop is both strenuous and risky — especially on stairs or in tight spaces. With Ungdomskraft's delivery we pick up everything at Jula and deliver directly to your workshop, garage or storage room. Our crew lift and carry regularly — you avoid risking a bad back or damage to the products.",
          },
          {
            heading: "Garden product delivery from Jula",
            content:
              "Jula has a wide range for garden and outdoor: garden furniture, parasols, grills, trampolines, robotic mowers, greenhouses, fencing, paving slabs and large planters. During spring and summer demand explodes, and campaign items sell out quickly. The challenge is getting it home — a lounge set in boxes becomes 3–5 large packages, a greenhouse is enormously bulky, and trampolines in packaging can be 2 metres long. Heavy garden products like paving slabs, concrete planters and gate posts often weigh 30–80 kg each. With Ungdomskraft we collect your entire garden order from Jula and drive it home. We carry out to the garden, patio or balcony — through the house or around the building. Perfect for anyone planning a garden renovation but lacking a suitable vehicle.",
          },
          {
            heading: "Building material delivery from Jula",
            content:
              "Jula offers a complete range of building materials — timber, plasterboard, insulation, sheet materials, flooring, windows, doors, paint and plumbing accessories. These products are almost always too heavy or bulky for an ordinary car. Plasterboard is 2.4 metres long, timber bundles weigh dozens of kilos and insulation boards take up enormous space. Jula sometimes offers delivery, but waiting times can be long and time slots limited. With Ungdomskraft you book delivery when it suits you. We pick up at Jula, load the building materials safely into the van and drive to your home. We carry to the build site, garage or storage room — wherever the renovation is taking place. The perfect complement for the DIY enthusiast who doesn't have access to a pickup truck or trailer.",
          },
          {
            heading: "How Jula delivery works with Ungdomskraft",
            listItems: [
              {
                label: "Book online",
                text: "Enter the Jula store address and your home address. Describe what you bought and choose a time. You see the price instantly.",
              },
              {
                label: "We pick up at Jula",
                text: "Our team arrive with a van. We collect your purchase at the store, load and secure everything with straps.",
              },
              {
                label: "We deliver home",
                text: "We drive to your home and carry to the right spot — garage, workshop, garden or basement.",
              },
              {
                label: "Done",
                text: "Your Jula purchase is at home — carried in and in place. Without lifting a finger.",
              },
            ],
            afterContent:
              "The whole process typically takes 1–3 hours depending on distance and amount. You can follow the status in real time.",
          },
          {
            heading: "We collect from all Jula stores",
            content:
              "We pick up from all Jula stores in Stockholm and Gothenburg. In Stockholm: Kungens Kurva, Barkarby, Bromma, Nacka, Täby, Haninge and all others. In Gothenburg: Sisjön, Bäckebol, Kungälv and all other locations. Just enter the store address when booking and we handle the rest. Shopping at Jula and another store the same day? We can pick up from multiple locations in the same trip — for example Jula and Bauhaus or Jula and Biltema in the same run.",
          },
          {
            heading: "Why book Jula delivery with Ungdomskraft?",
            content:
              "Jula sometimes offers delivery through their partners, but waiting times can be long, time slots limited and carrying to the right spot is rarely included. With Ungdomskraft you get fast delivery with carrying inside — whether items go to the garage, workshop, garden or bedroom. Plus you make a double impact every time you book: you get your purchase home smoothly and you help young people enter the workforce.\n\nWhat makes Ungdomskraft unique is our social mission. We employ young people — often aged 18–25 — who otherwise struggle to get their first job. Many lack work experience, networks or industry contacts. By working with us they gain real, practical experience that employers value: face-to-face customer service, logistics and planning, physical work under time pressure, teamwork and personal responsibility for completing a job properly. They learn to communicate professionally with customers, solve problems on site and deliver quality without shortcuts.\n\nEvery Jula delivery is staffed by our young workers. They collect your purchase at the store, load the van methodically, drive safely to your address and carry to the exact spot you want. They handle your goods with the same care as if they were their own — because they know that every satisfied customer strengthens their CV and opens doors to future employment. The energy and commitment they show is evident in every job.\n\nWe've been around since 2014 and have completed tens of thousands of transports and deliveries across Stockholm and Gothenburg. Over the years, hundreds of young people have gained their first work experience through Ungdomskraft — and many have gone on to permanent positions in logistics, construction, customer service and other industries. Our model works because it's built on real work with real customers, not internships without responsibility.\n\nSustainability runs through everything we do. We plan routes efficiently to minimise empty runs. We consolidate loads when possible. We recycle packaging materials and ensure cardboard, polystyrene and plastic are sorted correctly after delivery. For companies ordering Jula delivery we offer documentation for CSR reporting — every booking supports youth employment and sustainable logistics.\n\nEverything is fully insured. We have our own vans in multiple sizes — from small vans for tools and accessories to large vans that fit building materials and garden furniture. You always see the price before you book. No hidden fees, no surprises. And if anything is unexpectedly damaged during transport we replace it immediately.\n\nWhen you book Jula delivery with Ungdomskraft, you choose more than a transport service. You choose to give a young person the opportunity to learn, grow and build their future. It's smooth delivery with purpose.\n\nJula delivery does not qualify for the Swedish RUT tax deduction, but several of our other services do – including moving help, carrying help and furniture assembly with a 50% deduction on the labour cost. Feel free to combine services in a single booking.",
          },
        ],
      },
      faq: [
        {
          question: "How much does Jula delivery cost?",
          answer:
            "The price depends on the amount and distance. A smaller Jula delivery with tools or accessories starts at 595 SEK. Larger deliveries with garden furniture or building materials cost 749–2 495 SEK. You see the exact price when you book online.",
        },
        {
          question: "Can I get a RUT tax deduction on Jula delivery?",
          answer:
            "Jula delivery does not in itself qualify for the Swedish RUT tax deduction. RUT applies to household services carried out in your home, such as moving help, carrying help and furniture assembly. Ungdomskraft offers several RUT-eligible services with a 50% deduction on the labour cost – easily combine them in one booking via our website.",
        },
        {
          question: "Is a van included in the Jula delivery?",
          answer:
            "Yes, a van is always included. We choose the right size based on your purchase — you don't need to rent a vehicle or trailer.",
        },
        {
          question: "Is carrying inside included in the Jula delivery price?",
          answer:
            "Yes, carrying is always included. We carry your purchase to the right spot — garage, workshop, garden or basement — regardless of floor. No extra charges.",
        },
        {
          question: "Which Jula stores do you pick up from?",
          answer:
            "We pick up from all Jula stores in Stockholm and Gothenburg: Kungens Kurva, Barkarby, Bromma, Nacka, Täby, Haninge, Sisjön, Bäckebol, Kungälv and all others. Just enter the store address when booking.",
        },
        {
          question: "Can you deliver heavy tools and machinery from Jula?",
          answer:
            "Yes, we handle heavy products daily — tool trolleys, compressors, bench grinders and welding machines. We carry to the garage, workshop or basement regardless of weight.",
        },
        {
          question: "Can you deliver garden furniture from Jula to the patio?",
          answer:
            "Yes, we deliver directly to your garden, patio or balcony. We carry through the house or around the building — no dragging heavy boxes yourself.",
        },
        {
          question: "Can you deliver building materials from Jula?",
          answer:
            "Yes, we transport timber, plasterboard, insulation, flooring and other building materials. We load safely in the van and carry to the build site, garage or storage room.",
        },
        {
          question: "Can you carry heavy Jula purchases up stairs?",
          answer:
            "Yes, we carry to any floor — with or without a lift. Our crew are used to heavy items and narrow stairwells.",
        },
        {
          question: "Do you protect items during Jula transport?",
          answer:
            "Yes, we secure all packages with straps in the van. Fragile items get extra blanket protection. Everything is insured — if something is damaged we replace it.",
        },
        {
          question: "How quickly can you deliver from Jula?",
          answer:
            "We often offer delivery at short notice — often the same day — within a few hours. Book online and see available times instantly.",
        },
        {
          question: "Is Jula delivery available on weekends?",
          answer:
            "Yes, we offer delivery on weekends and some evenings. Check that the Jula store is open at your chosen time — we can't pick up if the store is closed.",
        },
        {
          question: "Can you pick up from Jula and another store in the same trip?",
          answer:
            "Yes, we can pick up from several stores during the same run if they're within a reasonable distance. Describe all pickup points when you book — for example Jula and Bauhaus in the same trip.",
        },
        {
          question: "Do I need to be at the Jula store?",
          answer:
            "No, you don't need to be at Jula. Make sure the purchase is paid for and ready to collect — we handle the rest. You just need to be home to receive the delivery.",
        },
        {
          question: "In which areas do you offer Jula delivery?",
          answer:
            "We offer Jula delivery across the entire Stockholm region: Botkyrka, Danderyd, Ekerö, Haninge, Huddinge, Järfälla, Lidingö, Nacka, Norrtälje, Nykvarn, Nynäshamn, Salem, Sigtuna, Sollentuna, Solna, Stockholm, Sundbyberg, Södertälje, Tyresö, Täby, Upplands Väsby, Upplands-Bro, Vallentuna, Vaxholm, Värmdö and Österåker. We're also in the Gothenburg region: Ale, Alingsås, Göteborg, Härryda, Kungälv, Kungsbacka, Lerum, Lilla Edet, Mölndal, Partille, Stenungsund, Tjörn and Öckerö.",
        },
        {
          question: "Why choose Ungdomskraft for Jula delivery?",
          answer:
            "We always include a van, carrying help and placement in the right spot. You see the price instantly when booking, we can often deliver the same day and everything is insured.",
        },
      ],
      priceHint: PACKAGE_PRICE_HINT_EN.bud,
      pricingRows: getPackagePricingRows("bud", "en"),
      dynamicHeadings: {
        threeSteps: "Jula delivery in 3 easy steps",
        step2Text:
          "Our crew pick up your purchase at Jula, load and secure everything in the van. Then we drive home to you.",
        step3Title: "All in place",
        step3Desc:
          "Your Jula purchase is at home — carried in and in place. Done.",
        ctaHeading: "Book Jula delivery today",
        ctaText:
          "Enter the Jula store address and your home address, describe what you bought and choose a time. You see the price instantly.",
      },
    },
  },

  {
    // Hemleverans Mio — config för /hemleverans-mio
    // Vinkel: Hemleverans av Mio-möbler (soffor, sängar, matbord, förvaring)
    // som inte får plats i bilen eller som Mios egen leverans inte täcker smidigt.
    slug: "hemleverans-mio",
    serviceCategory: "hemleverans",

    meta: {
      title:
        "Hemleverans Mio – Vi Hämtar Dina Möbler Och Kör Hem | Ungdomskraft",
      description:
        "Köpt soffa, säng eller matbord på Mio som inte får plats i bilen? Vi hämtar i butiken och levererar hem med inbärning. Fast pris, försäkrat. Boka idag!",
      h1: "Hemleverans Mio",
    },

    hero: {
      heading: "Vi hämtar dina möbler på Mio",
      intro:
        "Hittat din nya soffa, säng eller matgrupp på Mio men leveransen dröjer eller kostar för mycket? Mio-möbler är ofta stora, tunga och omöjliga att klämma in i en personbil. Vi löser det. Ungdomskraft hämtar ditt köp direkt på Mio, lastar skåpbilen och levererar hem till dig – med inbärning och uppställning i rätt rum. Vi kör från alla Mio-butiker i Stockholm och Göteborg.",
      heroImage: "/hero-hemleverans.png",
    },

    featureCards: {
      sinceText: "Mio-leverans sedan 2014",
      storyText:
        "Sedan 2014 har Ungdomskraft hjälpt tusentals kunder att få hem sina Mio-köp – från tunga sängramar och soffor till matgrupper, bokhyllor och garderober. Våra ungdomar hämtar i butiken, lastar skåpbilen och bär in hemma hos dig.",
      bookYourText: "Boka din Mio-leverans",
      bookDescText:
        "Ange Mio-butikens adress och din hemadress, beskriv vilka möbler som ska hämtas och välj en tid. Du ser priset direkt.",
      vehicleTitleText: "Skåpbilar perfekta för Mio-möbler",
      vehicleDescText:
        "Vi kommer med rätt storlek på skåpbil – oavsett om du hämtar en fåtölj eller ett helt vardagsrum. Du slipper hyra släp, köra själv eller göra flera turer.",
    },

    itemsSection: {
      heading: "Mio-möbler vi hämtar och levererar hem",
      items: [
        {
          name: "Soffor & fåtöljer",
          description:
            "Soffor, hörnsoffor, divansoffor, fåtöljer och soffbord från Mio",
          image: "/sofa.webp",
        },
        {
          name: "Sängar & madrasser",
          description:
            "Kontinentalsängar, sängramar, madrasser, sänggavlar och bäddmadrasser",
          image: "/bed.webp",
        },
        {
          name: "Matgrupper & stolar",
          description:
            "Matbord, stolar, bänkar, barbord och kompletta matgrupper",
          image: "/table.webp",
        },
        {
          name: "Förvaring & hyllor",
          description:
            "Bokhyllor, skåp, sideboards, vitrinskåp och TV-bänkar",
          image: "/wardrobe.webp",
        },
        {
          name: "Garderober",
          description:
            "Garderobssystem, klädskåp, skjutdörrsgarderober och hatthyllor",
          image: "/armchair.webp",
        },
        {
          name: "Inredning & textil",
          description:
            "Mattor, lampor, kuddar, filtar, tavlor och dekorationer i skrymmande förpackningar",
          image: "/bedding.webp",
        },
      ],
    },

    seoContent: {
      mainHeading:
        "Hemleverans från Mio – vi hämtar, kör och bär in dina möbler",
      mainText:
        "Mio är en av Sveriges mest uppskattade möbelkedjor med fokus på skandinavisk design, kvalitetsmaterial och genomtänkta möbler för hela hemmet. Butikerna i Stockholm och Göteborg erbjuder allt från soffor, sängar och matgrupper till bokhyllor, garderober, textilier och belysning. Det som skiljer Mio från lågpriskedjorna är kvaliteten – massivt trä, äkta läder, handvävda mattor och noggrant designade detaljer. Men den kvaliteten innebär också vikt. En Mio-soffa i äkta läder väger ofta 60–100 kg, en kontinentalsäng 80–120 kg och ett massivt matbord 40–80 kg. Möblerna levereras ibland monterade direkt från butiken, ibland i flatpack – men oavsett format är de för stora och tunga för en personbil. Mio erbjuder egen hemleverans, men väntetiderna kan vara flera veckor, tiderna begränsade och inbärning till rätt rum kostar ofta extra. Om du handlat på Mios lagerrea eller hittat en utställningsmöbel som ska hem samma dag fungerar inte Mios egen leverans alls. Ungdomskraft erbjuder ett snabbt och flexibelt alternativ. Vi hämtar ditt Mio-köp direkt i butiken, lastar allt i skåpbilen och kör hem till dig. Inbärning till rätt rum ingår alltid – vi bär soffa till vardagsrummet, säng till sovrummet, matbord till köket. Du ser priset direkt vid bokning, inga dolda avgifter tillkommer. Vi kör från alla Mio-butiker i Stockholm och Göteborg. Varje vecka levererar vi möbler från Mio och andra möbelkedjor. Våra ungdomar är vana vid att hantera tunga, ömtåliga möbler – vi skyddar med filtar, använder spännband och navigerar trånga trapphus och dörröppningar utan att repa golv eller väggar. Mio-möbler är en investering och vi behandlar dem därefter.",
      preparationHeading: "Förbered inför din Mio-leverans",
      preparationText:
        "Se till att köpet är betalt och klart för upphämtning – fråga Mios personal om möblerna är redo att hämtas. Ha din hemadress och eventuell portkod redo. Mät gärna dörröppningar, trapphus och hiss i förväg – särskilt för stora soffor och sängar. Om en gammal möbel ska bort först, se till att utrymmet är fritt. Kontrollera att skåpbilen kan parkera nära porten hemma hos dig. Ange vid bokning om möblerna ska till en specifik våning eller rum, och om det finns trappor utan hiss.",
      additionalSections: [
        {
          heading: "Hemleverans av soffor och fåtöljer från Mio",
          content:
            "Mio är kända för sina kvalitetssoffor – allt från kompakta 2-sitssoffor till stora hörnsoffor och divansoffor i äkta läder eller premium-tyg. En Mio-soffa väger ofta 50–100 kg och levereras ibland i ett stycke, ibland uppdelad i sektioner. Hörnsoffor kan vara upp till 3 meter långa och kräver noggrann planering för att komma genom dörröppningar och trapphus. Utställningsmöbler och reasoffor ska dessutom ofta hem samma dag – innan Mios egen leverans hinner planeras. Med Ungdomskrafts hemleverans hämtar vi din Mio-soffa direkt i butiken, skyddar med filtar och bär in till vardagsrummet. Vi navigerar trånga trapphus och dörröppningar utan att skada möbeln, golv eller väggar. Behöver soffa eller fåtölj vridas eller vinklas – vi har erfarenheten att lösa det.",
        },
        {
          heading: "Hemleverans av sängar och madrasser från Mio",
          content:
            "Mio erbjuder kontinentalsängar, ramsängar, madrasser, bäddmadrasser och sänggavlar i hög kvalitet. En kontinentalsäng från Mio väger ofta 80–120 kg totalt – uppdelat på underrede, övermadrass och gavlar. Dessa delar är skrymmande och tunga att bära, särskilt i trappor. Madrasser och bäddmadrasser i större storlekar (160–180 cm) är dessutom otympliga att transportera utan rätt fordon. Med vår hemleverans hämtar vi hela sängpaketet på Mio, transporterar säkert i skåpbilen och bär in till sovrummet. Vi ställer delarna på plats och tar bort förpackningsmaterial om du önskar. Perfekt för dig som köpt säng på Mios lagerrea eller som inte vill vänta veckor på Mios egen leverans.",
        },
        {
          heading: "Hemleverans av matgrupper och förvaring från Mio",
          content:
            "Mios matbord i massivt trä är populära – de väger ofta 40–80 kg och levereras monterade eller i tunga delar. En matgrupp med bord och sex stolar blir snabbt ett logistikprojekt: bordet i sig kräver minst två personer att lyfta, och stolarna tar stor plats. Bokhyllor, sideboards, TV-bänkar och vitrinskåp från Mio är ofta i massivt material eller fanér – tungt och ömtåligt. Med Ungdomskraft hämtar vi din matgrupp eller förvaringsmöbel på Mio, skyddar under transporten och bär in till rätt rum. Vi placerar bordet i köket, bokhyllan i vardagsrummet och TV-bänken vid väggen – du slipper flytta tunga möbler efteråt.",
        },
        {
          heading: "Så fungerar Mio-leverans med Ungdomskraft",
          listItems: [
            {
              label: "Boka online",
              text: "Ange Mio-butikens adress och din hemadress. Beskriv vilka möbler du köpt och välj en tid. Du ser priset direkt.",
            },
            {
              label: "Vi hämtar på Mio",
              text: "Vårt team kommer med skåpbil. Vi hämtar dina möbler i butiken, skyddar med filtar och säkrar med spännband.",
            },
            {
              label: "Vi levererar hem",
              text: "Vi kör hem till dig, bär in till rätt rum – vardagsrum, sovrum, kök eller kontor.",
            },
            {
              label: "Klart",
              text: "Dina Mio-möbler står hemma hos dig – inburna och på plats i rätt rum. Utan att du lyfte ett finger.",
            },
          ],
          afterContent:
            "Hela processen tar normalt 1–3 timmar beroende på avstånd och antal möbler. Du kan följa statusen i realtid.",
        },
        {
          heading: "Vi kör från alla Mio-butiker",
          content:
            "Vi hämtar från alla Mio-butiker i Stockholm och Göteborg. I Stockholm: Barkarby, Kungens Kurva, Nacka, Täby och alla övriga. I Göteborg: Kållered, Bäckebol och alla övriga butiker. Mio har också lager och outlets som vi hämtar från. Ange bara butikens eller lagrets adress vid bokning så sköter vi resten. Ska du hämta möbler från Mio och en annan butik samma dag? Vi kan köra från flera ställen i samma tur.",
        },
        {
          heading: "Varför boka Mio-leverans med Ungdomskraft?",
          content:
            "Mio erbjuder egen hemleverans, men väntetiderna kan vara flera veckor – särskilt under kampanjer, rea och högsäsong. Inbärning till rätt rum kostar ofta extra, och tiderna är begränsade. Utställningsmöbler och reafynd som ska hem direkt täcks sällan av Mios leveransbokning. Med Ungdomskraft får du snabb leverans med inbärning till exakt det rum du vill – utan veckors väntan. Dessutom gör du dubbel nytta varje gång du bokar: du får hem dina möbler smidigt och du bidrar till att unga människor kommer in på arbetsmarknaden.\n\nDet som gör Ungdomskraft unikt är vår samhällsnytta. Vi anställer unga människor – ofta i åldern 18–25 – som annars har svårt att få sitt första jobb. Många saknar arbetslivserfarenhet, nätverk eller kontakter i branschen. Genom att arbeta hos oss får de riktig, praktisk erfarenhet som arbetsgivare värdesätter: kundkontakt ansikte mot ansikte, logistik och planering, fysiskt arbete under tidspress, samarbete i team och eget ansvar för att ett uppdrag slutförs ordentligt. De lär sig att kommunicera professionellt med kunder, att lösa problem på plats och att leverera kvalitet utan genvägar.\n\nVarje Mio-leverans bemannas av våra ungdomar. De hämtar dina möbler i butiken, skyddar med filtar, lastar skåpbilen metodiskt, kör säkert till din adress och bär in till exakt det rum du önskar. De behandlar dina möbler med samma omsorg som om det vore deras egna – Mio-möbler är ofta en investering och vi respekterar det. Den energi och det engagemang de visar märks i varje uppdrag.\n\nVi har funnits sedan 2014 och har genomfört tiotusentals möbelleveranser i Stockholm och Göteborg. Under åren har hundratals ungdomar fått sin första arbetslivserfarenhet genom Ungdomskraft – och många har gått vidare till fasta tjänster inom logistik, bygg, kundservice och andra branscher. Vår modell fungerar för att den bygger på riktigt arbete med riktiga kunder, inte praktik utan ansvar.\n\nHållbarhet genomsyrar allt vi gör. Vi planerar körningar effektivt för att minimera tomkörning och samlastar när det är möjligt. Vi återvinner förpackningsmaterial – wellpapp, frigolit, plast och skyddshörnor sorteras korrekt efter leverans. För företag som beställer Mio-leveranser erbjuder vi dokumentation som kan användas i CSR-rapportering – varje bokning stödjer ungdomsarbete och hållbar logistik.\n\nAllt är helförsäkrat. Vi har egna skåpbilar i flera storlekar – från liten skåpbil för en fåtölj till stor skåpbil som rymmer en hel matgrupp och garderob. Du ser alltid priset innan du bokar. Inga dolda avgifter, inga överraskningar. Och om något mot förmodan skadas under transporten ersätter vi det omedelbart.\n\nNär du bokar Mio-leverans med Ungdomskraft väljer du mer än en transporttjänst. Du väljer att ge en ung människa möjligheten att lära, växa och bygga sin framtid. Det är smidig möbelleverans med mening.\n\nHemleverans från Mio ger inte rätt till RUT-avdrag, men flera av våra andra tjänster gör det – exempelvis flytt, bärhjälp och möbelmontering med 50 % avdrag på arbetskostnaden. Kombinera gärna tjänster i en och samma bokning.",
        },
      ],
    },

    faq: [
      {
        question: "Vad kostar hemleverans från Mio?",
        answer:
          "Priset beror på antal möbler och avstånd. En enklare Mio-leverans med en fåtölj eller soffbord kostar från 595 kr. Större leveranser med soffa, säng eller matgrupp kostar från 995–2 495 kr. Du ser det exakta priset när du bokar online.",
      },
      {
        question: "Kan jag få RUT-avdrag på hemleverans från Mio?",
        answer:
          "Hemleverans från Mio ger i sig inte rätt till RUT-avdrag. RUT gäller hushållsnära tjänster som utförs i din bostad, exempelvis flytt, bärhjälp och möbelmontering. Ungdomskraft erbjuder flera RUT-berättigade tjänster med 50 % avdrag på arbetskostnaden – kombinera enkelt i en bokning via vår hemsida.",
      },
      {
        question: "Ingår skåpbil i Mio-leveransen?",
        answer:
          "Ja, skåpbil ingår alltid. Vi väljer rätt storlek utifrån dina möbler – du behöver inte hyra bil eller släp.",
      },
      {
        question: "Ingår inbärning till rätt rum i priset?",
        answer:
          "Ja, inbärning till rätt rum ingår alltid. Vi bär soffa till vardagsrummet, säng till sovrummet, matbord till köket – oavsett våning. Inga extra avgifter.",
      },
      {
        question: "Vilka Mio-butiker hämtar ni från?",
        answer:
          "Vi hämtar från alla Mio-butiker i Stockholm och Göteborg: Barkarby, Kungens Kurva, Nacka, Täby, Kållered, Bäckebol och alla övriga. Vi hämtar även från Mios lager och outlets. Ange bara adressen vid bokning.",
      },
      {
        question: "Kan ni hämta utställningsmöbler på Mio samma dag?",
        answer:
          "Ja, vi hämtar utställningsmöbler, reafynd och lagersalumöbler som ska hem direkt. Boka online och se tillgängliga tider – ofta kan vi hämta inom kort.",
      },
      {
        question: "Kan ni leverera en tung soffa från Mio?",
        answer:
          "Ja, vi levererar soffor dagligen – från kompakta 2-sitssoffor till stora hörnsoffor i läder som väger 100 kg. Vi skyddar med filtar och navigerar trånga trapphus utan att skada möbeln eller väggar.",
      },
      {
        question: "Kan ni leverera en kontinentalsäng från Mio?",
        answer:
          "Ja, vi hanterar kontinentalsängar regelbundet. Vi hämtar alla delar – underrede, övermadrass, gavlar – transporterar säkert och bär in till sovrummet. Vi ställer delarna på plats åt dig.",
      },
      {
        question: "Kan ni bära tunga Mio-möbler i trappor?",
        answer:
          "Ja, vi bär till vilken våning som helst – med eller utan hiss. Våra ungdomar är vana vid tunga möbler och trånga trapphus. Vi skyddar golv och väggar under inbärningen.",
      },
      {
        question: "Skyddar ni möblerna under Mio-transporten?",
        answer:
          "Ja, vi skyddar alla möbler med filtar och säkrar med spännband i skåpbilen. Mio-möbler är ofta en investering och vi behandlar dem därefter. Allt är försäkrat – om något skadas ersätter vi det.",
      },
      {
        question: "Hur snabbt kan ni leverera från Mio?",
        answer:
          "Vi erbjuder ofta hemleverans med kort varsel – ofta redan samma dag – inom några timmar. Boka online och se tillgängliga tider direkt.",
      },
      {
        question: "Erbjuder ni Mio-leverans på helger?",
        answer:
          "Ja, vi erbjuder hemleverans på helger och vissa kvällar. Kontrollera att Mio-butiken har öppet den tid du bokar – vi kan inte hämta om butiken är stängd.",
      },
      {
        question: "Kan ni hämta från Mio och en annan butik i samma tur?",
        answer:
          "Ja, vi kan hämta från flera butiker under samma körning om de ligger i rimligt avstånd. Beskriv alla hämtpunkter vid bokning – till exempel Mio och IKEA i samma tur.",
      },
      {
        question: "Behöver jag vara med på Mio vid upphämtningen?",
        answer:
          "Nej, du behöver inte vara med på Mio. Se till att köpet är betalt och redo att hämtas – vi sköter resten. Du behöver bara vara hemma för att ta emot leveransen och visa var möblerna ska stå.",
      },
      {
        question:
          "I vilka kommuner erbjuder ni hemleverans från Mio?",
        answer:
          "Vi erbjuder Mio-leverans i hela Stockholmsområdet: Botkyrka, Danderyd, Ekerö, Haninge, Huddinge, Järfälla, Lidingö, Nacka, Norrtälje, Nykvarn, Nynäshamn, Salem, Sigtuna, Sollentuna, Solna, Stockholm, Sundbyberg, Södertälje, Tyresö, Täby, Upplands Väsby, Upplands-Bro, Vallentuna, Vaxholm, Värmdö och Österåker. Vi finns även i Göteborgsområdet: Ale, Alingsås, Göteborg, Härryda, Kungälv, Kungsbacka, Lerum, Lilla Edet, Mölndal, Partille, Stenungsund, Tjörn och Öckerö.",
      },
      {
        question: "Varför välja Ungdomskraft för Mio-leverans?",
        answer:
          "Hos oss ingår alltid skåpbil, bärhjälp och inbärning till rätt rum. Du ser priset direkt vid bokning, vi kan ofta köra samma dag och allt är försäkrat.",
      },
    ],

    priceHint: PACKAGE_PRICE_HINT_SV.bud,

    pricingRows: getPackagePricingRows("bud", "sv"),

    dynamicHeadings: {
      threeSteps: "Mio-leverans i 3 enkla steg",
      step2Text:
        "Våra ungdomar hämtar dina möbler på Mio, skyddar med filtar och säkrar allt i skåpbilen. Sedan kör vi hem till dig.",
      step3Title: "Allt på plats",
      step3Desc:
        "Dina Mio-möbler står hemma hos dig – inburna och på plats i rätt rum. Klart.",
      ctaHeading: "Boka Mio-leverans idag",
      ctaText:
        "Ange Mio-butikens adress och din hemadress, beskriv vilka möbler du köpt och välj en tid. Du ser priset direkt.",
    },

    en: {
      slug: "mio-delivery",
      meta: {
        title:
          "Mio Delivery – We Pick Up Your Furniture And Deliver Home | Ungdomskraft",
        description:
          "Bought a sofa, bed or dining table at Mio that won't fit in your car? We pick up at the store and deliver home with carrying included. Book Mio delivery today.",
        h1: "Mio delivery",
      },
      hero: {
        heading: "We pick up your furniture at Mio",
        intro:
          "Found your new sofa, bed or dining set at Mio but the delivery takes weeks or costs too much? Mio furniture is often large, heavy and impossible to fit in a regular car. We solve it. Ungdomskraft picks up your purchase directly at Mio, loads the van and delivers to your home — with carrying and placement in the right room. We collect from all Mio stores in Stockholm and Gothenburg.",
      },
      featureCards: {
        sinceText: "Mio delivery since 2014",
        bookYourText: "Book your Mio delivery",
        bookDescText:
          "Enter the Mio store address and your home address, describe which furniture to pick up and choose a time. You see the price instantly.",
        vehicleTitleText: "Vans perfect for Mio furniture",
        vehicleDescText:
          "We arrive with the right size van — whether you're picking up an armchair or a complete living room. No need to rent a trailer, drive yourself or make multiple trips.",
      },
      itemsSection: {
        heading: "Mio furniture we pick up and deliver home",
        items: [
          {
            name: "Sofas & armchairs",
            description:
              "Sofas, corner sofas, chaise longues, armchairs and coffee tables from Mio",
            image: "/sofa.webp",
          },
          {
            name: "Beds & mattresses",
            description:
              "Divan beds, bed frames, mattresses, headboards and mattress toppers",
            image: "/bed.webp",
          },
          {
            name: "Dining sets & chairs",
            description:
              "Dining tables, chairs, benches, bar tables and complete dining sets",
            image: "/table.webp",
          },
          {
            name: "Storage & shelving",
            description:
              "Bookcases, cabinets, sideboards, display cabinets and TV units",
            image: "/wardrobe.webp",
          },
          {
            name: "Wardrobes",
            description:
              "Wardrobe systems, clothes cabinets, sliding-door wardrobes and hat shelves",
            image: "/armchair.webp",
          },
          {
            name: "Decor & textiles",
            description:
              "Rugs, lamps, cushions, throws, artwork and decorations in bulky packaging",
            image: "/bedding.webp",
          },
        ],
      },
      seoContent: {
        mainHeading:
          "Delivery from Mio — we pick up, drive and carry your furniture inside",
        mainText:
          "Mio is one of Sweden's most respected furniture chains, focused on Scandinavian design, quality materials and thoughtfully designed furniture for the whole home. Their stores in Stockholm and Gothenburg offer everything from sofas, beds and dining sets to bookcases, wardrobes, textiles and lighting. What sets Mio apart from budget chains is the quality — solid wood, genuine leather, hand-woven rugs and carefully designed details. But that quality also means weight. A Mio sofa in genuine leather often weighs 60–100 kg, a divan bed 80–120 kg and a solid dining table 40–80 kg. The furniture is sometimes delivered assembled straight from the store, sometimes in flatpack — but regardless of format it's too large and heavy for a regular car. Mio offers their own delivery, but waiting times can be several weeks, time slots are limited and carrying to the right room often costs extra. If you've shopped at Mio's warehouse sale or found a display piece that needs to go home the same day, Mio's own delivery doesn't work at all. Ungdomskraft offers a fast and flexible alternative. We pick up your Mio purchase directly at the store, load everything into the van and drive to your home. Carrying to the right room is always included — we carry the sofa to the living room, the bed to the bedroom, the dining table to the kitchen. You see the price instantly when booking, no hidden fees. We collect from all Mio stores in Stockholm and Gothenburg. Every week we deliver furniture from Mio and other furniture chains. Our crew are used to handling heavy, delicate furniture — we protect with blankets, use straps and navigate narrow stairwells and doorways without scratching floors or walls. Mio furniture is an investment and we treat it accordingly.",
        preparationHeading: "Prepare for your Mio delivery",
        preparationText:
          "Make sure the purchase is paid for and ready for collection — ask Mio staff whether the furniture is ready to pick up. Have your home address and any door codes ready. Measure doorways, stairwells and lifts in advance — especially for large sofas and beds. If old furniture needs to be removed first, make sure the space is clear. Check that the van can park near the entrance at your home. When booking, specify which floor or room the furniture should go to, and whether there are stairs without a lift.",
        additionalSections: [
          {
            heading: "Sofa and armchair delivery from Mio",
            content:
              "Mio is known for their quality sofas — everything from compact 2-seaters to large corner sofas and chaise longues in genuine leather or premium fabric. A Mio sofa often weighs 50–100 kg and is sometimes delivered in one piece, sometimes split into sections. Corner sofas can be up to 3 metres long and require careful planning to get through doorways and stairwells. Display pieces and sale sofas also often need to go home the same day — before Mio's own delivery can be arranged. With Ungdomskraft's delivery we pick up your Mio sofa directly at the store, protect with blankets and carry into the living room. We navigate tight stairwells and doorways without damaging the furniture, floors or walls. Need the sofa rotated or angled — we have the experience to make it work.",
          },
          {
            heading: "Bed and mattress delivery from Mio",
            content:
              "Mio offers divan beds, bed frames, mattresses, mattress toppers and headboards in high quality. A divan bed from Mio often weighs 80–120 kg in total — split between base, top mattress and headboard. These parts are bulky and heavy to carry, especially on stairs. Mattresses and toppers in larger sizes (160–180 cm) are also unwieldy to transport without the right vehicle. With our delivery we pick up the entire bed package at Mio, transport safely in the van and carry into the bedroom. We place the parts in position and remove packaging material if you wish. Perfect if you've bought a bed at Mio's warehouse sale or don't want to wait weeks for Mio's own delivery.",
          },
          {
            heading: "Dining set and storage delivery from Mio",
            content:
              "Mio's solid-wood dining tables are popular — they often weigh 40–80 kg and come assembled or in heavy sections. A dining set with table and six chairs quickly becomes a logistics project: the table alone requires at least two people to lift, and the chairs take up lots of space. Bookcases, sideboards, TV units and display cabinets from Mio are often in solid materials or veneer — heavy and delicate. With Ungdomskraft we pick up your dining set or storage furniture at Mio, protect during transport and carry into the right room. We place the table in the kitchen, the bookcase in the living room and the TV unit against the wall — no need to move heavy furniture afterwards.",
          },
          {
            heading: "How Mio delivery works with Ungdomskraft",
            listItems: [
              {
                label: "Book online",
                text: "Enter the Mio store address and your home address. Describe which furniture you bought and choose a time. You see the price instantly.",
              },
              {
                label: "We pick up at Mio",
                text: "Our team arrive with a van. We collect your furniture at the store, protect with blankets and secure with straps.",
              },
              {
                label: "We deliver home",
                text: "We drive to your home and carry to the right room — living room, bedroom, kitchen or study.",
              },
              {
                label: "Done",
                text: "Your Mio furniture is at home — carried in and placed in the right room. Without lifting a finger.",
              },
            ],
            afterContent:
              "The whole process typically takes 1–3 hours depending on distance and number of items. You can follow the status in real time.",
          },
          {
            heading: "We collect from all Mio stores",
            content:
              "We pick up from all Mio stores in Stockholm and Gothenburg. In Stockholm: Barkarby, Kungens Kurva, Nacka, Täby and all others. In Gothenburg: Kållered, Bäckebol and all other locations. We also collect from Mio's warehouses and outlets. Just enter the store or warehouse address when booking and we handle the rest. Picking up furniture from Mio and another store the same day? We can collect from multiple locations in the same trip.",
          },
          {
            heading: "Why book Mio delivery with Ungdomskraft?",
            content:
              "Mio offers their own delivery, but waiting times can be several weeks — especially during campaigns, sales and peak season. Carrying to the right room often costs extra, and time slots are limited. Display pieces and sale finds that need to go home right away are rarely covered by Mio's delivery booking. With Ungdomskraft you get fast delivery with carrying to exactly the room you want — without weeks of waiting. Plus you make a double impact every time you book: you get your furniture home smoothly and you help young people enter the workforce.\n\nWhat makes Ungdomskraft unique is our social mission. We employ young people — often aged 18–25 — who otherwise struggle to get their first job. Many lack work experience, networks or industry contacts. By working with us they gain real, practical experience that employers value: face-to-face customer service, logistics and planning, physical work under time pressure, teamwork and personal responsibility for completing a job properly. They learn to communicate professionally with customers, solve problems on site and deliver quality without shortcuts.\n\nEvery Mio delivery is staffed by our young workers. They collect your furniture at the store, protect with blankets, load the van methodically, drive safely to your address and carry into exactly the room you want. They handle your furniture with the same care as if it were their own — Mio furniture is often an investment and they respect that. The energy and commitment they show is evident in every job.\n\nWe've been around since 2014 and have completed tens of thousands of furniture deliveries across Stockholm and Gothenburg. Over the years, hundreds of young people have gained their first work experience through Ungdomskraft — and many have gone on to permanent positions in logistics, construction, customer service and other industries. Our model works because it's built on real work with real customers, not internships without responsibility.\n\nSustainability runs through everything we do. We plan routes efficiently to minimise empty runs and consolidate loads when possible. We recycle packaging materials — cardboard, polystyrene, plastic and corner protectors are sorted correctly after delivery. For companies ordering Mio deliveries we offer documentation for CSR reporting — every booking supports youth employment and sustainable logistics.\n\nEverything is fully insured. We have our own vans in multiple sizes — from small vans for an armchair to large vans that fit an entire dining set and wardrobe. You always see the price before you book. No hidden fees, no surprises. And if anything is unexpectedly damaged during transport we replace it immediately.\n\nWhen you book Mio delivery with Ungdomskraft, you choose more than a transport service. You choose to give a young person the opportunity to learn, grow and build their future. It's smooth furniture delivery with purpose.\n\nMio delivery does not qualify for the Swedish RUT tax deduction, but several of our other services do – including moving help, carrying help and furniture assembly with a 50% deduction on the labour cost. Feel free to combine services in a single booking.",
          },
        ],
      },
      faq: [
        {
          question: "How much does Mio delivery cost?",
          answer:
            "The price depends on the number of items and distance. A simpler Mio delivery with an armchair or coffee table starts at 595 SEK. Larger deliveries with a sofa, bed or dining set cost 749–2 495 SEK. You see the exact price when you book online.",
        },
        {
          question: "Can I get a RUT tax deduction on Mio delivery?",
          answer:
            "Mio delivery does not in itself qualify for the Swedish RUT tax deduction. RUT applies to household services carried out in your home, such as moving help, carrying help and furniture assembly. Ungdomskraft offers several RUT-eligible services with a 50% deduction on the labour cost – easily combine them in one booking via our website.",
        },
        {
          question: "Is a van included in the Mio delivery?",
          answer:
            "Yes, a van is always included. We choose the right size based on your furniture — you don't need to rent a vehicle or trailer.",
        },
        {
          question: "Is carrying to the right room included?",
          answer:
            "Yes, carrying to the right room is always included. We carry the sofa to the living room, bed to the bedroom, dining table to the kitchen — regardless of floor. No extra charges.",
        },
        {
          question: "Which Mio stores do you pick up from?",
          answer:
            "We pick up from all Mio stores in Stockholm and Gothenburg: Barkarby, Kungens Kurva, Nacka, Täby, Kållered, Bäckebol and all others. We also collect from Mio's warehouses and outlets. Just enter the address when booking.",
        },
        {
          question: "Can you pick up display furniture from Mio the same day?",
          answer:
            "Yes, we pick up display pieces, sale finds and warehouse items that need to go home right away. Book online and see available times — we can often collect at short notice.",
        },
        {
          question: "Can you deliver a heavy sofa from Mio?",
          answer:
            "Yes, we deliver sofas daily — from compact 2-seaters to large leather corner sofas weighing 100 kg. We protect with blankets and navigate tight stairwells without damaging the furniture or walls.",
        },
        {
          question: "Can you deliver a divan bed from Mio?",
          answer:
            "Yes, we handle divan beds regularly. We collect all parts — base, top mattress, headboard — transport safely and carry into the bedroom. We place the parts in position for you.",
        },
        {
          question: "Can you carry heavy Mio furniture up stairs?",
          answer:
            "Yes, we carry to any floor — with or without a lift. Our crew are used to heavy furniture and narrow stairwells. We protect floors and walls during carrying.",
        },
        {
          question: "Do you protect the furniture during Mio transport?",
          answer:
            "Yes, we protect all furniture with blankets and secure with straps in the van. Mio furniture is often an investment and we treat it accordingly. Everything is insured — if something is damaged we replace it.",
        },
        {
          question: "How quickly can you deliver from Mio?",
          answer:
            "We often offer delivery at short notice — often the same day — within a few hours. Book online and see available times instantly.",
        },
        {
          question: "Is Mio delivery available on weekends?",
          answer:
            "Yes, we offer delivery on weekends and some evenings. Check that the Mio store is open at your chosen time — we can't pick up if the store is closed.",
        },
        {
          question: "Can you pick up from Mio and another store in the same trip?",
          answer:
            "Yes, we can pick up from several stores during the same run if they're within a reasonable distance. Describe all pickup points when you book — for example Mio and IKEA in the same trip.",
        },
        {
          question: "Do I need to be at the Mio store?",
          answer:
            "No, you don't need to be at Mio. Make sure the purchase is paid for and ready to collect — we handle the rest. You just need to be home to receive the delivery and show where the furniture should go.",
        },
        {
          question: "In which areas do you offer Mio delivery?",
          answer:
            "We offer Mio delivery across the entire Stockholm region: Botkyrka, Danderyd, Ekerö, Haninge, Huddinge, Järfälla, Lidingö, Nacka, Norrtälje, Nykvarn, Nynäshamn, Salem, Sigtuna, Sollentuna, Solna, Stockholm, Sundbyberg, Södertälje, Tyresö, Täby, Upplands Väsby, Upplands-Bro, Vallentuna, Vaxholm, Värmdö and Österåker. We're also in the Gothenburg region: Ale, Alingsås, Göteborg, Härryda, Kungälv, Kungsbacka, Lerum, Lilla Edet, Mölndal, Partille, Stenungsund, Tjörn and Öckerö.",
        },
        {
          question: "Why choose Ungdomskraft for Mio delivery?",
          answer:
            "We always include a van, carrying help and placement in the right room. You see the price instantly when booking, we can often deliver the same day and everything is insured.",
        },
      ],
      priceHint: PACKAGE_PRICE_HINT_EN.bud,
      pricingRows: getPackagePricingRows("bud", "en"),
      dynamicHeadings: {
        threeSteps: "Mio delivery in 3 easy steps",
        step2Text:
          "Our crew pick up your furniture at Mio, protect with blankets and secure everything in the van. Then we drive home to you.",
        step3Title: "All in place",
        step3Desc:
          "Your Mio furniture is at home — carried in and placed in the right room. Done.",
        ctaHeading: "Book Mio delivery today",
        ctaText:
          "Enter the Mio store address and your home address, describe which furniture you bought and choose a time. You see the price instantly.",
      },
    },
  },

  {
    // Hemleverans Bauhaus — config för /hemleverans-bauhaus
    // Vinkel: Hemleverans av Bauhaus-köp (bygg, trädgård, badrum, kök, verktyg)
    // Bauhaus = stort byggvaruhus. Virke, kakel, VVS, trädgård, verktyg, badrumsvaror.
    slug: "hemleverans-bauhaus",
    serviceCategory: "hemleverans",

    meta: {
      title:
        "Hemleverans Bauhaus – Vi Hämtar Ditt Köp Och Kör Hem | Ungdomskraft",
      description:
        "Köpt byggmaterial, kakel eller trädgårdsprodukter på Bauhaus som inte får plats i bilen? Vi hämtar i butiken och levererar hem med inbärning. Boka hemleverans Bauhaus idag.",
      h1: "Hemleverans Bauhaus",
    },

    hero: {
      heading: "Vi hämtar ditt köp på Bauhaus",
      intro:
        "Handlat på Bauhaus och allt ryms inte i bilen? Virke, kakel, badrumsmoduler, trädgårdsmöbler och tunga byggsäckar – det som gör Bauhaus till en komplett byggjätte är också det som är svårast att frakta hem. Vi löser det. Ungdomskraft hämtar ditt köp direkt på Bauhaus, lastar skåpbilen och levererar hem till dig – med inbärning och avlastning på plats. Vi kör från alla Bauhaus-butiker i Stockholm och Göteborg.",
      heroImage: "/hero-hemleverans.png",
    },

    featureCards: {
      sinceText: "Bauhaus-leverans sedan 2014",
      storyText:
        "Sedan 2014 har Ungdomskraft hjälpt tusentals kunder att få hem sina Bauhaus-köp – från tungt virke och kakelplattor till badrumsmoduler, trädgårdsmöbler och verktyg. Våra ungdomar hämtar i butiken, lastar skåpbilen och bär in hemma hos dig.",
      bookYourText: "Boka din Bauhaus-leverans",
      bookDescText:
        "Ange Bauhaus-butikens adress och din hemadress, beskriv vad som ska hämtas och välj en tid. Du ser priset direkt.",
      vehicleTitleText: "Skåpbilar perfekta för Bauhaus-leveranser",
      vehicleDescText:
        "Vi kommer med rätt storlek på skåpbil – oavsett om du hämtar en hink färg eller ett helt badrumsbygge. Du slipper hyra släp, köra själv eller göra flera turer.",
    },

    itemsSection: {
      heading: "Bauhaus-produkter vi hämtar och levererar hem",
      items: [
        {
          name: "Bygg & virke",
          description:
            "Virke, reglar, gipsskivor, OSB-skivor, isolering, cement och betong",
          image: "/box.webp",
        },
        {
          name: "Kakel & golv",
          description:
            "Klinker, kakelplattor, laminatgolv, trägolv och vinylgolv i tunga förpackningar",
          image: "/wardrobe.webp",
        },
        {
          name: "Badrum & VVS",
          description:
            "Duschväggar, tvättställ, toalettstolar, badkar, blandare och rördelar",
          image: "/washer.webp",
        },
        {
          name: "Trädgård & utomhus",
          description:
            "Trädgårdsmöbler, markplattor, staket, pergolor, grillar och stora krukor",
          image: "/patio-furniture.webp",
        },
        {
          name: "Verktyg & maskiner",
          description:
            "Elverktyg, kompressorer, bänkslipar, arbetsbänkar och verktygsvagnar",
          image: "/toolbox.webp",
        },
        {
          name: "Kök & förvaring",
          description:
            "Köksskåp, bänkskivor, diskbänkar, garagehyllor och förvaringssystem",
          image: "/dishes.webp",
        },
      ],
    },

    seoContent: {
      mainHeading:
        "Hemleverans från Bauhaus – vi hämtar, kör och bär in åt dig",
      mainText:
        "Bauhaus är ett av Nordens största byggvaruhus med ett enormt sortiment – allt från virke, gipsskivor och isolering till kakelplattor, badrumsmoduler, köksinredning, trädgårdsmöbler och elverktyg. Butikerna i Stockholm och Göteborg är gigantiska och lockar hundratusentals kunder varje vecka – både hemmafixare och professionella hantverkare. Det som gör Bauhaus unikt är bredden och djupet: under samma tak hittar du allt du behöver för ett komplett badrumsbyte, en takläggning, ett nytt golv, en trädgårdsrenovering eller en komplett verkstadsutrustning. Problemet som de flesta Bauhaus-kunder känner igen: du går in för att köpa en påse skruv och en rulle tejp, och går ut med en vagn full av kakelplattor, en duschvägg, tre säckar bruk, en bänkskiva och kanske några trädgårdsmöbler som stod på kampanj. Allt till bra priser – men helt omöjligt att klämma in i bilen. Virke sticker ut metern, gipsskivor är 2,4 meter långa, kakelkartonger väger 20–30 kg styck och duschväggar i glas måste transporteras stående. Att hyra släp löser en del men kostar tid och pengar, och du måste fortfarande lyfta allt själv. Bauhaus erbjuder ibland utkörning, men väntetiderna kan vara långa och tiderna begränsade – särskilt under renoverings­högsäsong på våren och sommaren. Ungdomskraft erbjuder ett snabbt och smidigt alternativ. Vi hämtar ditt Bauhaus-köp direkt i butiken, lastar allt i skåpbilen och kör hem till dig. Inbärning till rätt plats ingår alltid – vi bär in till bygget, badrummet, garaget, trädgården eller förrådet. Du ser priset direkt vid bokning, inga dolda avgifter tillkommer. Vi kör från alla Bauhaus-butiker i Stockholm och Göteborg. Varje vecka hämtar vi hundratals leveranser från Bauhaus och liknande byggvaruhus. Våra ungdomar är vana vid att hantera tungt byggmaterial – virke, gips, kakel, cement – och vet hur man lastar en skåpbil så att inget skadas eller förskjuts under transporten.",
      preparationHeading: "Förbered inför din Bauhaus-leverans",
      preparationText:
        "Se till att köpet är betalt och klart för upphämtning. Ha din hemadress och eventuell portkod redo. Om du handlat tungt byggmaterial – lista gärna de tyngsta posterna så att vi kan välja rätt skåpbilsstorlek. Kontrollera att skåpbilen kan parkera nära porten eller garageinfarten hemma hos dig. Om materialet ska till en specifik plats – badrummet, garaget, verkstaden – se till att vägen dit är fri. Ange vid bokning om det finns speciella önskemål, till exempel att kakelplattor ska bäras upp till andra våningen eller att virke ska läggas i garaget.",
      additionalSections: [
        {
          heading: "Hemleverans av byggmaterial och virke från Bauhaus",
          content:
            "Bauhaus har ett av marknadens bredaste sortiment av byggmaterial – virke i alla dimensioner, reglar, gipsskivor, OSB-skivor, plywood, isolering, cement, betong, spackel och skruv i storpack. Dessa produkter är nästan alltid för tunga, långa eller skrymmande för en personbil. Virke på 4–5 meter sticker ut, gipsskivor är 2,4 meter långa och väger 25–30 kg styck, isoleringsbalar tar enormt med plats och cementsäckar väger 25 kg var. Ett enda badrumsbygge kan innebära 200–400 kg material. Med Ungdomskrafts hemleverans hämtar vi allt på Bauhaus, lastar metodiskt i skåpbilen och kör hem till dig. Vi bär in till bygget, garaget eller förrådet – oavsett var renoveringen pågår. Perfekt komplement för hemmafixaren som inte har tillgång till pickup eller släpvagn.",
        },
        {
          heading: "Hemleverans av kakel, klinker och golv från Bauhaus",
          content:
            "Bauhaus erbjuder ett enormt utbud av kakelplattor, klinker, mosaik, laminatgolv, trägolv och vinylgolv. Kakel- och klinkerplattor säljs i kartonger som väger 20–30 kg styck – ett helt badrum kräver ofta 10–20 kartonger, det vill säga 200–600 kg totalt. Golvpaket i laminat eller trä väger 15–25 kg per förpackning och man behöver ofta 8–15 paket för ett rum. Dessa tunga, staplade varor är extremt opraktiska att frakta i en personbil – de sliter på fjädring, tar all plats och kräver flera turer. Med vår hemleverans hämtar vi alla kartonger på Bauhaus, lastar dem stabilt i skåpbilen och kör hem till dig. Vi bär in till badrummet, hallen, köket eller det rum där golvet ska läggas.",
        },
        {
          heading: "Hemleverans av badrum och VVS från Bauhaus",
          content:
            "Bauhaus har en av marknadens största badrums­avdelningar – duschväggar i härdat glas, handfat, toalettstolar, badkar, duschkabiner, badrumsmöbler och all VVS du behöver. Duschväggar måste transporteras stående och skyddas mot stötar, ett badkar väger 30–80 kg beroende på material, och toalettstolar i porslin är tunga och ömtåliga. Många kunder köper hela badrumspaket på Bauhaus – allt från kaklet till blandarna – och den totala volymen och vikten blir enorm. Med Ungdomskraft hämtar vi hela din badrumsbeställning på Bauhaus och kör hem. Vi bär in till badrummet eller det rum där renoveringen pågår, och hanterar glas och porslin med extra omsorg.",
        },
        {
          heading: "Så fungerar Bauhaus-leverans med Ungdomskraft",
          listItems: [
            {
              label: "Boka online",
              text: "Ange Bauhaus-butikens adress och din hemadress. Beskriv vad du handlat och välj en tid. Du ser priset direkt.",
            },
            {
              label: "Vi hämtar på Bauhaus",
              text: "Vårt team kommer med skåpbil. Vi hämtar ditt köp i butiken, lastar och säkrar allt med spännband.",
            },
            {
              label: "Vi levererar hem",
              text: "Vi kör hem till dig, bär in till rätt plats – bygget, badrummet, garaget eller trädgården.",
            },
            {
              label: "Klart",
              text: "Ditt Bauhaus-köp står hemma hos dig – inburet och på plats. Utan att du lyfte ett finger.",
            },
          ],
          afterContent:
            "Hela processen tar normalt 1–3 timmar beroende på avstånd och mängd. Du kan följa statusen i realtid.",
        },
        {
          heading: "Vi kör från alla Bauhaus-butiker",
          content:
            "Vi hämtar från alla Bauhaus-butiker i Stockholm och Göteborg. I Stockholm: Barkarby, Kungens Kurva, Bromma, Arninge, Länna och alla övriga. I Göteborg: Sisjön, Bäckebol och alla övriga butiker. Ange bara butikens adress vid bokning så sköter vi resten. Ska du handla på Bauhaus och en annan butik samma dag? Vi kan hämta från flera ställen i samma tur – till exempel Bauhaus och Jula eller Bauhaus och Kakelspecialisten i samma körning.",
        },
        {
          heading: "Varför boka Bauhaus-leverans med Ungdomskraft?",
          content:
            "Bauhaus erbjuder ibland egen hemleverans, men väntetiderna kan vara långa – särskilt under våren och sommaren när alla renoverar. Tiderna är begränsade och inbärning till rätt plats ingår sällan. Med Ungdomskraft får du snabb leverans med inbärning – oavsett om materialet ska till badrummet, garaget, trädgården eller källaren. Dessutom gör du dubbel nytta varje gång du bokar: du får hem ditt köp smidigt och du bidrar till att unga människor kommer in på arbetsmarknaden.\n\nDet som gör Ungdomskraft unikt är vår samhällsnytta. Vi anställer unga människor – ofta i åldern 18–25 – som annars har svårt att få sitt första jobb. Många saknar arbetslivserfarenhet, nätverk eller kontakter i branschen. Genom att arbeta hos oss får de riktig, praktisk erfarenhet som arbetsgivare värdesätter: kundkontakt ansikte mot ansikte, logistik och planering, fysiskt arbete under tidspress, samarbete i team och eget ansvar för att ett uppdrag slutförs ordentligt. De lär sig att kommunicera professionellt med kunder, att lösa problem på plats och att leverera kvalitet utan genvägar.\n\nVarje Bauhaus-leverans bemannas av våra ungdomar. De hämtar ditt köp i butiken, lastar skåpbilen metodiskt – tungt längst ner, ömtåligt skyddat – kör säkert till din adress och bär in till exakt den plats du önskar. De behandlar ditt material med samma omsorg oavsett om det är en kartong kakel eller tio gipsskivor. Den energi och det engagemang de visar märks i varje uppdrag.\n\nVi har funnits sedan 2014 och har genomfört tiotusentals transporter och leveranser i Stockholm och Göteborg. Under åren har hundratals ungdomar fått sin första arbetslivserfarenhet genom Ungdomskraft – och många har gått vidare till fasta tjänster inom logistik, bygg, kundservice och andra branscher. Vår modell fungerar för att den bygger på riktigt arbete med riktiga kunder, inte praktik utan ansvar.\n\nHållbarhet genomsyrar allt vi gör. Vi planerar körningar effektivt för att minimera tomkörning. Vi samlastar när det är möjligt. Vi återvinner förpackningsmaterial och ser till att wellpapp, frigolit, pall och plast sorteras korrekt efter leverans. För företag och hantverkare som beställer Bauhaus-leveranser erbjuder vi dokumentation som kan användas i CSR-rapportering – varje bokning stödjer ungdomsarbete och hållbar logistik.\n\nAllt är helförsäkrat. Vi har egna skåpbilar i flera storlekar – från liten skåpbil för verktyg och färg till stor skåpbil som rymmer virke, gipsskivor och kakelpallar. Du ser alltid priset innan du bokar. Inga dolda avgifter, inga överraskningar. Och om något mot förmodan skadas under transporten ersätter vi det omedelbart.\n\nNär du bokar Bauhaus-leverans med Ungdomskraft väljer du mer än en transporttjänst. Du väljer att ge en ung människa möjligheten att lära, växa och bygga sin framtid. Det är smidig leverans med mening.\n\nHemleverans från Bauhaus ger inte rätt till RUT-avdrag, men flera av våra andra tjänster gör det – exempelvis flytt, bärhjälp och möbelmontering med 50 % avdrag på arbetskostnaden. Kombinera gärna tjänster i en och samma bokning.",
        },
      ],
    },

    faq: [
      {
        question: "Vad kostar hemleverans från Bauhaus?",
        answer:
          "Priset beror på mängden och avståndet. En mindre Bauhaus-leverans med färg eller verktyg kostar från 595 kr. Större leveranser med kakel, virke eller badrumsvaror kostar från 995–2 495 kr. Du ser det exakta priset när du bokar online.",
      },
      {
        question: "Kan jag få RUT-avdrag på hemleverans från Bauhaus?",
        answer:
          "Hemleverans från Bauhaus ger i sig inte rätt till RUT-avdrag. RUT gäller hushållsnära tjänster som utförs i din bostad, exempelvis flytt, bärhjälp och möbelmontering. Ungdomskraft erbjuder flera RUT-berättigade tjänster med 50 % avdrag på arbetskostnaden – kombinera enkelt i en bokning via vår hemsida.",
      },
      {
        question: "Ingår skåpbil i Bauhaus-leveransen?",
        answer:
          "Ja, skåpbil ingår alltid. Vi väljer rätt storlek utifrån ditt köp – du behöver inte hyra bil eller släp.",
      },
      {
        question: "Ingår inbärning i priset för Bauhaus-leveransen?",
        answer:
          "Ja, inbärning ingår alltid. Vi bär ditt köp till rätt plats – badrummet, bygget, garaget eller trädgården – oavsett våning. Inga extra avgifter.",
      },
      {
        question: "Vilka Bauhaus-butiker hämtar ni från?",
        answer:
          "Vi hämtar från alla Bauhaus-butiker i Stockholm och Göteborg: Barkarby, Kungens Kurva, Bromma, Arninge, Länna, Sisjön, Bäckebol och alla övriga. Ange bara butikens adress vid bokning.",
      },
      {
        question: "Kan ni leverera tungt kakel och klinker från Bauhaus?",
        answer:
          "Ja, vi transporterar kakel, klinker och golvmaterial dagligen. Vi lastar kartongerna stabilt i skåpbilen och bär in till badrummet, hallen eller köket – oavsett vikt och våning.",
      },
      {
        question: "Kan ni leverera virke och gipsskivor från Bauhaus?",
        answer:
          "Ja, vi hanterar långt virke, gipsskivor, OSB-skivor och isolering regelbundet. Vi lastar säkert i skåpbilen och bär in till bygget, garaget eller förrådet.",
      },
      {
        question: "Kan ni leverera duschväggar och badrumsvaror från Bauhaus?",
        answer:
          "Ja, vi transporterar duschväggar, badkar, handfat och toalettstolar. Vi hanterar glas och porslin med extra omsorg och bär in till badrummet.",
      },
      {
        question: "Kan ni bära tungt Bauhaus-material i trappor?",
        answer:
          "Ja, vi bär till vilken våning som helst – med eller utan hiss. Våra ungdomar är vana vid tunga lyft och trånga trapphus. Vi bär kakel, gipsskivor och byggsäckar uppför trappor dagligen.",
      },
      {
        question: "Skyddar ni sakerna under Bauhaus-transporten?",
        answer:
          "Ja, vi säkrar allt med spännband i skåpbilen. Ömtåliga varor som duschväggar, kakel och porslin skyddas extra. Allt är försäkrat – om något skadas ersätter vi det.",
      },
      {
        question: "Hur snabbt kan ni leverera från Bauhaus?",
        answer:
          "Vi erbjuder ofta hemleverans med kort varsel – ofta redan samma dag – inom några timmar. Boka online och se tillgängliga tider direkt.",
      },
      {
        question: "Erbjuder ni Bauhaus-leverans på helger?",
        answer:
          "Ja, vi erbjuder hemleverans på helger och vissa kvällar. Kontrollera att Bauhaus-butiken har öppet den tid du bokar – vi kan inte hämta om butiken är stängd.",
      },
      {
        question: "Kan ni hämta från Bauhaus och en annan butik i samma tur?",
        answer:
          "Ja, vi kan hämta från flera butiker under samma körning om de ligger i rimligt avstånd. Beskriv alla hämtpunkter vid bokning – till exempel Bauhaus och Jula i samma tur.",
      },
      {
        question: "Behöver jag vara med på Bauhaus vid upphämtningen?",
        answer:
          "Nej, du behöver inte vara med på Bauhaus. Se till att köpet är betalt och redo att hämtas – vi sköter resten. Du behöver bara vara hemma för att ta emot leveransen.",
      },
      {
        question:
          "I vilka kommuner erbjuder ni hemleverans från Bauhaus?",
        answer:
          "Vi erbjuder Bauhaus-leverans i hela Stockholmsområdet: Botkyrka, Danderyd, Ekerö, Haninge, Huddinge, Järfälla, Lidingö, Nacka, Norrtälje, Nykvarn, Nynäshamn, Salem, Sigtuna, Sollentuna, Solna, Stockholm, Sundbyberg, Södertälje, Tyresö, Täby, Upplands Väsby, Upplands-Bro, Vallentuna, Vaxholm, Värmdö och Österåker. Vi finns även i Göteborgsområdet: Ale, Alingsås, Göteborg, Härryda, Kungälv, Kungsbacka, Lerum, Lilla Edet, Mölndal, Partille, Stenungsund, Tjörn och Öckerö.",
      },
      {
        question: "Varför välja Ungdomskraft för Bauhaus-leverans?",
        answer:
          "Hos oss ingår alltid skåpbil, bärhjälp och inbärning till rätt plats. Du ser priset direkt vid bokning, vi kan ofta köra samma dag och allt är försäkrat.",
      },
    ],

    priceHint: PACKAGE_PRICE_HINT_SV.bud,

    pricingRows: getPackagePricingRows("bud", "sv"),

    dynamicHeadings: {
      threeSteps: "Bauhaus-leverans i 3 enkla steg",
      step2Text:
        "Våra ungdomar hämtar ditt köp på Bauhaus, lastar och säkrar allt i skåpbilen. Sedan kör vi hem till dig.",
      step3Title: "Allt på plats",
      step3Desc:
        "Ditt Bauhaus-köp står hemma hos dig – inburet och på plats. Klart.",
      ctaHeading: "Boka Bauhaus-leverans idag",
      ctaText:
        "Ange Bauhaus-butikens adress och din hemadress, beskriv vad du handlat och välj en tid. Du ser priset direkt.",
    },

    en: {
      slug: "bauhaus-delivery",
      meta: {
        title:
          "Bauhaus Delivery – We Pick Up Your Purchase And Deliver Home | Ungdomskraft",
        description:
          "Bought building materials or tiles at Bauhaus that won't fit in your car? We pick up at the store and deliver home with carrying. Fixed price. Book today!",
        h1: "Bauhaus delivery",
      },
      hero: {
        heading: "We pick up your purchase at Bauhaus",
        intro:
          "Shopped at Bauhaus and it won't all fit in the car? Timber, tiles, bathroom modules, garden furniture and heavy building sacks — the things that make Bauhaus a one-stop renovation giant are also the hardest to get home. We solve it. Ungdomskraft picks up your purchase directly at Bauhaus, loads the van and delivers to your home — with carrying and placement included. We collect from all Bauhaus stores in Stockholm and Gothenburg.",
      },
      featureCards: {
        sinceText: "Bauhaus delivery since 2014",
        bookYourText: "Book your Bauhaus delivery",
        bookDescText:
          "Enter the Bauhaus store address and your home address, describe what you bought and choose a time. You see the price instantly.",
        vehicleTitleText: "Vans perfect for Bauhaus deliveries",
        vehicleDescText:
          "We arrive with the right size van — whether you're picking up a tin of paint or an entire bathroom renovation. No need to rent a trailer, drive yourself or make multiple trips.",
      },
      itemsSection: {
        heading: "Bauhaus products we pick up and deliver home",
        items: [
          {
            name: "Building & timber",
            description:
              "Timber, studs, plasterboard, OSB boards, insulation, cement and concrete",
            image: "/box.webp",
          },
          {
            name: "Tiles & flooring",
            description:
              "Ceramic tiles, porcelain tiles, laminate, hardwood flooring and vinyl in heavy packages",
            image: "/wardrobe.webp",
          },
          {
            name: "Bathroom & plumbing",
            description:
              "Shower screens, basins, toilets, bathtubs, taps and pipe fittings",
            image: "/washer.webp",
          },
          {
            name: "Garden & outdoor",
            description:
              "Garden furniture, paving slabs, fencing, pergolas, grills and large planters",
            image: "/patio-furniture.webp",
          },
          {
            name: "Tools & machinery",
            description:
              "Power tools, compressors, bench grinders, workbenches and tool trolleys",
            image: "/toolbox.webp",
          },
          {
            name: "Kitchen & storage",
            description:
              "Kitchen cabinets, worktops, sinks, garage shelving and storage systems",
            image: "/dishes.webp",
          },
        ],
      },
      seoContent: {
        mainHeading:
          "Delivery from Bauhaus — we pick up, drive and carry inside for you",
        mainText:
          "Bauhaus is one of the Nordic region's largest building supply stores with an enormous range — everything from timber, plasterboard and insulation to tiles, bathroom modules, kitchen fittings, garden furniture and power tools. Their stores in Stockholm and Gothenburg are huge and attract hundreds of thousands of customers every week — both DIY enthusiasts and professional tradespeople. What makes Bauhaus unique is the breadth and depth: under one roof you'll find everything you need for a complete bathroom renovation, a re-roofing project, new flooring, a garden makeover or a fully equipped workshop. The problem most Bauhaus customers recognise: you go in to buy a pack of screws and a roll of tape, and come out with a trolley full of tiles, a shower screen, three bags of mortar, a worktop and maybe some garden furniture that was on campaign. All at great prices — but completely impossible to fit in the car. Timber sticks out by the metre, plasterboard is 2.4 metres long, tile boxes weigh 20–30 kg each and glass shower screens must be transported upright. Renting a trailer solves part of it but costs time and money, and you still have to do all the heavy lifting yourself. Bauhaus sometimes offers delivery, but waiting times can be long and time slots limited — especially during peak renovation season in spring and summer. Ungdomskraft offers a fast and smooth alternative. We pick up your Bauhaus purchase directly at the store, load everything into the van and drive to your home. Carrying to the right spot is always included — we carry to the build site, bathroom, garage, garden or storage room. You see the price instantly when booking, no hidden fees. We collect from all Bauhaus stores in Stockholm and Gothenburg. Every week we make hundreds of deliveries from Bauhaus and similar stores. Our crew are used to handling heavy building materials — timber, plasterboard, tiles, cement — and know how to load a van so nothing gets damaged or shifts during transport.",
        preparationHeading: "Prepare for your Bauhaus delivery",
        preparationText:
          "Make sure the purchase is paid for and ready for collection. Have your home address and any door codes ready. If you've bought heavy building materials, list the heaviest items so we can choose the right van size. Check that the van can park near the entrance or garage at your home. If materials are going to a specific location — the bathroom, garage, workshop — make sure the path is clear. When booking, specify any special requests, such as carrying tiles up to the second floor or placing timber in the garage.",
        additionalSections: [
          {
            heading: "Building material and timber delivery from Bauhaus",
            content:
              "Bauhaus has one of the market's widest ranges of building materials — timber in all dimensions, studs, plasterboard, OSB boards, plywood, insulation, cement, concrete, filler and bulk-pack screws. These products are almost always too heavy, long or bulky for a regular car. 4–5 metre timber sticks out, plasterboard is 2.4 metres long and weighs 25–30 kg each, insulation bales take up enormous space and cement bags weigh 25 kg apiece. A single bathroom renovation can mean 200–400 kg of materials. With Ungdomskraft's delivery we pick up everything at Bauhaus, load methodically into the van and drive to your home. We carry to the build site, garage or storage room — wherever the renovation is taking place. The perfect complement for DIY enthusiasts who don't have access to a pickup truck or trailer.",
          },
          {
            heading: "Tile, ceramic and flooring delivery from Bauhaus",
            content:
              "Bauhaus offers an enormous selection of ceramic tiles, porcelain tiles, mosaic, laminate flooring, hardwood and vinyl. Tile boxes weigh 20–30 kg each — a full bathroom typically requires 10–20 boxes, meaning 200–600 kg in total. Laminate or hardwood flooring packs weigh 15–25 kg each and you often need 8–15 packs for one room. These heavy, stacked goods are extremely impractical to transport in a car — they strain the suspension, take up all the space and require multiple trips. With our delivery we collect all the boxes at Bauhaus, load them securely in the van and drive to your home. We carry inside to the bathroom, hallway, kitchen or whichever room the floor is being laid in.",
          },
          {
            heading: "Bathroom and plumbing delivery from Bauhaus",
            content:
              "Bauhaus has one of the market's largest bathroom departments — tempered glass shower screens, basins, toilets, bathtubs, shower enclosures, bathroom furniture and all the plumbing you need. Shower screens must be transported upright and protected against impacts, a bathtub weighs 30–80 kg depending on material, and porcelain toilets are heavy and fragile. Many customers buy complete bathroom packages at Bauhaus — everything from tiles to taps — and the total volume and weight becomes enormous. With Ungdomskraft we collect your entire bathroom order from Bauhaus and drive it home. We carry into the bathroom or the room where renovation is underway, handling glass and porcelain with extra care.",
          },
          {
            heading: "How Bauhaus delivery works with Ungdomskraft",
            listItems: [
              {
                label: "Book online",
                text: "Enter the Bauhaus store address and your home address. Describe what you bought and choose a time. You see the price instantly.",
              },
              {
                label: "We pick up at Bauhaus",
                text: "Our team arrive with a van. We collect your purchase at the store, load and secure everything with straps.",
              },
              {
                label: "We deliver home",
                text: "We drive to your home and carry to the right spot — build site, bathroom, garage or garden.",
              },
              {
                label: "Done",
                text: "Your Bauhaus purchase is at home — carried in and in place. Without lifting a finger.",
              },
            ],
            afterContent:
              "The whole process typically takes 1–3 hours depending on distance and amount. You can follow the status in real time.",
          },
          {
            heading: "We collect from all Bauhaus stores",
            content:
              "We pick up from all Bauhaus stores in Stockholm and Gothenburg. In Stockholm: Barkarby, Kungens Kurva, Bromma, Arninge, Länna and all others. In Gothenburg: Sisjön, Bäckebol and all other locations. Just enter the store address when booking and we handle the rest. Shopping at Bauhaus and another store the same day? We can pick up from multiple locations in the same trip — for example Bauhaus and Jula or Bauhaus and a tile specialist in the same run.",
          },
          {
            heading: "Why book Bauhaus delivery with Ungdomskraft?",
            content:
              "Bauhaus sometimes offers delivery, but waiting times can be long — especially during renovation season in spring and summer. Time slots are limited and carrying to the right spot is rarely included. With Ungdomskraft you get fast delivery with carrying inside — whether materials go to the bathroom, garage, garden or basement. Plus you make a double impact every time you book: you get your purchase home smoothly and you help young people enter the workforce.\n\nWhat makes Ungdomskraft unique is our social mission. We employ young people — often aged 18–25 — who otherwise struggle to get their first job. Many lack work experience, networks or industry contacts. By working with us they gain real, practical experience that employers value: face-to-face customer service, logistics and planning, physical work under time pressure, teamwork and personal responsibility for completing a job properly. They learn to communicate professionally with customers, solve problems on site and deliver quality without shortcuts.\n\nEvery Bauhaus delivery is staffed by our young workers. They collect your purchase at the store, load the van methodically — heavy items at the bottom, fragile items protected — drive safely to your address and carry to the exact spot you want. They handle your materials with the same care regardless of whether it's a box of tiles or ten plasterboards. The energy and commitment they show is evident in every job.\n\nWe've been around since 2014 and have completed tens of thousands of transports and deliveries across Stockholm and Gothenburg. Over the years, hundreds of young people have gained their first work experience through Ungdomskraft — and many have gone on to permanent positions in logistics, construction, customer service and other industries. Our model works because it's built on real work with real customers, not internships without responsibility.\n\nSustainability runs through everything we do. We plan routes efficiently to minimise empty runs. We consolidate loads when possible. We recycle packaging materials and ensure cardboard, polystyrene, pallets and plastic are sorted correctly after delivery. For companies and tradespeople ordering Bauhaus deliveries we offer documentation for CSR reporting — every booking supports youth employment and sustainable logistics.\n\nEverything is fully insured. We have our own vans in multiple sizes — from small vans for tools and paint to large vans that fit timber, plasterboard and tile pallets. You always see the price before you book. No hidden fees, no surprises. And if anything is unexpectedly damaged during transport we replace it immediately.\n\nWhen you book Bauhaus delivery with Ungdomskraft, you choose more than a transport service. You choose to give a young person the opportunity to learn, grow and build their future. It's smooth delivery with purpose.\n\nBauhaus delivery does not qualify for the Swedish RUT tax deduction, but several of our other services do – including moving help, carrying help and furniture assembly with a 50% deduction on the labour cost. Feel free to combine services in a single booking.",
          },
        ],
      },
      faq: [
        {
          question: "How much does Bauhaus delivery cost?",
          answer:
            "The price depends on the amount and distance. A smaller Bauhaus delivery with paint or tools starts at 595 SEK. Larger deliveries with tiles, timber or bathroom fittings cost 749–2 495 SEK. You see the exact price when you book online.",
        },
        {
          question: "Can I get a RUT tax deduction on Bauhaus delivery?",
          answer:
            "Bauhaus delivery does not in itself qualify for the Swedish RUT tax deduction. RUT applies to household services carried out in your home, such as moving help, carrying help and furniture assembly. Ungdomskraft offers several RUT-eligible services with a 50% deduction on the labour cost – easily combine them in one booking via our website.",
        },
        {
          question: "Is a van included in the Bauhaus delivery?",
          answer:
            "Yes, a van is always included. We choose the right size based on your purchase — you don't need to rent a vehicle or trailer.",
        },
        {
          question: "Is carrying inside included in the Bauhaus delivery price?",
          answer:
            "Yes, carrying is always included. We carry your purchase to the right spot — bathroom, build site, garage or garden — regardless of floor. No extra charges.",
        },
        {
          question: "Which Bauhaus stores do you pick up from?",
          answer:
            "We pick up from all Bauhaus stores in Stockholm and Gothenburg: Barkarby, Kungens Kurva, Bromma, Arninge, Länna, Sisjön, Bäckebol and all others. Just enter the store address when booking.",
        },
        {
          question: "Can you deliver heavy tiles and flooring from Bauhaus?",
          answer:
            "Yes, we transport tiles, ceramic and flooring daily. We load the boxes securely in the van and carry inside to the bathroom, hallway or kitchen — regardless of weight and floor.",
        },
        {
          question: "Can you deliver timber and plasterboard from Bauhaus?",
          answer:
            "Yes, we handle long timber, plasterboard, OSB boards and insulation regularly. We load safely in the van and carry to the build site, garage or storage room.",
        },
        {
          question: "Can you deliver shower screens and bathroom fittings from Bauhaus?",
          answer:
            "Yes, we transport shower screens, bathtubs, basins and toilets. We handle glass and porcelain with extra care and carry into the bathroom.",
        },
        {
          question: "Can you carry heavy Bauhaus materials up stairs?",
          answer:
            "Yes, we carry to any floor — with or without a lift. Our crew are used to heavy materials and narrow stairwells. We carry tiles, plasterboard and building sacks up stairs daily.",
        },
        {
          question: "Do you protect items during Bauhaus transport?",
          answer:
            "Yes, we secure everything with straps in the van. Fragile items like shower screens, tiles and porcelain get extra protection. Everything is insured — if something is damaged we replace it.",
        },
        {
          question: "How quickly can you deliver from Bauhaus?",
          answer:
            "We often offer delivery at short notice — often the same day — within a few hours. Book online and see available times instantly.",
        },
        {
          question: "Is Bauhaus delivery available on weekends?",
          answer:
            "Yes, we offer delivery on weekends and some evenings. Check that the Bauhaus store is open at your chosen time — we can't pick up if the store is closed.",
        },
        {
          question: "Can you pick up from Bauhaus and another store in the same trip?",
          answer:
            "Yes, we can pick up from several stores during the same run if they're within a reasonable distance. Describe all pickup points when you book — for example Bauhaus and Jula in the same trip.",
        },
        {
          question: "Do I need to be at the Bauhaus store?",
          answer:
            "No, you don't need to be at Bauhaus. Make sure the purchase is paid for and ready to collect — we handle the rest. You just need to be home to receive the delivery.",
        },
        {
          question: "In which areas do you offer Bauhaus delivery?",
          answer:
            "We offer Bauhaus delivery across the entire Stockholm region: Botkyrka, Danderyd, Ekerö, Haninge, Huddinge, Järfälla, Lidingö, Nacka, Norrtälje, Nykvarn, Nynäshamn, Salem, Sigtuna, Sollentuna, Solna, Stockholm, Sundbyberg, Södertälje, Tyresö, Täby, Upplands Väsby, Upplands-Bro, Vallentuna, Vaxholm, Värmdö and Österåker. We're also in the Gothenburg region: Ale, Alingsås, Göteborg, Härryda, Kungälv, Kungsbacka, Lerum, Lilla Edet, Mölndal, Partille, Stenungsund, Tjörn and Öckerö.",
        },
        {
          question: "Why choose Ungdomskraft for Bauhaus delivery?",
          answer:
            "We always include a van, carrying help and placement in the right spot. You see the price instantly when booking, we can often deliver the same day and everything is insured.",
        },
      ],
      priceHint: PACKAGE_PRICE_HINT_EN.bud,
      pricingRows: getPackagePricingRows("bud", "en"),
      dynamicHeadings: {
        threeSteps: "Bauhaus delivery in 3 easy steps",
        step2Text:
          "Our crew pick up your purchase at Bauhaus, load and secure everything in the van. Then we drive home to you.",
        step3Title: "All in place",
        step3Desc:
          "Your Bauhaus purchase is at home — carried in and in place. Done.",
        ctaHeading: "Book Bauhaus delivery today",
        ctaText:
          "Enter the Bauhaus store address and your home address, describe what you bought and choose a time. You see the price instantly.",
      },
    },
  },

  {
    // Hemleverans K-Rauta — config för /hemleverans-k-rauta
    // Vinkel: Hemleverans av K-Rauta-köp (bygg, badrum, kök, trädgård, verktyg, VVS)
    // K-Rauta = stort byggvaruhus med stark position inom badrum, kök och renovering.
    slug: "hemleverans-k-rauta",
    serviceCategory: "hemleverans",

    meta: {
      title:
        "Hemleverans K-Rauta – Vi Hämtar Ditt Köp Och Kör Hem | Ungdomskraft",
      description:
        "Köpt byggmaterial eller köksinredning på K-Rauta som inte får plats i bilen? Vi hämtar och levererar hem med inbärning. Fast pris. Boka idag!",
      h1: "Hemleverans K-Rauta",
    },

    hero: {
      heading: "Vi hämtar ditt köp på K-Rauta",
      intro:
        "Handlat på K-Rauta och bilen räcker inte till? Köksskåp, bänkskivor, kakel, trädgårdsmöbler och tunga byggsäckar – K-Rautas breda sortiment gör det lätt att handla stort, men svårt att frakta hem. Vi löser det. Ungdomskraft hämtar ditt köp direkt på K-Rauta, lastar skåpbilen och levererar hem till dig – med inbärning och avlastning på plats. Vi kör från alla K-Rauta-butiker i Stockholm och Göteborg.",
      heroImage: "/hero-hemleverans.png",
    },

    featureCards: {
      sinceText: "K-Rauta-leverans sedan 2014",
      storyText:
        "Sedan 2014 har Ungdomskraft hjälpt tusentals kunder att få hem sina K-Rauta-köp – från tunga köksluckor och bänkskivor till kakelplattor, trädgårdsmöbler och byggmaterial. Våra ungdomar hämtar i butiken, lastar skåpbilen och bär in hemma hos dig.",
      bookYourText: "Boka din K-Rauta-leverans",
      bookDescText:
        "Ange K-Rauta-butikens adress och din hemadress, beskriv vad som ska hämtas och välj en tid. Du ser priset direkt.",
      vehicleTitleText: "Skåpbilar perfekta för K-Rauta-leveranser",
      vehicleDescText:
        "Vi kommer med rätt storlek på skåpbil – oavsett om du hämtar en kruka färg eller ett helt köksrenoverings­paket. Du slipper hyra släp, köra själv eller göra flera turer.",
    },

    itemsSection: {
      heading: "K-Rauta-produkter vi hämtar och levererar hem",
      items: [
        {
          name: "Kök & bänkskivor",
          description:
            "Köksskåp, köksluckor, bänkskivor, diskbänkar och köksöar i flatpack eller monterade",
          image: "/dishes.webp",
        },
        {
          name: "Badrum & VVS",
          description:
            "Duschväggar, tvättställ, toalettstolar, badkar, blandare och badrumsmöbler",
          image: "/washer.webp",
        },
        {
          name: "Kakel & golv",
          description:
            "Klinker, kakelplattor, laminatgolv, trägolv och vinylgolv i tunga kartonger",
          image: "/wardrobe.webp",
        },
        {
          name: "Bygg & virke",
          description:
            "Virke, reglar, gipsskivor, isolering, cement, skruv och byggskivor",
          image: "/box.webp",
        },
        {
          name: "Trädgård & utomhus",
          description:
            "Trädgårdsmöbler, markplattor, staket, pergolor, grillar och stora krukor",
          image: "/patio-furniture.webp",
        },
        {
          name: "Verktyg & maskiner",
          description:
            "Elverktyg, kompressorer, arbetsbänkar, verktygsvagnar och förvaringslösningar",
          image: "/toolbox.webp",
        },
      ],
    },

    seoContent: {
      mainHeading:
        "Hemleverans från K-Rauta – vi hämtar, kör och bär in åt dig",
      mainText:
        "K-Rauta är ett av Sveriges ledande byggvaruhus med ett brett sortiment inom bygg, renovering, kök, badrum, trädgård och verktyg. Butikerna i Stockholm och Göteborg erbjuder allt från kompletta kök och badrumspaket till virke, kakel, VVS-material och trädgårdsinredning. K-Rauta har en särskilt stark position inom kök och badrum – med egna köksmiljöer, planeringstjänster och ett djupt sortiment av skåp, luckor, bänkskivor och tillbehör. Det gör K-Rauta till en favorit för den som renoverar, men det innebär också att köpen snabbt blir tunga och skrymmande. En köksleverans kan bestå av 15–30 kollin – skåpstommar, luckor, bänkskiva, diskbänk och beslag. Ett badrumspaket med kakel, duschvägg, handfat och toalett väger lätt 200–400 kg totalt. Trädgårdsmöbler, markplattor och staket tar enorm plats. Inget av detta ryms i en personbil. K-Rauta erbjuder egen hemleverans, men väntetiderna varierar och tiderna är ofta begränsade – särskilt under våren och sommaren när alla renoverar samtidigt. Inbärning till rätt rum kostar ofta extra. Ungdomskraft erbjuder ett snabbt och smidigt alternativ. Vi hämtar ditt K-Rauta-köp direkt i butiken, lastar allt i skåpbilen och kör hem till dig. Inbärning till rätt plats ingår alltid – vi bär in till köket, badrummet, garaget, trädgården eller förrådet. Du ser priset direkt vid bokning, inga dolda avgifter tillkommer. Vi kör från alla K-Rauta-butiker i Stockholm och Göteborg. Varje vecka hämtar vi hundratals leveranser från K-Rauta och liknande byggvaruhus. Våra ungdomar är vana vid att hantera tungt byggmaterial, ömtåliga kakelleveranser och skrymmande köksskåp – vi lastar metodiskt, skyddar ömtåligt gods och navigerar trånga trapphus.",
      preparationHeading: "Förbered inför din K-Rauta-leverans",
      preparationText:
        "Se till att köpet är betalt och klart för upphämtning. Ha din hemadress och eventuell portkod redo. Om du beställt ett helt kök eller badrum – lista gärna de största och tyngsta delarna så att vi kan välja rätt skåpbilsstorlek. Mät dörröppningar och trapphus i förväg om du beställt stora köksskåp eller bänkskivor. Kontrollera att skåpbilen kan parkera nära porten hemma hos dig. Ange vid bokning om materialet ska till en specifik våning eller rum, och om det finns trappor utan hiss.",
      additionalSections: [
        {
          heading: "Hemleverans av kök och köksinredning från K-Rauta",
          content:
            "K-Rauta har ett av marknadens starkaste kökssortiment – kompletta kök med skåpstommar, luckor, lådor, bänkskivor, diskbänkar, handtag och beslag. Ett komplett kök från K-Rauta kan bestå av 15–30 kollin: skåpstommar i flatpack väger 15–30 kg styck, bänkskivor i laminat eller sten 20–60 kg, och diskbänkar i rostfritt stål 10–20 kg. Allt måste transporteras plant, säkert och utan stötar – repade köksluckor eller en sprucken bänkskiva är ingen som vill ha. Med Ungdomskrafts hemleverans hämtar vi hela din köksbeställning på K-Rauta, lastar varje del methodiskt i skåpbilen och kör hem till dig. Vi bär in till köket och ställer alla kollin i rummet så att du eller din hantverkare enkelt kan börja monteringen. Perfekt för dig som renoverar köket men inte har tillgång till lämpligt fordon.",
        },
        {
          heading: "Hemleverans av badrum och VVS från K-Rauta",
          content:
            "K-Rauta erbjuder ett brett sortiment för badrumsrenovering – duschväggar i härdat glas, handfat, toalettstolar, badkar, badrumsmöbler, blandare och all VVS du behöver. Duschväggar måste transporteras stående och skyddas mot stötar, handfat och toalettstolar i porslin är tunga och ömtåliga, och ett badkar väger 30–80 kg. Komplettera med kakelplattor (200–600 kg för ett badrum) och volymen blir enorm. Med vår hemleverans hämtar vi hela din badrums­beställning på K-Rauta och kör hem. Vi hanterar glas och porslin med extra omsorg, bär in till badrummet och ställer allt på rätt plats. Du slipper riskera att en duschvägg spricker i bilen eller att en toalettstol ramlar av takräcket.",
        },
        {
          heading: "Hemleverans av kakel, golv och byggmaterial från K-Rauta",
          content:
            "K-Rauta har ett stort utbud av kakelplattor, klinker, mosaik, laminatgolv, trägolv och vinylgolv. Kakelkartonger väger 20–30 kg styck och ett badrum kräver ofta 10–20 kartonger – det kan bli 200–600 kg. Golvpaket väger 15–25 kg var och det behövs ofta 8–15 paket per rum. Lägg till virke, gipsskivor, isolering och cement och vikten stiger snabbt. Med Ungdomskraft hämtar vi allt på K-Rauta, lastar stabilt i skåpbilen och levererar hem. Vi bär in till badrummet, hallen, köket eller garaget – oavsett vikt och våning. Du slipper göra flera bilturer eller riskera att överbelasta personbilen.",
        },
        {
          heading: "Så fungerar K-Rauta-leverans med Ungdomskraft",
          listItems: [
            {
              label: "Boka online",
              text: "Ange K-Rauta-butikens adress och din hemadress. Beskriv vad du handlat och välj en tid. Du ser priset direkt.",
            },
            {
              label: "Vi hämtar på K-Rauta",
              text: "Vårt team kommer med skåpbil. Vi hämtar ditt köp i butiken, lastar och säkrar allt med spännband och filtar.",
            },
            {
              label: "Vi levererar hem",
              text: "Vi kör hem till dig, bär in till rätt plats – köket, badrummet, garaget eller trädgården.",
            },
            {
              label: "Klart",
              text: "Ditt K-Rauta-köp står hemma hos dig – inburet och på plats. Utan att du lyfte ett finger.",
            },
          ],
          afterContent:
            "Hela processen tar normalt 1–3 timmar beroende på avstånd och mängd. Du kan följa statusen i realtid.",
        },
        {
          heading: "Vi kör från alla K-Rauta-butiker",
          content:
            "Vi hämtar från alla K-Rauta-butiker i Stockholm och Göteborg. I Stockholm: Bromma, Kungens Kurva, Barkarby, Arninge, Nacka och alla övriga. I Göteborg: Sisjön, Bäckebol och alla övriga butiker. Ange bara butikens adress vid bokning så sköter vi resten. Ska du handla på K-Rauta och en annan butik samma dag? Vi kan hämta från flera ställen i samma tur – till exempel K-Rauta och Bauhaus eller K-Rauta och en kakelbutik i samma körning.",
        },
        {
          heading: "Varför boka K-Rauta-leverans med Ungdomskraft?",
          content:
            "K-Rauta erbjuder egen hemleverans, men väntetiderna kan vara långa – särskilt under renoveringssäsongen på våren och sommaren. Tiderna är begränsade och inbärning till rätt rum kostar ofta extra. Om du köpt på K-Rautas lagerrea eller hittat en kampanj som ska hem snabbt fungerar inte alltid K-Rautas planerade leveranser. Med Ungdomskraft får du snabb leverans med inbärning till exakt den plats du vill – utan veckors väntan. Dessutom gör du dubbel nytta varje gång du bokar: du får hem ditt köp smidigt och du bidrar till att unga människor kommer in på arbetsmarknaden.\n\nDet som gör Ungdomskraft unikt är vår samhällsnytta. Vi anställer unga människor – ofta i åldern 18–25 – som annars har svårt att få sitt första jobb. Många saknar arbetslivserfarenhet, nätverk eller kontakter i branschen. Genom att arbeta hos oss får de riktig, praktisk erfarenhet som arbetsgivare värdesätter: kundkontakt ansikte mot ansikte, logistik och planering, fysiskt arbete under tidspress, samarbete i team och eget ansvar för att ett uppdrag slutförs ordentligt. De lär sig att kommunicera professionellt med kunder, att lösa problem på plats och att leverera kvalitet utan genvägar.\n\nVarje K-Rauta-leverans bemannas av våra ungdomar. De hämtar ditt köp i butiken, lastar skåpbilen metodiskt – tungt längst ner, ömtåligt skyddat med filtar – kör säkert till din adress och bär in till exakt den plats du önskar. De behandlar ditt material med samma omsorg oavsett om det är ett köksskåp eller tjugo kartonger kakel. Den energi och det engagemang de visar märks i varje uppdrag.\n\nVi har funnits sedan 2014 och har genomfört tiotusentals transporter och leveranser i Stockholm och Göteborg. Under åren har hundratals ungdomar fått sin första arbetslivserfarenhet genom Ungdomskraft – och många har gått vidare till fasta tjänster inom logistik, bygg, kundservice och andra branscher. Vår modell fungerar för att den bygger på riktigt arbete med riktiga kunder, inte praktik utan ansvar.\n\nHållbarhet genomsyrar allt vi gör. Vi planerar körningar effektivt för att minimera tomkörning. Vi samlastar när det är möjligt. Vi återvinner förpackningsmaterial och ser till att wellpapp, frigolit, pall och plast sorteras korrekt efter leverans. För företag och hantverkare som beställer K-Rauta-leveranser erbjuder vi dokumentation som kan användas i CSR-rapportering – varje bokning stödjer ungdomsarbete och hållbar logistik.\n\nAllt är helförsäkrat. Vi har egna skåpbilar i flera storlekar – från liten skåpbil för verktyg och färg till stor skåpbil som rymmer ett komplett kök eller badrum. Du ser alltid priset innan du bokar. Inga dolda avgifter, inga överraskningar. Och om något mot förmodan skadas under transporten ersätter vi det omedelbart.\n\nNär du bokar K-Rauta-leverans med Ungdomskraft väljer du mer än en transporttjänst. Du väljer att ge en ung människa möjligheten att lära, växa och bygga sin framtid. Det är smidig leverans med mening.\n\nHemleverans från K-Rauta ger inte rätt till RUT-avdrag, men flera av våra andra tjänster gör det – exempelvis flytt, bärhjälp och möbelmontering med 50 % avdrag på arbetskostnaden. Kombinera gärna tjänster i en och samma bokning.",
        },
      ],
    },

    faq: [
      {
        question: "Vad kostar hemleverans från K-Rauta?",
        answer:
          "Priset beror på mängden och avståndet. En mindre K-Rauta-leverans med färg eller verktyg kostar från 595 kr. Större leveranser med kök, kakel eller badrumsvaror kostar från 995–2 495 kr. Du ser det exakta priset när du bokar online.",
      },
      {
        question: "Kan jag få RUT-avdrag på hemleverans från K-Rauta?",
        answer:
          "Hemleverans från K-Rauta ger i sig inte rätt till RUT-avdrag. RUT gäller hushållsnära tjänster som utförs i din bostad, exempelvis flytt, bärhjälp och möbelmontering. Ungdomskraft erbjuder flera RUT-berättigade tjänster med 50 % avdrag på arbetskostnaden – kombinera enkelt i en bokning via vår hemsida.",
      },
      {
        question: "Ingår skåpbil i K-Rauta-leveransen?",
        answer:
          "Ja, skåpbil ingår alltid. Vi väljer rätt storlek utifrån ditt köp – du behöver inte hyra bil eller släp.",
      },
      {
        question: "Ingår inbärning i priset för K-Rauta-leveransen?",
        answer:
          "Ja, inbärning ingår alltid. Vi bär ditt köp till rätt plats – köket, badrummet, garaget eller trädgården – oavsett våning. Inga extra avgifter.",
      },
      {
        question: "Vilka K-Rauta-butiker hämtar ni från?",
        answer:
          "Vi hämtar från alla K-Rauta-butiker i Stockholm och Göteborg: Bromma, Kungens Kurva, Barkarby, Arninge, Nacka, Sisjön, Bäckebol och alla övriga. Ange bara butikens adress vid bokning.",
      },
      {
        question: "Kan ni leverera ett komplett kök från K-Rauta?",
        answer:
          "Ja, vi levererar kompletta köksbeställningar regelbundet – skåpstommar, luckor, bänkskivor, diskbänkar och beslag. Vi bär in alla kollin till köket så att du eller din hantverkare kan börja montera direkt.",
      },
      {
        question: "Kan ni leverera badrumsvaror från K-Rauta?",
        answer:
          "Ja, vi transporterar duschväggar, handfat, toalettstolar, badkar och badrumsmöbler. Vi hanterar glas och porslin med extra omsorg och bär in till badrummet.",
      },
      {
        question: "Kan ni leverera tungt kakel och golv från K-Rauta?",
        answer:
          "Ja, vi transporterar kakel, klinker och golvmaterial dagligen. Kakelkartonger som väger 20–30 kg styck – vi lastar stabilt och bär in till rätt rum oavsett våning.",
      },
      {
        question: "Kan ni bära tungt K-Rauta-material i trappor?",
        answer:
          "Ja, vi bär till vilken våning som helst – med eller utan hiss. Våra ungdomar är vana vid tunga lyft och trånga trapphus. Vi bär köksskåp, kakel och byggsäckar uppför trappor dagligen.",
      },
      {
        question: "Skyddar ni sakerna under K-Rauta-transporten?",
        answer:
          "Ja, vi säkrar allt med spännband och skyddar ömtåligt gods med filtar. Duschväggar, porslin och köksluckor hanteras med extra omsorg. Allt är försäkrat – om något skadas ersätter vi det.",
      },
      {
        question: "Hur snabbt kan ni leverera från K-Rauta?",
        answer:
          "Vi erbjuder ofta hemleverans med kort varsel – ofta redan samma dag – inom några timmar. Boka online och se tillgängliga tider direkt.",
      },
      {
        question: "Erbjuder ni K-Rauta-leverans på helger?",
        answer:
          "Ja, vi erbjuder hemleverans på helger och vissa kvällar. Kontrollera att K-Rauta-butiken har öppet den tid du bokar – vi kan inte hämta om butiken är stängd.",
      },
      {
        question: "Kan ni hämta från K-Rauta och en annan butik i samma tur?",
        answer:
          "Ja, vi kan hämta från flera butiker under samma körning om de ligger i rimligt avstånd. Beskriv alla hämtpunkter vid bokning – till exempel K-Rauta och Bauhaus i samma tur.",
      },
      {
        question: "Behöver jag vara med på K-Rauta vid upphämtningen?",
        answer:
          "Nej, du behöver inte vara med på K-Rauta. Se till att köpet är betalt och redo att hämtas – vi sköter resten. Du behöver bara vara hemma för att ta emot leveransen.",
      },
      {
        question:
          "I vilka kommuner erbjuder ni hemleverans från K-Rauta?",
        answer:
          "Vi erbjuder K-Rauta-leverans i hela Stockholmsområdet: Botkyrka, Danderyd, Ekerö, Haninge, Huddinge, Järfälla, Lidingö, Nacka, Norrtälje, Nykvarn, Nynäshamn, Salem, Sigtuna, Sollentuna, Solna, Stockholm, Sundbyberg, Södertälje, Tyresö, Täby, Upplands Väsby, Upplands-Bro, Vallentuna, Vaxholm, Värmdö och Österåker. Vi finns även i Göteborgsområdet: Ale, Alingsås, Göteborg, Härryda, Kungälv, Kungsbacka, Lerum, Lilla Edet, Mölndal, Partille, Stenungsund, Tjörn och Öckerö.",
      },
      {
        question: "Varför välja Ungdomskraft för K-Rauta-leverans?",
        answer:
          "Hos oss ingår alltid skåpbil, bärhjälp och inbärning till rätt plats. Du ser priset direkt vid bokning, vi kan ofta köra samma dag och allt är försäkrat.",
      },
    ],

    priceHint: PACKAGE_PRICE_HINT_SV.bud,

    pricingRows: getPackagePricingRows("bud", "sv"),

    dynamicHeadings: {
      threeSteps: "K-Rauta-leverans i 3 enkla steg",
      step2Text:
        "Våra ungdomar hämtar ditt köp på K-Rauta, lastar och säkrar allt i skåpbilen. Sedan kör vi hem till dig.",
      step3Title: "Allt på plats",
      step3Desc:
        "Ditt K-Rauta-köp står hemma hos dig – inburet och på plats. Klart.",
      ctaHeading: "Boka K-Rauta-leverans idag",
      ctaText:
        "Ange K-Rauta-butikens adress och din hemadress, beskriv vad du handlat och välj en tid. Du ser priset direkt.",
    },

    en: {
      slug: "k-rauta-delivery",
      meta: {
        title:
          "K-Rauta Delivery – We Pick Up Your Purchase And Deliver Home | Ungdomskraft",
        description:
          "Bought building materials or kitchen fittings at K-Rauta that won't fit in your car? We pick up and deliver home with carrying. Fixed price. Book today!",
        h1: "K-Rauta delivery",
      },
      hero: {
        heading: "We pick up your purchase at K-Rauta",
        intro:
          "Shopped at K-Rauta and your car can't handle it? Kitchen cabinets, worktops, tiles, garden furniture and heavy building sacks — K-Rauta's wide range makes it easy to shop big, but hard to get it all home. We solve it. Ungdomskraft picks up your purchase directly at K-Rauta, loads the van and delivers to your home — with carrying and placement included. We collect from all K-Rauta stores in Stockholm and Gothenburg.",
      },
      featureCards: {
        sinceText: "K-Rauta delivery since 2014",
        bookYourText: "Book your K-Rauta delivery",
        bookDescText:
          "Enter the K-Rauta store address and your home address, describe what you bought and choose a time. You see the price instantly.",
        vehicleTitleText: "Vans perfect for K-Rauta deliveries",
        vehicleDescText:
          "We arrive with the right size van — whether you're picking up a tin of paint or a complete kitchen renovation. No need to rent a trailer, drive yourself or make multiple trips.",
      },
      itemsSection: {
        heading: "K-Rauta products we pick up and deliver home",
        items: [
          {
            name: "Kitchen & worktops",
            description:
              "Kitchen cabinets, cabinet doors, worktops, sinks and kitchen islands in flatpack or assembled",
            image: "/dishes.webp",
          },
          {
            name: "Bathroom & plumbing",
            description:
              "Shower screens, basins, toilets, bathtubs, taps and bathroom furniture",
            image: "/washer.webp",
          },
          {
            name: "Tiles & flooring",
            description:
              "Ceramic tiles, porcelain tiles, laminate, hardwood and vinyl in heavy boxes",
            image: "/wardrobe.webp",
          },
          {
            name: "Building & timber",
            description:
              "Timber, studs, plasterboard, insulation, cement, screws and building boards",
            image: "/box.webp",
          },
          {
            name: "Garden & outdoor",
            description:
              "Garden furniture, paving slabs, fencing, pergolas, grills and large planters",
            image: "/patio-furniture.webp",
          },
          {
            name: "Tools & machinery",
            description:
              "Power tools, compressors, workbenches, tool trolleys and storage solutions",
            image: "/toolbox.webp",
          },
        ],
      },
      seoContent: {
        mainHeading:
          "Delivery from K-Rauta — we pick up, drive and carry inside for you",
        mainText:
          "K-Rauta is one of Sweden's leading building supply stores with a wide range spanning building, renovation, kitchens, bathrooms, garden and tools. Their stores in Stockholm and Gothenburg offer everything from complete kitchens and bathroom packages to timber, tiles, plumbing supplies and garden furnishings. K-Rauta has a particularly strong position in kitchens and bathrooms — with dedicated kitchen displays, planning services and a deep range of cabinets, doors, worktops and accessories. This makes K-Rauta a favourite for anyone renovating, but it also means purchases quickly become heavy and bulky. A kitchen delivery can consist of 15–30 packages — cabinet frames, doors, a worktop, sink and hardware. A bathroom package with tiles, shower screen, basin and toilet easily weighs 200–400 kg total. Garden furniture, paving slabs and fencing take up enormous space. None of this fits in a regular car. K-Rauta offers their own delivery, but waiting times vary and time slots are often limited — especially in spring and summer when everyone renovates at once. Carrying to the right room often costs extra. Ungdomskraft offers a fast and smooth alternative. We pick up your K-Rauta purchase directly at the store, load everything into the van and drive to your home. Carrying to the right spot is always included — we carry to the kitchen, bathroom, garage, garden or storage room. You see the price instantly when booking, no hidden fees. We collect from all K-Rauta stores in Stockholm and Gothenburg. Every week we make hundreds of deliveries from K-Rauta and similar stores. Our crew are used to handling heavy building materials, delicate tile deliveries and bulky kitchen cabinets — we load methodically, protect fragile items and navigate narrow stairwells.",
        preparationHeading: "Prepare for your K-Rauta delivery",
        preparationText:
          "Make sure the purchase is paid for and ready for collection. Have your home address and any door codes ready. If you've ordered a complete kitchen or bathroom, list the largest and heaviest parts so we can choose the right van size. Measure doorways and stairwells in advance if you've ordered large kitchen cabinets or worktops. Check that the van can park near the entrance at your home. When booking, specify which floor or room the materials should go to, and whether there are stairs without a lift.",
        additionalSections: [
          {
            heading: "Kitchen and worktop delivery from K-Rauta",
            content:
              "K-Rauta has one of the market's strongest kitchen ranges — complete kitchens with cabinet frames, doors, drawers, worktops, sinks, handles and hardware. A complete kitchen from K-Rauta can consist of 15–30 packages: cabinet frames in flatpack weigh 15–30 kg each, worktops in laminate or stone 20–60 kg, and stainless steel sinks 10–20 kg. Everything must be transported flat, safely and without impacts — nobody wants scratched cabinet doors or a cracked worktop. With Ungdomskraft's delivery we pick up your entire kitchen order at K-Rauta, load each part methodically into the van and drive to your home. We carry everything into the kitchen and place all packages in the room so you or your fitter can start installation right away. Perfect for anyone renovating their kitchen but lacking a suitable vehicle.",
          },
          {
            heading: "Bathroom and plumbing delivery from K-Rauta",
            content:
              "K-Rauta offers a wide range for bathroom renovation — tempered glass shower screens, basins, toilets, bathtubs, bathroom furniture, taps and all the plumbing you need. Shower screens must be transported upright and protected against impacts, basins and toilets in porcelain are heavy and fragile, and a bathtub weighs 30–80 kg. Add tiles (200–600 kg for a bathroom) and the volume becomes enormous. With our delivery we collect your entire bathroom order from K-Rauta and drive it home. We handle glass and porcelain with extra care, carry into the bathroom and place everything in position. No risking a shower screen cracking in the car or a toilet falling off the roof rack.",
          },
          {
            heading: "Tile, flooring and building material delivery from K-Rauta",
            content:
              "K-Rauta has a large selection of ceramic tiles, porcelain tiles, mosaic, laminate flooring, hardwood and vinyl. Tile boxes weigh 20–30 kg each and a bathroom typically requires 10–20 boxes — that's 200–600 kg. Flooring packs weigh 15–25 kg each and you often need 8–15 packs per room. Add timber, plasterboard, insulation and cement and the weight climbs fast. With Ungdomskraft we collect everything at K-Rauta, load securely into the van and deliver home. We carry inside to the bathroom, hallway, kitchen or garage — regardless of weight and floor. No multiple car trips or risking an overloaded vehicle.",
          },
          {
            heading: "How K-Rauta delivery works with Ungdomskraft",
            listItems: [
              {
                label: "Book online",
                text: "Enter the K-Rauta store address and your home address. Describe what you bought and choose a time. You see the price instantly.",
              },
              {
                label: "We pick up at K-Rauta",
                text: "Our team arrive with a van. We collect your purchase at the store, load and secure everything with straps and blankets.",
              },
              {
                label: "We deliver home",
                text: "We drive to your home and carry to the right spot — kitchen, bathroom, garage or garden.",
              },
              {
                label: "Done",
                text: "Your K-Rauta purchase is at home — carried in and in place. Without lifting a finger.",
              },
            ],
            afterContent:
              "The whole process typically takes 1–3 hours depending on distance and amount. You can follow the status in real time.",
          },
          {
            heading: "We collect from all K-Rauta stores",
            content:
              "We pick up from all K-Rauta stores in Stockholm and Gothenburg. In Stockholm: Bromma, Kungens Kurva, Barkarby, Arninge, Nacka and all others. In Gothenburg: Sisjön, Bäckebol and all other locations. Just enter the store address when booking and we handle the rest. Shopping at K-Rauta and another store the same day? We can pick up from multiple locations in the same trip — for example K-Rauta and Bauhaus or K-Rauta and a tile specialist in the same run.",
          },
          {
            heading: "Why book K-Rauta delivery with Ungdomskraft?",
            content:
              "K-Rauta offers their own delivery, but waiting times can be long — especially during renovation season in spring and summer. Time slots are limited and carrying to the right room often costs extra. Campaign purchases or items that need to go home quickly aren't always covered by K-Rauta's scheduled deliveries. With Ungdomskraft you get fast delivery with carrying to exactly the spot you want — without weeks of waiting. Plus you make a double impact every time you book: you get your purchase home smoothly and you help young people enter the workforce.\n\nWhat makes Ungdomskraft unique is our social mission. We employ young people — often aged 18–25 — who otherwise struggle to get their first job. Many lack work experience, networks or industry contacts. By working with us they gain real, practical experience that employers value: face-to-face customer service, logistics and planning, physical work under time pressure, teamwork and personal responsibility for completing a job properly. They learn to communicate professionally with customers, solve problems on site and deliver quality without shortcuts.\n\nEvery K-Rauta delivery is staffed by our young workers. They collect your purchase at the store, load the van methodically — heavy items at the bottom, fragile items protected with blankets — drive safely to your address and carry to the exact spot you want. They handle your materials with the same care regardless of whether it's a kitchen cabinet or twenty boxes of tiles. The energy and commitment they show is evident in every job.\n\nWe've been around since 2014 and have completed tens of thousands of transports and deliveries across Stockholm and Gothenburg. Over the years, hundreds of young people have gained their first work experience through Ungdomskraft — and many have gone on to permanent positions in logistics, construction, customer service and other industries. Our model works because it's built on real work with real customers, not internships without responsibility.\n\nSustainability runs through everything we do. We plan routes efficiently to minimise empty runs. We consolidate loads when possible. We recycle packaging materials and ensure cardboard, polystyrene, pallets and plastic are sorted correctly after delivery. For companies and tradespeople ordering K-Rauta deliveries we offer documentation for CSR reporting — every booking supports youth employment and sustainable logistics.\n\nEverything is fully insured. We have our own vans in multiple sizes — from small vans for tools and paint to large vans that fit a complete kitchen or bathroom. You always see the price before you book. No hidden fees, no surprises. And if anything is unexpectedly damaged during transport we replace it immediately.\n\nWhen you book K-Rauta delivery with Ungdomskraft, you choose more than a transport service. You choose to give a young person the opportunity to learn, grow and build their future. It's smooth delivery with purpose.\n\nK-Rauta delivery does not qualify for the Swedish RUT tax deduction, but several of our other services do – including moving help, carrying help and furniture assembly with a 50% deduction on the labour cost. Feel free to combine services in a single booking.",
          },
        ],
      },
      faq: [
        {
          question: "How much does K-Rauta delivery cost?",
          answer:
            "The price depends on the amount and distance. A smaller K-Rauta delivery with paint or tools starts at 595 SEK. Larger deliveries with kitchen fittings, tiles or bathroom products cost 749–2 495 SEK. You see the exact price when you book online.",
        },
        {
          question: "Can I get a RUT tax deduction on K-Rauta delivery?",
          answer:
            "K-Rauta delivery does not in itself qualify for the Swedish RUT tax deduction. RUT applies to household services carried out in your home, such as moving help, carrying help and furniture assembly. Ungdomskraft offers several RUT-eligible services with a 50% deduction on the labour cost – easily combine them in one booking via our website.",
        },
        {
          question: "Is a van included in the K-Rauta delivery?",
          answer:
            "Yes, a van is always included. We choose the right size based on your purchase — you don't need to rent a vehicle or trailer.",
        },
        {
          question: "Is carrying inside included in the K-Rauta delivery price?",
          answer:
            "Yes, carrying is always included. We carry your purchase to the right spot — kitchen, bathroom, garage or garden — regardless of floor. No extra charges.",
        },
        {
          question: "Which K-Rauta stores do you pick up from?",
          answer:
            "We pick up from all K-Rauta stores in Stockholm and Gothenburg: Bromma, Kungens Kurva, Barkarby, Arninge, Nacka, Sisjön, Bäckebol and all others. Just enter the store address when booking.",
        },
        {
          question: "Can you deliver a complete kitchen from K-Rauta?",
          answer:
            "Yes, we deliver complete kitchen orders regularly — cabinet frames, doors, worktops, sinks and hardware. We carry all packages into the kitchen so you or your fitter can start installation straight away.",
        },
        {
          question: "Can you deliver bathroom fittings from K-Rauta?",
          answer:
            "Yes, we transport shower screens, basins, toilets, bathtubs and bathroom furniture. We handle glass and porcelain with extra care and carry into the bathroom.",
        },
        {
          question: "Can you deliver heavy tiles and flooring from K-Rauta?",
          answer:
            "Yes, we transport tiles, ceramic and flooring daily. Tile boxes weighing 20–30 kg each — we load securely and carry to the right room regardless of floor.",
        },
        {
          question: "Can you carry heavy K-Rauta materials up stairs?",
          answer:
            "Yes, we carry to any floor — with or without a lift. Our crew are used to heavy materials and narrow stairwells. We carry kitchen cabinets, tiles and building sacks up stairs daily.",
        },
        {
          question: "Do you protect items during K-Rauta transport?",
          answer:
            "Yes, we secure everything with straps and protect fragile items with blankets. Shower screens, porcelain and cabinet doors are handled with extra care. Everything is insured — if something is damaged we replace it.",
        },
        {
          question: "How quickly can you deliver from K-Rauta?",
          answer:
            "We often offer delivery at short notice — often the same day — within a few hours. Book online and see available times instantly.",
        },
        {
          question: "Is K-Rauta delivery available on weekends?",
          answer:
            "Yes, we offer delivery on weekends and some evenings. Check that the K-Rauta store is open at your chosen time — we can't pick up if the store is closed.",
        },
        {
          question: "Can you pick up from K-Rauta and another store in the same trip?",
          answer:
            "Yes, we can pick up from several stores during the same run if they're within a reasonable distance. Describe all pickup points when you book — for example K-Rauta and Bauhaus in the same trip.",
        },
        {
          question: "Do I need to be at the K-Rauta store?",
          answer:
            "No, you don't need to be at K-Rauta. Make sure the purchase is paid for and ready to collect — we handle the rest. You just need to be home to receive the delivery.",
        },
        {
          question: "In which areas do you offer K-Rauta delivery?",
          answer:
            "We offer K-Rauta delivery across the entire Stockholm region: Botkyrka, Danderyd, Ekerö, Haninge, Huddinge, Järfälla, Lidingö, Nacka, Norrtälje, Nykvarn, Nynäshamn, Salem, Sigtuna, Sollentuna, Solna, Stockholm, Sundbyberg, Södertälje, Tyresö, Täby, Upplands Väsby, Upplands-Bro, Vallentuna, Vaxholm, Värmdö and Österåker. We're also in the Gothenburg region: Ale, Alingsås, Göteborg, Härryda, Kungälv, Kungsbacka, Lerum, Lilla Edet, Mölndal, Partille, Stenungsund, Tjörn and Öckerö.",
        },
        {
          question: "Why choose Ungdomskraft for K-Rauta delivery?",
          answer:
            "We always include a van, carrying help and placement in the right spot. You see the price instantly when booking, we can often deliver the same day and everything is insured.",
        },
      ],
      priceHint: PACKAGE_PRICE_HINT_EN.bud,
      pricingRows: getPackagePricingRows("bud", "en"),
      dynamicHeadings: {
        threeSteps: "K-Rauta delivery in 3 easy steps",
        step2Text:
          "Our crew pick up your purchase at K-Rauta, load and secure everything in the van. Then we drive home to you.",
        step3Title: "All in place",
        step3Desc:
          "Your K-Rauta purchase is at home — carried in and in place. Done.",
        ctaHeading: "Book K-Rauta delivery today",
        ctaText:
          "Enter the K-Rauta store address and your home address, describe what you bought and choose a time. You see the price instantly.",
      },
    },
  },

  {
    // Hemleverans Byggmax — config för /hemleverans-byggmax
    // Vinkel: Hemleverans av Byggmax-köp (virke, trall, golv, isolering, tak, fasad)
    // Byggmax = lågpris-byggvaruhus. Stark på virke, trall, golv, tak, fasad. Inga kök/badrum.
    slug: "hemleverans-byggmax",
    serviceCategory: "hemleverans",

    meta: {
      title:
        "Hemleverans Byggmax – Vi Hämtar Ditt Köp Och Kör Hem | Ungdomskraft",
      description:
        "Köpt virke, trall eller golv på Byggmax som inte får plats i bilen? Vi hämtar i butiken och levererar hem med inbärning. Boka hemleverans Byggmax idag.",
      h1: "Hemleverans Byggmax",
    },

    hero: {
      heading: "Vi hämtar ditt köp på Byggmax",
      intro:
        "Handlat på Byggmax och allt är för långt, tungt eller skrymmande för bilen? Virke, trallbrädor, gipsskivor, isolering och golvpaket – Byggmax lågpriser gör det lätt att handla stort, men transporten hem blir en utmaning. Vi löser det. Ungdomskraft hämtar ditt köp direkt på Byggmax, lastar skåpbilen och levererar hem till dig – med inbärning och avlastning på plats. Vi kör från alla Byggmax-butiker i Stockholm och Göteborg.",
      heroImage: "/hero-hemleverans.png",
    },

    featureCards: {
      sinceText: "Byggmax-leverans sedan 2014",
      storyText:
        "Sedan 2014 har Ungdomskraft hjälpt tusentals kunder att få hem sina Byggmax-köp – från långa virkespaket och trallbrädor till tunga golvpallar och isolering. Våra ungdomar hämtar i butiken, lastar skåpbilen och bär in hemma hos dig.",
      bookYourText: "Boka din Byggmax-leverans",
      bookDescText:
        "Ange Byggmax-butikens adress och din hemadress, beskriv vad som ska hämtas och välj en tid. Du ser priset direkt.",
      vehicleTitleText: "Skåpbilar perfekta för Byggmax-leveranser",
      vehicleDescText:
        "Vi kommer med rätt storlek på skåpbil – oavsett om du hämtar ett par golvpaket eller virke till hela altanbygget. Du slipper hyra släp, köra själv eller göra flera turer.",
    },

    itemsSection: {
      heading: "Byggmax-produkter vi hämtar och levererar hem",
      items: [
        {
          name: "Virke & reglar",
          description:
            "Konstruktionsvirke, reglar, stolpar, planhyvlat och impregnerat virke i alla längder",
          image: "/box.webp",
        },
        {
          name: "Trall & altan",
          description:
            "Trallbrädor, altanbrädor, komposittrall, räcken, stolpar och altantillbehör",
          image: "/patio-furniture.webp",
        },
        {
          name: "Golv",
          description:
            "Laminatgolv, trägolv, vinylgolv och parkettgolv i tunga förpackningar",
          image: "/wardrobe.webp",
        },
        {
          name: "Isolering & skivor",
          description:
            "Mineralull, cellplast, gipsskivor, OSB-skivor och plywood i skrymmande format",
          image: "/office-desk.webp",
        },
        {
          name: "Tak & fasad",
          description:
            "Takpannor, takplåt, fasadpanel, vindskivor och hängrännor",
          image: "/air-conditioner.webp",
        },
        {
          name: "Färg & tillbehör",
          description:
            "Färghinkar, grundfärg, lasyr, spackel, lim och förbrukningsmaterial i storpack",
          image: "/microwave.webp",
        },
      ],
    },

    seoContent: {
      mainHeading:
        "Hemleverans från Byggmax – vi hämtar, kör och bär in åt dig",
      mainText:
        "Byggmax är Sveriges största lågpris-byggvaruhus med fokus på basbyggmaterial till marknadens bästa priser. Butikerna i Stockholm och Göteborg erbjuder virke, trall, golv, isolering, gipsskivor, tak, fasad, färg och allt annat som behövs för att bygga, renovera och underhålla. Byggmax affärsmodell bygger på effektivitet och låga priser – inga dyra utställningar, inga köksmiljöer, ingen onödig overhead. Det gör att du som kund får marknadens lägsta priser på basvaror som virke, trall, golv och isolering. Problemet med Byggmax-köp är att nästan allt är för stort, tungt eller långt för en personbil. Virke i 4–5 meters längder sticker ut, trallbrädor är 3–5 meter långa, gipsskivor mäter 2,4 meter, isoleringsbalar tar enormt med plats och en pall golvpaket väger 300–500 kg. Ett enda altanbygge kan innebära en ton material. Byggmax-butikerna ligger dessutom oftast i industriområden utanför stan, så flera turer äter upp hela dagen. Att hyra släp löser en del, men du måste fortfarande lasta och lossa själv – och släpet måste lämnas tillbaka. Byggmax erbjuder hemleverans på utvalda produkter, men väntetiderna varierar, utbudet är begränsat och inbärning sällan inkluderad. Ungdomskraft erbjuder ett snabbt och komplett alternativ. Vi hämtar ditt Byggmax-köp direkt i butiken, lastar allt i skåpbilen och kör hem till dig. Inbärning till rätt plats ingår alltid – vi bär in till bygget, garaget, trädgården, altanprojektet eller förrådet. Du ser priset direkt vid bokning, inga dolda avgifter tillkommer. Vi kör från alla Byggmax-butiker i Stockholm och Göteborg. Våra ungdomar är vana vid att hantera långt virke, tunga golvpallar och skrymmande isolering – vi lastar effektivt och säkrar med spännband så att inget förskjuts eller skadas.",
      preparationHeading: "Förbered inför din Byggmax-leverans",
      preparationText:
        "Se till att köpet är betalt och klart för upphämtning – Byggmax har ofta självbetjäning, så dubbelkolla att ordern är registrerad. Ha din hemadress och eventuell portkod redo. Om du handlat stora mängder virke eller tunga golvpallar – ange gärna ungefärlig totalvikt vid bokning så att vi tar rätt skåpbil. Kontrollera att skåpbilen kan parkera nära avlastnings­platsen hemma hos dig. Om virke eller trall ska till baksidan av huset – se till att grind eller passage är öppen. Ange vid bokning om materialet ska till en specifik plats, till exempel garaget, trädgården eller en pågående bygge.",
      additionalSections: [
        {
          heading: "Hemleverans av virke och trall från Byggmax",
          content:
            "Byggmax är marknadsledande på virke och trall till låga priser – konstruktionsvirke, reglar, stolpar, planhyvlat, impregnerat, trallbrädor, komposittrall och altanbrädor. Längderna varierar från 2,4 till 5,4 meter, och ett enda altanprojekt kan innebära 50–100 brädor som tillsammans väger 500–1 000 kg. Virke sticker ut ur personbilar, det kräver säkring och det är tungt att lyfta. Med Ungdomskrafts hemleverans hämtar vi hela din virkes- eller trallbeställning på Byggmax, lastar allt säkert i skåpbilen och kör hem till dig. Vi bär eller ställer materialet vid altanbygget, i garaget eller på tomten – du slipper hyra släp, köra själv eller be grannen om hjälp. Perfekt för altanbyggare, staketbyggare och hemmafixare som vill fokusera på bygget istället för logistiken.",
        },
        {
          heading: "Hemleverans av golv från Byggmax",
          content:
            "Byggmax har ett brett sortiment av golv till låga priser – laminat, trägolv, vinyl och parkett. Golvpaket väger 15–25 kg styck och för ett normalstort rum behövs ofta 8–15 paket, det vill säga 120–375 kg. För en hel lägenhet eller ett helt hus kan det bli 40–60 paket och uppåt 1 000 kg. Dessa tunga, staplade förpackningar ryms inte i en personbil – och att göra flera turer med 4–5 paket per gång tar hela dagen. Med vår hemleverans hämtar vi alla golvpaket på Byggmax, lastar dem stabilt i skåpbilen och kör hem till dig. Vi bär in till rätt rum så att du kan börja lägga golvet direkt. Inga tunga lyft, inga bilresor, ingen värkande rygg.",
        },
        {
          heading: "Hemleverans av isolering, gips och skivor från Byggmax",
          content:
            "Byggmax erbjuder isolering (mineralull, cellplast), gipsskivor, OSB-skivor och plywood till marknadens lägsta priser. Dessa produkter är extremt skrymmande – isoleringsbalar tar enormt med plats trots relativt låg vikt, gipsskivor är 2,4 meter långa och väger 25–30 kg styck, och plywoodskivor är tunga och otympliga. Ett tak- eller väggprojekt kräver ofta 20–40 skivor och 10–20 balar isolering. Med Ungdomskraft hämtar vi allt på Byggmax, lastar i skåpbilen och kör hem. Vi bär in till bygget, vinden, garaget eller förrådet. Perfekt för dig som isolerar vinden, bygger om garaget eller renoverar ett rum – och inte har plats för 40 gipsskivor i bilen.",
        },
        {
          heading: "Så fungerar Byggmax-leverans med Ungdomskraft",
          listItems: [
            {
              label: "Boka online",
              text: "Ange Byggmax-butikens adress och din hemadress. Beskriv vad du handlat och välj en tid. Du ser priset direkt.",
            },
            {
              label: "Vi hämtar på Byggmax",
              text: "Vårt team kommer med skåpbil. Vi hämtar ditt köp i butiken, lastar och säkrar allt med spännband.",
            },
            {
              label: "Vi levererar hem",
              text: "Vi kör hem till dig, bär in till rätt plats – altanbygget, garaget, trädgården eller förrådet.",
            },
            {
              label: "Klart",
              text: "Ditt Byggmax-köp står hemma hos dig – inburet och på plats. Utan att du lyfte ett finger.",
            },
          ],
          afterContent:
            "Hela processen tar normalt 1–3 timmar beroende på avstånd och mängd. Du kan följa statusen i realtid.",
        },
        {
          heading: "Vi kör från alla Byggmax-butiker",
          content:
            "Vi hämtar från alla Byggmax-butiker i Stockholm och Göteborg. I Stockholm: Kungens Kurva, Barkarby, Bromma, Haninge, Täby, Sollentuna, Nacka och alla övriga. I Göteborg: Sisjön, Bäckebol, Kungälv och alla övriga butiker. Ange bara butikens adress vid bokning så sköter vi resten. Ska du handla på Byggmax och en annan butik samma dag? Vi kan hämta från flera ställen i samma tur – till exempel Byggmax och Jula eller Byggmax och Bauhaus i samma körning.",
        },
        {
          heading: "Varför boka Byggmax-leverans med Ungdomskraft?",
          content:
            "Byggmax erbjuder hemleverans på utvalda produkter, men utbudet är begränsat, väntetiderna varierar och inbärning till rätt plats sällan ingår. Med Ungdomskraft får du snabb leverans av allt du handlat – med inbärning till altanbygget, garaget, trädgården eller källaren. Dessutom gör du dubbel nytta varje gång du bokar: du får hem ditt material smidigt och du bidrar till att unga människor kommer in på arbetsmarknaden.\n\nDet som gör Ungdomskraft unikt är vår samhällsnytta. Vi anställer unga människor – ofta i åldern 18–25 – som annars har svårt att få sitt första jobb. Många saknar arbetslivserfarenhet, nätverk eller kontakter i branschen. Genom att arbeta hos oss får de riktig, praktisk erfarenhet som arbetsgivare värdesätter: kundkontakt ansikte mot ansikte, logistik och planering, fysiskt arbete under tidspress, samarbete i team och eget ansvar för att ett uppdrag slutförs ordentligt. De lär sig att kommunicera professionellt med kunder, att lösa problem på plats och att leverera kvalitet utan genvägar.\n\nVarje Byggmax-leverans bemannas av våra ungdomar. De hämtar ditt köp i butiken, lastar skåpbilen metodiskt – virke säkrat med spännband, golvpaket staplade stabilt, isolering packad effektivt – kör säkert till din adress och bär in till exakt den plats du önskar. De behandlar ditt material med omsorg oavsett om det är tio trallbrädor eller femtio golvpaket. Den energi och det engagemang de visar märks i varje uppdrag.\n\nVi har funnits sedan 2014 och har genomfört tiotusentals transporter och leveranser i Stockholm och Göteborg. Under åren har hundratals ungdomar fått sin första arbetslivserfarenhet genom Ungdomskraft – och många har gått vidare till fasta tjänster inom logistik, bygg, kundservice och andra branscher. Vår modell fungerar för att den bygger på riktigt arbete med riktiga kunder, inte praktik utan ansvar.\n\nHållbarhet genomsyrar allt vi gör. Vi planerar körningar effektivt för att minimera tomkörning. Vi samlastar när det är möjligt. Vi återvinner förpackningsmaterial och ser till att wellpapp, plast och pallvirke sorteras korrekt efter leverans. För företag och hantverkare som beställer Byggmax-leveranser erbjuder vi dokumentation som kan användas i CSR-rapportering – varje bokning stödjer ungdomsarbete och hållbar logistik.\n\nAllt är helförsäkrat. Vi har egna skåpbilar i flera storlekar – från liten skåpbil för golvpaket och färg till stor skåpbil som rymmer virke, trall och gipsskivor. Du ser alltid priset innan du bokar. Inga dolda avgifter, inga överraskningar. Och om något mot förmodan skadas under transporten ersätter vi det omedelbart.\n\nNär du bokar Byggmax-leverans med Ungdomskraft väljer du mer än en transporttjänst. Du väljer att ge en ung människa möjligheten att lära, växa och bygga sin framtid. Det är smidig byggleverans med mening.\n\nHemleverans från Byggmax ger inte rätt till RUT-avdrag, men flera av våra andra tjänster gör det – exempelvis flytt, bärhjälp och möbelmontering med 50 % avdrag på arbetskostnaden. Kombinera gärna tjänster i en och samma bokning.",
        },
      ],
    },

    faq: [
      {
        question: "Vad kostar hemleverans från Byggmax?",
        answer:
          "Priset beror på mängden och avståndet. En mindre Byggmax-leverans med golvpaket eller färg kostar från 595 kr. Större leveranser med virke, trall eller isolering kostar från 995–2 495 kr. Du ser det exakta priset när du bokar online.",
      },
      {
        question: "Kan jag få RUT-avdrag på hemleverans från Byggmax?",
        answer:
          "Hemleverans från Byggmax ger i sig inte rätt till RUT-avdrag. RUT gäller hushållsnära tjänster som utförs i din bostad, exempelvis flytt, bärhjälp och möbelmontering. Ungdomskraft erbjuder flera RUT-berättigade tjänster med 50 % avdrag på arbetskostnaden – kombinera enkelt i en bokning via vår hemsida.",
      },
      {
        question: "Ingår skåpbil i Byggmax-leveransen?",
        answer:
          "Ja, skåpbil ingår alltid. Vi väljer rätt storlek utifrån ditt köp – du behöver inte hyra bil eller släp.",
      },
      {
        question: "Ingår inbärning i priset för Byggmax-leveransen?",
        answer:
          "Ja, inbärning ingår alltid. Vi bär ditt köp till rätt plats – altanbygget, garaget, trädgården eller förrådet – oavsett avstånd från bilen. Inga extra avgifter.",
      },
      {
        question: "Vilka Byggmax-butiker hämtar ni från?",
        answer:
          "Vi hämtar från alla Byggmax-butiker i Stockholm och Göteborg: Kungens Kurva, Barkarby, Bromma, Haninge, Täby, Sollentuna, Nacka, Sisjön, Bäckebol, Kungälv och alla övriga. Ange bara butikens adress vid bokning.",
      },
      {
        question: "Kan ni leverera långt virke och trall från Byggmax?",
        answer:
          "Ja, vi hanterar virke och trall i alla längder dagligen – från 2,4 till 5,4 meter. Vi lastar säkert i skåpbilen och bär till altanbygget, garaget eller tomten.",
      },
      {
        question: "Kan ni leverera tunga golvpaket från Byggmax?",
        answer:
          "Ja, vi transporterar golvpaket regelbundet. Vi hämtar alla paket på Byggmax, lastar stabilt och bär in till rätt rum – oavsett antal paket och våning.",
      },
      {
        question: "Kan ni leverera gipsskivor och isolering från Byggmax?",
        answer:
          "Ja, vi hanterar gipsskivor, OSB-skivor, plywood och isolering. Vi lastar skivorna säkert i skåpbilen och bär in till bygget, vinden eller garaget.",
      },
      {
        question: "Kan ni bära tungt Byggmax-material i trappor?",
        answer:
          "Ja, vi bär till vilken våning som helst – med eller utan hiss. Våra ungdomar är vana vid tunga lyft och trånga trapphus. Vi bär golvpaket, gipsskivor och byggsäckar uppför trappor dagligen.",
      },
      {
        question: "Skyddar ni materialet under Byggmax-transporten?",
        answer:
          "Ja, vi säkrar allt med spännband i skåpbilen. Virke och skivor lastas stabilt så att inget förskjuts. Allt är försäkrat – om något skadas ersätter vi det.",
      },
      {
        question: "Hur snabbt kan ni leverera från Byggmax?",
        answer:
          "Vi erbjuder ofta hemleverans med kort varsel – ofta redan samma dag – inom några timmar. Boka online och se tillgängliga tider direkt.",
      },
      {
        question: "Erbjuder ni Byggmax-leverans på helger?",
        answer:
          "Ja, vi erbjuder hemleverans på helger och vissa kvällar. Kontrollera att Byggmax-butiken har öppet den tid du bokar – vi kan inte hämta om butiken är stängd.",
      },
      {
        question: "Kan ni hämta från Byggmax och en annan butik i samma tur?",
        answer:
          "Ja, vi kan hämta från flera butiker under samma körning om de ligger i rimligt avstånd. Beskriv alla hämtpunkter vid bokning – till exempel Byggmax och Jula i samma tur.",
      },
      {
        question: "Behöver jag vara med på Byggmax vid upphämtningen?",
        answer:
          "Nej, du behöver inte vara med på Byggmax. Se till att köpet är betalt och redo att hämtas – vi sköter resten. Du behöver bara vara hemma för att ta emot leveransen.",
      },
      {
        question:
          "I vilka kommuner erbjuder ni hemleverans från Byggmax?",
        answer:
          "Vi erbjuder Byggmax-leverans i hela Stockholmsområdet: Botkyrka, Danderyd, Ekerö, Haninge, Huddinge, Järfälla, Lidingö, Nacka, Norrtälje, Nykvarn, Nynäshamn, Salem, Sigtuna, Sollentuna, Solna, Stockholm, Sundbyberg, Södertälje, Tyresö, Täby, Upplands Väsby, Upplands-Bro, Vallentuna, Vaxholm, Värmdö och Österåker. Vi finns även i Göteborgsområdet: Ale, Alingsås, Göteborg, Härryda, Kungälv, Kungsbacka, Lerum, Lilla Edet, Mölndal, Partille, Stenungsund, Tjörn och Öckerö.",
      },
      {
        question: "Varför välja Ungdomskraft för Byggmax-leverans?",
        answer:
          "Hos oss ingår alltid skåpbil, bärhjälp och inbärning till rätt plats. Du ser priset direkt vid bokning, vi kan ofta köra samma dag och allt är försäkrat.",
      },
    ],

    priceHint: PACKAGE_PRICE_HINT_SV.bud,

    pricingRows: getPackagePricingRows("bud", "sv"),

    dynamicHeadings: {
      threeSteps: "Byggmax-leverans i 3 enkla steg",
      step2Text:
        "Våra ungdomar hämtar ditt köp på Byggmax, lastar och säkrar allt i skåpbilen. Sedan kör vi hem till dig.",
      step3Title: "Allt på plats",
      step3Desc:
        "Ditt Byggmax-köp står hemma hos dig – inburet och på plats. Klart.",
      ctaHeading: "Boka Byggmax-leverans idag",
      ctaText:
        "Ange Byggmax-butikens adress och din hemadress, beskriv vad du handlat och välj en tid. Du ser priset direkt.",
    },

    en: {
      slug: "byggmax-delivery",
      meta: {
        title:
          "Byggmax Delivery – We Pick Up Your Purchase And Deliver Home | Ungdomskraft",
        description:
          "Bought timber, decking or flooring at Byggmax that won't fit in your car? We pick up at the store and deliver home with carrying. Book today!",
        h1: "Byggmax delivery",
      },
      hero: {
        heading: "We pick up your purchase at Byggmax",
        intro:
          "Shopped at Byggmax and everything is too long, heavy or bulky for the car? Timber, decking boards, plasterboard, insulation and flooring packs — Byggmax's low prices make it easy to shop big, but getting it home becomes a challenge. We solve it. Ungdomskraft picks up your purchase directly at Byggmax, loads the van and delivers to your home — with carrying and placement included. We collect from all Byggmax stores in Stockholm and Gothenburg.",
      },
      featureCards: {
        sinceText: "Byggmax delivery since 2014",
        bookYourText: "Book your Byggmax delivery",
        bookDescText:
          "Enter the Byggmax store address and your home address, describe what you bought and choose a time. You see the price instantly.",
        vehicleTitleText: "Vans perfect for Byggmax deliveries",
        vehicleDescText:
          "We arrive with the right size van — whether you're picking up a few flooring packs or timber for an entire deck build. No need to rent a trailer, drive yourself or make multiple trips.",
      },
      itemsSection: {
        heading: "Byggmax products we pick up and deliver home",
        items: [
          {
            name: "Timber & studs",
            description:
              "Structural timber, studs, posts, planed and pressure-treated timber in all lengths",
            image: "/box.webp",
          },
          {
            name: "Decking & patio",
            description:
              "Deck boards, composite decking, railings, posts and deck accessories",
            image: "/patio-furniture.webp",
          },
          {
            name: "Flooring",
            description:
              "Laminate, hardwood, vinyl and parquet flooring in heavy packages",
            image: "/wardrobe.webp",
          },
          {
            name: "Insulation & boards",
            description:
              "Mineral wool, polystyrene, plasterboard, OSB boards and plywood in bulky formats",
            image: "/office-desk.webp",
          },
          {
            name: "Roofing & cladding",
            description:
              "Roof tiles, metal roofing, facade panels, barge boards and guttering",
            image: "/air-conditioner.webp",
          },
          {
            name: "Paint & supplies",
            description:
              "Paint tins, primers, wood stain, filler, adhesive and consumables in bulk",
            image: "/microwave.webp",
          },
        ],
      },
      seoContent: {
        mainHeading:
          "Delivery from Byggmax — we pick up, drive and carry inside for you",
        mainText:
          "Byggmax is Sweden's largest budget building supply store, focused on core building materials at the market's best prices. Their stores in Stockholm and Gothenburg offer timber, decking, flooring, insulation, plasterboard, roofing, cladding, paint and everything else needed for building, renovating and maintaining. Byggmax's business model is built on efficiency and low prices — no expensive displays, no kitchen showrooms, no unnecessary overhead. This means you as a customer get the market's lowest prices on staples like timber, decking, flooring and insulation. The problem with Byggmax purchases is that almost everything is too large, heavy or long for a regular car. Timber in 4–5 metre lengths sticks out, deck boards are 3–5 metres long, plasterboard measures 2.4 metres, insulation bales take up enormous space and a pallet of flooring weighs 300–500 kg. A single deck project can mean a tonne of materials. Byggmax stores are also usually located in industrial areas outside the city centre, so multiple trips eat up the whole day. Renting a trailer solves part of it, but you still have to load and unload yourself — and the trailer needs to be returned. Byggmax offers delivery on selected products, but waiting times vary, the range is limited and carrying is rarely included. Ungdomskraft offers a fast and complete alternative. We pick up your Byggmax purchase directly at the store, load everything into the van and drive to your home. Carrying to the right spot is always included — we carry to the deck build, garage, garden, or storage room. You see the price instantly when booking, no hidden fees. We collect from all Byggmax stores in Stockholm and Gothenburg. Our crew are used to handling long timber, heavy flooring pallets and bulky insulation — we load efficiently and secure with straps so nothing shifts or gets damaged.",
        preparationHeading: "Prepare for your Byggmax delivery",
        preparationText:
          "Make sure the purchase is paid for and ready for collection — Byggmax often has self-service, so double-check that the order is registered. Have your home address and any door codes ready. If you've bought large quantities of timber or heavy flooring pallets, specify the approximate total weight when booking so we bring the right van. Check that the van can park near the drop-off point at your home. If timber or decking is going to the back of the house, make sure the gate or passage is open. When booking, specify if materials should go to a specific location, such as the garage, garden or an ongoing build.",
        additionalSections: [
          {
            heading: "Timber and decking delivery from Byggmax",
            content:
              "Byggmax is the market leader in timber and decking at low prices — structural timber, studs, posts, planed, pressure-treated, deck boards, composite decking and patio boards. Lengths range from 2.4 to 5.4 metres, and a single deck project can mean 50–100 boards weighing 500–1,000 kg together. Timber sticks out of cars, requires securing and is heavy to lift. With Ungdomskraft's delivery we collect your entire timber or decking order from Byggmax, load everything safely in the van and drive to your home. We carry or place the materials at the deck build, in the garage or on the plot — no need to rent a trailer, drive yourself or ask the neighbour for help. Perfect for deck builders, fence builders and DIY enthusiasts who want to focus on building rather than logistics.",
          },
          {
            heading: "Flooring delivery from Byggmax",
            content:
              "Byggmax has a wide range of flooring at low prices — laminate, hardwood, vinyl and parquet. Flooring packs weigh 15–25 kg each and for an average room you often need 8–15 packs, meaning 120–375 kg. For an entire flat or house it can be 40–60 packs and upwards of 1,000 kg. These heavy, stacked packages don't fit in a car — and making multiple trips with 4–5 packs at a time takes all day. With our delivery we collect all flooring packs at Byggmax, load them securely in the van and drive to your home. We carry inside to the right room so you can start laying the floor straight away. No heavy lifting, no car trips, no aching back.",
          },
          {
            heading: "Insulation, plasterboard and board delivery from Byggmax",
            content:
              "Byggmax offers insulation (mineral wool, polystyrene), plasterboard, OSB boards and plywood at the market's lowest prices. These products are extremely bulky — insulation bales take up enormous space despite relatively low weight, plasterboard is 2.4 metres long and weighs 25–30 kg each, and plywood boards are heavy and unwieldy. A roof or wall project often requires 20–40 boards and 10–20 bales of insulation. With Ungdomskraft we collect everything at Byggmax, load into the van and drive home. We carry to the build site, attic, garage or storage room. Perfect for anyone insulating the loft, converting the garage or renovating a room — and who doesn't have space for 40 plasterboards in the car.",
          },
          {
            heading: "How Byggmax delivery works with Ungdomskraft",
            listItems: [
              {
                label: "Book online",
                text: "Enter the Byggmax store address and your home address. Describe what you bought and choose a time. You see the price instantly.",
              },
              {
                label: "We pick up at Byggmax",
                text: "Our team arrive with a van. We collect your purchase at the store, load and secure everything with straps.",
              },
              {
                label: "We deliver home",
                text: "We drive to your home and carry to the right spot — the deck build, garage, garden or storage room.",
              },
              {
                label: "Done",
                text: "Your Byggmax purchase is at home — carried in and in place. Without lifting a finger.",
              },
            ],
            afterContent:
              "The whole process typically takes 1–3 hours depending on distance and amount. You can follow the status in real time.",
          },
          {
            heading: "We collect from all Byggmax stores",
            content:
              "We pick up from all Byggmax stores in Stockholm and Gothenburg. In Stockholm: Kungens Kurva, Barkarby, Bromma, Haninge, Täby, Sollentuna, Nacka and all others. In Gothenburg: Sisjön, Bäckebol, Kungälv and all other locations. Just enter the store address when booking and we handle the rest. Shopping at Byggmax and another store the same day? We can pick up from multiple locations in the same trip — for example Byggmax and Jula or Byggmax and Bauhaus in the same run.",
          },
          {
            heading: "Why book Byggmax delivery with Ungdomskraft?",
            content:
              "Byggmax offers delivery on selected products, but the range is limited, waiting times vary and carrying to the right spot is rarely included. With Ungdomskraft you get fast delivery of everything you've bought — with carrying to the deck build, garage, garden or basement. Plus you make a double impact every time you book: you get your materials home smoothly and you help young people enter the workforce.\n\nWhat makes Ungdomskraft unique is our social mission. We employ young people — often aged 18–25 — who otherwise struggle to get their first job. Many lack work experience, networks or industry contacts. By working with us they gain real, practical experience that employers value: face-to-face customer service, logistics and planning, physical work under time pressure, teamwork and personal responsibility for completing a job properly. They learn to communicate professionally with customers, solve problems on site and deliver quality without shortcuts.\n\nEvery Byggmax delivery is staffed by our young workers. They collect your purchase at the store, load the van methodically — timber secured with straps, flooring packs stacked securely, insulation packed efficiently — drive safely to your address and carry to the exact spot you want. They handle your materials with care regardless of whether it's ten deck boards or fifty flooring packs. The energy and commitment they show is evident in every job.\n\nWe've been around since 2014 and have completed tens of thousands of transports and deliveries across Stockholm and Gothenburg. Over the years, hundreds of young people have gained their first work experience through Ungdomskraft — and many have gone on to permanent positions in logistics, construction, customer service and other industries. Our model works because it's built on real work with real customers, not internships without responsibility.\n\nSustainability runs through everything we do. We plan routes efficiently to minimise empty runs. We consolidate loads when possible. We recycle packaging materials and ensure cardboard, plastic and pallet timber are sorted correctly after delivery. For companies and tradespeople ordering Byggmax deliveries we offer documentation for CSR reporting — every booking supports youth employment and sustainable logistics.\n\nEverything is fully insured. We have our own vans in multiple sizes — from small vans for flooring packs and paint to large vans that fit timber, decking and plasterboard. You always see the price before you book. No hidden fees, no surprises. And if anything is unexpectedly damaged during transport we replace it immediately.\n\nWhen you book Byggmax delivery with Ungdomskraft, you choose more than a transport service. You choose to give a young person the opportunity to learn, grow and build their future. It's smooth building delivery with purpose.\n\nByggmax delivery does not qualify for the Swedish RUT tax deduction, but several of our other services do – including moving help, carrying help and furniture assembly with a 50% deduction on the labour cost. Feel free to combine services in a single booking.",
          },
        ],
      },
      faq: [
        {
          question: "How much does Byggmax delivery cost?",
          answer:
            "The price depends on the amount and distance. A smaller Byggmax delivery with flooring packs or paint starts at 595 SEK. Larger deliveries with timber, decking or insulation cost 749–2 495 SEK. You see the exact price when you book online.",
        },
        {
          question: "Can I get a RUT tax deduction on Byggmax delivery?",
          answer:
            "Byggmax delivery does not in itself qualify for the Swedish RUT tax deduction. RUT applies to household services carried out in your home, such as moving help, carrying help and furniture assembly. Ungdomskraft offers several RUT-eligible services with a 50% deduction on the labour cost – easily combine them in one booking via our website.",
        },
        {
          question: "Is a van included in the Byggmax delivery?",
          answer:
            "Yes, a van is always included. We choose the right size based on your purchase — you don't need to rent a vehicle or trailer.",
        },
        {
          question: "Is carrying included in the Byggmax delivery price?",
          answer:
            "Yes, carrying is always included. We carry your purchase to the right spot — the deck build, garage, garden or storage room — regardless of distance from the van. No extra charges.",
        },
        {
          question: "Which Byggmax stores do you pick up from?",
          answer:
            "We pick up from all Byggmax stores in Stockholm and Gothenburg: Kungens Kurva, Barkarby, Bromma, Haninge, Täby, Sollentuna, Nacka, Sisjön, Bäckebol, Kungälv and all others. Just enter the store address when booking.",
        },
        {
          question: "Can you deliver long timber and decking from Byggmax?",
          answer:
            "Yes, we handle timber and decking in all lengths daily — from 2.4 to 5.4 metres. We load safely in the van and carry to the deck build, garage or plot.",
        },
        {
          question: "Can you deliver heavy flooring packs from Byggmax?",
          answer:
            "Yes, we transport flooring packs regularly. We collect all packs at Byggmax, load securely and carry to the right room — regardless of number of packs and floor.",
        },
        {
          question: "Can you deliver plasterboard and insulation from Byggmax?",
          answer:
            "Yes, we handle plasterboard, OSB boards, plywood and insulation. We load the boards safely in the van and carry to the build site, attic or garage.",
        },
        {
          question: "Can you carry heavy Byggmax materials up stairs?",
          answer:
            "Yes, we carry to any floor — with or without a lift. Our crew are used to heavy materials and narrow stairwells. We carry flooring packs, plasterboard and building sacks up stairs daily.",
        },
        {
          question: "Do you protect materials during Byggmax transport?",
          answer:
            "Yes, we secure everything with straps in the van. Timber and boards are loaded securely so nothing shifts. Everything is insured — if something is damaged we replace it.",
        },
        {
          question: "How quickly can you deliver from Byggmax?",
          answer:
            "We often offer delivery at short notice — often the same day — within a few hours. Book online and see available times instantly.",
        },
        {
          question: "Is Byggmax delivery available on weekends?",
          answer:
            "Yes, we offer delivery on weekends and some evenings. Check that the Byggmax store is open at your chosen time — we can't pick up if the store is closed.",
        },
        {
          question: "Can you pick up from Byggmax and another store in the same trip?",
          answer:
            "Yes, we can pick up from several stores during the same run if they're within a reasonable distance. Describe all pickup points when you book — for example Byggmax and Jula in the same trip.",
        },
        {
          question: "Do I need to be at the Byggmax store?",
          answer:
            "No, you don't need to be at Byggmax. Make sure the purchase is paid for and ready to collect — we handle the rest. You just need to be home to receive the delivery.",
        },
        {
          question: "In which areas do you offer Byggmax delivery?",
          answer:
            "We offer Byggmax delivery across the entire Stockholm region: Botkyrka, Danderyd, Ekerö, Haninge, Huddinge, Järfälla, Lidingö, Nacka, Norrtälje, Nykvarn, Nynäshamn, Salem, Sigtuna, Sollentuna, Solna, Stockholm, Sundbyberg, Södertälje, Tyresö, Täby, Upplands Väsby, Upplands-Bro, Vallentuna, Vaxholm, Värmdö and Österåker. We're also in the Gothenburg region: Ale, Alingsås, Göteborg, Härryda, Kungälv, Kungsbacka, Lerum, Lilla Edet, Mölndal, Partille, Stenungsund, Tjörn and Öckerö.",
        },
        {
          question: "Why choose Ungdomskraft for Byggmax delivery?",
          answer:
            "We always include a van, carrying help and placement in the right spot. You see the price instantly when booking, we can often deliver the same day and everything is insured.",
        },
      ],
      priceHint: PACKAGE_PRICE_HINT_EN.bud,
      pricingRows: getPackagePricingRows("bud", "en"),
      dynamicHeadings: {
        threeSteps: "Byggmax delivery in 3 easy steps",
        step2Text:
          "Our crew pick up your purchase at Byggmax, load and secure everything in the van. Then we drive home to you.",
        step3Title: "All in place",
        step3Desc:
          "Your Byggmax purchase is at home — carried in and in place. Done.",
        ctaHeading: "Book Byggmax delivery today",
        ctaText:
          "Enter the Byggmax store address and your home address, describe what you bought and choose a time. You see the price instantly.",
      },
    },
  },

  {
    // Hemleverans Biltema — config för /hemleverans-biltema
    // Vinkel: Hemleverans av Biltema-köp (bil, verktyg, fritid, hem, trädgård, bygg)
    // Biltema = lågpris-varuhus med rötter i biltillbehör, nu bredd till hem/trädgård/fritid/verktyg.
    slug: "hemleverans-biltema",
    serviceCategory: "hemleverans",

    meta: {
      title:
        "Hemleverans Biltema – Vi Hämtar Ditt Köp Och Kör Hem | Ungdomskraft",
      description:
        "Köpt verktyg, biltillbehör eller trädgårdsprodukter på Biltema som inte får plats i bilen? Vi hämtar i butiken och levererar hem med inbärning. Boka hemleverans Biltema idag.",
      h1: "Hemleverans Biltema",
    },

    hero: {
      heading: "Vi hämtar ditt köp på Biltema",
      intro:
        "Handlat på Biltema och vagnen blev tyngre än planerat? Takboxar, kompressorer, garagehyllor, trädgårdsmöbler och bildäck – Biltemas lågpriser gör det lätt att fylla vagnen, men svårt att få hem allt. Vi löser det. Ungdomskraft hämtar ditt köp direkt på Biltema, lastar skåpbilen och levererar hem till dig – med inbärning och uppställning på plats. Vi kör från alla Biltema-butiker i Stockholm och Göteborg.",
      heroImage: "/hero-hemleverans.png",
    },

    featureCards: {
      sinceText: "Biltema-leverans sedan 2014",
      storyText:
        "Sedan 2014 har Ungdomskraft hjälpt tusentals kunder att få hem sina Biltema-köp – från tunga verktygsmaskiner och bildäck till trädgårdsmöbler, garagehyllor och fritidsutrustning. Våra ungdomar hämtar i butiken, lastar skåpbilen och bär in hemma hos dig.",
      bookYourText: "Boka din Biltema-leverans",
      bookDescText:
        "Ange Biltema-butikens adress och din hemadress, beskriv vad som ska hämtas och välj en tid. Du ser priset direkt.",
      vehicleTitleText: "Skåpbilar perfekta för Biltema-leveranser",
      vehicleDescText:
        "Vi kommer med rätt storlek på skåpbil – oavsett om du hämtar en verktygslåda eller ett helt garagebygge. Du slipper hyra släp, köra själv eller göra flera turer.",
    },

    itemsSection: {
      heading: "Biltema-produkter vi hämtar och levererar hem",
      items: [
        {
          name: "Bil & motor",
          description:
            "Takboxar, dragkrokar, däck, fälgar, motorolja, bilbatterier och biltillbehör",
          image: "/microwave.webp",
        },
        {
          name: "Verktyg & maskiner",
          description:
            "Kompressorer, svetsaggregat, bänkslipar, verktygsvagnar och elverktyg",
          image: "/toolbox.webp",
        },
        {
          name: "Trädgård & utomhus",
          description:
            "Trädgårdsmöbler, grillar, studsmattor, pooler, parasoll och robotgräsklippare",
          image: "/patio-furniture.webp",
        },
        {
          name: "Förvaring & garage",
          description:
            "Garagehyllor, verktygsskåp, arbetsbänkar, förvaringslådor och organisationssystem",
          image: "/office-desk.webp",
        },
        {
          name: "Fritid & sport",
          description:
            "Cyklar, kajaker, paddleboards, campingutrustning och skotrar i stora förpackningar",
          image: "/road-bike.webp",
        },
        {
          name: "Hem & el",
          description:
            "Generatorer, strålkastare, kabelvindor, element och tyngre hemelektronik",
          image: "/tv.webp",
        },
      ],
    },

    seoContent: {
      mainHeading:
        "Hemleverans från Biltema – vi hämtar, kör och bär in åt dig",
      mainText:
        "Biltema är ett av Sveriges mest populära lågprisvaruhus med rötter i biltillbehör och en utveckling som gjort kedjan till en komplett varuhusupplevelse. Butikerna i Stockholm och Göteborg erbjuder allt från bildäck, motorolja och dragkrokar till verktyg, trädgårdsmöbler, fritidsutrustning, hemprodukter och leksaker. Sortimentet är enormt brett och priserna konsekvent låga – det gör Biltema till en favorit för alla som gillar att fixa, bygga och underhålla. Problemet är välkänt: du går till Biltema för att köpa en burk olja och en uppsättning nycklar, och kommer ut med en verktygsvagn, fyra vinterdäck, en kompressor, en garagehylla och kanske en studsmatta till barnen. Allt till fantastiska priser – men totalt omöjligt att få med sig hem i bilen. Takboxar, däck, kompressorer och garagehyllor är tunga och skrymmande. Studsmattor och pooler kommer i förpackningar som är 2 meter långa. Trädgårdsmöbler i kartong tar enormt med plats. Biltema-butikerna ligger dessutom ofta i handels­områden utanför stadskärnan, vilket gör att flera turer tar hela dagen. Biltema erbjuder inte egen hemleverans i traditionell mening – det är en självbetjänings­butik där du handlar och tar med dig varorna själv. Det fungerar bra för småsaker, men inte när köpet väger 200 kg och inte ryms i bagageluckan. Ungdomskraft erbjuder ett snabbt och smidigt alternativ. Vi hämtar ditt Biltema-köp direkt i butiken, lastar allt i skåpbilen och kör hem till dig. Inbärning till rätt plats ingår alltid – vi bär in till garaget, verkstaden, trädgården, förrådet eller vilken plats du vill. Du ser priset direkt vid bokning, inga dolda avgifter tillkommer. Vi kör från alla Biltema-butiker i Stockholm och Göteborg. Varje vecka hämtar vi hundratals leveranser från Biltema och liknande varuhus. Våra ungdomar är vana vid att hantera allt från tunga däckstaplar och kompressorer till skrymmande studsmattor och trädgårdsmöbler.",
      preparationHeading: "Förbered inför din Biltema-leverans",
      preparationText:
        "Se till att köpet är betalt och klart för upphämtning. Ha din hemadress och eventuell portkod redo. Om du handlat tungt eller skrymmande – lista gärna de största föremålen så att vi kan välja rätt skåpbilsstorlek. Kontrollera att skåpbilen kan parkera nära garageinfarten eller porten hemma hos dig. Om varorna ska till garaget, verkstaden eller trädgården – se till att passage och dörrar är öppna. Ange vid bokning om det finns speciella önskemål, till exempel att däck ska in i garaget eller att en studsmatta ska till trädgården.",
      additionalSections: [
        {
          heading: "Hemleverans av biltillbehör och däck från Biltema",
          content:
            "Biltema har sitt ursprung i biltillbehör och det märks – sortimentet av bildäck, fälgar, takboxar, dragkrokar, motorolja, bilbatterier och reservdelar är enormt. Fyra vinterdäck väger 40–60 kg totalt och tar stor plats. En takbox är 2 meter lång och otymplig. Dragkrokar, batterier och oljefat är tunga. Många kunder köper dessutom däck och tillbehör i kombination med andra Biltema-produkter, och vagnen blir snabbt omöjlig att hantera. Med Ungdomskrafts hemleverans hämtar vi dina biltillbehör på Biltema och levererar direkt till garaget, uppfarten eller verkstaden. Du slipper klämma in fyra däck i baksätet eller binda en takbox på taket av den bil den ska monteras på.",
        },
        {
          heading: "Hemleverans av verktyg och garageutrustning från Biltema",
          content:
            "Biltema erbjuder ett brett utbud av verktyg och garageutrustning till låga priser – kompressorer, svetsaggregat, bänkslipar, verktygsvagnar, arbetsbänkar, garagehyllor och förvaringslösningar. En verktygsvagn i stål väger ofta 30–50 kg, en kompressor 25–50 kg och garagehyllor i metall 20–40 kg. Dessa produkter är inte bara tunga utan också stora och otympliga i förpackning. Med vår hemleverans hämtar vi allt på Biltema och levererar direkt till ditt garage eller din verkstad. Vi bär in oavsett var utrustningen ska stå – garaget, källaren eller förrådet. Perfekt för dig som uppgraderar verkstaden men inte har lämpligt transportfordon.",
        },
        {
          heading: "Hemleverans av trädgård och fritid från Biltema",
          content:
            "Biltemas sortiment av trädgårds- och fritidsprodukter växer ständigt – trädgårdsmöbler, grillar, studsmattor, pooler, parasoll, cyklar, kajaker och campingutrustning. Under vår och sommar är dessa avdelningar enormt populära. Problemet: en studsmatta i förpackning är 1,5–2 meter lång, en pool i kartong likaså, trädgårdsmöbler i flatpack tar 3–5 kollin och en cykel i kartong är stor och otymplig. Allt till Biltemas låga priser, men inget av det ryms i bilen – särskilt inte om du köper flera saker samtidigt. Med Ungdomskraft hämtar vi hela din trädgårds- och fritidsbeställning på Biltema och kör hem. Vi bär ut till trädgården, altanen eller garaget. Du fokuserar på att bygga studsmattan istället för att slita med logistiken.",
        },
        {
          heading: "Så fungerar Biltema-leverans med Ungdomskraft",
          listItems: [
            {
              label: "Boka online",
              text: "Ange Biltema-butikens adress och din hemadress. Beskriv vad du handlat och välj en tid. Du ser priset direkt.",
            },
            {
              label: "Vi hämtar på Biltema",
              text: "Vårt team kommer med skåpbil. Vi hämtar ditt köp i butiken, lastar och säkrar allt med spännband.",
            },
            {
              label: "Vi levererar hem",
              text: "Vi kör hem till dig, bär in till rätt plats – garaget, verkstaden, trädgården eller förrådet.",
            },
            {
              label: "Klart",
              text: "Ditt Biltema-köp står hemma hos dig – inburet och på plats. Utan att du lyfte ett finger.",
            },
          ],
          afterContent:
            "Hela processen tar normalt 1–3 timmar beroende på avstånd och mängd. Du kan följa statusen i realtid.",
        },
        {
          heading: "Vi kör från alla Biltema-butiker",
          content:
            "Vi hämtar från alla Biltema-butiker i Stockholm och Göteborg. I Stockholm: Kungens Kurva, Barkarby, Bromma, Nacka, Täby, Haninge, Sollentuna och alla övriga. I Göteborg: Sisjön, Bäckebol, Kungälv och alla övriga butiker. Ange bara butikens adress vid bokning så sköter vi resten. Ska du handla på Biltema och en annan butik samma dag? Vi kan hämta från flera ställen i samma tur – till exempel Biltema och Jula eller Biltema och Bauhaus i samma körning.",
        },
        {
          heading: "Varför boka Biltema-leverans med Ungdomskraft?",
          content:
            "Biltema erbjuder ingen traditionell hemleverans – det är en självbetjäningsbutik där du förväntas ta med dig varorna själv. Det fungerar för smått, men inte när du handlat fyra däck, en kompressor, en garagehylla och en studsmatta. Med Ungdomskraft får du snabb leverans med inbärning till rätt plats – oavsett om varorna ska till garaget, verkstaden, trädgården eller källaren. Dessutom gör du dubbel nytta varje gång du bokar: du får hem ditt köp smidigt och du bidrar till att unga människor kommer in på arbetsmarknaden.\n\nDet som gör Ungdomskraft unikt är vår samhällsnytta. Vi anställer unga människor – ofta i åldern 18–25 – som annars har svårt att få sitt första jobb. Många saknar arbetslivserfarenhet, nätverk eller kontakter i branschen. Genom att arbeta hos oss får de riktig, praktisk erfarenhet som arbetsgivare värdesätter: kundkontakt ansikte mot ansikte, logistik och planering, fysiskt arbete under tidspress, samarbete i team och eget ansvar för att ett uppdrag slutförs ordentligt. De lär sig att kommunicera professionellt med kunder, att lösa problem på plats och att leverera kvalitet utan genvägar.\n\nVarje Biltema-leverans bemannas av våra ungdomar. De hämtar ditt köp i butiken, lastar skåpbilen metodiskt, kör säkert till din adress och bär in till exakt den plats du önskar. De behandlar dina varor med samma omsorg som om det vore deras egna – för de vet att varje nöjd kund stärker deras CV och öppnar dörrar till framtida jobb. Den energi och det engagemang de visar märks i varje uppdrag.\n\nVi har funnits sedan 2014 och har genomfört tiotusentals transporter och leveranser i Stockholm och Göteborg. Under åren har hundratals ungdomar fått sin första arbetslivserfarenhet genom Ungdomskraft – och många har gått vidare till fasta tjänster inom logistik, bygg, kundservice och andra branscher. Vår modell fungerar för att den bygger på riktigt arbete med riktiga kunder, inte praktik utan ansvar.\n\nHållbarhet genomsyrar allt vi gör. Vi planerar körningar effektivt för att minimera tomkörning. Vi samlastar när det är möjligt. Vi återvinner förpackningsmaterial och ser till att wellpapp, frigolit och plast sorteras korrekt efter leverans. För företag som beställer Biltema-leveranser erbjuder vi dokumentation som kan användas i CSR-rapportering – varje bokning stödjer ungdomsarbete och hållbar logistik.\n\nAllt är helförsäkrat. Vi har egna skåpbilar i flera storlekar – från liten skåpbil för verktyg och tillbehör till stor skåpbil som rymmer däck, garagehyllor och trädgårdsmöbler. Du ser alltid priset innan du bokar. Inga dolda avgifter, inga överraskningar. Och om något mot förmodan skadas under transporten ersätter vi det omedelbart.\n\nNär du bokar Biltema-leverans med Ungdomskraft väljer du mer än en transporttjänst. Du väljer att ge en ung människa möjligheten att lära, växa och bygga sin framtid. Det är smidig leverans med mening.\n\nHemleverans från Biltema ger inte rätt till RUT-avdrag, men flera av våra andra tjänster gör det – exempelvis flytt, bärhjälp och möbelmontering med 50 % avdrag på arbetskostnaden. Kombinera gärna tjänster i en och samma bokning.",
        },
      ],
    },

    faq: [
      {
        question: "Vad kostar hemleverans från Biltema?",
        answer:
          "Priset beror på mängden och avståndet. En mindre Biltema-leverans med verktyg eller tillbehör kostar från 595 kr. Större leveranser med däck, garagehyllor eller trädgårdsmöbler kostar från 995–2 495 kr. Du ser det exakta priset när du bokar online.",
      },
      {
        question: "Kan jag få RUT-avdrag på hemleverans från Biltema?",
        answer:
          "Hemleverans från Biltema ger i sig inte rätt till RUT-avdrag. RUT gäller hushållsnära tjänster som utförs i din bostad, exempelvis flytt, bärhjälp och möbelmontering. Ungdomskraft erbjuder flera RUT-berättigade tjänster med 50 % avdrag på arbetskostnaden – kombinera enkelt i en bokning via vår hemsida.",
      },
      {
        question: "Ingår skåpbil i Biltema-leveransen?",
        answer:
          "Ja, skåpbil ingår alltid. Vi väljer rätt storlek utifrån ditt köp – du behöver inte hyra bil eller släp.",
      },
      {
        question: "Ingår inbärning i priset för Biltema-leveransen?",
        answer:
          "Ja, inbärning ingår alltid. Vi bär ditt köp till rätt plats – garaget, verkstaden, trädgården eller förrådet – oavsett avstånd från bilen. Inga extra avgifter.",
      },
      {
        question: "Vilka Biltema-butiker hämtar ni från?",
        answer:
          "Vi hämtar från alla Biltema-butiker i Stockholm och Göteborg: Kungens Kurva, Barkarby, Bromma, Nacka, Täby, Haninge, Sollentuna, Sisjön, Bäckebol, Kungälv och alla övriga. Ange bara butikens adress vid bokning.",
      },
      {
        question: "Kan ni leverera däck och biltillbehör från Biltema?",
        answer:
          "Ja, vi hanterar däck, fälgar, takboxar, dragkrokar och biltillbehör dagligen. Vi levererar direkt till garaget, uppfarten eller verkstaden.",
      },
      {
        question: "Kan ni leverera tunga verktyg och maskiner från Biltema?",
        answer:
          "Ja, vi hanterar tunga produkter dagligen – verktygsvagnar, kompressorer, bänkslipar och svetsaggregat. Vi bär in till garaget, verkstaden eller källaren oavsett tyngd.",
      },
      {
        question: "Kan ni leverera trädgårdsmöbler och studsmattor från Biltema?",
        answer:
          "Ja, vi levererar trädgårdsmöbler, studsmattor, pooler och all annan skrymmande fritidsutrustning. Vi bär ut till trädgården, altanen eller garaget.",
      },
      {
        question: "Kan ni bära tunga Biltema-köp i trappor?",
        answer:
          "Ja, vi bär till vilken våning som helst – med eller utan hiss. Våra ungdomar är vana vid tunga lyft och trånga trapphus.",
      },
      {
        question: "Skyddar ni sakerna under Biltema-transporten?",
        answer:
          "Ja, vi säkrar alla kollin med spännband i skåpbilen. Ömtåliga föremål skyddas extra med filtar. Allt är försäkrat – om något skadas ersätter vi det.",
      },
      {
        question: "Hur snabbt kan ni leverera från Biltema?",
        answer:
          "Vi erbjuder ofta hemleverans samma dag – ofta redan samma dag – inom några timmar. Boka online och se tillgängliga tider direkt.",
      },
      {
        question: "Erbjuder ni Biltema-leverans på helger?",
        answer:
          "Ja, vi erbjuder hemleverans på helger och vissa kvällar. Kontrollera att Biltema-butiken har öppet den tid du bokar – vi kan inte hämta om butiken är stängd.",
      },
      {
        question: "Kan ni hämta från Biltema och en annan butik i samma tur?",
        answer:
          "Ja, vi kan hämta från flera butiker under samma körning om de ligger i rimligt avstånd. Beskriv alla hämtpunkter vid bokning – till exempel Biltema och Jula i samma tur.",
      },
      {
        question: "Behöver jag vara med på Biltema vid upphämtningen?",
        answer:
          "Nej, du behöver inte vara med på Biltema. Se till att köpet är betalt och redo att hämtas – vi sköter resten. Du behöver bara vara hemma för att ta emot leveransen.",
      },
      {
        question:
          "I vilka kommuner erbjuder ni hemleverans från Biltema?",
        answer:
          "Vi erbjuder Biltema-leverans i hela Stockholmsområdet: Botkyrka, Danderyd, Ekerö, Haninge, Huddinge, Järfälla, Lidingö, Nacka, Norrtälje, Nykvarn, Nynäshamn, Salem, Sigtuna, Sollentuna, Solna, Stockholm, Sundbyberg, Södertälje, Tyresö, Täby, Upplands Väsby, Upplands-Bro, Vallentuna, Vaxholm, Värmdö och Österåker. Vi finns även i Göteborgsområdet: Ale, Alingsås, Göteborg, Härryda, Kungälv, Kungsbacka, Lerum, Lilla Edet, Mölndal, Partille, Stenungsund, Tjörn och Öckerö.",
      },
      {
        question: "Varför välja Ungdomskraft för Biltema-leverans?",
        answer:
          "Hos oss ingår alltid skåpbil, bärhjälp och inbärning till rätt plats. Du ser priset direkt vid bokning, vi kan ofta köra samma dag och allt är försäkrat.",
      },
    ],

    priceHint: PACKAGE_PRICE_HINT_SV.bud,

    pricingRows: getPackagePricingRows("bud", "sv"),

    dynamicHeadings: {
      threeSteps: "Biltema-leverans i 3 enkla steg",
      step2Text:
        "Våra ungdomar hämtar ditt köp på Biltema, lastar och säkrar allt i skåpbilen. Sedan kör vi hem till dig.",
      step3Title: "Allt på plats",
      step3Desc:
        "Ditt Biltema-köp står hemma hos dig – inburet och på plats. Klart.",
      ctaHeading: "Boka Biltema-leverans idag",
      ctaText:
        "Ange Biltema-butikens adress och din hemadress, beskriv vad du handlat och välj en tid. Du ser priset direkt.",
    },

    en: {
      slug: "biltema-delivery",
      meta: {
        title:
          "Biltema Delivery – We Pick Up Your Purchase And Deliver Home | Ungdomskraft",
        description:
          "Bought tools or car accessories at Biltema that won't fit in your car? We pick up at the store and deliver home with carrying included. Book today!",
        h1: "Biltema delivery",
      },
      hero: {
        heading: "We pick up your purchase at Biltema",
        intro:
          "Shopped at Biltema and the trolley got heavier than planned? Roof boxes, compressors, garage shelving, garden furniture and car tyres — Biltema's low prices make it easy to fill the trolley, but hard to get it all home. We solve it. Ungdomskraft picks up your purchase directly at Biltema, loads the van and delivers to your home — with carrying and placement included. We collect from all Biltema stores in Stockholm and Gothenburg.",
      },
      featureCards: {
        sinceText: "Biltema delivery since 2014",
        bookYourText: "Book your Biltema delivery",
        bookDescText:
          "Enter the Biltema store address and your home address, describe what you bought and choose a time. You see the price instantly.",
        vehicleTitleText: "Vans perfect for Biltema deliveries",
        vehicleDescText:
          "We arrive with the right size van — whether you're picking up a toolbox or a full garage setup. No need to rent a trailer, drive yourself or make multiple trips.",
      },
      itemsSection: {
        heading: "Biltema products we pick up and deliver home",
        items: [
          {
            name: "Car & motor",
            description:
              "Roof boxes, tow bars, tyres, rims, motor oil, car batteries and car accessories",
            image: "/microwave.webp",
          },
          {
            name: "Tools & machinery",
            description:
              "Compressors, welding machines, bench grinders, tool trolleys and power tools",
            image: "/toolbox.webp",
          },
          {
            name: "Garden & outdoor",
            description:
              "Garden furniture, grills, trampolines, pools, parasols and robotic mowers",
            image: "/patio-furniture.webp",
          },
          {
            name: "Storage & garage",
            description:
              "Garage shelving, tool cabinets, workbenches, storage boxes and organisation systems",
            image: "/office-desk.webp",
          },
          {
            name: "Leisure & sport",
            description:
              "Bicycles, kayaks, paddleboards, camping gear and scooters in large packaging",
            image: "/road-bike.webp",
          },
          {
            name: "Home & electrical",
            description:
              "Generators, floodlights, cable drums, heaters and heavier home electronics",
            image: "/tv.webp",
          },
        ],
      },
      seoContent: {
        mainHeading:
          "Delivery from Biltema — we pick up, drive and carry inside for you",
        mainText:
          "Biltema is one of Sweden's most popular budget department stores with roots in car accessories and a development that has turned the chain into a complete retail experience. Their stores in Stockholm and Gothenburg offer everything from car tyres, motor oil and tow bars to tools, garden furniture, leisure equipment, home products and toys. The range is enormously wide and prices are consistently low — making Biltema a favourite for anyone who enjoys fixing, building and maintaining. The problem is well known: you go to Biltema to buy a can of oil and a set of wrenches, and come out with a tool trolley, four winter tyres, a compressor, a garage shelf and maybe a trampoline for the kids. All at fantastic prices — but completely impossible to get home in the car. Roof boxes, tyres, compressors and garage shelving are heavy and bulky. Trampolines and pools come in packaging that is 2 metres long. Garden furniture in boxes takes up enormous space. Biltema stores are also usually located in retail parks outside the city centre, so multiple trips eat up the whole day. Biltema doesn't offer traditional home delivery — it's a self-service store where you buy and take the goods yourself. That works fine for small items, but not when the purchase weighs 200 kg and doesn't fit in the boot. Ungdomskraft offers a fast and smooth alternative. We pick up your Biltema purchase directly at the store, load everything into the van and drive to your home. Carrying to the right spot is always included — we carry to the garage, workshop, garden, storage room or wherever you want. You see the price instantly when booking, no hidden fees. We collect from all Biltema stores in Stockholm and Gothenburg. Every week we make hundreds of deliveries from Biltema and similar stores. Our crew are used to handling everything from heavy tyre stacks and compressors to bulky trampolines and garden furniture.",
        preparationHeading: "Prepare for your Biltema delivery",
        preparationText:
          "Make sure the purchase is paid for and ready for collection. Have your home address and any door codes ready. If you've bought heavy or bulky items, list the largest ones so we can choose the right van size. Check that the van can park near the garage entrance or door at your home. If items are going to the garage, workshop or garden, make sure the passage and doors are open. When booking, specify any special requests, such as tyres going into the garage or a trampoline going to the garden.",
        additionalSections: [
          {
            heading: "Car accessories and tyre delivery from Biltema",
            content:
              "Biltema has its origins in car accessories and it shows — the range of car tyres, rims, roof boxes, tow bars, motor oil, car batteries and spare parts is enormous. Four winter tyres weigh 40–60 kg total and take up a lot of space. A roof box is 2 metres long and unwieldy. Tow bars, batteries and oil drums are heavy. Many customers also buy tyres and accessories combined with other Biltema products, and the trolley quickly becomes impossible to manage. With Ungdomskraft's delivery we collect your car accessories from Biltema and deliver directly to the garage, driveway or workshop. No squeezing four tyres into the back seat or tying a roof box onto the car it's meant to be mounted on.",
          },
          {
            heading: "Tool and garage equipment delivery from Biltema",
            content:
              "Biltema offers a wide range of tools and garage equipment at low prices — compressors, welding machines, bench grinders, tool trolleys, workbenches, garage shelving and storage solutions. A steel tool trolley often weighs 30–50 kg, a compressor 25–50 kg and metal garage shelving 20–40 kg. These products are not just heavy but also large and unwieldy in packaging. With our delivery we collect everything from Biltema and deliver directly to your garage or workshop. We carry inside regardless of where the equipment goes — garage, basement or storage room. Perfect for anyone upgrading their workshop but lacking a suitable vehicle.",
          },
          {
            heading: "Garden and leisure product delivery from Biltema",
            content:
              "Biltema's range of garden and leisure products is constantly growing — garden furniture, grills, trampolines, pools, parasols, bicycles, kayaks and camping equipment. During spring and summer these departments are hugely popular. The problem: a trampoline in packaging is 1.5–2 metres long, a pool box likewise, garden furniture in flatpack takes 3–5 packages and a bicycle in a box is large and unwieldy. All at Biltema's low prices, but none of it fits in the car — especially if you buy several items at once. With Ungdomskraft we collect your entire garden and leisure order from Biltema and drive it home. We carry out to the garden, patio or garage. You focus on building the trampoline instead of struggling with logistics.",
          },
          {
            heading: "How Biltema delivery works with Ungdomskraft",
            listItems: [
              {
                label: "Book online",
                text: "Enter the Biltema store address and your home address. Describe what you bought and choose a time. You see the price instantly.",
              },
              {
                label: "We pick up at Biltema",
                text: "Our team arrive with a van. We collect your purchase at the store, load and secure everything with straps.",
              },
              {
                label: "We deliver home",
                text: "We drive to your home and carry to the right spot — garage, workshop, garden or storage room.",
              },
              {
                label: "Done",
                text: "Your Biltema purchase is at home — carried in and in place. Without lifting a finger.",
              },
            ],
            afterContent:
              "The whole process typically takes 1–3 hours depending on distance and amount. You can follow the status in real time.",
          },
          {
            heading: "We collect from all Biltema stores",
            content:
              "We pick up from all Biltema stores in Stockholm and Gothenburg. In Stockholm: Kungens Kurva, Barkarby, Bromma, Nacka, Täby, Haninge, Sollentuna and all others. In Gothenburg: Sisjön, Bäckebol, Kungälv and all other locations. Just enter the store address when booking and we handle the rest. Shopping at Biltema and another store the same day? We can pick up from multiple locations in the same trip — for example Biltema and Jula or Biltema and Bauhaus in the same run.",
          },
          {
            heading: "Why book Biltema delivery with Ungdomskraft?",
            content:
              "Biltema doesn't offer traditional home delivery — it's a self-service store where you're expected to take the goods yourself. That works for small items, but not when you've bought four tyres, a compressor, garage shelving and a trampoline. With Ungdomskraft you get fast delivery with carrying to the right spot — whether items go to the garage, workshop, garden or basement. Plus you make a double impact every time you book: you get your purchase home smoothly and you help young people enter the workforce.\n\nWhat makes Ungdomskraft unique is our social mission. We employ young people — often aged 18–25 — who otherwise struggle to get their first job. Many lack work experience, networks or industry contacts. By working with us they gain real, practical experience that employers value: face-to-face customer service, logistics and planning, physical work under time pressure, teamwork and personal responsibility for completing a job properly. They learn to communicate professionally with customers, solve problems on site and deliver quality without shortcuts.\n\nEvery Biltema delivery is staffed by our young workers. They collect your purchase at the store, load the van methodically, drive safely to your address and carry to the exact spot you want. They handle your goods with the same care as if they were their own — because they know that every satisfied customer strengthens their CV and opens doors to future employment. The energy and commitment they show is evident in every job.\n\nWe've been around since 2014 and have completed tens of thousands of transports and deliveries across Stockholm and Gothenburg. Over the years, hundreds of young people have gained their first work experience through Ungdomskraft — and many have gone on to permanent positions in logistics, construction, customer service and other industries. Our model works because it's built on real work with real customers, not internships without responsibility.\n\nSustainability runs through everything we do. We plan routes efficiently to minimise empty runs. We consolidate loads when possible. We recycle packaging materials and ensure cardboard, polystyrene and plastic are sorted correctly after delivery. For companies ordering Biltema deliveries we offer documentation for CSR reporting — every booking supports youth employment and sustainable logistics.\n\nEverything is fully insured. We have our own vans in multiple sizes — from small vans for tools and accessories to large vans that fit tyres, garage shelving and garden furniture. You always see the price before you book. No hidden fees, no surprises. And if anything is unexpectedly damaged during transport we replace it immediately.\n\nWhen you book Biltema delivery with Ungdomskraft, you choose more than a transport service. You choose to give a young person the opportunity to learn, grow and build their future. It's smooth delivery with purpose.\n\nBiltema delivery does not qualify for the Swedish RUT tax deduction, but several of our other services do – including moving help, carrying help and furniture assembly with a 50% deduction on the labour cost. Feel free to combine services in a single booking.",
          },
        ],
      },
      faq: [
        {
          question: "How much does Biltema delivery cost?",
          answer:
            "The price depends on the amount and distance. A smaller Biltema delivery with tools or accessories starts at 595 SEK. Larger deliveries with tyres, garage shelving or garden furniture cost 749–2 495 SEK. You see the exact price when you book online.",
        },
        {
          question: "Can I get a RUT tax deduction on Biltema delivery?",
          answer:
            "Biltema delivery does not in itself qualify for the Swedish RUT tax deduction. RUT applies to household services carried out in your home, such as moving help, carrying help and furniture assembly. Ungdomskraft offers several RUT-eligible services with a 50% deduction on the labour cost – easily combine them in one booking via our website.",
        },
        {
          question: "Is a van included in the Biltema delivery?",
          answer:
            "Yes, a van is always included. We choose the right size based on your purchase — you don't need to rent a vehicle or trailer.",
        },
        {
          question: "Is carrying included in the Biltema delivery price?",
          answer:
            "Yes, carrying is always included. We carry your purchase to the right spot — garage, workshop, garden or storage room — regardless of distance from the van. No extra charges.",
        },
        {
          question: "Which Biltema stores do you pick up from?",
          answer:
            "We pick up from all Biltema stores in Stockholm and Gothenburg: Kungens Kurva, Barkarby, Bromma, Nacka, Täby, Haninge, Sollentuna, Sisjön, Bäckebol, Kungälv and all others. Just enter the store address when booking.",
        },
        {
          question: "Can you deliver tyres and car accessories from Biltema?",
          answer:
            "Yes, we handle tyres, rims, roof boxes, tow bars and car accessories daily. We deliver directly to the garage, driveway or workshop.",
        },
        {
          question: "Can you deliver heavy tools and machinery from Biltema?",
          answer:
            "Yes, we handle heavy products daily — tool trolleys, compressors, bench grinders and welding machines. We carry to the garage, workshop or basement regardless of weight.",
        },
        {
          question: "Can you deliver garden furniture and trampolines from Biltema?",
          answer:
            "Yes, we deliver garden furniture, trampolines, pools and all other bulky leisure equipment. We carry out to the garden, patio or garage.",
        },
        {
          question: "Can you carry heavy Biltema purchases up stairs?",
          answer:
            "Yes, we carry to any floor — with or without a lift. Our crew are used to heavy items and narrow stairwells.",
        },
        {
          question: "Do you protect items during Biltema transport?",
          answer:
            "Yes, we secure all packages with straps in the van. Fragile items get extra blanket protection. Everything is insured — if something is damaged we replace it.",
        },
        {
          question: "How quickly can you deliver from Biltema?",
          answer:
            "We often offer delivery the same day — within a few hours. Book online and see available times instantly.",
        },
        {
          question: "Is Biltema delivery available on weekends?",
          answer:
            "Yes, we offer delivery on weekends and some evenings. Check that the Biltema store is open at your chosen time — we can't pick up if the store is closed.",
        },
        {
          question: "Can you pick up from Biltema and another store in the same trip?",
          answer:
            "Yes, we can pick up from several stores during the same run if they're within a reasonable distance. Describe all pickup points when you book — for example Biltema and Jula in the same trip.",
        },
        {
          question: "Do I need to be at the Biltema store?",
          answer:
            "No, you don't need to be at Biltema. Make sure the purchase is paid for and ready to collect — we handle the rest. You just need to be home to receive the delivery.",
        },
        {
          question: "In which areas do you offer Biltema delivery?",
          answer:
            "We offer Biltema delivery across the entire Stockholm region: Botkyrka, Danderyd, Ekerö, Haninge, Huddinge, Järfälla, Lidingö, Nacka, Norrtälje, Nykvarn, Nynäshamn, Salem, Sigtuna, Sollentuna, Solna, Stockholm, Sundbyberg, Södertälje, Tyresö, Täby, Upplands Väsby, Upplands-Bro, Vallentuna, Vaxholm, Värmdö and Österåker. We're also in the Gothenburg region: Ale, Alingsås, Göteborg, Härryda, Kungälv, Kungsbacka, Lerum, Lilla Edet, Mölndal, Partille, Stenungsund, Tjörn and Öckerö.",
        },
        {
          question: "Why choose Ungdomskraft for Biltema delivery?",
          answer:
            "We always include a van, carrying help and placement in the right spot. You see the price instantly when booking, we can often deliver the same day and everything is insured.",
        },
      ],
      priceHint: PACKAGE_PRICE_HINT_EN.bud,
      pricingRows: getPackagePricingRows("bud", "en"),
      dynamicHeadings: {
        threeSteps: "Biltema delivery in 3 easy steps",
        step2Text:
          "Our crew pick up your purchase at Biltema, load and secure everything in the van. Then we drive home to you.",
        step3Title: "All in place",
        step3Desc:
          "Your Biltema purchase is at home — carried in and in place. Done.",
        ctaHeading: "Book Biltema delivery today",
        ctaText:
          "Enter the Biltema store address and your home address, describe what you bought and choose a time. You see the price instantly.",
      },
    },
  },

  // ────────────────────────────────────────────────────────────────────────────
  // HEMLEVERANS AUKTION (generell)
  // Vinkel: Kunden har vunnit ett auktionsobjekt (konst, antikviteter, möbler,
  // designföremål) och behöver hämtning från auktionshuset + leverans hem.
  // ────────────────────────────────────────────────────────────────────────────
  {
    slug: "hemleverans-auktion",
    serviceCategory: "hemleverans",

    meta: {
      title:
        "Hemleverans Auktion – Vi Hämtar Ditt Auktionsfynd Och Kör Hem | Ungdomskraft",
      description:
        "Vunnit ett auktionsobjekt? Vi hämtar på auktionshuset och levererar hem med varsam inbärning. Konst, möbler, antikviteter – boka hemleverans auktion idag.",
      h1: "Hemleverans auktion",
    },

    hero: {
      heading: "Vi hämtar ditt vunna objekt – Hemleverans auktion",
      intro:
        "Grattis till vinsten! Men hur får du hem din nyvunna byrå, tavla eller kristallkrona? Hämtfristerna är korta och objekten tunga. Vi löser det. Ungdomskraft hämtar ditt auktionsobjekt direkt på auktionshuset, skyddar det under transporten och levererar hem till dig – med varsam inbärning och uppställning på plats. Vi hämtar från alla auktionshus: PS Auktion, Bukowskis, Stockholms Auktionsverk, Uppsala Auktionskammare, Budi, Auctionet, Klaravik och alla andra.",
      heroImage: "/hero-hemleverans.png",
    },

    featureCards: {
      sinceText: "Auktionsleverans sedan 2014",
      storyText:
        "Sedan 2014 har Ungdomskraft hjälpt auktionsköpare att få hem sina fynd – från tunga antika möbler och ömtålig konst till designklassiker och samlarföremål. Våra ungdomar vet hur man hanterar värdefulla objekt med den extra omsorg de förtjänar.",
      bookYourText: "Boka din auktionsleverans",
      bookDescText:
        "Ange auktionshusets adress och din hemadress, beskriv objektet och välj en tid. Du ser priset direkt.",
      vehicleTitleText: "Skåpbilar anpassade för auktionsleveranser",
      vehicleDescText:
        "Vi kommer med rätt storlek på skåpbil – oavsett om det gäller en liten tavla eller ett stort möblemang. Allt skyddas med filtar, bubbelplast och spännband.",
    },

    itemsSection: {
      heading: "Vanliga auktionsobjekt vi levererar hem",
      items: [
        {
          name: "Antika möbler",
          description:
            "Byråer, skåp, bord, stolar, sekretärer och andra möbler från 1700- till 1900-talet",
          image: "/wardrobe.webp",
        },
        {
          name: "Konst & tavlor",
          description:
            "Oljemålningar, litografier, skulpturer, ramar och annan konst som kräver varsam hantering",
          image: "/painting.webp",
        },
        {
          name: "Designmöbler",
          description:
            "Fåtöljer, soffor, lampor och bord av kända formgivare – Bruno Mathsson, Alvar Aalto, Arne Jacobsen",
          image: "/armchair.webp",
        },
        {
          name: "Mattor & textilier",
          description:
            "Persiska mattor, orientaliska mattor, gobelänger och andra textila samlingsföremål",
          image: "/bedding.webp",
        },
        {
          name: "Porslin & glas",
          description:
            "Servis, kristall, vaser, figuriner och andra ömtåliga samlingsföremål",
          image: "/dishes.webp",
        },
        {
          name: "Övrigt",
          description:
            "Ljuskronor, silverbestick, klockor, vinförvaringsskåp, konst och andra skrymmande auktionsfynd",
          image: "/box.webp",
        },
      ],
    },

    seoContent: {
      mainHeading:
        "Hemleverans från auktion – vi hämtar, transporterar och bär in ditt auktionsfynd",
      mainText:
        "Du har lagt det vinnande budet på en vacker byrå från 1800-talet, en oljemålning av en känd konstnär eller en klassisk designfåtölj. Spänningen efter klubbslaget är fantastisk – men sedan kommer den praktiska frågan: hur får jag hem det här? Hämtfristen är kort – ofta bara tre till fem arbetsdagar innan lagringsavgifter börjar ticka. Att hyra egen skåpbil och köra själv innebär risk för skador på värdefulla föremål som saknar originalförpackning. Ungdomskraft erbjuder hemleverans från alla auktionshus i Stockholm och Göteborg. Vi hämtar ditt vunna objekt direkt på auktionshuset, skyddar det varsamt med filtar, bubbelplast och spännband och kör hem till dig. Vi bär in genom trapphus, hiss eller trångt garage och ställer på plats där du vill ha det. Inbärning ingår alltid – oavsett våning. Du ser priset direkt när du bokar, inga dolda avgifter tillkommer. Vi fungerar med alla auktionshus: PS Auktion, Bukowskis, Stockholms Auktionsverk, Uppsala Auktionskammare, Budi, Auctionet, Klaravik och alla andra – fysiska och online. Oavsett om du köpt på en stor nätauktion eller en lokal auktionskväll hämtar vi ditt objekt och levererar hem. Vi hanterar även köp från Tradera och andra plattformar som förmedlar auktionsföremål. Varje vecka hämtar vi auktionsfynd i alla storlekar – från en enstaka tavla till ett helt dödsbo med möbler, mattor och konst. Våra ungdomar är vana vid att hantera ömtåliga och värdefulla föremål. Vi vet att en antik byrå inte tål att vältas på sidan, att en oljemålning behöver stå upprätt och skyddas mot stötar och att kristallkronor kräver extra omsorg. Vi tar med oss möbelfiltar, bubbelplast, bärremmar och spännband. Om objektet har ovanlig form eller extra högt värde beskriver du det vid bokning så anpassar vi hanteringen. Tänk på att vi ansvarar för transporten från auktionshuset hem till dig – objektets skick och autenticitet är mellan dig och auktionshuset.",
      preparationHeading: "Förbered inför din auktionsleverans",
      preparationText:
        "Se till att auktionsobjektet är fullt betalt och redo att hämtas. Kontrollera auktionshusets hämtfrist – de flesta ger tre till fem arbetsdagar innan lagringsavgift börjar. Ha ditt kundnummer eller objektnummer redo så att vårt team kan identifiera rätt föremål vid upphämtning. Meddela oss om objektet är särskilt ömtåligt, tungt eller har ovanlig form – vi anpassar skyddsmaterial och bemanning. Mät gärna dörröppningar och trapphus hemma hos dig om det rör sig om stora möbler. Kolla att skåpbilen kan parkera nära porten. Ju mer information du anger vid bokning, desto smidigare och tryggare blir leveransen.",
      additionalSections: [
        {
          heading: "Hemleverans av antika möbler från auktion",
          content:
            "Antika möbler är det vanligaste vi hämtar från auktionshus. En gustaviansk byrå, ett jugendskåp eller ett stort matbord i massiv ek – de väger ofta 50–150 kg och saknar originalförpackning. Att köra själv med en hyrd släpkärra innebär stor risk för repor, bucklor och fuktskador. Med Ungdomskrafts auktionsleverans hämtar vi dina möbler direkt på auktionshuset. Vi skyddar varje yta med mjuka filtar, tejpar fast lösa delar som lådor och handtag och säkrar allt med spännband i skåpbilen. Vi kör försiktigt och undviker hårda inbromsningar som kan flytta lasten. Hemma hos dig bär vi in genom trapphus och dörrar och ställer möbeln exakt där du vill ha den. Behöver möbeln monteras eller demonteras för att passa genom en dörröppning? Beskriv det vid bokning så löser vi det. Vi hanterar allt från enstaka stolar till kompletta matsalsgrupper.",
        },
        {
          heading: "Hemleverans av konst och ömtåliga föremål",
          content:
            "Konst och ömtåliga samlingsföremål kräver extra omsorg under transport. En oljemålning med förgylld ram kan väga 20–40 kg och tål inte stötar, fukt eller tryck mot duken. Skulpturer i brons, marmor eller keramik är tunga och känsliga för slag. Kristall, porslin och glas kan gå sönder vid minsta ovarsamma rörelse. Vi skyddar konst med bubbelplast, hörnskydd och mjuka filtar. Tavlor transporteras alltid stående – aldrig liggande med vikten på ramen. Skulpturer säkras individuellt så att de inte kan förskjutas under körningen. Porslin och kristall packas i lådor med stötdämpande material. Vi bär in och ställer på plats hemma hos dig – på väggen, på piedestalen eller i vitrinskåpet. Har du köpt flera ömtåliga föremål på samma auktion hämtar vi allt i en tur och skyddar varje objekt separat.",
        },
        {
          heading: "Hämtfrister och lagringsavgifter på auktionshus",
          content:
            "De flesta auktionshus ger dig tre till fem arbetsdagar att hämta ditt vunna objekt efter att fakturan är betald. Efter det börjar lagringsavgifter ticka – ofta 50–200 kr per dag beroende på objektets storlek. Vissa auktionshus skickar tillbaka föremål till ny auktion om de inte hämtas inom angiven tid. Med Ungdomskraft slipper du stressa. Boka din hemleverans så snart du fått hämtbesked – vi kan ofta köra redan samma dag eller nästa dag. Du anger auktionshusets adress, ditt objektnummer och önskad tid. Vi sköter upphämtningen och levererar hem innan fristen löper ut. Planerar du att buda på flera objekt under samma auktionskväll? Beskriv allt vid bokning – vi hämtar alla objekt i en tur.",
        },
        {
          heading: "Så fungerar auktionsleverans med Ungdomskraft",
          listItems: [
            {
              label: "Boka online",
              text: "Ange auktionshusets adress, din hemadress och beskriv objektet. Ange gärna objektnummer. Du ser priset direkt.",
            },
            {
              label: "Vi hämtar på auktionshuset",
              text: "Vårt team kommer med skåpbil och rätt skyddsmaterial. Vi identifierar ditt objekt, skyddar det varsamt och lastar.",
            },
            {
              label: "Vi levererar hem",
              text: "Vi kör hem till dig, bär in genom trapphus och dörrar och ställer ditt auktionsfynd på plats.",
            },
            {
              label: "Klart",
              text: "Ditt auktionsfynd står hemma hos dig – varsamt inburet och uppställt. Utan att du riskerade en repa.",
            },
          ],
          afterContent:
            "Hela processen tar normalt 1–3 timmar beroende på avstånd och objektets hanteringsbehov. Du kan följa statusen i realtid.",
        },
        {
          heading: "Vilka auktionshus hämtar vi från?",
          content:
            "Vi hämtar från alla auktionshus i Stockholm och Göteborg – både fysiska och online-auktioner med lokal upphämtning. De vanligaste är PS Auktion, Bukowskis, Stockholms Auktionsverk, Uppsala Auktionskammare, Budi, Auctionet och Klaravik. Men vi kör från vilket auktionshus som helst – ange bara adressen vid bokning. Vi hämtar även föremål från gallerier, antikaffärer och privata försäljningar om du behöver transport av värdefulla objekt. Ska du hämta från flera auktionshus i samma tur? Det går utmärkt – beskriv alla hämtpunkter vid bokning.",
        },
        {
          heading: "Varför boka auktionsleverans med Ungdomskraft?",
          content:
            "Det som gör Ungdomskraft unikt är att vi anställer unga människor och ger dem riktig arbetslivserfarenhet. När du bokar hemleverans av ditt auktionsfynd får du inte bara professionell och varsam hantering – du bidrar också till att unga människor kommer in på arbetsmarknaden och bygger sin framtid.\n\nVarje uppdrag bemannas av motiverade ungdomar som lär sig allt från kundservice och logistik till varsam hantering av värdefulla föremål. De lär sig att kommunicera med kunder, planera rutter, skydda ömtåliga ytor och arbeta effektivt i team. Det är arbetslivserfarenhet som inte går att få i ett klassrum – och varje auktionsleverans vi kör ger en ung person möjlighet att utvecklas.\n\nVi har funnits sedan 2014 och har genomfört tiotusentals transporter i Stockholm och Göteborg. Våra ungdomar har burit in allt från tunga ekskåp och bräckliga oljemålningar till lyxmöbler och antikviteter värda hundratusentals kronor. Vi vet vad som krävs: rätt skyddsmaterial, försiktig lastning, jämn körning och varsam inbärning.\n\nKvalitet genomsyrar allt vi gör. Våra ungdomar tar inga genvägar – de lägger extra filtar, kontrollerar att spännbanden sitter och kommunicerar med dig under hela uppdraget. Den energi och det engagemang som unga medarbetare har märks i varje leverans. De vill visa vad de kan och de tar uppdraget på allvar.\n\nHållbarhet är en annan grundpelare. Vi optimerar rutter för att minimera utsläpp, använder återanvändbara skyddsmaterial och uppmuntrar kunder att ge föremål nytt liv genom auktionsköp istället för att köpa nytt. Varje auktionsobjekt som får ett nytt hem är ett steg mot en mer cirkulär ekonomi – och vi är stolta över att vara en del av den kedjan.\n\nFör företag som köper på auktion – konst till kontoret, möbler till lobbyn eller designföremål till showroom – erbjuder vi även faktura och underlag för CSR-rapportering. Du kan visa att din transport genomfördes av ett företag som sysselsätter ungdomar och bidrar till social hållbarhet.\n\nVi erbjuder snabb hämtning, inbärning på plats och ett konkurrenskraftigt pris utan dolda avgifter. Du ser priset direkt vid bokning, allt är försäkrat och vi har egna skåpbilar i flera storlekar.\n\nDet är varsam, professionell hjälp med mening.\n\nHemleverans från auktion ger inte rätt till RUT-avdrag, men flera av våra andra tjänster gör det – exempelvis flytt, bärhjälp och möbelmontering med 50 % avdrag på arbetskostnaden. Kombinera gärna tjänster i en och samma bokning.",
        },
      ],
    },

    faq: [
      {
        question: "Vad kostar hemleverans från auktion?",
        answer:
          "Priset beror på objektets storlek och avståndet. En tavla eller mindre föremål kostar från 595 kr. Större möbler kostar från 995–2 495 kr. Du ser det exakta priset direkt när du bokar online.",
      },
      {
        question: "Kan jag få RUT-avdrag på hemleverans från auktion?",
        answer:
          "Hemleverans från auktion ger i sig inte rätt till RUT-avdrag. RUT gäller hushållsnära tjänster som utförs i din bostad, exempelvis flytt, bärhjälp och möbelmontering. Ungdomskraft erbjuder flera RUT-berättigade tjänster med 50 % avdrag på arbetskostnaden – kombinera enkelt i en bokning via vår hemsida.",
      },
      {
        question: "Ingår skåpbil i auktionsleveransen?",
        answer:
          "Ja, skåpbil ingår alltid. Vi väljer rätt storlek utifrån vad som ska transporteras – du behöver inte hyra bil eller köra själv.",
      },
      {
        question: "Ingår inbärning i priset för auktionsleverans?",
        answer:
          "Ja, inbärning ingår alltid. Vi bär in ditt auktionsobjekt genom trapphus, hiss och dörrar och ställer på plats – oavsett våning. Inga extra avgifter per våningsplan.",
      },
      {
        question: "Hur skyddar ni ömtåliga auktionsföremål under transporten?",
        answer:
          "Vi använder möbelfiltar, bubbelplast, hörnskydd och spännband. Tavlor transporteras stående, skulpturer säkras individuellt och porslin packas med stötdämpande material. Allt är försäkrat – om något skadas ersätter vi det.",
      },
      {
        question: "Vilka auktionshus hämtar ni från?",
        answer:
          "Vi hämtar från alla auktionshus i Stockholm och Göteborg: PS Auktion, Bukowskis, Stockholms Auktionsverk, Uppsala Auktionskammare, Budi, Auctionet, Klaravik och alla andra. Ange bara auktionshusets adress vid bokning.",
      },
      {
        question: "Kan ni hämta tunga antika möbler från auktion?",
        answer:
          "Ja, våra ungdomar är vana vid att bära tunga föremål – byråer, skåp, matbord och andra möbler som väger 50–150 kg. Vi skyddar alla ytor med filtar och bär varsamt genom trapphus och trånga dörröppningar.",
      },
      {
        question: "Hur snabbt kan ni hämta från auktionshuset?",
        answer:
          "Vi kan ofta hämta redan samma dag eller nästa dag. Boka så snart du fått hämtbesked från auktionshuset – det hjälper dig att undvika lagringsavgifter.",
      },
      {
        question: "Tar ni ansvar för auktionsobjektets skick?",
        answer:
          "Vi ansvarar för transporten från auktionshuset hem till dig – objektet är försäkrat mot transportskador. Objektets skick, autenticitet och eventuella befintliga brister är mellan dig och auktionshuset. Vi rekommenderar att du inspekterar objektet innan vi lastar.",
      },
      {
        question: "Behöver jag vara med på auktionshuset vid hämtning?",
        answer:
          "Nej, du behöver inte vara med. Se till att objektet är fullt betalt och redo att hämtas. Ange ditt kundnummer eller objektnummer vid bokning så identifierar vi rätt föremål. Du behöver bara vara hemma för att ta emot leveransen.",
      },
      {
        question: "Kan ni leverera auktionsobjekt på helger?",
        answer:
          "Ja, vi erbjuder hemleverans på helger och vissa kvällar. Kontrollera att auktionshuset har öppet för upphämtning den tid du bokar.",
      },
      {
        question: "Kan ni hämta flera objekt från samma auktion?",
        answer:
          "Ja, vi hämtar alla dina vunna objekt i en tur. Beskriv samtliga föremål vid bokning – vi anpassar skåpbilsstorlek och skyddsmaterial efter hela lasten.",
      },
      {
        question: "Vad händer om jag missar hämtfristen på auktionshuset?",
        answer:
          "De flesta auktionshus börjar ta lagringsavgift efter tre till fem arbetsdagar – ofta 50–200 kr per dag. Boka din hemleverans så snart du fått hämtbesked så slipper du extra kostnader.",
      },
      {
        question: "Är auktionsleveransen försäkrad?",
        answer:
          "Ja, alla våra transporter är helförsäkrade. Om ditt auktionsobjekt skadas under lastning, transport eller inbärning ersätter vi det. Du kan boka med trygghet.",
      },
      {
        question:
          "I vilka kommuner erbjuder ni hemleverans från auktion?",
        answer:
          "Vi erbjuder hemleverans från auktion i hela Stockholmsområdet: Botkyrka, Danderyd, Ekerö, Haninge, Huddinge, Järfälla, Lidingö, Nacka, Norrtälje, Nykvarn, Nynäshamn, Salem, Sigtuna, Sollentuna, Solna, Stockholm, Sundbyberg, Södertälje, Tyresö, Täby, Upplands Väsby, Upplands-Bro, Vallentuna, Vaxholm, Värmdö och Österåker. Vi finns även i Göteborgsområdet: Ale, Alingsås, Göteborg, Härryda, Kungälv, Kungsbacka, Lerum, Lilla Edet, Mölndal, Partille, Stenungsund, Tjörn och Öckerö.",
      },
      {
        question: "Kan ni hämta från auktionshus och en butik i samma tur?",
        answer:
          "Ja, vi kan kombinera hämtning från ett auktionshus med andra stopp – till exempel en möbelbutik eller ett annat auktionshus. Beskriv alla hämtpunkter vid bokning.",
      },
      {
        question: "Varför välja Ungdomskraft för auktionsleverans?",
        answer:
          "Hos oss ingår alltid skåpbil, bärhjälp och inbärning till rätt plats – och du ser priset direkt vid bokning. Vi kan ofta köra redan samma dag eller nästa dag, allt är försäkrat och du bidrar till att unga människor får arbetslivserfarenhet.",
      },
    ],

    priceHint: PACKAGE_PRICE_HINT_SV.bud,

    pricingRows: getPackagePricingRows("bud", "sv"),

    dynamicHeadings: {
      threeSteps: "Auktionsleverans i 3 enkla steg",
      step2Text:
        "Våra ungdomar hämtar ditt objekt på auktionshuset, skyddar det varsamt med filtar och bubbelplast och lastar skåpbilen. Sedan kör vi hem till dig.",
      step3Title: "Ditt fynd är hemma",
      step3Desc:
        "Ditt auktionsobjekt står hemma hos dig – varsamt inburet och på plats. Klart.",
      ctaHeading: "Boka auktionsleverans idag",
      ctaText:
        "Ange auktionshusets adress och din hemadress, beskriv objektet och välj en tid. Du ser priset direkt.",
    },

    en: {
      slug: "auction-delivery",

      meta: {
        title:
          "Auction Delivery – We Pick Up Your Auction Item & Deliver Home | Ungdomskraft",
        description:
          "Won an auction item? We pick up from the auction house and deliver home with careful carrying. Art, furniture, antiques – book auction delivery today.",
        h1: "Auction delivery",
      },

      hero: {
        heading: "We pick up your winning bid – Auction delivery",
        intro:
          "Congratulations on your winning bid! But how do you get that antique dresser, oil painting or crystal chandelier home? Pickup deadlines are tight and the items are heavy. Ungdomskraft picks up your auction item directly from the auction house, protects it during transport and delivers to your door – with careful carrying and placement inside. We pick up from all auction houses: PS Auktion, Bukowskis, Stockholms Auktionsverk, Uppsala Auktionskammare, Budi, Auctionet, Klaravik and more.",
      },

      featureCards: {
        sinceText: "Auction delivery since 2014",
        storyText:
          "Since 2014, Ungdomskraft has helped auction buyers get their finds home – from heavy antique furniture and delicate art to design classics and collectibles. Our young workers know how to handle valuable items with the extra care they deserve.",
        bookYourText: "Book your auction delivery",
        bookDescText:
          "Enter the auction house address and your home address, describe the item and choose a time. You see the price instantly.",
        vehicleTitleText: "Vans equipped for auction deliveries",
        vehicleDescText:
          "We bring the right van size – whether it's a small painting or a full set of furniture. Everything is protected with blankets, bubble wrap and straps.",
      },

      itemsSection: {
        heading: "Common auction items we deliver",
        items: [
          {
            name: "Antique furniture",
            description:
              "Dressers, cabinets, tables, chairs and other furniture from the 18th to 20th century",
            image: "/wardrobe.webp",
          },
          {
            name: "Art & paintings",
            description:
              "Oil paintings, lithographs, sculptures, frames and other art requiring careful handling",
            image: "/painting.webp",
          },
          {
            name: "Design furniture",
            description:
              "Armchairs, sofas, lamps and tables by renowned designers – Bruno Mathsson, Alvar Aalto, Arne Jacobsen",
            image: "/armchair.webp",
          },
          {
            name: "Rugs & textiles",
            description:
              "Persian rugs, oriental rugs, tapestries and other textile collectibles",
            image: "/bedding.webp",
          },
          {
            name: "Porcelain & glass",
            description:
              "Dinnerware sets, crystal, vases, figurines and other fragile collectibles",
            image: "/dishes.webp",
          },
          {
            name: "Other items",
            description:
              "Chandeliers, silverware, clocks, wine cabinets, artwork and other bulky auction finds",
            image: "/box.webp",
          },
        ],
      },

      seoContent: {
        mainHeading:
          "Auction delivery – we pick up, transport and carry in your auction find",
        mainText:
          "You placed the winning bid on a beautiful 19th-century dresser, an oil painting by a celebrated artist or a classic design armchair. The excitement after the hammer falls is wonderful – but then comes the practical question: how do I get this home? The pickup deadline is usually just three to five business days before storage fees start accumulating. Renting your own van and driving it yourself means risking damage to valuable items that have no original packaging. Ungdomskraft offers home delivery from all auction houses in Stockholm and Gothenburg. We pick up your winning item directly at the auction house, protect it carefully with blankets, bubble wrap and straps, and drive it home to you. We carry it in through stairwells, lifts or narrow garages and place it exactly where you want it. Carrying is always included – regardless of floor. You see the price when you book, no hidden fees. We work with all auction houses: PS Auktion, Bukowskis, Stockholms Auktionsverk, Uppsala Auktionskammare, Budi, Auctionet, Klaravik and all others – physical and online. Whether you bought at a major online auction or a local evening event, we pick up your item and deliver home. Every week we collect auction finds of all sizes – from a single painting to an entire estate lot with furniture, rugs and art. Our young workers are experienced with fragile and valuable items. We know that an antique dresser cannot be tipped on its side, that an oil painting must stand upright and be shielded from impacts, and that crystal chandeliers require extra care. We bring furniture blankets, bubble wrap, carrying straps and tie-down straps. Please note that we are responsible for the transport from the auction house to your home – the item's condition and authenticity are between you and the auction house.",
        preparationHeading: "Prepare for your auction delivery",
        preparationText:
          "Make sure the auction item is fully paid for and ready for pickup. Check the auction house's pickup deadline – most give three to five business days before storage fees begin. Have your customer number or lot number ready so our team can identify the right item at pickup. Let us know if the item is particularly fragile, heavy or unusually shaped – we will adapt protective materials and crew. Measure doorways and stairwells at home if the item is large. Check that the van can park close to your entrance. The more information you provide when booking, the smoother and safer the delivery.",
        additionalSections: [
          {
            heading: "Delivering antique furniture from auctions",
            content:
              "Antique furniture is the most common item we pick up from auction houses. A Gustavian dresser, an Art Nouveau cabinet or a large solid oak dining table – they often weigh 50–150 kg and come without packaging. Driving yourself with a rented trailer means serious risk of scratches, dents and moisture damage. With Ungdomskraft's auction delivery, we pick up your furniture directly at the auction house. We protect every surface with soft blankets, tape down loose parts like drawers and handles, and secure everything with straps in the van. We drive carefully, avoiding hard braking that could shift the load. At your home we carry in through stairwells and doors and place the piece exactly where you want it.",
          },
          {
            heading: "Delivering art and fragile items from auctions",
            content:
              "Art and fragile collectibles require extra care during transport. An oil painting with a gilded frame can weigh 20–40 kg and cannot tolerate impacts, moisture or pressure on the canvas. Bronze, marble or ceramic sculptures are heavy and sensitive to blows. Crystal, porcelain and glass can break with the slightest rough movement. We protect art with bubble wrap, corner guards and soft blankets. Paintings are always transported standing – never lying with weight on the frame. Sculptures are secured individually so they cannot shift during the drive. Porcelain and crystal are packed in boxes with shock-absorbing material. We carry in and place at your home – on the wall, on the pedestal or in the display cabinet.",
          },
          {
            heading: "Pickup deadlines and storage fees at auction houses",
            content:
              "Most auction houses give you three to five business days to collect your winning item after the invoice is paid. After that, storage fees start – often SEK 50–200 per day depending on the item's size. Some auction houses send items back to auction if not collected within the specified time. With Ungdomskraft you avoid the stress. Book your delivery as soon as you receive the pickup notification – we can often drive the same day or the next day. You enter the auction house address, your lot number and preferred time. We handle the pickup and deliver home before the deadline expires.",
          },
          {
            heading: "How auction delivery works with Ungdomskraft",
            listItems: [
              {
                label: "Book online",
                text: "Enter the auction house address, your home address and describe the item. Include the lot number if possible. You see the price instantly.",
              },
              {
                label: "We pick up at the auction house",
                text: "Our crew arrives with a van and the right protective materials. We identify your item, protect it carefully and load it.",
              },
              {
                label: "We deliver home",
                text: "We drive to your home, carry in through stairwells and doors and place your auction find where you want it.",
              },
              {
                label: "Done",
                text: "Your auction find is at home – carefully carried in and in place. Without risking a single scratch.",
              },
            ],
            afterContent:
              "The whole process usually takes 1–3 hours depending on distance and handling requirements. You can track the status in real time.",
          },
          {
            heading: "Which auction houses do we pick up from?",
            content:
              "We pick up from all auction houses in Stockholm and Gothenburg – both physical and online auctions with local pickup. The most common are PS Auktion, Bukowskis, Stockholms Auktionsverk, Uppsala Auktionskammare, Budi, Auctionet and Klaravik. But we pick up from any auction house – just enter the address when booking. We also pick up items from galleries, antique shops and private sales if you need transport of valuable objects.",
          },
          {
            heading: "Why book auction delivery with Ungdomskraft?",
            content:
              "What makes Ungdomskraft unique is that we employ young people and give them real work experience. When you book delivery of your auction find, you don't just get professional, careful handling – you also help young people enter the job market and build their futures.\n\nEvery job is staffed by motivated young workers who learn everything from customer service and logistics to careful handling of valuable objects. They learn to communicate with customers, plan routes, protect delicate surfaces and work efficiently as a team. This is work experience you can't get in a classroom – and every auction delivery we make gives a young person the chance to grow.\n\nWe have been operating since 2014 and have completed tens of thousands of transports in Stockholm and Gothenburg. Our young workers have carried in everything from heavy oak cabinets and fragile oil paintings to luxury furniture and antiques worth hundreds of thousands of kronor. We know what it takes: the right protective materials, careful loading, smooth driving and gentle carrying.\n\nQuality runs through everything we do. Our workers don't take shortcuts – they add extra blankets, double-check the straps and communicate with you throughout the job. The energy and commitment that young workers bring shows in every delivery. They want to prove themselves and they take the job seriously.\n\nSustainability is another cornerstone. We optimise routes to minimise emissions, use reusable protective materials and encourage customers to give items new life through auction purchases instead of buying new. Every auction item that finds a new home is a step toward a more circular economy – and we are proud to be part of that chain.\n\nFor businesses buying at auction – art for the office, furniture for the lobby or design pieces for the showroom – we also offer invoicing and documentation for CSR reporting. You can show that your transport was carried out by a company that employs young people and contributes to social sustainability.\n\nWe offer fast pickup, carrying to the right spot and a competitive price with no hidden fees. You see the price when you book, everything is insured and we have our own vans in several sizes.\n\nIt is careful, professional help with purpose.\n\nAuction delivery does not qualify for the Swedish RUT tax deduction, but several of our other services do – including moving help, carrying help and furniture assembly with a 50% deduction on the labour cost. Feel free to combine services in a single booking.",
          },
        ],
      },

      faq: [
        {
          question: "How much does auction delivery cost?",
          answer:
            "The price depends on the item's size and distance. A painting or smaller item starts from SEK 595. Larger furniture starts from SEK 995–1,699. You see the exact price when you book online.",
        },
        {
          question: "Can I get a RUT tax deduction on auction delivery?",
          answer:
            "Auction delivery does not in itself qualify for the Swedish RUT tax deduction. RUT applies to household services carried out in your home, such as moving help, carrying help and furniture assembly. Ungdomskraft offers several RUT-eligible services with a 50% deduction on the labour cost – easily combine them in one booking via our website.",
        },
        {
          question: "Is a van included in the auction delivery?",
          answer:
            "Yes, a van is always included. We choose the right size based on what needs to be transported – you don't need to rent a vehicle or drive yourself.",
        },
        {
          question: "Is carrying included in the auction delivery price?",
          answer:
            "Yes, carrying is always included. We carry your auction item through stairwells, lifts and doors and place it where you want it – regardless of floor. No extra fees per floor.",
        },
        {
          question: "How do you protect fragile auction items during transport?",
          answer:
            "We use furniture blankets, bubble wrap, corner guards and straps. Paintings are transported standing, sculptures are secured individually and porcelain is packed with shock-absorbing material. Everything is insured – if something is damaged we replace it.",
        },
        {
          question: "Which auction houses do you pick up from?",
          answer:
            "We pick up from all auction houses in Stockholm and Gothenburg: PS Auktion, Bukowskis, Stockholms Auktionsverk, Uppsala Auktionskammare, Budi, Auctionet, Klaravik and all others. Just enter the auction house address when booking.",
        },
        {
          question: "Can you carry heavy antique furniture from auctions?",
          answer:
            "Yes, our workers are experienced with heavy items – dressers, cabinets, dining tables and other furniture weighing 50–150 kg. We protect all surfaces with blankets and carry carefully through stairwells and narrow doorways.",
        },
        {
          question: "How quickly can you pick up from the auction house?",
          answer:
            "We can often pick up the same day or next day. Book as soon as you receive the pickup notification from the auction house – it helps you avoid storage fees.",
        },
        {
          question: "Are you responsible for the auction item's condition?",
          answer:
            "We are responsible for transport from the auction house to your home – the item is insured against transport damage. The item's condition, authenticity and any pre-existing issues are between you and the auction house. We recommend inspecting the item before we load it.",
        },
        {
          question: "Do I need to be at the auction house for pickup?",
          answer:
            "No, you don't need to be there. Make sure the item is fully paid for and ready for collection. Provide your customer number or lot number when booking so we can identify the right item. You just need to be home to receive the delivery.",
        },
        {
          question: "Do you offer auction delivery on weekends?",
          answer:
            "Yes, we offer home delivery on weekends and some evenings. Check that the auction house is open for pickup at the time you book.",
        },
        {
          question: "Can you pick up multiple items from the same auction?",
          answer:
            "Yes, we pick up all your winning items in one trip. Describe all items when booking – we adjust the van size and protective materials for the entire load.",
        },
        {
          question: "What happens if I miss the auction house pickup deadline?",
          answer:
            "Most auction houses start charging storage fees after three to five business days – often SEK 50–200 per day. Book your delivery as soon as you receive the pickup notification to avoid extra costs.",
        },
        {
          question: "Is the auction delivery insured?",
          answer:
            "Yes, all our transports are fully insured. If your auction item is damaged during loading, transport or carrying, we will compensate you. You can book with confidence.",
        },
        {
          question:
            "In which municipalities do you offer auction delivery?",
          answer:
            "We offer auction delivery across the entire Stockholm area: Botkyrka, Danderyd, Ekerö, Haninge, Huddinge, Järfälla, Lidingö, Nacka, Norrtälje, Nykvarn, Nynäshamn, Salem, Sigtuna, Sollentuna, Solna, Stockholm, Sundbyberg, Södertälje, Tyresö, Täby, Upplands Väsby, Upplands-Bro, Vallentuna, Vaxholm, Värmdö and Österåker. We also serve the Gothenburg area: Ale, Alingsås, Gothenburg, Härryda, Kungälv, Kungsbacka, Lerum, Lilla Edet, Mölndal, Partille, Stenungsund, Tjörn and Öckerö.",
        },
      ],

      priceHint: PACKAGE_PRICE_HINT_EN.bud,

      pricingRows: getPackagePricingRows("bud", "en"),

      dynamicHeadings: {
        threeSteps: "Auction delivery in 3 easy steps",
        step2Text:
          "Our crew picks up your item at the auction house, protects it carefully with blankets and bubble wrap and loads the van. Then we drive home to you.",
        step3Title: "Your find is home",
        step3Desc:
          "Your auction item is at home – carefully carried in and in place. Done.",
        ctaHeading: "Book auction delivery today",
        ctaText:
          "Enter the auction house address and your home address, describe the item and choose a time. You see the price instantly.",
      },
    },
  },

  // ────────────────────────────────────────────────────────────────────────────
  // HEMLEVERANS PS AUKTION
  // Vinkel: Kunden har vunnit ett objekt på PS Auktion (nätauktion sedan 1958,
  // konkurs- & överskottsauktioner). Brett sortiment: möbler, elektronik,
  // restaurangutrustning, verktyg, bygg, fordon. Upphämtning krävs ofta.
  // ────────────────────────────────────────────────────────────────────────────
  {
    slug: "hemleverans-ps-auktion",
    serviceCategory: "hemleverans",

    meta: {
      title:
        "Hemleverans PS Auktion – Vi Hämtar Ditt Vunna Objekt | Ungdomskraft",
      description:
        "Vunnit något på PS Auktion? Vi hämtar ditt objekt och levererar hem med skåpbil och inbärning. Möbler, elektronik, maskiner – boka hemleverans PS Auktion idag.",
      h1: "Hemleverans PS Auktion",
    },

    hero: {
      heading: "Vi hämtar ditt vunna objekt – Hemleverans PS Auktion",
      intro:
        "Vunnit en auktion på PS Auktion men saknar transport hem? PS Auktion säljer allt från möbler och elektronik till industrimaskiner och restaurangutrustning – och de flesta objekt kräver egen upphämtning. Vi löser det. Ungdomskraft hämtar ditt PS Auktion-köp på angiven upphämtningsplats, lastar skåpbilen och levererar hem till dig – med inbärning och uppställning på plats. Vi hämtar från alla PS Auktions upphämtningsställen i Stockholm och Göteborg.",
      heroImage: "/hero-hemleverans.png",
    },

    featureCards: {
      sinceText: "PS Auktion-leverans sedan 2014",
      storyText:
        "Sedan 2014 har Ungdomskraft hjälpt auktionsköpare att få hem sina fynd från PS Auktion – från tunga kontorsmöbler och vitvaror till byggmaskiner och restaurangutrustning. Våra ungdomar är vana vid att hantera det breda sortiment som PS Auktion erbjuder.",
      bookYourText: "Boka din PS Auktion-leverans",
      bookDescText:
        "Ange upphämtningsadressen och din hemadress, beskriv objektet och välj en tid. Du ser priset direkt.",
      vehicleTitleText: "Skåpbilar för alla typer av PS Auktion-köp",
      vehicleDescText:
        "Vi kommer med rätt storlek på skåpbil – oavsett om det gäller en kontorsstol, en restaurangugn eller ett helt möblemang. Du slipper hyra bil, köra själv eller organisera hjälp.",
    },

    itemsSection: {
      heading: "Vanliga objekt vi hämtar från PS Auktion",
      items: [
        {
          name: "Möbler & inredning",
          description:
            "Kontorsmöbler, hemma-möbler, bokhyllor, skrivbord, stolar, soffor och förvaring från konkurs- och överskottsauktioner",
          image: "/sofa.webp",
        },
        {
          name: "Elektronik & datorer",
          description:
            "Datorer, bildskärmar, servrar, nätverksutrustning, kameror och annan elektronik",
          image: "/tv.webp",
        },
        {
          name: "Vitvaror & kök",
          description:
            "Kylskåp, frysar, tvättmaskiner, diskmaskiner, spisar och restaurangutrustning",
          image: "/washer.webp",
        },
        {
          name: "Verktyg & maskiner",
          description:
            "Elverktyg, kompressorer, svetsutrustning, mätinstrument och industriella maskiner",
          image: "/toolbox.webp",
        },
        {
          name: "Trädgård & utemiljö",
          description:
            "Trädgårdsmaskiner, gräsklippare, utemöbler, grillar och odlingsutrustning",
          image: "/plant.webp",
        },
        {
          name: "Övrigt skrymmande",
          description:
            "Gymutrustning, kontorsinredning, butiksinredning, belysning och andra större auktionsfynd",
          image: "/box.webp",
        },
      ],
    },

    seoContent: {
      mainHeading:
        "Hemleverans från PS Auktion – vi hämtar, kör och bär in ditt auktionsfynd",
      mainText:
        "PS Auktion är ett av Sveriges största nätauktionshus med rötter sedan 1958. Varje dag avslutas hundratals auktioner med allt från kontorsmöbler och hemelektronik till industrimaskiner, restaurangutrustning och byggmaterial. Priserna är ofta en bråkdel av nypriset – men den stora frågan för många köpare är: hur får jag hem det här? De flesta objekt på PS Auktion kräver egen upphämtning. Objektet står kvar på upphämtningsplatsen – ofta ett lager, en konkursdrabbad lokal eller en industrilokal – och du har normalt fem till sju arbetsdagar på dig att hämta innan lagringsavgift börjar. Att hyra egen skåpbil och köra dit tar tid, kräver rätt fordon och ibland extra muskelkraft. Många objekt väger 50–200 kg och ryms inte i en vanlig personbil. Ungdomskraft erbjuder hemleverans av allt du vinner på PS Auktion i Stockholm och Göteborg. Vi hämtar ditt objekt på angiven upphämtningsplats, lastar skåpbilen och kör hem till dig. Vi bär in genom trapphus, hiss eller garage och ställer på plats. Inbärning ingår alltid – du betalar ett pris som du ser direkt vid bokning. Vi är vana vid det breda sortiment som PS Auktion erbjuder. Våra ungdomar har hämtat allt från kompletta kontorsinredningar och restaurangkök till servrar, verktygsskåp, vitvaror och hemmamöbler. Vi vet att en restaurangugn kan väga 150 kg och att ett pallställ kräver lång lastyta. Vi anpassar skåpbilsstorlek och bemanning efter objektet. Tänk på att vi ansvarar för transporten från upphämtningsplatsen hem till dig – objektets skick och funktion är mellan dig och PS Auktion. Vi rekommenderar att du inspekterar objektet vid upphämtningen eller att du kollar bilderna noga på psauction.com innan du bokar.",
      preparationHeading: "Förbered inför din PS Auktion-leverans",
      preparationText:
        "Se till att objektet är fullt betalt på PS Auktion och att du fått upphämtningsinformation – adress, kontaktperson och öppettider. Ha ditt auktionsnummer eller kundnummer redo. Kontrollera hämtfristen – PS Auktion ger normalt fem till sju arbetsdagar innan lagringsavgift tillkommer. Om objektet är tungt, skrymmande eller ovanligt format – beskriv det tydligt vid bokning. Mät dörröppningar och trapphus hemma hos dig om det rör sig om stora möbler eller maskiner. Kolla att skåpbilen kan parkera nära upphämtningsplatsen och nära din port hemma.",
      additionalSections: [
        {
          heading: "Hemleverans av möbler från PS Auktion",
          content:
            "Möbler och kontorsinredning är bland de vanligaste kategorierna på PS Auktion. Du kan hitta allt från ett enstaka skrivbord och kontorsstol till kompletta kontorsmöblemang från konkursbon – skrivbord, arkivskåp, konferensbord, hyllsystem och förvaringsskåp. Hemma-möbler som soffor, sängar, matbord och bokhyllor dyker också upp regelbundet till bra priser. Problemet är att möblerna ofta står i en lagerlokal eller i den gamla kontorslokalen – ibland utanför stan – och kräver skåpbil och minst två personer att bära. Med Ungdomskraft hämtar vi dina möbler direkt på upphämtningsplatsen. Vi skyddar med filtar, säkrar med spännband och kör hem till dig. Vi bär in genom trapphus och dörrar och ställer på plats. Har du vunnit flera möbler i samma auktion hämtar vi allt i en tur och anpassar skåpbilsstorleken efter lasten.",
        },
        {
          heading: "Hemleverans av elektronik och vitvaror från PS Auktion",
          content:
            "PS Auktion säljer regelbundet elektronik och vitvaror från konkurser och överskottslager – datorer, bildskärmar, servrar, kylskåp, tvättmaskiner, diskmaskiner och restaurangutrustning som storkök, ugnar och kylbänkar. Många av dessa objekt väger 50–100 kg och kräver varsam hantering. Elektronik är känslig för stötar och fukt, och restaurangutrustning i rostfritt stål kan vara otymplig att bära genom trånga dörröppningar. Vi hämtar ditt PS Auktion-köp, skyddar med filtar och bubbelplast och lastar varsamt i skåpbilen. Vi bär in till rätt plats hemma hos dig eller i din verksamhet. Tänk på att vi ansvarar för transporten – att objektet fungerar och är i det skick som beskrivits på PS Auktion är mellan dig och säljaren.",
        },
        {
          heading: "Hemleverans av verktyg och maskiner från PS Auktion",
          content:
            "PS Auktions kategori Verkstad & Industri innehåller allt från handverktyg och elverktyg till kompressorer, svetsar, svarvar och CNC-maskiner. Även byggmaskiner, truckar och mindre fordon auktioneras ut. Många av dessa objekt är tunga – en industriell kompressor kan väga 100–300 kg – och kräver rätt fordon och bemanning. Vi hämtar verktyg och maskiner på upphämtningsplatsen, lastar med rätt utrustning och kör hem eller till din verkstad. För extremt tunga maskiner behöver vi veta vikten i förväg så att vi kan välja rätt skåpbil och eventuellt extra bemanning. Beskriv objektet så detaljerat som möjligt vid bokning – vikt, mått och om det står på pall eller har hjul.",
        },
        {
          heading: "Så fungerar PS Auktion-leverans med Ungdomskraft",
          listItems: [
            {
              label: "Boka online",
              text: "Ange upphämtningsadressen (från PS Auktions hämtinformation), din hemadress och beskriv objektet. Du ser priset direkt.",
            },
            {
              label: "Vi hämtar på upphämtningsplatsen",
              text: "Vårt team kommer med skåpbil. Vi identifierar ditt objekt, skyddar och lastar. Vi har auktionsnumret redo.",
            },
            {
              label: "Vi levererar hem",
              text: "Vi kör hem till dig, bär in genom trapphus och dörrar och ställer ditt PS Auktion-köp på plats.",
            },
            {
              label: "Klart",
              text: "Ditt auktionsfynd från PS Auktion står hemma hos dig – inburet och på plats. Du sparade både tid och rygg.",
            },
          ],
          afterContent:
            "Hela processen tar normalt 1–3 timmar beroende på avstånd och objektets storlek. Du kan följa statusen i realtid.",
        },
        {
          heading: "PS Auktions hämtfrister och lagringsavgifter",
          content:
            "PS Auktion ger normalt fem till sju arbetsdagar att hämta ditt vunna objekt efter att fakturan betalats. Efter det kan lagringsavgifter tillkomma, och i värsta fall kan objektet säljas på nytt. Boka din hemleverans med Ungdomskraft så snart du fått hämtbesked – vi kan ofta köra redan samma dag eller nästa dag. Du anger upphämtningsadressen, ditt auktionsnummer och önskad tid. Vi sköter resten och ser till att ditt objekt är hemma hos dig i god tid innan fristen löper ut.",
        },
        {
          heading: "Varför boka PS Auktion-leverans med Ungdomskraft?",
          content:
            "Det som gör Ungdomskraft unikt är att vi anställer unga människor och ger dem riktig arbetslivserfarenhet. När du bokar hemleverans av ditt PS Auktion-köp får du inte bara professionell hantering – du bidrar också till att unga människor kommer in på arbetsmarknaden och bygger sin framtid.\n\nVarje uppdrag bemannas av motiverade ungdomar som lär sig allt från kundservice och logistik till fysiskt arbete och ansvarsfullt miljötänk. De lär sig att kommunicera med kunder, planera rutter, hantera tunga och skrymmande objekt och arbeta effektivt i team. Det är arbetslivserfarenhet som inte går att få i ett klassrum – och varje leverans vi kör ger en ung person möjlighet att utvecklas.\n\nVi har funnits sedan 2014 och har genomfört tiotusentals transporter i Stockholm och Göteborg. Våra ungdomar har hämtat allt från enstaka kontorsstolar till hela kontorsinredningar, restaurangkök, verktygsskåp och tunga industrimaskiner. Vi vet vad som krävs: rätt skåpbilsstorlek, tillräcklig bemanning, skyddsmaterial och en plan för att navigera trånga trapphus och dörröppningar.\n\nKvalitet genomsyrar allt vi gör. Våra ungdomar tar inga genvägar – de lägger extra filtar, kontrollerar spännband och kommunicerar med dig under hela uppdraget. Den energi och det engagemang som unga medarbetare har märks i varje leverans. De vill visa vad de kan och de tar uppdraget på allvar.\n\nHållbarhet är en annan grundpelare. PS Auktion bidrar till cirkulär ekonomi genom att ge överskott och konkursföremål nytt liv – och vi är stolta över att vara en del av den kedjan genom att transportera föremålen hem till sin nya ägare. Vi optimerar rutter för att minimera utsläpp och använder återanvändbara skyddsmaterial.\n\nFör företag som köper på PS Auktion – kontorsmöbler, restaurangutrustning eller verktyg – erbjuder vi faktura och underlag för CSR-rapportering. Du kan visa att din transport genomfördes av ett företag som sysselsätter ungdomar och bidrar till social hållbarhet.\n\nJämfört med att hyra egen skåpbil och organisera hjälp sparar du tid, slipper slitage och får allt levererat med professionell hantering. Du ser priset direkt vid bokning, allt är försäkrat och vi har egna skåpbilar i flera storlekar.\n\nDet är smidig, pålitlig hjälp med mening.\n\nHemleverans från PS Auktion ger inte rätt till RUT-avdrag, men flera av våra andra tjänster gör det – exempelvis flytt, bärhjälp och möbelmontering med 50 % avdrag på arbetskostnaden. Kombinera gärna tjänster i en och samma bokning.",
        },
      ],
    },

    faq: [
      {
        question: "Vad kostar hemleverans från PS Auktion?",
        answer:
          "Priset beror på objektets storlek, vikt och avstånd. En kontorsstol eller mindre elektronik kostar från 595 kr. Större möbler eller maskiner kostar från 995–2 495 kr. Du ser det exakta priset direkt när du bokar online.",
      },
      {
        question: "Kan jag få RUT-avdrag på hemleverans från PS Auktion?",
        answer:
          "Hemleverans från PS Auktion ger i sig inte rätt till RUT-avdrag. RUT gäller hushållsnära tjänster som utförs i din bostad, exempelvis flytt, bärhjälp och möbelmontering. Ungdomskraft erbjuder flera RUT-berättigade tjänster med 50 % avdrag på arbetskostnaden – kombinera enkelt i en bokning via vår hemsida.",
      },
      {
        question: "Ingår skåpbil i PS Auktion-leveransen?",
        answer:
          "Ja, skåpbil ingår alltid. Vi väljer rätt storlek utifrån vad som ska transporteras – du behöver inte hyra bil eller köra själv.",
      },
      {
        question: "Ingår inbärning i priset för PS Auktion-leverans?",
        answer:
          "Ja, inbärning ingår alltid. Vi bär in ditt objekt genom trapphus, hiss och dörrar och ställer på plats – oavsett våning. Inga extra avgifter per våningsplan.",
      },
      {
        question: "Kan ni hämta tunga maskiner och utrustning från PS Auktion?",
        answer:
          "Ja, vi hämtar objekt upp till flera hundra kilo. Beskriv objektets vikt och mått vid bokning så anpassar vi skåpbilsstorlek och bemanning. För extremt tunga objekt kan vi behöva extra personal.",
      },
      {
        question: "Hur fungerar upphämtning från PS Auktion?",
        answer:
          "PS Auktion anger en upphämtningsadress, kontaktperson och öppettider för varje objekt. Du anger dessa uppgifter vid bokning – vi kör dit, identifierar objektet med auktionsnumret, lastar och kör hem till dig.",
      },
      {
        question: "Hur snabbt kan ni hämta från PS Auktion?",
        answer:
          "Vi kan ofta hämta redan samma dag eller nästa dag. Boka så snart du fått hämtinformation från PS Auktion – det hjälper dig att undvika lagringsavgifter och missa hämtfristen.",
      },
      {
        question: "Tar ni ansvar för objektets skick från PS Auktion?",
        answer:
          "Vi ansvarar för transporten – objektet är försäkrat mot transportskador. Objektets skick, funktion och överensstämmelse med auktionsbeskrivningen är mellan dig och PS Auktion. Vi rekommenderar att du inspekterar objektet vid upphämtningen.",
      },
      {
        question: "Behöver jag vara med vid upphämtningen på PS Auktion?",
        answer:
          "Nej, du behöver inte vara med. Se till att objektet är betalt och att vi har rätt auktionsnummer, upphämtningsadress och kontaktuppgifter. Du behöver bara vara hemma för att ta emot leveransen.",
      },
      {
        question: "Kan ni hämta flera objekt från samma PS Auktion-auktion?",
        answer:
          "Ja, vi hämtar alla dina vunna objekt i en tur om de finns på samma upphämtningsplats. Beskriv samtliga objekt vid bokning – vi anpassar skåpbilsstorlek och bemanning efter hela lasten.",
      },
      {
        question: "Erbjuder ni PS Auktion-leverans på helger?",
        answer:
          "Ja, vi erbjuder hemleverans på helger och vissa kvällar. Kontrollera att upphämtningsplatsen är öppen den tid du bokar – öppettiderna finns i PS Auktions hämtinformation.",
      },
      {
        question: "Kan ni leverera PS Auktion-köp till mitt företag?",
        answer:
          "Ja, vi levererar till både privatpersoner och företag. Vi erbjuder faktura för företagskunder och kan leverera till kontor, verkstad, restaurang eller lager. Ange leveransadressen vid bokning.",
      },
      {
        question: "Vad händer om jag missar hämtfristen på PS Auktion?",
        answer:
          "PS Auktion ger normalt fem till sju arbetsdagar att hämta. Efter det kan lagringsavgifter tillkomma och i värsta fall säljs objektet på nytt. Boka din hemleverans så snart du fått hämtbesked.",
      },
      {
        question: "Skyddar ni objektet under transporten?",
        answer:
          "Ja, vi använder möbelfiltar, bubbelplast, spännband och hörnskydd. Vi anpassar skyddet efter objektets typ – ömtålig elektronik, tunga möbler och känsliga ytor hanteras med extra omsorg. Allt är försäkrat.",
      },
      {
        question: "Är PS Auktion-leveransen försäkrad?",
        answer:
          "Ja, alla våra transporter är helförsäkrade. Om ditt objekt skadas under lastning, transport eller inbärning ersätter vi det. Du kan boka med trygghet.",
      },
      {
        question:
          "I vilka kommuner erbjuder ni hemleverans från PS Auktion?",
        answer:
          "Vi erbjuder hemleverans från PS Auktion i hela Stockholmsområdet: Botkyrka, Danderyd, Ekerö, Haninge, Huddinge, Järfälla, Lidingö, Nacka, Norrtälje, Nykvarn, Nynäshamn, Salem, Sigtuna, Sollentuna, Solna, Stockholm, Sundbyberg, Södertälje, Tyresö, Täby, Upplands Väsby, Upplands-Bro, Vallentuna, Vaxholm, Värmdö och Österåker. Vi finns även i Göteborgsområdet: Ale, Alingsås, Göteborg, Härryda, Kungälv, Kungsbacka, Lerum, Lilla Edet, Mölndal, Partille, Stenungsund, Tjörn och Öckerö.",
      },
      {
        question: "Är det billigare än att hyra egen skåpbil?",
        answer:
          "Ofta ja – när du räknar in hyreskostnad, bensin, tid och risken att skada objektet. Hos oss ingår skåpbil, bärhjälp och inbärning till rätt plats. Du ser priset direkt vid bokning utan dolda avgifter.",
      },
    ],

    priceHint: PACKAGE_PRICE_HINT_SV.bud,

    pricingRows: getPackagePricingRows("bud", "sv"),

    dynamicHeadings: {
      threeSteps: "PS Auktion-leverans i 3 enkla steg",
      step2Text:
        "Våra ungdomar hämtar ditt objekt på PS Auktions upphämtningsplats, skyddar med filtar och lastar skåpbilen. Sedan kör vi hem till dig.",
      step3Title: "Ditt fynd är hemma",
      step3Desc:
        "Ditt PS Auktion-köp står hemma hos dig – inburet och på plats. Klart.",
      ctaHeading: "Boka PS Auktion-leverans idag",
      ctaText:
        "Ange upphämtningsadressen och din hemadress, beskriv objektet och välj en tid. Du ser priset direkt.",
    },

    en: {
      slug: "ps-auction-delivery",

      meta: {
        title:
          "PS Auction Delivery – We Pick Up Your Winning Item & Deliver Home | Ungdomskraft",
        description:
          "Won something on PS Auction? We pick up your item and deliver home with van and carrying. Furniture, electronics, machinery – book PS Auction delivery today.",
        h1: "PS Auction delivery",
      },

      hero: {
        heading: "We pick up your winning item – PS Auction delivery",
        intro:
          "Won an auction on PS Auction but lack the transport to get it home? PS Auction sells everything from furniture and electronics to industrial machinery and restaurant equipment – and most items require your own pickup. Ungdomskraft picks up your PS Auction purchase at the designated collection point, loads the van and delivers to your door – with carrying and placement inside. We collect from all PS Auction pickup locations in Stockholm and Gothenburg.",
      },

      featureCards: {
        sinceText: "PS Auction delivery since 2014",
        storyText:
          "Since 2014, Ungdomskraft has helped auction buyers get their PS Auction finds home – from heavy office furniture and appliances to construction machinery and restaurant equipment. Our young workers are experienced with the wide range of items PS Auction offers.",
        bookYourText: "Book your PS Auction delivery",
        bookDescText:
          "Enter the pickup address and your home address, describe the item and choose a time. You see the price instantly.",
        vehicleTitleText: "Vans for every type of PS Auction purchase",
        vehicleDescText:
          "We bring the right van size – whether it's an office chair, a commercial oven or a full furniture set. You don't need to rent a vehicle, drive yourself or organise helpers.",
      },

      itemsSection: {
        heading: "Common items we collect from PS Auction",
        items: [
          {
            name: "Furniture & interiors",
            description:
              "Office furniture, home furniture, bookcases, desks, chairs, sofas and storage from bankruptcy and surplus auctions",
            image: "/sofa.webp",
          },
          {
            name: "Electronics & computers",
            description:
              "Computers, monitors, servers, networking equipment, cameras and other electronics",
            image: "/tv.webp",
          },
          {
            name: "Appliances & kitchen",
            description:
              "Fridges, freezers, washing machines, dishwashers, cookers and commercial kitchen equipment",
            image: "/washer.webp",
          },
          {
            name: "Tools & machinery",
            description:
              "Power tools, compressors, welding equipment, measuring instruments and industrial machines",
            image: "/toolbox.webp",
          },
          {
            name: "Garden & outdoor",
            description:
              "Garden machines, lawnmowers, outdoor furniture, barbecues and growing equipment",
            image: "/plant.webp",
          },
          {
            name: "Other bulky items",
            description:
              "Gym equipment, office interiors, shop fittings, lighting and other large auction finds",
            image: "/box.webp",
          },
        ],
      },

      seoContent: {
        mainHeading:
          "PS Auction delivery – we pick up, transport and carry in your auction find",
        mainText:
          "PS Auction is one of Sweden's largest online auction houses, with roots going back to 1958. Every day hundreds of auctions close with everything from office furniture and home electronics to industrial machinery, restaurant equipment and building materials. Prices are often a fraction of the retail cost – but the big question for many buyers is: how do I get this home? Most items on PS Auction require your own pickup. The item remains at the collection point – often a warehouse, a bankrupt business premises or an industrial facility – and you typically have five to seven business days to collect before storage fees start. Renting your own van and driving there takes time, requires the right vehicle and sometimes extra muscle. Many items weigh 50–200 kg and won't fit in a regular car. Ungdomskraft offers home delivery of everything you win on PS Auction in Stockholm and Gothenburg. We pick up your item at the designated collection point, load the van and drive it home to you. We carry in through stairwells, lifts or garages and place it where you want it. Carrying is always included – you pay one price that you see when you book. We are experienced with the wide range PS Auction offers. Our young workers have collected everything from complete office interiors and commercial kitchens to servers, tool cabinets, appliances and home furniture. We know that a commercial oven can weigh 150 kg and that pallet racking requires long cargo space. We match van size and crew to the item. Please note that we are responsible for the transport from the collection point to your home – the item's condition and function are between you and PS Auction.",
        preparationHeading: "Prepare for your PS Auction delivery",
        preparationText:
          "Make sure the item is fully paid for on PS Auction and that you have received the pickup information – address, contact person and opening hours. Have your auction number or customer number ready. Check the pickup deadline – PS Auction typically gives five to seven business days before storage fees apply. If the item is heavy, bulky or unusually shaped, describe it clearly when booking. Measure doorways and stairwells at home if the item is large. Check that the van can park close to the collection point and close to your entrance at home.",
        additionalSections: [
          {
            heading: "Delivering furniture from PS Auction",
            content:
              "Furniture and office interiors are among the most common categories on PS Auction. You can find everything from a single desk and office chair to complete office furniture sets from bankrupt businesses – desks, filing cabinets, conference tables, shelving systems and storage cupboards. Home furniture such as sofas, beds, dining tables and bookcases also appear regularly at good prices. The challenge is that the furniture is often stored in a warehouse or the old business premises – sometimes outside the city – and requires a van and at least two people to carry. With Ungdomskraft, we pick up your furniture directly at the collection point, protect it with blankets, secure with straps and drive home to you. We carry in and place where you want it.",
          },
          {
            heading: "Delivering electronics and appliances from PS Auction",
            content:
              "PS Auction regularly sells electronics and appliances from bankruptcies and surplus stock – computers, monitors, servers, fridges, washing machines, dishwashers and commercial kitchen equipment such as ovens, cooler counters and display fridges. Many of these items weigh 50–100 kg and require careful handling. Electronics are sensitive to impacts and moisture, and stainless steel restaurant equipment can be awkward to carry through narrow doorways. We pick up your PS Auction purchase, protect it with blankets and bubble wrap and load carefully into the van. We carry in to the right spot in your home or business. Remember that we are responsible for the transport – whether the item works and matches the auction description is between you and PS Auction.",
          },
          {
            heading: "Delivering tools and machinery from PS Auction",
            content:
              "PS Auction's Workshop & Industry category includes everything from hand tools and power tools to compressors, welders, lathes and CNC machines. Construction machines, forklifts and smaller vehicles are also auctioned off. Many of these items are heavy – an industrial compressor can weigh 100–300 kg – and require the right vehicle and crew. We pick up tools and machines at the collection point, load with the right equipment and deliver to your home or workshop. For extremely heavy machines, we need to know the weight in advance so we can choose the right van and possibly extra crew. Describe the item as thoroughly as possible when booking – weight, dimensions and whether it's on a pallet or has wheels.",
          },
          {
            heading: "How PS Auction delivery works with Ungdomskraft",
            listItems: [
              {
                label: "Book online",
                text: "Enter the pickup address (from PS Auction's collection information), your home address and describe the item. You see the price instantly.",
              },
              {
                label: "We collect at the pickup point",
                text: "Our crew arrives with a van. We identify your item using the auction number, protect and load it.",
              },
              {
                label: "We deliver home",
                text: "We drive to your home, carry in through stairwells and doors and place your PS Auction purchase where you want it.",
              },
              {
                label: "Done",
                text: "Your PS Auction find is at home – carried in and in place. You saved both time and your back.",
              },
            ],
            afterContent:
              "The whole process usually takes 1–3 hours depending on distance and item size. You can track the status in real time.",
          },
          {
            heading: "PS Auction pickup deadlines and storage fees",
            content:
              "PS Auction typically gives five to seven business days to collect your winning item after the invoice is paid. After that, storage fees may apply, and in the worst case the item may be resold. Book your delivery with Ungdomskraft as soon as you receive the pickup notification – we can often drive the same day or next day. You enter the pickup address, your auction number and preferred time. We handle the rest and make sure your item is home well before the deadline.",
          },
          {
            heading: "Why book PS Auction delivery with Ungdomskraft?",
            content:
              "What makes Ungdomskraft unique is that we employ young people and give them real work experience. When you book delivery of your PS Auction purchase, you don't just get professional handling – you also help young people enter the job market and build their futures.\n\nEvery job is staffed by motivated young workers who learn everything from customer service and logistics to physical work and environmental responsibility. They learn to communicate with customers, plan routes, handle heavy and bulky items and work efficiently as a team. This is work experience you can't get in a classroom – and every delivery we make gives a young person the chance to grow.\n\nWe have been operating since 2014 and have completed tens of thousands of transports in Stockholm and Gothenburg. Our young workers have collected everything from single office chairs to complete office interiors, commercial kitchens, tool cabinets and heavy industrial machines. We know what it takes: the right van size, sufficient crew, protective materials and a plan for navigating narrow stairwells and doorways.\n\nQuality runs through everything we do. Our workers don't take shortcuts – they add extra blankets, check the straps and communicate with you throughout the job. The energy and commitment that young workers bring shows in every delivery.\n\nSustainability is another cornerstone. PS Auction contributes to the circular economy by giving surplus and bankrupt items new life – and we are proud to be part of that chain by transporting items home to their new owners. We optimise routes to minimise emissions and use reusable protective materials.\n\nFor businesses buying on PS Auction – office furniture, restaurant equipment or tools – we also offer invoicing and documentation for CSR reporting. You can show that your transport was carried out by a company that employs young people and contributes to social sustainability.\n\nCompared to renting your own van and organising help, you save time, avoid wear and tear and get everything delivered with professional handling. You see the price when you book, everything is insured and we have our own vans in several sizes.\n\nIt is smooth, reliable help with purpose.\n\nPS Auction delivery does not qualify for the Swedish RUT tax deduction, but several of our other services do – including moving help, carrying help and furniture assembly with a 50% deduction on the labour cost. Feel free to combine services in a single booking.",
          },
        ],
      },

      faq: [
        {
          question: "How much does PS Auction delivery cost?",
          answer:
            "The price depends on the item's size, weight and distance. An office chair or smaller electronics starts from SEK 595. Larger furniture or machinery starts from SEK 995–1,699. You see the exact price when you book online.",
        },
        {
          question: "Can I get a RUT tax deduction on PS Auction delivery?",
          answer:
            "PS Auction delivery does not in itself qualify for the Swedish RUT tax deduction. RUT applies to household services carried out in your home, such as moving help, carrying help and furniture assembly. Ungdomskraft offers several RUT-eligible services with a 50% deduction on the labour cost – easily combine them in one booking via our website.",
        },
        {
          question: "Is a van included in the PS Auction delivery?",
          answer:
            "Yes, a van is always included. We choose the right size based on what needs to be transported – you don't need to rent a vehicle or drive yourself.",
        },
        {
          question: "Is carrying included in the PS Auction delivery price?",
          answer:
            "Yes, carrying is always included. We carry your item through stairwells, lifts and doors and place it where you want it – regardless of floor. No extra fees per floor.",
        },
        {
          question: "Can you pick up heavy machinery from PS Auction?",
          answer:
            "Yes, we pick up items weighing up to several hundred kilograms. Describe the item's weight and dimensions when booking so we can match van size and crew. For extremely heavy items we may need extra staff.",
        },
        {
          question: "How does pickup from PS Auction work?",
          answer:
            "PS Auction provides a pickup address, contact person and opening hours for each item. You enter these details when booking – we drive there, identify the item using the auction number, load and drive home to you.",
        },
        {
          question: "How quickly can you pick up from PS Auction?",
          answer:
            "We can often pick up the same day or next day. Book as soon as you receive the pickup information from PS Auction – it helps you avoid storage fees and missing the deadline.",
        },
        {
          question: "Are you responsible for the item's condition from PS Auction?",
          answer:
            "We are responsible for transport – the item is insured against transport damage. The item's condition, function and match with the auction description are between you and PS Auction. We recommend inspecting the item at pickup.",
        },
        {
          question: "Do I need to be at the PS Auction pickup point?",
          answer:
            "No, you don't need to be there. Make sure the item is paid for and that we have the correct auction number, pickup address and contact details. You just need to be home to receive the delivery.",
        },
        {
          question: "Can you pick up multiple items from the same PS Auction auction?",
          answer:
            "Yes, we pick up all your winning items in one trip if they are at the same collection point. Describe all items when booking – we adjust van size and crew for the entire load.",
        },
        {
          question: "Do you offer PS Auction delivery on weekends?",
          answer:
            "Yes, we offer delivery on weekends and some evenings. Check that the collection point is open at the time you book – opening hours are listed in PS Auction's pickup information.",
        },
        {
          question: "Can you deliver PS Auction purchases to my business?",
          answer:
            "Yes, we deliver to both private individuals and businesses. We offer invoicing for business customers and can deliver to offices, workshops, restaurants or warehouses. Enter the delivery address when booking.",
        },
        {
          question: "What happens if I miss the PS Auction pickup deadline?",
          answer:
            "PS Auction typically gives five to seven business days to collect. After that, storage fees may apply and in the worst case the item may be resold. Book your delivery as soon as you receive the pickup notification.",
        },
        {
          question: "Is the PS Auction delivery insured?",
          answer:
            "Yes, all our transports are fully insured. If your item is damaged during loading, transport or carrying, we will compensate you. You can book with confidence.",
        },
        {
          question:
            "In which municipalities do you offer PS Auction delivery?",
          answer:
            "We offer PS Auction delivery across the entire Stockholm area: Botkyrka, Danderyd, Ekerö, Haninge, Huddinge, Järfälla, Lidingö, Nacka, Norrtälje, Nykvarn, Nynäshamn, Salem, Sigtuna, Sollentuna, Solna, Stockholm, Sundbyberg, Södertälje, Tyresö, Täby, Upplands Väsby, Upplands-Bro, Vallentuna, Vaxholm, Värmdö and Österåker. We also serve the Gothenburg area: Ale, Alingsås, Gothenburg, Härryda, Kungälv, Kungsbacka, Lerum, Lilla Edet, Mölndal, Partille, Stenungsund, Tjörn and Öckerö.",
        },
      ],

      priceHint: PACKAGE_PRICE_HINT_EN.bud,

      pricingRows: getPackagePricingRows("bud", "en"),

      dynamicHeadings: {
        threeSteps: "PS Auction delivery in 3 easy steps",
        step2Text:
          "Our crew picks up your item at the PS Auction collection point, protects it with blankets and loads the van. Then we drive home to you.",
        step3Title: "Your find is home",
        step3Desc:
          "Your PS Auction purchase is at home – carried in and in place. Done.",
        ctaHeading: "Book PS Auction delivery today",
        ctaText:
          "Enter the pickup address and your home address, describe the item and choose a time. You see the price instantly.",
      },
    },
  },

  // ────────────────────────────────────────────────────────────────────────────
  // HEMLEVERANS BUDI
  // Vinkel: Kunden har vunnit ett objekt på Budi Auktioner (nätauktion från
  // Bromma, Stockholm). Konkurs-, överskotts- och returauktioner. Brett
  // sortiment: möbler, elektronik, restaurang, verktyg, sport/gym, bygg m.m.
  // ────────────────────────────────────────────────────────────────────────────
  {
    slug: "hemleverans-budi",
    serviceCategory: "hemleverans",

    meta: {
      title:
        "Hemleverans Budi – Vi Hämtar Ditt Auktionsfynd Och Kör Hem | Ungdomskraft",
      description:
        "Vunnit något på Budi Auktioner? Vi hämtar ditt objekt och levererar hem med skåpbil och inbärning. Möbler, elektronik, verktyg – boka hemleverans Budi idag.",
      h1: "Hemleverans Budi",
    },

    hero: {
      heading: "Vi hämtar ditt vunna objekt – Hemleverans Budi",
      intro:
        "Vunnit en auktion på Budi men saknar transport hem? Budi Auktioner säljer allt från möbler och elektronik till restaurangutrustning, gymmaskiner och byggmaterial – och de flesta objekt kräver egen upphämtning. Vi löser det. Ungdomskraft hämtar ditt Budi-köp på upphämtningsplatsen, lastar skåpbilen och levererar hem till dig – med inbärning och uppställning på plats.",
      heroImage: "/hero-hemleverans.png",
    },

    featureCards: {
      sinceText: "Budi-leverans sedan 2014",
      storyText:
        "Sedan 2014 har Ungdomskraft hjälpt auktionsköpare att få hem sina Budi-fynd – från tunga kontorsmöbler och vitvaror till gymutrustning och restaurangkök. Våra ungdomar är vana vid det breda sortiment som Budi erbjuder.",
      bookYourText: "Boka din Budi-leverans",
      bookDescText:
        "Ange upphämtningsadressen och din hemadress, beskriv objektet och välj en tid. Du ser priset direkt.",
      vehicleTitleText: "Skåpbilar för alla typer av Budi-köp",
      vehicleDescText:
        "Vi kommer med rätt storlek på skåpbil – oavsett om det gäller en kontorsstol, ett löpband eller en hel restauranginredning. Du slipper hyra bil eller organisera hjälp.",
    },

    itemsSection: {
      heading: "Vanliga objekt vi hämtar från Budi",
      items: [
        {
          name: "Möbler & inredning",
          description:
            "Kontorsmöbler, hemmamöbler, soffor, bord, stolar, bokhyllor och förvaring från konkurs- och överskottsauktioner",
          image: "/sofa.webp",
        },
        {
          name: "Elektronik & IT",
          description:
            "Datorer, bildskärmar, servrar, skrivare, kameror, ljud- och musikutrustning",
          image: "/tv.webp",
        },
        {
          name: "Vitvaror & restaurang",
          description:
            "Kylskåp, tvättmaskiner, diskmaskiner, storköksutrustning, ugnar och kylbänkar",
          image: "/washer.webp",
        },
        {
          name: "Verktyg & bygg",
          description:
            "Elverktyg, kompressorer, byggmaterial, entreprenadmaskiner och verktygsvagnar",
          image: "/toolbox.webp",
        },
        {
          name: "Sport & gym",
          description:
            "Löpband, crosstrainers, hantlar, styrketräningsutrustning och sportartiklar",
          image: "/treadmill.webp",
        },
        {
          name: "Övrigt skrymmande",
          description:
            "Hem & fritid, musikinstrument, butiksinredning, belysning och andra större auktionsfynd",
          image: "/box.webp",
        },
      ],
    },

    seoContent: {
      mainHeading:
        "Hemleverans från Budi – vi hämtar, kör och bär in ditt auktionsfynd",
      mainText:
        "Budi Auktioner är en av Sveriges mest populära plattformar för nätauktioner. Från huvudkontoret i Bromma i Stockholm hanterar Budi tusentals auktioner varje vecka med objekt från konkurser, överskottslager, returer och avvecklingar. Sortimentet spänner från möbler och elektronik till restaurangutrustning, gymmaskiner, verktyg, byggmaterial och mycket mer. Priserna är ofta en bråkdel av nypriset – men många köpare fastnar på samma fråga: hur får jag hem det här? De flesta objekt på Budi kräver egen upphämtning. Objektet finns på en angiven plats – ofta i Bromma, men ibland på andra platser runt om i Sverige – och du har en begränsad tid att hämta innan lagringsavgift eller omauktionering. Att hyra skåpbil och köra dit tar tid, och många objekt väger för mycket för en personbil. Ungdomskraft erbjuder hemleverans av allt du vinner på Budi i Stockholm och Göteborg. Vi hämtar ditt objekt på upphämtningsplatsen, lastar skåpbilen och kör hem till dig. Vi bär in genom trapphus, hiss eller garage och ställer på plats. Inbärning ingår alltid – du ser priset direkt vid bokning. Vi är vana vid Budis breda sortiment. Våra ungdomar har hämtat allt från kompletta kontorsinredningar och restaurangkök till löpband, musikutrustning, pallar med returvaror och tunga industrimaskiner. Vi anpassar skåpbilsstorlek och bemanning efter objektet och ser till att allt är skyddat under transporten. Tänk på att vi ansvarar för transporten från upphämtningsplatsen hem till dig – objektets skick och funktion är mellan dig och Budi. Vi rekommenderar att du inspekterar objektet vid upphämtningen eller kollar bilderna noga på budi.se innan du bokar.",
      preparationHeading: "Förbered inför din Budi-leverans",
      preparationText:
        "Se till att objektet är fullt betalt på Budi och att du fått upphämtningsinformation – adress, datum och eventuella öppettider. Ha ditt auktionsnummer redo. Kontrollera hämtfristen – Budi anger alltid sista hämtdag för varje objekt. Om objektet är tungt, skrymmande eller ovanligt format – beskriv det tydligt vid bokning. Mät dörröppningar och trapphus hemma hos dig om det rör sig om stora möbler, maskiner eller gymutrustning. Kolla att skåpbilen kan parkera nära upphämtningsplatsen och nära din port hemma.",
      additionalSections: [
        {
          heading: "Hemleverans av möbler och inredning från Budi",
          content:
            "Möbler och inredning är en av Budis största kategorier. Här hittar du allt från enstaka skrivbord och kontorsstolar till kompletta kontorsmöblemang, hemmamöbler, soffor, matgrupper och förvaringsmöbler – ofta från konkurser eller företagsavvecklingar till en bråkdel av nypriset. Utmaningen är att möblerna ofta står i en lagerlokal eller gammal kontorslokal och kräver skåpbil och muskelkraft att flytta. Med Ungdomskraft hämtar vi dina Budi-möbler direkt på upphämtningsplatsen. Vi skyddar med filtar, säkrar med spännband och kör hem till dig. Vi bär in genom trapphus och dörrar och ställer på plats. Har du vunnit flera möbler i samma auktion hämtar vi allt i en tur.",
        },
        {
          heading: "Hemleverans av elektronik och vitvaror från Budi",
          content:
            "Budi säljer regelbundet elektronik, IT-utrustning och vitvaror – datorer, bildskärmar, servrar, kylskåp, tvättmaskiner, diskmaskiner och storköksutrustning som ugnar, kylbänkar och frysar. Dessa objekt väger ofta 50–100 kg och kräver varsam hantering. Elektronik är känslig för stötar, och restaurangutrustning i rostfritt stål kan vara otymplig i trånga dörröppningar. Vi hämtar ditt Budi-köp, skyddar med filtar och bubbelplast och lastar varsamt i skåpbilen. Vi bär in till rätt plats hemma eller i din verksamhet. Tänk på att vi ansvarar för transporten – att objektet fungerar och är i beskrivet skick är mellan dig och Budi.",
        },
        {
          heading: "Hemleverans av sport- och gymutrustning från Budi",
          content:
            "Budis kategori Sport & Gym innehåller löpband, crosstrainers, roddmaskiner, hantlar, styrketräningsbänkar och annan gymutrustning – ofta från nedlagda gym eller företag som uppgraderat sin utrustning. Ett kommersiellt löpband väger 100–200 kg och ryms inte i en personbil. En komplett hantelvagn med vikter kan väga ännu mer. Vi hämtar din gymutrustning på Budis upphämtningsplats, lastar i skåpbilen och levererar hem. Vi bär in genom trapphus och dörrar och ställer på plats – i hemmagymmet, garaget eller källaren. Beskriv utrustningens vikt och mått vid bokning så anpassar vi bemanning och skåpbilsstorlek.",
        },
        {
          heading: "Så fungerar Budi-leverans med Ungdomskraft",
          listItems: [
            {
              label: "Boka online",
              text: "Ange upphämtningsadressen (från Budis hämtinformation), din hemadress och beskriv objektet. Du ser priset direkt.",
            },
            {
              label: "Vi hämtar på upphämtningsplatsen",
              text: "Vårt team kommer med skåpbil. Vi identifierar ditt objekt med auktionsnumret, skyddar och lastar.",
            },
            {
              label: "Vi levererar hem",
              text: "Vi kör hem till dig, bär in genom trapphus och dörrar och ställer ditt Budi-köp på plats.",
            },
            {
              label: "Klart",
              text: "Ditt auktionsfynd från Budi står hemma hos dig – inburet och på plats. Du sparade både tid och rygg.",
            },
          ],
          afterContent:
            "Hela processen tar normalt 1–3 timmar beroende på avstånd och objektets storlek. Du kan följa statusen i realtid.",
        },
        {
          heading: "Budis hämtfrister och upphämtning",
          content:
            "Budi anger alltid sista hämtdag för varje vunnet objekt. Hämtfristen varierar men är ofta tre till sju dagar efter att betalningen är genomförd. Missar du fristen riskerar du lagringsavgifter eller att objektet omauktioneras. Boka din hemleverans med Ungdomskraft så snart du fått hämtbesked – vi kan ofta köra redan samma dag eller nästa dag. Du anger upphämtningsadressen, ditt auktionsnummer och önskad tid. Vi sköter resten och ser till att ditt objekt är hemma hos dig i god tid.",
        },
        {
          heading: "Varför boka Budi-leverans med Ungdomskraft?",
          content:
            "Det som gör Ungdomskraft unikt är att vi anställer unga människor och ger dem riktig arbetslivserfarenhet. När du bokar hemleverans av ditt Budi-köp får du inte bara professionell hantering – du bidrar också till att unga människor kommer in på arbetsmarknaden och bygger sin framtid.\n\nVarje uppdrag bemannas av motiverade ungdomar som lär sig allt från kundservice och logistik till fysiskt arbete och ansvarsfullt miljötänk. De lär sig att kommunicera med kunder, planera rutter, hantera tunga och skrymmande objekt och arbeta effektivt i team. Det är arbetslivserfarenhet som inte går att få i ett klassrum – och varje leverans vi kör ger en ung person möjlighet att utvecklas.\n\nVi har funnits sedan 2014 och har genomfört tiotusentals transporter i Stockholm och Göteborg. Våra ungdomar har hämtat allt från enstaka kontorsstolar till hela kontorsinredningar, löpband, restaurangkök och tunga verktygspaket. Vi vet vad som krävs: rätt skåpbilsstorlek, tillräcklig bemanning, skyddsmaterial och en plan för att navigera trånga trapphus och dörröppningar.\n\nKvalitet genomsyrar allt vi gör. Våra ungdomar tar inga genvägar – de lägger extra filtar, kontrollerar spännband och kommunicerar med dig under hela uppdraget. Den energi och det engagemang som unga medarbetare har märks i varje leverans. De vill visa vad de kan och de tar uppdraget på allvar.\n\nHållbarhet är en annan grundpelare. Budi bidrar till cirkulär ekonomi genom att ge överskott, returer och konkursföremål nytt liv – och vi är stolta över att vara en del av den kedjan genom att transportera föremålen hem till sin nya ägare. Vi optimerar rutter för att minimera utsläpp och använder återanvändbara skyddsmaterial.\n\nFör företag som köper på Budi – kontorsmöbler, restaurangutrustning, gymutrustning eller verktyg – erbjuder vi faktura och underlag för CSR-rapportering. Du kan visa att din transport genomfördes av ett företag som sysselsätter ungdomar och bidrar till social hållbarhet.\n\nJämfört med att hyra egen skåpbil och organisera hjälp sparar du tid, slipper slitage och får allt levererat med professionell hantering. Du ser priset direkt vid bokning, allt är försäkrat och vi har egna skåpbilar i flera storlekar.\n\nDet är smidig, pålitlig hjälp med mening.\n\nHemleverans från Budi ger inte rätt till RUT-avdrag, men flera av våra andra tjänster gör det – exempelvis flytt, bärhjälp och möbelmontering med 50 % avdrag på arbetskostnaden. Kombinera gärna tjänster i en och samma bokning.",
        },
      ],
    },

    faq: [
      {
        question: "Vad kostar hemleverans från Budi?",
        answer:
          "Priset beror på objektets storlek, vikt och avstånd. En kontorsstol eller mindre elektronik kostar från 595 kr. Större möbler, gymutrustning eller maskiner kostar från 995–2 495 kr. Du ser det exakta priset direkt när du bokar online.",
      },
      {
        question: "Kan jag få RUT-avdrag på hemleverans från Budi?",
        answer:
          "Hemleverans från Budi ger i sig inte rätt till RUT-avdrag. RUT gäller hushållsnära tjänster som utförs i din bostad, exempelvis flytt, bärhjälp och möbelmontering. Ungdomskraft erbjuder flera RUT-berättigade tjänster med 50 % avdrag på arbetskostnaden – kombinera enkelt i en bokning via vår hemsida.",
      },
      {
        question: "Ingår skåpbil i Budi-leveransen?",
        answer:
          "Ja, skåpbil ingår alltid. Vi väljer rätt storlek utifrån vad som ska transporteras – du behöver inte hyra bil eller köra själv.",
      },
      {
        question: "Ingår inbärning i priset för Budi-leverans?",
        answer:
          "Ja, inbärning ingår alltid. Vi bär in ditt objekt genom trapphus, hiss och dörrar och ställer på plats – oavsett våning. Inga extra avgifter per våningsplan.",
      },
      {
        question: "Kan ni hämta tunga objekt som löpband eller restaurangutrustning från Budi?",
        answer:
          "Ja, vi hämtar objekt upp till flera hundra kilo. Löpband, kylbänkar, industriugnar och tung gymutrustning – beskriv objektets vikt och mått vid bokning så anpassar vi skåpbilsstorlek och bemanning.",
      },
      {
        question: "Hur fungerar upphämtning från Budi?",
        answer:
          "Budi anger en upphämtningsadress och hämtdagar för varje objekt. Du anger dessa uppgifter vid bokning – vi kör dit, identifierar objektet med ditt auktionsnummer, lastar och kör hem till dig.",
      },
      {
        question: "Hur snabbt kan ni hämta från Budi?",
        answer:
          "Vi kan ofta hämta redan samma dag eller nästa dag. Boka så snart du fått hämtinformation från Budi – det hjälper dig att undvika att missa hämtfristen.",
      },
      {
        question: "Tar ni ansvar för objektets skick från Budi?",
        answer:
          "Vi ansvarar för transporten – objektet är försäkrat mot transportskador. Objektets skick, funktion och överensstämmelse med auktionsbeskrivningen är mellan dig och Budi. Vi rekommenderar att du inspekterar objektet vid upphämtningen.",
      },
      {
        question: "Behöver jag vara med vid upphämtningen från Budi?",
        answer:
          "Nej, du behöver inte vara med. Se till att objektet är betalt och att vi har rätt auktionsnummer och upphämtningsadress. Du behöver bara vara hemma för att ta emot leveransen.",
      },
      {
        question: "Kan ni hämta flera objekt från samma Budi-auktion?",
        answer:
          "Ja, vi hämtar alla dina vunna objekt i en tur om de finns på samma upphämtningsplats. Beskriv samtliga objekt vid bokning – vi anpassar skåpbilsstorlek och bemanning efter hela lasten.",
      },
      {
        question: "Hämtar ni från Budis lager i Bromma?",
        answer:
          "Ja, vi hämtar regelbundet från Budis huvudlager i Bromma och från alla andra upphämtningsplatser som Budi anger. Ange bara adressen vid bokning.",
      },
      {
        question: "Erbjuder ni Budi-leverans på helger?",
        answer:
          "Ja, vi erbjuder hemleverans på helger och vissa kvällar. Kontrollera att upphämtningsplatsen är öppen den tid du bokar – öppettiderna finns i Budis hämtinformation.",
      },
      {
        question: "Kan ni leverera Budi-köp till mitt företag?",
        answer:
          "Ja, vi levererar till både privatpersoner och företag. Vi erbjuder faktura för företagskunder och kan leverera till kontor, verkstad, restaurang, gym eller lager.",
      },
      {
        question: "Skyddar ni objektet under transporten från Budi?",
        answer:
          "Ja, vi använder möbelfiltar, bubbelplast, spännband och hörnskydd. Vi anpassar skyddet efter objektets typ – ömtålig elektronik, tunga möbler och känsliga ytor hanteras med extra omsorg. Allt är försäkrat.",
      },
      {
        question: "Är Budi-leveransen försäkrad?",
        answer:
          "Ja, alla våra transporter är helförsäkrade. Om ditt objekt skadas under lastning, transport eller inbärning ersätter vi det. Du kan boka med trygghet.",
      },
      {
        question:
          "I vilka kommuner erbjuder ni hemleverans från Budi?",
        answer:
          "Vi erbjuder hemleverans från Budi i hela Stockholmsområdet: Botkyrka, Danderyd, Ekerö, Haninge, Huddinge, Järfälla, Lidingö, Nacka, Norrtälje, Nykvarn, Nynäshamn, Salem, Sigtuna, Sollentuna, Solna, Stockholm, Sundbyberg, Södertälje, Tyresö, Täby, Upplands Väsby, Upplands-Bro, Vallentuna, Vaxholm, Värmdö och Österåker. Vi finns även i Göteborgsområdet: Ale, Alingsås, Göteborg, Härryda, Kungälv, Kungsbacka, Lerum, Lilla Edet, Mölndal, Partille, Stenungsund, Tjörn och Öckerö.",
      },
      {
        question: "Är det billigare än att hyra egen skåpbil?",
        answer:
          "Ofta ja – när du räknar in hyra, bensin, tid och risken att skada objektet. Hos oss ingår skåpbil, bärhjälp och inbärning till rätt plats. Du ser priset direkt vid bokning utan dolda avgifter.",
      },
    ],

    priceHint: PACKAGE_PRICE_HINT_SV.bud,

    pricingRows: getPackagePricingRows("bud", "sv"),

    dynamicHeadings: {
      threeSteps: "Budi-leverans i 3 enkla steg",
      step2Text:
        "Våra ungdomar hämtar ditt objekt på Budis upphämtningsplats, skyddar med filtar och lastar skåpbilen. Sedan kör vi hem till dig.",
      step3Title: "Ditt fynd är hemma",
      step3Desc:
        "Ditt Budi-köp står hemma hos dig – inburet och på plats. Klart.",
      ctaHeading: "Boka Budi-leverans idag",
      ctaText:
        "Ange upphämtningsadressen och din hemadress, beskriv objektet och välj en tid. Du ser priset direkt.",
    },

    en: {
      slug: "budi-auction-delivery",

      meta: {
        title:
          "Budi Auction Delivery – We Pick Up Your Winning Item & Deliver Home | Ungdomskraft",
        description:
          "Won something on Budi Auktioner? We pick up your item and deliver home with van and carrying. Furniture, electronics, gym equipment – book Budi delivery today.",
        h1: "Budi auction delivery",
      },

      hero: {
        heading: "We pick up your winning item – Budi auction delivery",
        intro:
          "Won an auction on Budi but lack the transport to get it home? Budi Auktioner sells everything from furniture and electronics to restaurant equipment, gym machines and building materials – and most items require your own pickup. Ungdomskraft picks up your Budi purchase at the collection point, loads the van and delivers to your door – with carrying and placement inside.",
      },

      featureCards: {
        sinceText: "Budi delivery since 2014",
        storyText:
          "Since 2014, Ungdomskraft has helped auction buyers get their Budi finds home – from heavy office furniture and appliances to gym equipment and commercial kitchens. Our young workers are experienced with the wide range of items Budi offers.",
        bookYourText: "Book your Budi delivery",
        bookDescText:
          "Enter the pickup address and your home address, describe the item and choose a time. You see the price instantly.",
        vehicleTitleText: "Vans for every type of Budi purchase",
        vehicleDescText:
          "We bring the right van size – whether it's an office chair, a treadmill or a full restaurant interior. You don't need to rent a vehicle or organise helpers.",
      },

      itemsSection: {
        heading: "Common items we collect from Budi",
        items: [
          {
            name: "Furniture & interiors",
            description:
              "Office furniture, home furniture, sofas, tables, chairs, bookcases and storage from bankruptcy and surplus auctions",
            image: "/sofa.webp",
          },
          {
            name: "Electronics & IT",
            description:
              "Computers, monitors, servers, printers, cameras, audio and music equipment",
            image: "/tv.webp",
          },
          {
            name: "Appliances & restaurant",
            description:
              "Fridges, washing machines, dishwashers, commercial kitchen equipment, ovens and cooler counters",
            image: "/washer.webp",
          },
          {
            name: "Tools & construction",
            description:
              "Power tools, compressors, building materials, construction machines and tool trolleys",
            image: "/toolbox.webp",
          },
          {
            name: "Sport & gym",
            description:
              "Treadmills, cross trainers, dumbbells, strength training equipment and sports gear",
            image: "/treadmill.webp",
          },
          {
            name: "Other bulky items",
            description:
              "Home & leisure, musical instruments, shop fittings, lighting and other large auction finds",
            image: "/box.webp",
          },
        ],
      },

      seoContent: {
        mainHeading:
          "Budi delivery – we pick up, transport and carry in your auction find",
        mainText:
          "Budi Auktioner is one of Sweden's most popular online auction platforms. From their headquarters in Bromma, Stockholm, Budi handles thousands of auctions every week with items from bankruptcies, surplus stock, returns and business closures. The range spans furniture and electronics to restaurant equipment, gym machines, tools, building materials and much more. Prices are often a fraction of the retail cost – but many buyers face the same question: how do I get this home? Most items on Budi require your own pickup. The item is at a designated location – often in Bromma but sometimes elsewhere in Sweden – and you have a limited time to collect before the deadline passes. Renting your own van and driving there takes time, and many items are too heavy for a regular car. Ungdomskraft offers home delivery of everything you win on Budi in Stockholm and Gothenburg. We pick up your item at the collection point, load the van and drive it home to you. We carry in through stairwells, lifts or garages and place it where you want it. Carrying is always included – you see the price when you book. We are experienced with Budi's wide range. Our young workers have collected everything from complete office interiors and commercial kitchens to treadmills, music equipment, pallets of returned goods and heavy industrial machines. We match van size and crew to the item and make sure everything is protected during transport. Please note that we are responsible for the transport from the collection point to your home – the item's condition and function are between you and Budi.",
        preparationHeading: "Prepare for your Budi delivery",
        preparationText:
          "Make sure the item is fully paid for on Budi and that you have received the pickup information – address, dates and opening hours. Have your auction number ready. Check the pickup deadline – Budi always specifies the last collection date for each item. If the item is heavy, bulky or unusually shaped, describe it clearly when booking. Measure doorways and stairwells at home if the item is large. Check that the van can park close to the collection point and your entrance at home.",
        additionalSections: [
          {
            heading: "Delivering furniture and interiors from Budi",
            content:
              "Furniture and interiors are one of Budi's largest categories. You can find everything from a single desk and office chair to complete office furniture sets, home furniture, sofas, dining groups and storage units – often from bankruptcies or business closures at a fraction of the retail price. The challenge is that the furniture is often stored in a warehouse or former business premises and requires a van and at least two people to carry. With Ungdomskraft, we pick up your Budi furniture directly at the collection point, protect it with blankets, secure with straps and drive home to you. We carry in and place where you want it. Won multiple pieces in the same auction? We collect everything in one trip.",
          },
          {
            heading: "Delivering electronics and appliances from Budi",
            content:
              "Budi regularly sells electronics, IT equipment and appliances – computers, monitors, servers, fridges, washing machines, dishwashers and commercial kitchen equipment such as ovens, cooler counters and freezers. These items often weigh 50–100 kg and need careful handling. Electronics are sensitive to impacts, and stainless steel restaurant equipment can be awkward in narrow doorways. We pick up your Budi purchase, protect with blankets and bubble wrap and load carefully into the van. We carry in to the right spot in your home or business. Remember that we are responsible for the transport – whether the item works and matches the description is between you and Budi.",
          },
          {
            heading: "Delivering sport and gym equipment from Budi",
            content:
              "Budi's Sport & Gym category features treadmills, cross trainers, rowing machines, dumbbells, strength training benches and other gym equipment – often from closed gyms or businesses upgrading their equipment. A commercial treadmill weighs 100–200 kg and won't fit in a car. A full dumbbell rack with weights can weigh even more. We pick up your gym equipment at Budi's collection point, load into the van and deliver home. We carry in through stairwells and doors and place it where you want it – in the home gym, garage or basement. Describe the equipment's weight and dimensions when booking so we can match crew and van size.",
          },
          {
            heading: "How Budi delivery works with Ungdomskraft",
            listItems: [
              {
                label: "Book online",
                text: "Enter the pickup address (from Budi's collection information), your home address and describe the item. You see the price instantly.",
              },
              {
                label: "We collect at the pickup point",
                text: "Our crew arrives with a van. We identify your item using the auction number, protect and load it.",
              },
              {
                label: "We deliver home",
                text: "We drive to your home, carry in through stairwells and doors and place your Budi purchase where you want it.",
              },
              {
                label: "Done",
                text: "Your Budi find is at home – carried in and in place. You saved both time and your back.",
              },
            ],
            afterContent:
              "The whole process usually takes 1–3 hours depending on distance and item size. You can track the status in real time.",
          },
          {
            heading: "Budi pickup deadlines",
            content:
              "Budi always specifies the last collection date for each won item. The deadline varies but is often three to seven days after payment is completed. If you miss it, you risk storage fees or the item being re-auctioned. Book your delivery with Ungdomskraft as soon as you receive the pickup notification – we can often drive the same day or next day. You enter the pickup address, your auction number and preferred time. We handle the rest.",
          },
          {
            heading: "Why book Budi delivery with Ungdomskraft?",
            content:
              "What makes Ungdomskraft unique is that we employ young people and give them real work experience. When you book delivery of your Budi purchase, you don't just get professional handling – you also help young people enter the job market and build their futures.\n\nEvery job is staffed by motivated young workers who learn everything from customer service and logistics to physical work and environmental responsibility. They learn to communicate with customers, plan routes, handle heavy and bulky items and work efficiently as a team. This is work experience you can't get in a classroom – and every delivery we make gives a young person the chance to grow.\n\nWe have been operating since 2014 and have completed tens of thousands of transports in Stockholm and Gothenburg. Our young workers have collected everything from single office chairs to complete office interiors, treadmills, commercial kitchens and heavy tool packages. We know what it takes: the right van size, sufficient crew, protective materials and a plan for navigating narrow stairwells and doorways.\n\nQuality runs through everything we do. Our workers don't take shortcuts – they add extra blankets, check the straps and communicate with you throughout the job. The energy and commitment that young workers bring shows in every delivery.\n\nSustainability is another cornerstone. Budi contributes to the circular economy by giving surplus, returns and bankrupt items new life – and we are proud to be part of that chain by transporting items home to their new owners. We optimise routes to minimise emissions and use reusable protective materials.\n\nFor businesses buying on Budi – office furniture, restaurant equipment, gym equipment or tools – we also offer invoicing and documentation for CSR reporting. You can show that your transport was carried out by a company that employs young people and contributes to social sustainability.\n\nCompared to renting your own van and organising help, you save time, avoid wear and tear and get everything delivered with professional handling. You see the price when you book, everything is insured and we have our own vans in several sizes.\n\nIt is smooth, reliable help with purpose.\n\nBudi delivery does not qualify for the Swedish RUT tax deduction, but several of our other services do – including moving help, carrying help and furniture assembly with a 50% deduction on the labour cost. Feel free to combine services in a single booking.",
          },
        ],
      },

      faq: [
        {
          question: "How much does Budi delivery cost?",
          answer:
            "The price depends on the item's size, weight and distance. An office chair or smaller electronics starts from SEK 595. Larger furniture, gym equipment or machinery starts from SEK 995–1,699. You see the exact price when you book online.",
        },
        {
          question: "Can I get a RUT tax deduction on Budi delivery?",
          answer:
            "Budi delivery does not in itself qualify for the Swedish RUT tax deduction. RUT applies to household services carried out in your home, such as moving help, carrying help and furniture assembly. Ungdomskraft offers several RUT-eligible services with a 50% deduction on the labour cost – easily combine them in one booking via our website.",
        },
        {
          question: "Is a van included in the Budi delivery?",
          answer:
            "Yes, a van is always included. We choose the right size based on what needs to be transported – you don't need to rent a vehicle or drive yourself.",
        },
        {
          question: "Is carrying included in the Budi delivery price?",
          answer:
            "Yes, carrying is always included. We carry your item through stairwells, lifts and doors and place it where you want it – regardless of floor. No extra fees per floor.",
        },
        {
          question: "Can you pick up heavy items like treadmills or restaurant equipment from Budi?",
          answer:
            "Yes, we pick up items weighing up to several hundred kilograms. Treadmills, cooler counters, commercial ovens and heavy gym equipment – describe the item's weight and dimensions when booking so we can match van size and crew.",
        },
        {
          question: "How does pickup from Budi work?",
          answer:
            "Budi provides a pickup address and collection dates for each item. You enter these details when booking – we drive there, identify the item using the auction number, load and drive home to you.",
        },
        {
          question: "How quickly can you pick up from Budi?",
          answer:
            "We can often pick up the same day or next day. Book as soon as you receive the pickup information from Budi – it helps you avoid missing the collection deadline.",
        },
        {
          question: "Are you responsible for the item's condition from Budi?",
          answer:
            "We are responsible for transport – the item is insured against transport damage. The item's condition, function and match with the auction description are between you and Budi. We recommend inspecting the item at pickup.",
        },
        {
          question: "Do I need to be at the Budi pickup point?",
          answer:
            "No, you don't need to be there. Make sure the item is paid for and that we have the correct auction number and pickup address. You just need to be home to receive the delivery.",
        },
        {
          question: "Do you pick up from Budi's warehouse in Bromma?",
          answer:
            "Yes, we regularly pick up from Budi's main warehouse in Bromma and from all other collection points that Budi specifies. Just enter the address when booking.",
        },
        {
          question: "Can you pick up multiple items from the same Budi auction?",
          answer:
            "Yes, we pick up all your winning items in one trip if they are at the same collection point. Describe all items when booking – we adjust van size and crew for the entire load.",
        },
        {
          question: "Do you offer Budi delivery on weekends?",
          answer:
            "Yes, we offer delivery on weekends and some evenings. Check that the collection point is open at the time you book – opening hours are listed in Budi's pickup information.",
        },
        {
          question: "Can you deliver Budi purchases to my business?",
          answer:
            "Yes, we deliver to both private individuals and businesses. We offer invoicing for business customers and can deliver to offices, workshops, restaurants, gyms or warehouses.",
        },
        {
          question: "Is the Budi delivery insured?",
          answer:
            "Yes, all our transports are fully insured. If your item is damaged during loading, transport or carrying, we will compensate you. You can book with confidence.",
        },
        {
          question:
            "In which municipalities do you offer Budi delivery?",
          answer:
            "We offer Budi delivery across the entire Stockholm area: Botkyrka, Danderyd, Ekerö, Haninge, Huddinge, Järfälla, Lidingö, Nacka, Norrtälje, Nykvarn, Nynäshamn, Salem, Sigtuna, Sollentuna, Solna, Stockholm, Sundbyberg, Södertälje, Tyresö, Täby, Upplands Väsby, Upplands-Bro, Vallentuna, Vaxholm, Värmdö and Österåker. We also serve the Gothenburg area: Ale, Alingsås, Gothenburg, Härryda, Kungälv, Kungsbacka, Lerum, Lilla Edet, Mölndal, Partille, Stenungsund, Tjörn and Öckerö.",
        },
      ],

      priceHint: PACKAGE_PRICE_HINT_EN.bud,

      pricingRows: getPackagePricingRows("bud", "en"),

      dynamicHeadings: {
        threeSteps: "Budi delivery in 3 easy steps",
        step2Text:
          "Our crew picks up your item at the Budi collection point, protects it with blankets and loads the van. Then we drive home to you.",
        step3Title: "Your find is home",
        step3Desc:
          "Your Budi purchase is at home – carried in and in place. Done.",
        ctaHeading: "Book Budi delivery today",
        ctaText:
          "Enter the pickup address and your home address, describe the item and choose a time. You see the price instantly.",
      },
    },
  },

  // ────────────────────────────────────────────────────────────────────────────
  // HEMLEVERANS STOCKHOLMS AUKTIONSVERK
  // Vinkel: Sveriges äldsta och mest prestigefyllda auktionshus (grundat 1674).
  // Konst, antikviteter, design, smycken, klockor, silver, mattor, asiatisk
  // konst. Upphämtning Nybrogatan 32 och Magasin 5 Frihamnen, Stockholm.
  // ────────────────────────────────────────────────────────────────────────────
  {
    slug: "hemleverans-stockholms-auktionsverk",
    serviceCategory: "hemleverans",

    meta: {
      title:
        "Hemleverans Stockholms Auktionsverk – Vi Hämtar Ditt Auktionsfynd | Ungdomskraft",
      description:
        "Köpt på Stockholms Auktionsverk? Vi hämtar ditt objekt på Nybrogatan eller Magasin 5 och levererar hem med varsam inbärning. Försäkrat. Boka idag!",
      h1: "Hemleverans Stockholms Auktionsverk",
    },

    hero: {
      heading: "Vi hämtar ditt vunna objekt – Hemleverans Stockholms Auktionsverk",
      intro:
        "Vunnit ett bud på Stockholms Auktionsverk? Grattis – men hur får du hem din antika byrå, oljemålning eller designfåtölj? Hämtfristerna är korta och objekten tunga. Vi löser det. Ungdomskraft hämtar ditt objekt på Nybrogatan 32 eller Magasin 5 i Frihamnen, skyddar det varsamt och levererar hem till dig – med inbärning och uppställning på plats.",
      heroImage: "/hero-hemleverans.png",
    },

    featureCards: {
      sinceText: "Auktionsverksleverans sedan 2014",
      storyText:
        "Sedan 2014 har Ungdomskraft hjälpt köpare på Stockholms Auktionsverk att få hem sina fynd – från tunga antika möbler och ömtålig konst till designklassiker, smyckesskåp och ljuskronor. Våra ungdomar vet hur man hanterar värdefulla föremål med den extra omsorg de förtjänar.",
      bookYourText: "Boka din auktionsverksleverans",
      bookDescText:
        "Ange hämtplats (Nybrogatan eller Magasin 5) och din hemadress, beskriv objektet och välj en tid. Du ser priset direkt.",
      vehicleTitleText: "Skåpbilar för värdefulla auktionsfynd",
      vehicleDescText:
        "Vi kommer med rätt storlek på skåpbil och rätt skyddsmaterial – oavsett om det gäller en liten silvervas eller ett stort möblemang. Allt skyddas med filtar, bubbelplast och spännband.",
    },

    itemsSection: {
      heading: "Vanliga objekt vi hämtar från Stockholms Auktionsverk",
      items: [
        {
          name: "Antika möbler",
          description:
            "Gustavianska byråer, sekretärer, skåp, bord, stolar och möbler från 1500-tal till 1900-tal",
          image: "/wardrobe.webp",
        },
        {
          name: "Konst & tavlor",
          description:
            "Oljemålningar, grafik, skulpturer, fotografier och ramar – svensk och internationell konst",
          image: "/painting.webp",
        },
        {
          name: "Designmöbler",
          description:
            "Fåtöljer, soffor, lampor, bord och belysning av kända svenska och internationella formgivare",
          image: "/armchair.webp",
        },
        {
          name: "Mattor & textilier",
          description:
            "Persiska mattor, orientaliska mattor, ryor, gobelänger och andra textila föremål",
          image: "/bedding.webp",
        },
        {
          name: "Silver, porslin & glas",
          description:
            "Silverbestick, silverkandelabrar, porslinserviser, kristall, vaser och figuriner",
          image: "/dishes.webp",
        },
        {
          name: "Smycken, klockor & övrigt",
          description:
            "Smycken, armbandsur, fickur, ljuskronor, speglar, asiatisk konst och böcker",
          image: "/box.webp",
        },
      ],
    },

    seoContent: {
      mainHeading:
        "Hemleverans från Stockholms Auktionsverk – vi hämtar, transporterar och bär in ditt auktionsfynd",
      mainText:
        "Stockholms Auktionsverk är Sveriges äldsta auktionshus, grundat 1674, och ett av de mest respekterade i världen. Med avdelningar för konst, design, antika möbler, smycken, klockor, silver, asiatisk konst och böcker auktioneras tusentals föremål varje månad – från vardagsföremål till objekt värda miljontals kronor. Att vinna ett bud på Stockholms Auktionsverk är spännande, men sedan kommer den praktiska frågan: hur får jag hem det här? Hämtfristen är begränsad och lagringsavgifter börjar ticka efter några dagar. Att transportera värdefulla antika möbler, ömtålig konst eller tunga skulpturer i egen bil innebär stor risk för skador. Ungdomskraft erbjuder hemleverans från Stockholms Auktionsverks samtliga lokaler – Nybrogatan 32 i city och Magasin 5 i Frihamnen. Vi hämtar ditt vunna objekt, skyddar det varsamt med filtar, bubbelplast och spännband och kör hem till dig. Vi bär in genom trapphus, hiss eller trångt garage och ställer på plats där du vill ha det. Inbärning ingår alltid – du ser priset direkt vid bokning. Varje vecka hämtar vi föremål från Stockholms Auktionsverk i alla prisklasser och storlekar – från en enstaka tavla eller silverservis till ett helt dödsbo med möbler, mattor och konst. Våra ungdomar är vana vid att hantera ömtåliga och värdefulla föremål. Vi vet att en gustaviansk byrå kräver mjuka filtar och jämn körning, att en oljemålning måste stå upprätt och skyddas mot stötar och att en kristallkrona behöver extra polstring. Vi tar med oss allt skyddsmaterial som behövs. Tänk på att vi ansvarar för transporten från Auktionsverket hem till dig – objektets skick, autenticitet och proveniens är mellan dig och Stockholms Auktionsverk.",
      preparationHeading: "Förbered inför din leverans från Stockholms Auktionsverk",
      preparationText:
        "Se till att ditt köp är fullt betalt och redo att hämtas. Kontrollera vilken lokal objektet finns på – Nybrogatan 32 eller Magasin 5 i Frihamnen. Ha ditt kundnummer eller objektnummer redo så att vårt team kan identifiera rätt föremål. Kontrollera hämtfristen – Auktionsverket anger alltid sista hämtdag. Om objektet är särskilt ömtåligt, tungt eller har ovanlig form – beskriv det tydligt vid bokning. Mät dörröppningar och trapphus hemma om det rör sig om stora möbler. Kolla att skåpbilen kan parkera nära din port.",
      additionalSections: [
        {
          heading: "Hemleverans av antika möbler från Stockholms Auktionsverk",
          content:
            "Avdelningen Äldre möbler & konsthantverk på Stockholms Auktionsverk erbjuder möbler från 1500-talet till sekelskiftet 1900 – gustavianska byråer, rokokoskåp, empiresekreterärer, jugendmöbler och mycket mer. Dessa föremål är ofta tunga (50–200 kg), saknar originalförpackning och har ytor som är känsliga för repor och stötar. Att transportera dem i en vanlig bil eller med ett hyrt släp innebär stor risk. Med Ungdomskraft hämtar vi dina antika möbler direkt på Nybrogatan 32 eller Magasin 5. Vi sveper in varje yta med mjuka filtar, tejpar fast lösa delar som lådor och handtag och säkrar med spännband. Vi kör försiktigt och undviker hårda inbromsningar. Hemma hos dig bär vi in genom trapphus och dörrar och ställer möbeln exakt där du vill ha den. Behöver vi demontera delar för att passera en trång dörröppning löser vi det.",
        },
        {
          heading: "Hemleverans av konst från Stockholms Auktionsverk",
          content:
            "Stockholms Auktionsverk har fyra konstavdelningar: Modern konst, Samtida konst, Äldre & klassisk konst och Asiatisk konst & konsthantverk. Här auktioneras allt från svenska mästerverk och internationella verk till skulpturer i brons, marmor och keramik. En oljemålning med förgylld ram kan väga 20–40 kg och tål inte stötar, fukt eller tryck mot duken. Vi skyddar konst med bubbelplast, hörnskydd och mjuka filtar. Tavlor transporteras alltid stående – aldrig liggande. Skulpturer säkras individuellt. Vi bär in och ställer på plats hemma hos dig – på väggen, på piedestalen eller i vitrinskåpet. Köpt flera verk på samma auktion? Vi hämtar allt i en tur och skyddar varje objekt separat.",
        },
        {
          heading: "Hemleverans av design, smycken och silver",
          content:
            "Stockholms Auktionsverks designavdelning erbjuder möbler, belysning och inredning av kända formgivare – Bruno Mathsson, Josef Frank, Alvar Aalto, Hans Wegner och många andra. Designmöbler kräver varsam hantering eftersom ytorna ofta är i känsligt material som läder, teak eller lackerat trä. Smycken, klockor och silverföremål är mindre till storleken men kan ha mycket högt värde. Vi transporterar dem säkert i skåpbilen, skyddade från stötar och fukt. Silver som kandelabrar, bestickset och brickor packas individuellt. Om du har vunnit en kombination av föremål – till exempel en designfåtölj, en tavla och ett silverbestick – hämtar vi allt på samma gång och anpassar skyddet för varje objektstyp.",
        },
        {
          heading: "Hämtplatser på Stockholms Auktionsverk",
          content:
            "Stockholms Auktionsverk har två huvudsakliga hämtplatser i Stockholm. Nybrogatan 32 i centrala Östermalm är auktionsverkets huvudlokaler där de flesta kvalitetsauktioner hålls. Magasin 5 i Frihamnen används för onlineauktioner och visningar av större volymer. Ange rätt adress vid bokning – den finns i din hämtinformation från Auktionsverket. Vi hämtar från båda lokalerna och kör från alla Auktionsverkets lokaler i Stockholmsområdet. Behöver du hämta från flera platser i samma tur? Beskriv det vid bokning.",
        },
        {
          heading: "Så fungerar leverans från Stockholms Auktionsverk",
          listItems: [
            {
              label: "Boka online",
              text: "Ange hämtplats (Nybrogatan 32 eller Magasin 5), din hemadress och beskriv objektet. Ange gärna objektnummer. Du ser priset direkt.",
            },
            {
              label: "Vi hämtar på Auktionsverket",
              text: "Vårt team kommer med skåpbil och rätt skyddsmaterial. Vi identifierar ditt objekt, skyddar det varsamt och lastar.",
            },
            {
              label: "Vi levererar hem",
              text: "Vi kör hem till dig, bär in genom trapphus och dörrar och ställer ditt auktionsfynd på plats.",
            },
            {
              label: "Klart",
              text: "Ditt fynd från Stockholms Auktionsverk står hemma hos dig – varsamt inburet och uppställt.",
            },
          ],
          afterContent:
            "Hela processen tar normalt 1–3 timmar beroende på avstånd och objektets hanteringsbehov. Du kan följa statusen i realtid.",
        },
        {
          heading: "Varför boka leverans från Stockholms Auktionsverk med Ungdomskraft?",
          content:
            "Det som gör Ungdomskraft unikt är att vi anställer unga människor och ger dem riktig arbetslivserfarenhet. När du bokar hemleverans av ditt auktionsfynd från Stockholms Auktionsverk får du inte bara professionell och varsam hantering – du bidrar också till att unga människor kommer in på arbetsmarknaden och bygger sin framtid.\n\nVarje uppdrag bemannas av motiverade ungdomar som lär sig allt från kundservice och logistik till varsam hantering av värdefulla föremål. De lär sig att kommunicera med kunder, planera rutter, skydda ömtåliga ytor och arbeta effektivt i team. Det är arbetslivserfarenhet som inte går att få i ett klassrum – och varje leverans vi kör ger en ung person möjlighet att utvecklas.\n\nVi har funnits sedan 2014 och har genomfört tiotusentals transporter i Stockholm och Göteborg. Våra ungdomar har burit in allt från gustavianska möblemang och oljemålningar av svenska mästare till ljuskronor, silversamlingar och designklassiker värda hundratusentals kronor. Vi vet vad som krävs: rätt skyddsmaterial, försiktig lastning, jämn körning och varsam inbärning.\n\nKvalitet genomsyrar allt vi gör. Våra ungdomar tar inga genvägar – de lägger extra filtar, kontrollerar att spännbanden sitter och kommunicerar med dig under hela uppdraget. Den energi och det engagemang som unga medarbetare har märks i varje leverans. De vill visa vad de kan och de tar uppdraget på allvar.\n\nHållbarhet är en annan grundpelare. Att köpa på auktion är i sig ett hållbart val – du ger föremål nytt liv istället för att köpa nytt. Vi är stolta över att vara en del av den kedjan genom att transportera föremålen hem till sin nya ägare. Vi optimerar rutter för att minimera utsläpp och använder återanvändbara skyddsmaterial.\n\nFör företag som köper konst eller design på Stockholms Auktionsverk – till kontoret, lobbyn eller showroom – erbjuder vi faktura och underlag för CSR-rapportering. Du kan visa att din transport genomfördes av ett företag som sysselsätter ungdomar och bidrar till social hållbarhet.\n\nVi erbjuder snabb hämtning, inbärning till rätt plats och ett konkurrenskraftigt pris utan dolda avgifter. Du ser priset direkt vid bokning, allt är försäkrat och vi har egna skåpbilar i flera storlekar.\n\nDet är varsam, professionell hjälp med mening.\n\nHemleverans från Stockholms Auktionsverk ger inte rätt till RUT-avdrag, men flera av våra andra tjänster gör det – exempelvis flytt, bärhjälp och möbelmontering med 50 % avdrag på arbetskostnaden. Kombinera gärna tjänster i en och samma bokning.",
        },
      ],
    },

    faq: [
      {
        question: "Vad kostar hemleverans från Stockholms Auktionsverk?",
        answer:
          "Priset beror på objektets storlek och avstånd. En tavla eller silverföremål kostar från 595 kr. Större möbler kostar från 995–2 495 kr. Du ser det exakta priset direkt när du bokar online.",
      },
      {
        question: "Kan jag få RUT-avdrag på hemleverans från Stockholms Auktionsverk?",
        answer:
          "Hemleverans från Stockholms Auktionsverk ger i sig inte rätt till RUT-avdrag. RUT gäller hushållsnära tjänster som utförs i din bostad, exempelvis flytt, bärhjälp och möbelmontering. Ungdomskraft erbjuder flera RUT-berättigade tjänster med 50 % avdrag på arbetskostnaden – kombinera enkelt i en bokning via vår hemsida.",
      },
      {
        question: "Ingår skåpbil i leveransen från Stockholms Auktionsverk?",
        answer:
          "Ja, skåpbil ingår alltid. Vi väljer rätt storlek utifrån vad som ska transporteras – du behöver inte hyra bil eller köra själv.",
      },
      {
        question: "Ingår inbärning i priset för leverans från Auktionsverket?",
        answer:
          "Ja, inbärning ingår alltid. Vi bär in ditt objekt genom trapphus, hiss och dörrar och ställer på plats – oavsett våning. Inga extra avgifter per våningsplan.",
      },
      {
        question: "Hur skyddar ni värdefulla föremål från Stockholms Auktionsverk?",
        answer:
          "Vi använder möbelfiltar, bubbelplast, hörnskydd och spännband. Tavlor transporteras stående, skulpturer säkras individuellt och silver eller porslin packas med stötdämpande material. Allt är försäkrat.",
      },
      {
        question: "Hämtar ni från både Nybrogatan och Magasin 5?",
        answer:
          "Ja, vi hämtar från båda Stockholms Auktionsverks lokaler – Nybrogatan 32 i city och Magasin 5 i Frihamnen. Ange rätt adress vid bokning.",
      },
      {
        question: "Kan ni hämta tunga antika möbler från Auktionsverket?",
        answer:
          "Ja, våra ungdomar är vana vid att bära tunga föremål – byråer, skåp, matbord och andra möbler som väger 50–200 kg. Vi skyddar alla ytor med filtar och bär varsamt genom trapphus och trånga dörröppningar.",
      },
      {
        question: "Hur snabbt kan ni hämta från Stockholms Auktionsverk?",
        answer:
          "Vi kan ofta hämta redan samma dag eller nästa dag. Boka så snart du fått hämtbesked – det hjälper dig att undvika lagringsavgifter.",
      },
      {
        question: "Tar ni ansvar för objektets skick från Auktionsverket?",
        answer:
          "Vi ansvarar för transporten – objektet är försäkrat mot transportskador. Objektets skick, autenticitet och proveniens är mellan dig och Stockholms Auktionsverk. Vi rekommenderar att du inspekterar objektet innan vi lastar.",
      },
      {
        question: "Behöver jag vara med vid hämtning på Auktionsverket?",
        answer:
          "Nej, du behöver inte vara med. Se till att köpet är betalt och att vi har ditt kundnummer eller objektnummer. Du kan även ge oss fullmakt att hämta – ange det vid bokning. Du behöver bara vara hemma för att ta emot leveransen.",
      },
      {
        question: "Kan ni leverera konst och möbler från Auktionsverket på helger?",
        answer:
          "Ja, vi erbjuder hemleverans på helger och vissa kvällar. Kontrollera att Auktionsverket har öppet för upphämtning den tid du bokar.",
      },
      {
        question: "Kan ni hämta flera objekt från samma auktion?",
        answer:
          "Ja, vi hämtar alla dina vunna objekt i en tur. Beskriv samtliga föremål vid bokning – vi anpassar skåpbilsstorlek och skyddsmaterial efter hela lasten.",
      },
      {
        question: "Kan ni transportera smycken och klockor från Auktionsverket?",
        answer:
          "Ja, vi transporterar även mindre men värdefulla objekt som smycken, klockor och silverföremål. Allt skyddas och försäkras under transporten.",
      },
      {
        question: "Vad händer om jag missar hämtfristen på Auktionsverket?",
        answer:
          "Stockholms Auktionsverk tar ut lagringsavgift efter angiven hämtfrist. Boka din hemleverans så snart du fått hämtbesked så slipper du extra kostnader.",
      },
      {
        question: "Är leveransen från Stockholms Auktionsverk försäkrad?",
        answer:
          "Ja, alla våra transporter är helförsäkrade. Om ditt objekt skadas under lastning, transport eller inbärning ersätter vi det. Du kan boka med trygghet.",
      },
      {
        question:
          "I vilka kommuner erbjuder ni hemleverans från Stockholms Auktionsverk?",
        answer:
          "Vi erbjuder hemleverans från Stockholms Auktionsverk i hela Stockholmsområdet: Botkyrka, Danderyd, Ekerö, Haninge, Huddinge, Järfälla, Lidingö, Nacka, Norrtälje, Nykvarn, Nynäshamn, Salem, Sigtuna, Sollentuna, Solna, Stockholm, Sundbyberg, Södertälje, Tyresö, Täby, Upplands Väsby, Upplands-Bro, Vallentuna, Vaxholm, Värmdö och Österåker. Vi finns även i Göteborgsområdet: Ale, Alingsås, Göteborg, Härryda, Kungälv, Kungsbacka, Lerum, Lilla Edet, Mölndal, Partille, Stenungsund, Tjörn och Öckerö.",
      },
      {
        question: "Varför välja Ungdomskraft för leverans från Auktionsverket?",
        answer:
          "Hos oss ingår alltid skåpbil, bärhjälp och inbärning till rätt plats – och du ser priset direkt vid bokning. Allt är försäkrat och vi kan ofta köra redan samma dag eller nästa dag.",
      },
    ],

    priceHint: PACKAGE_PRICE_HINT_SV.bud,

    pricingRows: getPackagePricingRows("bud", "sv"),

    dynamicHeadings: {
      threeSteps: "Leverans från Stockholms Auktionsverk i 3 enkla steg",
      step2Text:
        "Våra ungdomar hämtar ditt objekt på Nybrogatan eller Magasin 5, skyddar det varsamt med filtar och bubbelplast och lastar skåpbilen. Sedan kör vi hem till dig.",
      step3Title: "Ditt fynd är hemma",
      step3Desc:
        "Ditt auktionsfynd från Stockholms Auktionsverk står hemma hos dig – varsamt inburet och på plats. Klart.",
      ctaHeading: "Boka leverans från Stockholms Auktionsverk idag",
      ctaText:
        "Ange hämtplats och din hemadress, beskriv objektet och välj en tid. Du ser priset direkt.",
    },

    en: {
      slug: "stockholms-auktionsverk-delivery",

      meta: {
        title:
          "Stockholms Auktionsverk Delivery – We Pick Up & Deliver Home | Ungdomskraft",
        description:
          "Bought at Stockholms Auktionsverk? We pick up your item at Nybrogatan or Magasin 5 and deliver home with careful carrying. Book auction delivery today.",
        h1: "Stockholms Auktionsverk delivery",
      },

      hero: {
        heading: "We pick up your winning bid – Stockholms Auktionsverk delivery",
        intro:
          "Won a bid at Stockholms Auktionsverk? Congratulations – but how do you get that antique dresser, oil painting or design armchair home? Pickup deadlines are tight and the items are heavy. Ungdomskraft picks up your item at Nybrogatan 32 or Magasin 5 in Frihamnen, protects it carefully and delivers to your door – with carrying and placement inside.",
      },

      featureCards: {
        sinceText: "Auktionsverk delivery since 2014",
        storyText:
          "Since 2014, Ungdomskraft has helped buyers at Stockholms Auktionsverk get their finds home – from heavy antique furniture and delicate art to design classics, silver collections and chandeliers. Our young workers know how to handle valuable items with the extra care they deserve.",
        bookYourText: "Book your Auktionsverk delivery",
        bookDescText:
          "Enter the pickup location (Nybrogatan or Magasin 5) and your home address, describe the item and choose a time. You see the price instantly.",
        vehicleTitleText: "Vans equipped for valuable auction finds",
        vehicleDescText:
          "We bring the right van size and protective materials – whether it's a small silver vase or a full furniture set. Everything is protected with blankets, bubble wrap and straps.",
      },

      itemsSection: {
        heading: "Common items we collect from Stockholms Auktionsverk",
        items: [
          {
            name: "Antique furniture",
            description:
              "Gustavian dressers, writing desks, cabinets, tables, chairs and furniture from the 16th to 20th century",
            image: "/wardrobe.webp",
          },
          {
            name: "Art & paintings",
            description:
              "Oil paintings, prints, sculptures, photographs and frames – Swedish and international art",
            image: "/painting.webp",
          },
          {
            name: "Design furniture",
            description:
              "Armchairs, sofas, lamps, tables and lighting by renowned Swedish and international designers",
            image: "/armchair.webp",
          },
          {
            name: "Rugs & textiles",
            description:
              "Persian rugs, oriental rugs, tapestries and other textile collectibles",
            image: "/bedding.webp",
          },
          {
            name: "Silver, porcelain & glass",
            description:
              "Silverware, candelabras, dinnerware sets, crystal, vases and figurines",
            image: "/dishes.webp",
          },
          {
            name: "Jewellery, watches & other",
            description:
              "Jewellery, wristwatches, pocket watches, chandeliers, mirrors, Asian art and books",
            image: "/box.webp",
          },
        ],
      },

      seoContent: {
        mainHeading:
          "Delivery from Stockholms Auktionsverk – we pick up, transport and carry in your auction find",
        mainText:
          "Stockholms Auktionsverk is Sweden's oldest auction house, founded in 1674, and one of the most respected in the world. With departments for art, design, antique furniture, jewellery, watches, silver, Asian art and books, thousands of items are auctioned every month – from everyday objects to pieces worth millions of kronor. Winning a bid at Stockholms Auktionsverk is exciting, but then comes the practical question: how do I get this home? The pickup deadline is limited and storage fees start accumulating after a few days. Transporting valuable antique furniture, delicate art or heavy sculptures in your own car means significant risk of damage. Ungdomskraft offers home delivery from all Stockholms Auktionsverk locations – Nybrogatan 32 in the city centre and Magasin 5 in Frihamnen. We pick up your winning item, protect it carefully with blankets, bubble wrap and straps, and drive it home to you. We carry in through stairwells, lifts or narrow garages and place it exactly where you want it. Carrying is always included – you see the price when you book. Every week we collect items from Stockholms Auktionsverk in all price ranges and sizes – from a single painting or silver set to an entire estate lot with furniture, rugs and art. Our young workers are experienced with fragile and valuable items. We know that a Gustavian dresser needs soft blankets and smooth driving, that an oil painting must stand upright and be shielded from impacts, and that a crystal chandelier requires extra padding. Please note that we are responsible for the transport from the Auction House to your home – the item's condition, authenticity and provenance are between you and Stockholms Auktionsverk.",
        preparationHeading: "Prepare for your delivery from Stockholms Auktionsverk",
        preparationText:
          "Make sure your purchase is fully paid for and ready for collection. Check which location the item is at – Nybrogatan 32 or Magasin 5 in Frihamnen. Have your customer number or lot number ready so our team can identify the right item. Check the pickup deadline – the Auction House always specifies the last collection date. If the item is particularly fragile, heavy or unusually shaped, describe it clearly when booking. Measure doorways and stairwells at home if the item is large. Check that the van can park close to your entrance.",
        additionalSections: [
          {
            heading: "Delivering antique furniture from Stockholms Auktionsverk",
            content:
              "The Antique Furniture & Crafts department at Stockholms Auktionsverk offers furniture from the 16th to 20th century – Gustavian dressers, Rococo cabinets, Empire writing desks, Art Nouveau furniture and much more. These items are often heavy (50–200 kg), come without packaging and have surfaces sensitive to scratches and impacts. Transporting them in your own car or with a rented trailer means significant risk. With Ungdomskraft, we pick up your antique furniture directly at Nybrogatan 32 or Magasin 5. We wrap every surface with soft blankets, tape down loose parts like drawers and handles, and secure everything with straps. We drive carefully and avoid hard braking. At your home we carry in through stairwells and doors and place the piece exactly where you want it.",
          },
          {
            heading: "Delivering art from Stockholms Auktionsverk",
            content:
              "Stockholms Auktionsverk has four art departments: Modern Art, Contemporary Art, Older & Classical Art and Asian Art & Crafts. Everything from Swedish masterpieces and international works to sculptures in bronze, marble and ceramics is auctioned here. An oil painting with a gilded frame can weigh 20–40 kg and cannot tolerate impacts, moisture or pressure on the canvas. We protect art with bubble wrap, corner guards and soft blankets. Paintings are always transported standing – never lying. Sculptures are secured individually. We carry in and place at your home – on the wall, on the pedestal or in the display cabinet. Bought multiple works at the same auction? We collect everything in one trip and protect each piece separately.",
          },
          {
            heading: "Delivering design, jewellery and silver",
            content:
              "The Design department offers furniture, lighting and interiors by renowned designers – Bruno Mathsson, Josef Frank, Alvar Aalto, Hans Wegner and many more. Design furniture requires careful handling as surfaces are often in sensitive materials like leather, teak or lacquered wood. Jewellery, watches and silver objects are smaller but can have very high value. We transport them securely in the van, protected from impacts and moisture. Silver candelabras, cutlery sets and trays are packed individually. If you have won a combination of items – for example a design armchair, a painting and a silver set – we collect everything at once and adapt the protection for each item type.",
          },
          {
            heading: "Pickup locations at Stockholms Auktionsverk",
            content:
              "Stockholms Auktionsverk has two main pickup locations in Stockholm. Nybrogatan 32 in central Östermalm is the main auction hall where most quality auctions are held. Magasin 5 in Frihamnen is used for online auctions and larger-volume viewings. Enter the correct address when booking – it is listed in your collection information from the Auction House. We pick up from both locations and from all Auktionsverk facilities in the Stockholm area. Need to collect from multiple locations in one trip? Describe it when booking.",
          },
          {
            heading: "How delivery from Stockholms Auktionsverk works",
            listItems: [
              {
                label: "Book online",
                text: "Enter the pickup location (Nybrogatan 32 or Magasin 5), your home address and describe the item. Include the lot number if possible. You see the price instantly.",
              },
              {
                label: "We pick up at the Auction House",
                text: "Our crew arrives with a van and the right protective materials. We identify your item, protect it carefully and load it.",
              },
              {
                label: "We deliver home",
                text: "We drive to your home, carry in through stairwells and doors and place your auction find where you want it.",
              },
              {
                label: "Done",
                text: "Your find from Stockholms Auktionsverk is at home – carefully carried in and in place.",
              },
            ],
            afterContent:
              "The whole process usually takes 1–3 hours depending on distance and handling requirements. You can track the status in real time.",
          },
          {
            heading: "Why book Stockholms Auktionsverk delivery with Ungdomskraft?",
            content:
              "What makes Ungdomskraft unique is that we employ young people and give them real work experience. When you book delivery of your find from Stockholms Auktionsverk, you don't just get professional, careful handling – you also help young people enter the job market and build their futures.\n\nEvery job is staffed by motivated young workers who learn everything from customer service and logistics to careful handling of valuable objects. They learn to communicate with customers, plan routes, protect delicate surfaces and work efficiently as a team. This is work experience you can't get in a classroom – and every delivery we make gives a young person the chance to grow.\n\nWe have been operating since 2014 and have completed tens of thousands of transports in Stockholm and Gothenburg. Our young workers have carried in everything from Gustavian furniture sets and oil paintings by Swedish masters to chandeliers, silver collections and design classics worth hundreds of thousands of kronor. We know what it takes: the right protective materials, careful loading, smooth driving and gentle carrying.\n\nQuality runs through everything we do. Our workers don't take shortcuts – they add extra blankets, double-check the straps and communicate with you throughout the job. The energy and commitment that young workers bring shows in every delivery. They want to prove themselves and they take the job seriously.\n\nSustainability is another cornerstone. Buying at auction is inherently sustainable – you give items new life instead of buying new. We are proud to be part of that chain by transporting items home to their new owners. We optimise routes to minimise emissions and use reusable protective materials.\n\nFor businesses buying art or design at Stockholms Auktionsverk – for the office, lobby or showroom – we also offer invoicing and documentation for CSR reporting. You can show that your transport was carried out by a company that employs young people and contributes to social sustainability.\n\nWe offer fast pickup, carrying to the right spot and a competitive price with no hidden fees. You see the price when you book, everything is insured and we have our own vans in several sizes.\n\nIt is careful, professional help with purpose.\n\nDelivery from Stockholms Auktionsverk does not qualify for the Swedish RUT tax deduction, but several of our other services do – including moving help, carrying help and furniture assembly with a 50% deduction on the labour cost. Feel free to combine services in a single booking.",
          },
        ],
      },

      faq: [
        {
          question: "How much does delivery from Stockholms Auktionsverk cost?",
          answer:
            "The price depends on the item's size and distance. A painting or silver item starts from SEK 595. Larger furniture starts from SEK 995–1,699. You see the exact price when you book online.",
        },
        {
          question: "Can I get a RUT tax deduction on delivery from Stockholms Auktionsverk?",
          answer:
            "Delivery from Stockholms Auktionsverk does not in itself qualify for the Swedish RUT tax deduction. RUT applies to household services carried out in your home, such as moving help, carrying help and furniture assembly. Ungdomskraft offers several RUT-eligible services with a 50% deduction on the labour cost – easily combine them in one booking via our website.",
        },
        {
          question: "Is a van included in the Auktionsverk delivery?",
          answer:
            "Yes, a van is always included. We choose the right size based on what needs to be transported – you don't need to rent a vehicle or drive yourself.",
        },
        {
          question: "Is carrying included in the delivery price?",
          answer:
            "Yes, carrying is always included. We carry your item through stairwells, lifts and doors and place it where you want it – regardless of floor. No extra fees per floor.",
        },
        {
          question: "How do you protect valuable items from Stockholms Auktionsverk?",
          answer:
            "We use furniture blankets, bubble wrap, corner guards and straps. Paintings are transported standing, sculptures are secured individually and silver or porcelain is packed with shock-absorbing material. Everything is insured.",
        },
        {
          question: "Do you pick up from both Nybrogatan and Magasin 5?",
          answer:
            "Yes, we pick up from both Stockholms Auktionsverk locations – Nybrogatan 32 in the city centre and Magasin 5 in Frihamnen. Enter the correct address when booking.",
        },
        {
          question: "Can you carry heavy antique furniture from the Auction House?",
          answer:
            "Yes, our workers are experienced with heavy items – dressers, cabinets, dining tables and other furniture weighing 50–200 kg. We protect all surfaces with blankets and carry carefully through stairwells and narrow doorways.",
        },
        {
          question: "How quickly can you pick up from Stockholms Auktionsverk?",
          answer:
            "We can often pick up the same day or next day. Book as soon as you receive the pickup notification – it helps you avoid storage fees.",
        },
        {
          question: "Are you responsible for the item's condition?",
          answer:
            "We are responsible for transport – the item is insured against transport damage. The item's condition, authenticity and provenance are between you and Stockholms Auktionsverk. We recommend inspecting the item before we load it.",
        },
        {
          question: "Do I need to be at the Auction House for pickup?",
          answer:
            "No, you don't need to be there. Make sure the purchase is paid for and that we have your customer number or lot number. You can also authorise us to collect on your behalf – specify this when booking. You just need to be home to receive the delivery.",
        },
        {
          question: "Can you pick up multiple items from the same auction?",
          answer:
            "Yes, we pick up all your winning items in one trip. Describe all items when booking – we adjust van size and protective materials for the entire load.",
        },
        {
          question: "Can you transport jewellery and watches from the Auction House?",
          answer:
            "Yes, we also transport smaller but valuable items like jewellery, watches and silver objects. Everything is protected and insured during transport.",
        },
        {
          question: "Do you offer delivery from Stockholms Auktionsverk on weekends?",
          answer:
            "Yes, we offer delivery on weekends and some evenings. Check that the Auction House is open for pickup at the time you book.",
        },
        {
          question: "Is the delivery insured?",
          answer:
            "Yes, all our transports are fully insured. If your item is damaged during loading, transport or carrying, we will compensate you. You can book with confidence.",
        },
        {
          question:
            "In which municipalities do you offer delivery from Stockholms Auktionsverk?",
          answer:
            "We offer delivery from Stockholms Auktionsverk across the entire Stockholm area: Botkyrka, Danderyd, Ekerö, Haninge, Huddinge, Järfälla, Lidingö, Nacka, Norrtälje, Nykvarn, Nynäshamn, Salem, Sigtuna, Sollentuna, Solna, Stockholm, Sundbyberg, Södertälje, Tyresö, Täby, Upplands Väsby, Upplands-Bro, Vallentuna, Vaxholm, Värmdö and Österåker. We also serve the Gothenburg area: Ale, Alingsås, Gothenburg, Härryda, Kungälv, Kungsbacka, Lerum, Lilla Edet, Mölndal, Partille, Stenungsund, Tjörn and Öckerö.",
        },
      ],

      priceHint: PACKAGE_PRICE_HINT_EN.bud,

      pricingRows: getPackagePricingRows("bud", "en"),

      dynamicHeadings: {
        threeSteps: "Auktionsverk delivery in 3 easy steps",
        step2Text:
          "Our crew picks up your item at Nybrogatan or Magasin 5, protects it carefully with blankets and bubble wrap and loads the van. Then we drive home to you.",
        step3Title: "Your find is home",
        step3Desc:
          "Your find from Stockholms Auktionsverk is at home – carefully carried in and in place. Done.",
        ctaHeading: "Book Stockholms Auktionsverk delivery today",
        ctaText:
          "Enter the pickup location and your home address, describe the item and choose a time. You see the price instantly.",
      },
    },
  },

  // ────────────────────────────────────────────────────────────────────
  // Hemleverans Bukowskis
  // Vinkel: Hemleverans av auktionsfynd från Bukowskis – konst, design,
  // antika möbler, smycken, klockor och silver. Vi hämtar på Arsenalsgatan
  // eller Bukowskis Market och levererar hem med varsam inbärning.
  // ────────────────────────────────────────────────────────────────────
  {
    slug: "hemleverans-bukowskis",
    serviceCategory: "hemleverans",

    meta: {
      title:
        "Hemleverans Bukowskis – Vi Hämtar Ditt Auktionsfynd | Ungdomskraft",
      description:
        "Köpt på Bukowskis? Vi hämtar ditt objekt på Arsenalsgatan eller Bukowskis Market och levererar hem med varsam inbärning. Försäkrat. Boka idag!",
      h1: "Hemleverans Bukowskis",
    },

    hero: {
      heading: "Vi hämtar ditt vunna objekt – Hemleverans Bukowskis",
      intro:
        "Vunnit ett bud på Bukowskis? Grattis – men hur får du hem din oljemålning, designfåtölj eller antika byrå? Hämtfristerna är korta och objekten tunga eller ömtåliga. Vi löser det. Ungdomskraft hämtar ditt objekt på Arsenalsgatan 2 vid Berzelii Park eller från Bukowskis Market, skyddar det varsamt och levererar hem till dig – med inbärning och uppställning på plats.",
      heroImage: "/hero-hemleverans.png",
    },

    featureCards: {
      sinceText: "Bukowskis-leverans sedan 2014",
      storyText:
        "Sedan 2014 har Ungdomskraft hjälpt köpare på Bukowskis att få hem sina fynd – från tunga antika möbler och ömtålig konst till designklassiker, smycken och ljuskronor. Våra ungdomar vet hur man hanterar värdefulla föremål med den extra omsorg de förtjänar.",
      bookYourText: "Boka din Bukowskis-leverans",
      bookDescText:
        "Ange hämtplats (Arsenalsgatan eller Bukowskis Market) och din hemadress, beskriv objektet och välj en tid. Du ser priset direkt.",
      vehicleTitleText: "Skåpbilar för värdefulla auktionsfynd",
      vehicleDescText:
        "Vi kommer med rätt storlek på skåpbil och rätt skyddsmaterial – oavsett om det gäller en liten silverbägare eller ett stort möblemang. Allt skyddas med filtar, bubbelplast och spännband.",
    },

    itemsSection: {
      heading: "Vanliga objekt vi hämtar från Bukowskis",
      items: [
        {
          name: "Antika möbler",
          description:
            "Gustavianska byråer, skåp, sekretärer, bord, stolar och möbler från 1600-tal till tidigt 1900-tal",
          image: "/wardrobe.webp",
        },
        {
          name: "Konst & tavlor",
          description:
            "Oljemålningar, akvareller, grafik, skulpturer och fotografier – svensk och internationell konst",
          image: "/painting.webp",
        },
        {
          name: "Designmöbler",
          description:
            "Fåtöljer, soffor, lampor, bord och belysning av kända formgivare som Josef Frank, Bruno Mathsson och Hans Wegner",
          image: "/armchair.webp",
        },
        {
          name: "Mattor & textilier",
          description:
            "Persiska mattor, orientaliska mattor, svenska ryor, gobelänger och andra textila föremål",
          image: "/bedding.webp",
        },
        {
          name: "Silver, porslin & glas",
          description:
            "Silverbestick, kandelabrar, porslinserviser, kristallglas, vaser och figuriner",
          image: "/dishes.webp",
        },
        {
          name: "Smycken, klockor & övrigt",
          description:
            "Armbandsur, fickklockor, ringar, halsband, ljuskronor, speglar och asiatisk konst",
          image: "/box.webp",
        },
      ],
    },

    seoContent: {
      mainHeading:
        "Hemleverans från Bukowskis – vi hämtar, transporterar och bär in ditt auktionsfynd",
      mainText:
        "Bukowskis är ett av Nordens ledande auktionshus, grundat 1870 av Henning Bukowski. Med avdelningar för konst, modern design, antika möbler, smycken, klockor, silver och asiatisk konst auktioneras tusentals föremål varje år – från vardagsobjekt på Bukowskis Market till mästerverk på Important-auktionerna som når miljonbelopp. Att vinna ett bud på Bukowskis är spännande, men sedan kommer den praktiska frågan: hur får jag hem det här? Hämtfristen är begränsad och lagringsavgifter börjar ticka efter bara några dagar. Att transportera värdefulla antika möbler, ömtålig konst eller tunga skulpturer i egen bil innebär stor risk för skador. Ungdomskraft erbjuder hemleverans från Bukowskis lokaler – Arsenalsgatan 2 vid Berzelii Park i centrala Stockholm och Bukowskis Market. Vi hämtar ditt vunna objekt, skyddar det varsamt med filtar, bubbelplast och spännband och kör hem till dig. Vi bär in genom trapphus, hiss eller trångt garage och ställer på plats där du vill ha det. Inbärning ingår alltid – du ser priset direkt vid bokning. Varje vecka hämtar vi föremål från Bukowskis i alla prisklasser och storlekar – från en enstaka tavla eller silverbägare till ett helt dödsbo med möbler, mattor och konst. Våra ungdomar är vana vid att hantera ömtåliga och värdefulla föremål. Vi vet att en gustaviansk byrå kräver mjuka filtar och jämn körning, att en oljemålning måste stå upprätt och skyddas mot stötar och att en kristallkrona behöver extra polstring. Vi tar med oss allt skyddsmaterial som behövs. Tänk på att vi ansvarar för transporten från Bukowskis hem till dig – objektets skick, autenticitet och proveniens är mellan dig och Bukowskis.",
      preparationHeading: "Förbered inför din leverans från Bukowskis",
      preparationText:
        "Se till att ditt köp är fullt betalt och redo att hämtas. Kontrollera vilken lokal objektet finns på – Arsenalsgatan 2 eller Bukowskis Market. Ha ditt kundnummer eller objektnummer redo så att vårt team kan identifiera rätt föremål. Kontrollera hämtfristen – Bukowskis anger alltid sista hämtdag och lagringsavgifter tillkommer efter det. Om objektet är särskilt ömtåligt, tungt eller har ovanlig form – beskriv det tydligt vid bokning. Mät dörröppningar och trapphus hemma om det rör sig om stora möbler. Kolla att skåpbilen kan parkera nära din port.",
      additionalSections: [
        {
          heading: "Hemleverans av konst från Bukowskis",
          content:
            "Bukowskis konstavdelning är en av Nordens mest respekterade. Här auktioneras allt från svenska mästerverk av Anders Zorn, Carl Larsson och Hilma af Klint till internationella verk, skulpturer i brons och marmor och samtida konst. En oljemålning med förgylld ram kan väga 20–40 kg och tål inte stötar, fukt eller tryck mot duken. Vi skyddar konst med bubbelplast, hörnskydd och mjuka filtar. Tavlor transporteras alltid stående – aldrig liggande. Skulpturer säkras individuellt med polstring runt alla utsatta delar. Vi bär in och ställer på plats hemma hos dig – på väggen, på piedestalen eller i vitrinskåpet. Köpt flera verk på samma auktion? Vi hämtar allt i en tur och skyddar varje objekt separat. Vi hanterar konst i alla storlekar – från en liten akvarell till en monumental oljemålning som kräver specialhantering genom trånga dörröppningar.",
        },
        {
          heading: "Hemleverans av antika möbler och design från Bukowskis",
          content:
            "Bukowskis erbjuder möbler från 1600-talet till modern skandinavisk design – gustavianska byråer, rokokoskåp, empiresekreterärer, jugendmöbler och designklassiker av Josef Frank, Bruno Mathsson, Alvar Aalto och Hans Wegner. Dessa föremål är ofta tunga (50–200 kg), saknar originalförpackning och har ytor som är känsliga för repor och stötar. Att transportera dem i vanlig bil eller med ett hyrt släp innebär stor risk. Med Ungdomskraft hämtar vi dina möbler direkt på Arsenalsgatan 2 eller Bukowskis Market. Vi sveper in varje yta med mjuka filtar, tejpar fast lösa delar som lådor och handtag och säkrar med spännband i skåpbilen. Vi kör försiktigt och undviker hårda inbromsningar. Hemma hos dig bär vi in genom trapphus och dörrar och ställer möbeln exakt där du vill ha den. Behöver vi demontera delar för att passera en trång dörröppning löser vi det. Designmöbler kräver extra omsorg – läder, teak och lackerade ytor skyddas med ytterligare lager av filtar.",
        },
        {
          heading: "Hemleverans av smycken, klockor och silver från Bukowskis",
          content:
            "Bukowskis smyckes- och klocksavdelning auktionerar allt från antika ringar och diamanthalsband till vintage-klockor av Rolex, Patek Philippe och Omega. Silveravdelningen erbjuder kandelabrar, bestickuppsättningar, brickor, vaser och andra dekorativa föremål. Dessa objekt är mindre till storleken men kan ha mycket högt värde. Vi transporterar dem säkert i skåpbilen, skyddade från stötar och fukt. Silver packas individuellt med mjuk polstring. Om du har vunnit en kombination av föremål – till exempel en designfåtölj, en tavla och en silverkandelaber – hämtar vi allt på samma gång och anpassar skyddet för varje objektstyp. Vi ser till att varje föremål transporteras under optimala förhållanden oavsett storlek och värde.",
        },
        {
          heading: "Hämtplatser på Bukowskis",
          content:
            "Bukowskis huvudlokaler ligger på Arsenalsgatan 2 vid Berzelii Park i centrala Stockholm. Här hålls de stora Important-auktionerna med konst, design, antika möbler, smycken och klockor. Bukowskis Market hanterar objekt i lägre prisklasser och onlineauktioner med bredare utbud. Ange rätt adress vid bokning – den finns i din hämtinformation från Bukowskis. Vi hämtar från båda lokalerna och kör från alla Bukowskis anläggningar i Stockholmsområdet. Behöver du hämta från flera platser i samma tur? Beskriv det vid bokning så optimerar vi rutten.",
        },
        {
          heading: "Så fungerar leverans från Bukowskis",
          listItems: [
            {
              label: "Boka online",
              text: "Ange hämtplats (Arsenalsgatan 2 eller Bukowskis Market), din hemadress och beskriv objektet. Ange gärna objektnummer. Du ser priset direkt.",
            },
            {
              label: "Vi hämtar på Bukowskis",
              text: "Vårt team kommer med skåpbil och rätt skyddsmaterial. Vi identifierar ditt objekt, skyddar det varsamt och lastar.",
            },
            {
              label: "Vi levererar hem",
              text: "Vi kör hem till dig, bär in genom trapphus och dörrar och ställer ditt auktionsfynd på plats.",
            },
            {
              label: "Klart",
              text: "Ditt fynd från Bukowskis står hemma hos dig – varsamt inburet och uppställt.",
            },
          ],
          afterContent:
            "Hela processen tar normalt 1–3 timmar beroende på avstånd och objektets hanteringsbehov. Du kan följa statusen i realtid.",
        },
        {
          heading: "Varför boka leverans från Bukowskis med Ungdomskraft?",
          content:
            "Det som gör Ungdomskraft unikt är att vi anställer unga människor och ger dem riktig arbetslivserfarenhet. När du bokar hemleverans av ditt auktionsfynd från Bukowskis får du inte bara professionell och varsam hantering – du bidrar också till att unga människor kommer in på arbetsmarknaden och bygger sin framtid.\n\nVarje uppdrag bemannas av motiverade ungdomar som lär sig allt från kundservice och logistik till varsam hantering av värdefulla föremål. De lär sig att kommunicera med kunder, planera rutter, skydda ömtåliga ytor och arbeta effektivt i team. Det är arbetslivserfarenhet som inte går att få i ett klassrum – och varje leverans vi kör ger en ung person möjlighet att utvecklas.\n\nVi har funnits sedan 2014 och har genomfört tiotusentals transporter i Stockholm och Göteborg. Våra ungdomar har burit in allt från gustavianska möblemang och oljemålningar av svenska mästare till ljuskronor, silversamlingar och designklassiker värda hundratusentals kronor. Vi vet vad som krävs: rätt skyddsmaterial, försiktig lastning, jämn körning och varsam inbärning.\n\nKvalitet genomsyrar allt vi gör. Våra ungdomar tar inga genvägar – de lägger extra filtar, kontrollerar att spännbanden sitter och kommunicerar med dig under hela uppdraget. Den energi och det engagemang som unga medarbetare har märks i varje leverans. De vill visa vad de kan och de tar uppdraget på allvar.\n\nHållbarhet är en annan grundpelare. Att köpa på auktion är i sig ett hållbart val – du ger föremål nytt liv istället för att köpa nytt. Vi är stolta över att vara en del av den kedjan genom att transportera föremålen hem till sin nya ägare. Vi optimerar rutter för att minimera utsläpp och använder återanvändbara skyddsmaterial.\n\nFör företag som köper konst eller design på Bukowskis – till kontoret, lobbyn eller showroom – erbjuder vi faktura och underlag för CSR-rapportering. Du kan visa att din transport genomfördes av ett företag som sysselsätter ungdomar och bidrar till social hållbarhet.\n\nVi erbjuder snabb hämtning, inbärning till rätt plats och ett konkurrenskraftigt pris utan dolda avgifter. Du ser priset direkt vid bokning, allt är försäkrat och vi har egna skåpbilar i flera storlekar.\n\nDet är varsam, professionell hjälp med mening.\n\nHemleverans från Bukowskis ger inte rätt till RUT-avdrag, men flera av våra andra tjänster gör det – exempelvis flytt, bärhjälp och möbelmontering med 50 % avdrag på arbetskostnaden. Kombinera gärna tjänster i en och samma bokning.",
        },
      ],
    },

    faq: [
      {
        question: "Vad kostar hemleverans från Bukowskis?",
        answer:
          "Priset beror på objektets storlek och avstånd. En tavla eller silverföremål kostar från 595 kr. Större möbler kostar från 995–2 495 kr. Du ser det exakta priset direkt när du bokar online.",
      },
      {
        question: "Kan jag få RUT-avdrag på hemleverans från Bukowskis?",
        answer:
          "Hemleverans från Bukowskis ger i sig inte rätt till RUT-avdrag. RUT gäller hushållsnära tjänster som utförs i din bostad, exempelvis flytt, bärhjälp och möbelmontering. Ungdomskraft erbjuder flera RUT-berättigade tjänster med 50 % avdrag på arbetskostnaden – kombinera enkelt i en bokning via vår hemsida.",
      },
      {
        question: "Ingår skåpbil i leveransen från Bukowskis?",
        answer:
          "Ja, skåpbil ingår alltid. Vi väljer rätt storlek utifrån vad som ska transporteras – du behöver inte hyra bil eller köra själv.",
      },
      {
        question: "Ingår inbärning i priset för leverans från Bukowskis?",
        answer:
          "Ja, inbärning ingår alltid. Vi bär in ditt objekt genom trapphus, hiss och dörrar och ställer på plats – oavsett våning. Inga extra avgifter per våningsplan.",
      },
      {
        question: "Hur skyddar ni värdefulla föremål från Bukowskis?",
        answer:
          "Vi använder möbelfiltar, bubbelplast, hörnskydd och spännband. Tavlor transporteras stående, skulpturer säkras individuellt och silver eller porslin packas med stötdämpande material. Allt är försäkrat.",
      },
      {
        question: "Hämtar ni från både Arsenalsgatan och Bukowskis Market?",
        answer:
          "Ja, vi hämtar från båda Bukowskis lokaler – Arsenalsgatan 2 vid Berzelii Park och Bukowskis Market. Ange rätt adress vid bokning.",
      },
      {
        question: "Kan ni hämta tunga antika möbler från Bukowskis?",
        answer:
          "Ja, våra ungdomar är vana vid att bära tunga föremål – byråer, skåp, matbord och andra möbler som väger 50–200 kg. Vi skyddar alla ytor med filtar och bär varsamt genom trapphus och trånga dörröppningar.",
      },
      {
        question: "Hur snabbt kan ni hämta från Bukowskis?",
        answer:
          "Vi kan ofta hämta redan samma dag eller nästa dag. Boka så snart du fått hämtbesked – det hjälper dig att undvika lagringsavgifter.",
      },
      {
        question: "Tar ni ansvar för objektets skick från Bukowskis?",
        answer:
          "Vi ansvarar för transporten – objektet är försäkrat mot transportskador. Objektets skick, autenticitet och proveniens är mellan dig och Bukowskis. Vi rekommenderar att du inspekterar objektet innan vi lastar.",
      },
      {
        question: "Behöver jag vara med vid hämtning på Bukowskis?",
        answer:
          "Nej, du behöver inte vara med. Se till att köpet är betalt och att vi har ditt kundnummer eller objektnummer. Du kan även ge oss fullmakt att hämta – ange det vid bokning. Du behöver bara vara hemma för att ta emot leveransen.",
      },
      {
        question: "Kan ni leverera konst och möbler från Bukowskis på helger?",
        answer:
          "Ja, vi erbjuder hemleverans på helger och vissa kvällar. Kontrollera att Bukowskis har öppet för upphämtning den tid du bokar.",
      },
      {
        question: "Kan ni hämta flera objekt från samma Bukowskis-auktion?",
        answer:
          "Ja, vi hämtar alla dina vunna objekt i en tur. Beskriv samtliga föremål vid bokning – vi anpassar skåpbilsstorlek och skyddsmaterial efter hela lasten.",
      },
      {
        question: "Kan ni transportera smycken och klockor från Bukowskis?",
        answer:
          "Ja, vi transporterar även mindre men värdefulla objekt som smycken, klockor och silverföremål. Allt skyddas och försäkras under transporten.",
      },
      {
        question: "Är leveransen från Bukowskis försäkrad?",
        answer:
          "Ja, alla våra transporter är helförsäkrade. Om ditt objekt skadas under lastning, transport eller inbärning ersätter vi det. Du kan boka med trygghet.",
      },
      {
        question:
          "I vilka kommuner erbjuder ni hemleverans från Bukowskis?",
        answer:
          "Vi erbjuder hemleverans från Bukowskis i hela Stockholmsområdet: Botkyrka, Danderyd, Ekerö, Haninge, Huddinge, Järfälla, Lidingö, Nacka, Norrtälje, Nykvarn, Nynäshamn, Salem, Sigtuna, Sollentuna, Solna, Stockholm, Sundbyberg, Södertälje, Tyresö, Täby, Upplands Väsby, Upplands-Bro, Vallentuna, Vaxholm, Värmdö och Österåker. Vi finns även i Göteborgsområdet: Ale, Alingsås, Göteborg, Härryda, Kungälv, Kungsbacka, Lerum, Lilla Edet, Mölndal, Partille, Stenungsund, Tjörn och Öckerö.",
      },
    ],

    priceHint: PACKAGE_PRICE_HINT_SV.bud,

    pricingRows: getPackagePricingRows("bud", "sv"),

    dynamicHeadings: {
      threeSteps: "Leverans från Bukowskis i 3 enkla steg",
      step2Text:
        "Våra ungdomar hämtar ditt objekt på Arsenalsgatan eller Bukowskis Market, skyddar det varsamt med filtar och bubbelplast och lastar skåpbilen. Sedan kör vi hem till dig.",
      step3Title: "Ditt fynd är hemma",
      step3Desc:
        "Ditt auktionsfynd från Bukowskis står hemma hos dig – varsamt inburet och på plats. Klart.",
      ctaHeading: "Boka leverans från Bukowskis idag",
      ctaText:
        "Ange hämtplats och din hemadress, beskriv objektet och välj en tid. Du ser priset direkt.",
    },

    en: {
      slug: "bukowskis-delivery",

      meta: {
        title:
          "Bukowskis Delivery – We Pick Up & Deliver Your Auction Find Home | Ungdomskraft",
        description:
          "Bought at Bukowskis? We pick up your item at Arsenalsgatan or Bukowskis Market and deliver home with careful carrying. Book auction delivery today.",
        h1: "Bukowskis delivery",
      },

      hero: {
        heading: "We pick up your winning bid – Bukowskis delivery",
        intro:
          "Won a bid at Bukowskis? Congratulations – but how do you get that oil painting, design armchair or antique dresser home? Pickup deadlines are tight and the items are heavy or fragile. Ungdomskraft picks up your item at Arsenalsgatan 2 by Berzelii Park or from Bukowskis Market, protects it carefully and delivers to your door – with carrying and placement inside.",
      },

      featureCards: {
        sinceText: "Bukowskis delivery since 2014",
        storyText:
          "Since 2014, Ungdomskraft has helped buyers at Bukowskis get their finds home – from heavy antique furniture and delicate art to design classics, jewellery and chandeliers. Our young workers know how to handle valuable items with the extra care they deserve.",
        bookYourText: "Book your Bukowskis delivery",
        bookDescText:
          "Enter the pickup location (Arsenalsgatan or Bukowskis Market) and your home address, describe the item and choose a time. You see the price instantly.",
        vehicleTitleText: "Vans equipped for valuable auction finds",
        vehicleDescText:
          "We bring the right van size and protective materials – whether it's a small silver goblet or a full furniture set. Everything is protected with blankets, bubble wrap and straps.",
      },

      itemsSection: {
        heading: "Common items we collect from Bukowskis",
        items: [
          {
            name: "Antique furniture",
            description:
              "Gustavian dressers, cabinets, writing desks, tables, chairs and furniture from the 17th to early 20th century",
            image: "/wardrobe.webp",
          },
          {
            name: "Art & paintings",
            description:
              "Oil paintings, watercolours, prints, sculptures and photographs – Swedish and international art",
            image: "/painting.webp",
          },
          {
            name: "Design furniture",
            description:
              "Armchairs, sofas, lamps, tables and lighting by renowned designers such as Josef Frank, Bruno Mathsson and Hans Wegner",
            image: "/armchair.webp",
          },
          {
            name: "Rugs & textiles",
            description:
              "Persian rugs, oriental rugs, Swedish rya rugs, tapestries and other textile collectibles",
            image: "/bedding.webp",
          },
          {
            name: "Silver, porcelain & glass",
            description:
              "Silverware, candelabras, dinnerware sets, crystal glass, vases and figurines",
            image: "/dishes.webp",
          },
          {
            name: "Jewellery, watches & other",
            description:
              "Wristwatches, pocket watches, rings, necklaces, chandeliers, mirrors and Asian art",
            image: "/box.webp",
          },
        ],
      },

      seoContent: {
        mainHeading:
          "Delivery from Bukowskis – we pick up, transport and carry in your auction find",
        mainText:
          "Bukowskis is one of the leading auction houses in the Nordic region, founded in 1870 by Henning Bukowski. With departments for fine art, modern design, antique furniture, jewellery, watches, silver and Asian art, thousands of items are auctioned every year – from everyday objects at Bukowskis Market to masterpieces at the Important auctions reaching millions of kronor. Winning a bid at Bukowskis is exciting, but then comes the practical question: how do I get this home? The pickup deadline is limited and storage fees start accumulating after just a few days. Transporting valuable antique furniture, delicate art or heavy sculptures in your own car means significant risk of damage. Ungdomskraft offers home delivery from Bukowskis locations – Arsenalsgatan 2 by Berzelii Park in central Stockholm and Bukowskis Market. We pick up your winning item, protect it carefully with blankets, bubble wrap and straps, and drive it home to you. We carry in through stairwells, lifts or narrow garages and place it exactly where you want it. Carrying is always included – you see the price when you book. Every week we collect items from Bukowskis in all price ranges and sizes – from a single painting or silver goblet to an entire estate lot with furniture, rugs and art. Our young workers are experienced with fragile and valuable items. We know that a Gustavian dresser needs soft blankets and smooth driving, that an oil painting must stand upright and be shielded from impacts, and that a crystal chandelier requires extra padding. Please note that we are responsible for the transport from Bukowskis to your home – the item's condition, authenticity and provenance are between you and Bukowskis.",
        preparationHeading: "Prepare for your delivery from Bukowskis",
        preparationText:
          "Make sure your purchase is fully paid for and ready for collection. Check which location the item is at – Arsenalsgatan 2 or Bukowskis Market. Have your customer number or lot number ready so our team can identify the right item. Check the pickup deadline – Bukowskis always specifies the last collection date and storage fees apply after that. If the item is particularly fragile, heavy or unusually shaped, describe it clearly when booking. Measure doorways and stairwells at home if the item is large. Check that the van can park close to your entrance.",
        additionalSections: [
          {
            heading: "Delivering art from Bukowskis",
            content:
              "Bukowskis art department is one of the most respected in the Nordic region. Everything from Swedish masterpieces by Anders Zorn, Carl Larsson and Hilma af Klint to international works, sculptures in bronze and marble and contemporary art is auctioned here. An oil painting with a gilded frame can weigh 20–40 kg and cannot tolerate impacts, moisture or pressure on the canvas. We protect art with bubble wrap, corner guards and soft blankets. Paintings are always transported standing – never lying. Sculptures are secured individually with padding around all exposed parts. We carry in and place at your home – on the wall, on the pedestal or in the display cabinet. Bought multiple works at the same auction? We collect everything in one trip and protect each piece separately. We handle art of all sizes – from a small watercolour to a monumental oil painting requiring special handling through narrow doorways.",
          },
          {
            heading: "Delivering antique furniture and design from Bukowskis",
            content:
              "Bukowskis offers furniture from the 17th century to modern Scandinavian design – Gustavian dressers, Rococo cabinets, Empire writing desks, Art Nouveau furniture and design classics by Josef Frank, Bruno Mathsson, Alvar Aalto and Hans Wegner. These items are often heavy (50–200 kg), come without packaging and have surfaces sensitive to scratches and impacts. Transporting them in your own car or with a rented trailer means significant risk. With Ungdomskraft, we pick up your furniture directly at Arsenalsgatan 2 or Bukowskis Market. We wrap every surface with soft blankets, tape down loose parts like drawers and handles, and secure everything with straps. We drive carefully and avoid hard braking. At your home we carry in through stairwells and doors and place the piece exactly where you want it. Design furniture requires extra care – leather, teak and lacquered surfaces are protected with additional layers of blankets.",
          },
          {
            heading: "Delivering jewellery, watches and silver from Bukowskis",
            content:
              "Bukowskis jewellery and watch department auctions everything from antique rings and diamond necklaces to vintage watches by Rolex, Patek Philippe and Omega. The silver department offers candelabras, cutlery sets, trays, vases and other decorative objects. These items are smaller but can have very high value. We transport them securely in the van, protected from impacts and moisture. Silver is packed individually with soft padding. If you have won a combination of items – for example a design armchair, a painting and a silver candelabra – we collect everything at once and adapt the protection for each item type. We ensure every item is transported under optimal conditions regardless of size and value.",
          },
          {
            heading: "Pickup locations at Bukowskis",
            content:
              "Bukowskis main premises are at Arsenalsgatan 2 by Berzelii Park in central Stockholm. This is where the major Important auctions are held with art, design, antique furniture, jewellery and watches. Bukowskis Market handles lower-priced items and online auctions with a broader range. Enter the correct address when booking – it is listed in your collection information from Bukowskis. We pick up from both locations and from all Bukowskis facilities in the Stockholm area. Need to collect from multiple locations in one trip? Describe it when booking and we will optimise the route.",
          },
          {
            heading: "How delivery from Bukowskis works",
            listItems: [
              {
                label: "Book online",
                text: "Enter the pickup location (Arsenalsgatan 2 or Bukowskis Market), your home address and describe the item. Include the lot number if possible. You see the price instantly.",
              },
              {
                label: "We pick up at Bukowskis",
                text: "Our crew arrives with a van and the right protective materials. We identify your item, protect it carefully and load it.",
              },
              {
                label: "We deliver home",
                text: "We drive to your home, carry in through stairwells and doors and place your auction find where you want it.",
              },
              {
                label: "Done",
                text: "Your find from Bukowskis is at home – carefully carried in and in place.",
              },
            ],
            afterContent:
              "The whole process usually takes 1–3 hours depending on distance and handling requirements. You can track the status in real time.",
          },
          {
            heading: "Why book Bukowskis delivery with Ungdomskraft?",
            content:
              "What makes Ungdomskraft unique is that we employ young people and give them real work experience. When you book delivery of your find from Bukowskis, you don't just get professional, careful handling – you also help young people enter the job market and build their futures.\n\nEvery job is staffed by motivated young workers who learn everything from customer service and logistics to careful handling of valuable objects. They learn to communicate with customers, plan routes, protect delicate surfaces and work efficiently as a team. This is work experience you can't get in a classroom – and every delivery we make gives a young person the chance to grow.\n\nWe have been operating since 2014 and have completed tens of thousands of transports in Stockholm and Gothenburg. Our young workers have carried in everything from Gustavian furniture sets and oil paintings by Swedish masters to chandeliers, silver collections and design classics worth hundreds of thousands of kronor. We know what it takes: the right protective materials, careful loading, smooth driving and gentle carrying.\n\nQuality runs through everything we do. Our workers don't take shortcuts – they add extra blankets, double-check the straps and communicate with you throughout the job. The energy and commitment that young workers bring shows in every delivery. They want to prove themselves and they take the job seriously.\n\nSustainability is another cornerstone. Buying at auction is inherently sustainable – you give items new life instead of buying new. We are proud to be part of that chain by transporting items home to their new owners. We optimise routes to minimise emissions and use reusable protective materials.\n\nFor businesses buying art or design at Bukowskis – for the office, lobby or showroom – we also offer invoicing and documentation for CSR reporting. You can show that your transport was carried out by a company that employs young people and contributes to social sustainability.\n\nWe offer fast pickup, carrying to the right spot and a competitive price with no hidden fees. You see the price when you book, everything is insured and we have our own vans in several sizes.\n\nIt is careful, professional help with purpose.\n\nDelivery from Bukowskis does not qualify for the Swedish RUT tax deduction, but several of our other services do – including moving help, carrying help and furniture assembly with a 50% deduction on the labour cost. Feel free to combine services in a single booking.",
          },
        ],
      },

      faq: [
        {
          question: "How much does delivery from Bukowskis cost?",
          answer:
            "The price depends on the item's size and distance. A painting or silver item starts from SEK 595. Larger furniture starts from SEK 995–1,699. You see the exact price when you book online.",
        },
        {
          question: "Can I get a RUT tax deduction on delivery from Bukowskis?",
          answer:
            "Delivery from Bukowskis does not in itself qualify for the Swedish RUT tax deduction. RUT applies to household services carried out in your home, such as moving help, carrying help and furniture assembly. Ungdomskraft offers several RUT-eligible services with a 50% deduction on the labour cost – easily combine them in one booking via our website.",
        },
        {
          question: "Is a van included in the Bukowskis delivery?",
          answer:
            "Yes, a van is always included. We choose the right size based on what needs to be transported – you don't need to rent a vehicle or drive yourself.",
        },
        {
          question: "Is carrying included in the Bukowskis delivery price?",
          answer:
            "Yes, carrying is always included. We carry your item through stairwells, lifts and doors and place it where you want it – regardless of floor. No extra fees per floor.",
        },
        {
          question: "How do you protect valuable items from Bukowskis?",
          answer:
            "We use furniture blankets, bubble wrap, corner guards and straps. Paintings are transported standing, sculptures are secured individually and silver or porcelain is packed with shock-absorbing material. Everything is insured.",
        },
        {
          question: "Do you pick up from both Arsenalsgatan and Bukowskis Market?",
          answer:
            "Yes, we pick up from both Bukowskis locations – Arsenalsgatan 2 by Berzelii Park and Bukowskis Market. Enter the correct address when booking.",
        },
        {
          question: "Can you carry heavy antique furniture from Bukowskis?",
          answer:
            "Yes, our workers are experienced with heavy items – dressers, cabinets, dining tables and other furniture weighing 50–200 kg. We protect all surfaces with blankets and carry carefully through stairwells and narrow doorways.",
        },
        {
          question: "How quickly can you pick up from Bukowskis?",
          answer:
            "We can often pick up the same day or next day. Book as soon as you receive the pickup notification – it helps you avoid storage fees.",
        },
        {
          question: "Are you responsible for the item's condition from Bukowskis?",
          answer:
            "We are responsible for transport – the item is insured against transport damage. The item's condition, authenticity and provenance are between you and Bukowskis. We recommend inspecting the item before we load it.",
        },
        {
          question: "Do I need to be at Bukowskis for pickup?",
          answer:
            "No, you don't need to be there. Make sure the purchase is paid for and that we have your customer number or lot number. You can also authorise us to collect on your behalf – specify this when booking. You just need to be home to receive the delivery.",
        },
        {
          question: "Can you pick up multiple items from the same Bukowskis auction?",
          answer:
            "Yes, we pick up all your winning items in one trip. Describe all items when booking – we adjust van size and protective materials for the entire load.",
        },
        {
          question: "Can you transport jewellery and watches from Bukowskis?",
          answer:
            "Yes, we also transport smaller but valuable items like jewellery, watches and silver objects. Everything is protected and insured during transport.",
        },
        {
          question: "Do you offer delivery from Bukowskis on weekends?",
          answer:
            "Yes, we offer delivery on weekends and some evenings. Check that Bukowskis is open for pickup at the time you book.",
        },
        {
          question: "Is the Bukowskis delivery insured?",
          answer:
            "Yes, all our transports are fully insured. If your item is damaged during loading, transport or carrying, we will compensate you. You can book with confidence.",
        },
        {
          question:
            "In which municipalities do you offer delivery from Bukowskis?",
          answer:
            "We offer delivery from Bukowskis across the entire Stockholm area: Botkyrka, Danderyd, Ekerö, Haninge, Huddinge, Järfälla, Lidingö, Nacka, Norrtälje, Nykvarn, Nynäshamn, Salem, Sigtuna, Sollentuna, Solna, Stockholm, Sundbyberg, Södertälje, Tyresö, Täby, Upplands Väsby, Upplands-Bro, Vallentuna, Vaxholm, Värmdö and Österåker. We also serve the Gothenburg area: Ale, Alingsås, Gothenburg, Härryda, Kungälv, Kungsbacka, Lerum, Lilla Edet, Mölndal, Partille, Stenungsund, Tjörn and Öckerö.",
        },
      ],

      priceHint: PACKAGE_PRICE_HINT_EN.bud,

      pricingRows: getPackagePricingRows("bud", "en"),

      dynamicHeadings: {
        threeSteps: "Bukowskis delivery in 3 easy steps",
        step2Text:
          "Our crew picks up your item at Arsenalsgatan or Bukowskis Market, protects it carefully with blankets and bubble wrap and loads the van. Then we drive home to you.",
        step3Title: "Your find is home",
        step3Desc:
          "Your find from Bukowskis is at home – carefully carried in and in place. Done.",
        ctaHeading: "Book Bukowskis delivery today",
        ctaText:
          "Enter the pickup location and your home address, describe the item and choose a time. You see the price instantly.",
      },
    },
  },

  // ────────────────────────────────────────────────────────────────────
  // Hemleverans Uppsala Auktionskammare
  // Vinkel: Hemleverans av auktionsfynd från Uppsala Auktionskammare –
  // konst, antika möbler, design, silver, smycken och porslin. Vi hämtar
  // i Uppsala och levererar hem med varsam inbärning.
  // ────────────────────────────────────────────────────────────────────
  {
    slug: "hemleverans-uppsala-auktionskammare",
    serviceCategory: "hemleverans",

    meta: {
      title:
        "Hemleverans Uppsala Auktionskammare – Vi Hämtar Ditt Auktionsfynd | Ungdomskraft",
      description:
        "Köpt på Uppsala Auktionskammare? Vi hämtar ditt objekt i Uppsala och levererar hem med varsam inbärning. Skåpbil och bärhjälp ingår. Försäkrat. Boka idag!",
      h1: "Hemleverans Uppsala Auktionskammare",
    },

    hero: {
      heading:
        "Vi hämtar ditt vunna objekt – Hemleverans Uppsala Auktionskammare",
      intro:
        "Vunnit ett bud på Uppsala Auktionskammare? Grattis – men hur får du hem din antika byrå, tavla eller designfåtölj? Hämtfristerna är korta och objekten ofta tunga eller ömtåliga. Vi löser det. Ungdomskraft hämtar ditt objekt på Kungsängsgatan 27 i Uppsala, skyddar det varsamt och levererar hem till dig – med inbärning och uppställning på plats.",
      heroImage: "/hero-hemleverans.png",
    },

    featureCards: {
      sinceText: "Auktionsleverans sedan 2014",
      storyText:
        "Sedan 2014 har Ungdomskraft hjälpt köpare på auktionshus runt om i Mälardalen att få hem sina fynd – från tunga antika möbler och ömtålig konst till designklassiker, silver och porslin. Våra ungdomar vet hur man hanterar värdefulla föremål med den extra omsorg de förtjänar.",
      bookYourText: "Boka din auktionsleverans",
      bookDescText:
        "Ange Uppsala Auktionskammares adress och din hemadress, beskriv objektet och välj en tid. Du ser priset direkt.",
      vehicleTitleText: "Skåpbilar för värdefulla auktionsfynd",
      vehicleDescText:
        "Vi kommer med rätt storlek på skåpbil och rätt skyddsmaterial – oavsett om det gäller en liten silverbägare eller ett stort möblemang. Allt skyddas med filtar, bubbelplast och spännband.",
    },

    itemsSection: {
      heading: "Vanliga objekt vi hämtar från Uppsala Auktionskammare",
      items: [
        {
          name: "Antika möbler",
          description:
            "Gustavianska byråer, skåp, sekretärer, bord, stolar och möbler från 1700-tal till tidigt 1900-tal",
          image: "/wardrobe.webp",
        },
        {
          name: "Konst & tavlor",
          description:
            "Oljemålningar, akvareller, grafik, litografier, skulpturer och fotografier – svensk och internationell konst",
          image: "/painting.webp",
        },
        {
          name: "Designmöbler",
          description:
            "Fåtöljer, soffor, lampor, bord och belysning av kända skandinaviska formgivare",
          image: "/armchair.webp",
        },
        {
          name: "Mattor & textilier",
          description:
            "Persiska mattor, orientaliska mattor, svenska ryor, gobelänger och andra textila föremål",
          image: "/bedding.webp",
        },
        {
          name: "Silver, porslin & glas",
          description:
            "Silverbestick, kandelabrar, porslinserviser, kristallglas, vaser och figuriner",
          image: "/dishes.webp",
        },
        {
          name: "Smycken, klockor & övrigt",
          description:
            "Armbandsur, fickklockor, ringar, halsband, ljuskronor, speglar och kuriosa",
          image: "/box.webp",
        },
      ],
    },

    seoContent: {
      mainHeading:
        "Hemleverans från Uppsala Auktionskammare – vi hämtar, transporterar och bär in ditt auktionsfynd",
      mainText:
        "Uppsala Auktionskammare är ett av Mellansveriges mest etablerade auktionshus med regelbundna auktioner av konst, antika möbler, design, silver, porslin, smycken och klockor. Auktionshusets lokaler på Kungsängsgatan 27 i Uppsala drar köpare från hela Mälardalen – Uppsala, Stockholm, Enköping, Västerås och långt därutöver. Att vinna ett bud på Uppsala Auktionskammare är spännande, men sedan kommer den praktiska frågan: hur får jag hem det här? Hämtfristen är begränsad och lagringsavgifter börjar ticka efter bara några dagar. Att transportera värdefulla antika möbler, ömtålig konst eller tunga skulpturer i egen bil innebär stor risk för skador – och avståndet från Uppsala hem kan göra resan extra besvärlig med ömtåliga föremål. Ungdomskraft erbjuder hemleverans från Uppsala Auktionskammares lokaler på Kungsängsgatan 27. Vi hämtar ditt vunna objekt, skyddar det varsamt med filtar, bubbelplast och spännband och kör hem till dig – oavsett om du bor i Uppsala, Stockholm eller någon annanstans i Mälardalen. Vi bär in genom trapphus, hiss eller trångt garage och ställer på plats där du vill ha det. Inbärning ingår alltid – du ser priset direkt vid bokning. Varje vecka hämtar vi föremål från auktionshus i alla prisklasser och storlekar – från en enstaka tavla eller silverservis till ett helt dödsbo med möbler, mattor och konst. Våra ungdomar är vana vid att hantera ömtåliga och värdefulla föremål. Vi vet att en gustaviansk byrå kräver mjuka filtar och jämn körning, att en oljemålning måste stå upprätt och skyddas mot stötar och att en kristallkrona behöver extra polstring. Vi tar med oss allt skyddsmaterial som behövs. Tänk på att vi ansvarar för transporten från Uppsala Auktionskammare hem till dig – objektets skick, autenticitet och proveniens är mellan dig och auktionshuset.",
      preparationHeading:
        "Förbered inför din leverans från Uppsala Auktionskammare",
      preparationText:
        "Se till att ditt köp är fullt betalt och redo att hämtas. Ha ditt kundnummer eller objektnummer redo så att vårt team kan identifiera rätt föremål vid hämtning. Kontrollera hämtfristen – Uppsala Auktionskammare anger alltid sista hämtdag och lagringsavgifter tillkommer efter det. Om objektet är särskilt ömtåligt, tungt eller har ovanlig form – beskriv det tydligt vid bokning. Mät dörröppningar och trapphus hemma om det rör sig om stora möbler. Kolla att skåpbilen kan parkera nära din port.",
      additionalSections: [
        {
          heading: "Hemleverans av konst från Uppsala Auktionskammare",
          content:
            "Uppsala Auktionskammare auktionerar regelbundet konst av både svenska och internationella konstnärer – oljemålningar, akvareller, grafik, litografier och skulpturer. En oljemålning med förgylld ram kan väga 20–40 kg och tål inte stötar, fukt eller tryck mot duken. Vi skyddar konst med bubbelplast, hörnskydd och mjuka filtar. Tavlor transporteras alltid stående – aldrig liggande – och skulpturer säkras individuellt med polstring runt alla utsatta delar. Vi bär in och ställer på plats hemma hos dig – på väggen, på piedestalen eller i vitrinskåpet. Köpt flera verk på samma auktion? Vi hämtar allt i en tur och skyddar varje objekt separat. Oavsett om det är en liten akvarell eller en stor oljemålning som kräver specialhantering genom trånga dörröppningar har vi erfarenheten och materialet för att hantera det varsamt.",
        },
        {
          heading:
            "Hemleverans av antika möbler och design från Uppsala Auktionskammare",
          content:
            "Uppsala Auktionskammare erbjuder möbler från 1700-talet till modern skandinavisk design – gustavianska byråer, rokokoskåp, empiresekreterärer och designklassiker av kända formgivare. Dessa föremål är ofta tunga (50–200 kg), saknar originalförpackning och har ytor som är känsliga för repor och stötar. Att transportera dem i vanlig bil eller med ett hyrt släp innebär stor risk – särskilt på den längre sträckan från Uppsala hem. Med Ungdomskraft hämtar vi dina möbler direkt på Kungsängsgatan 27. Vi sveper in varje yta med mjuka filtar, tejpar fast lösa delar som lådor och handtag och säkrar med spännband i skåpbilen. Vi kör försiktigt och undviker hårda inbromsningar. Hemma hos dig bär vi in genom trapphus och dörrar och ställer möbeln exakt där du vill ha den. Behöver vi demontera delar för att passera en trång dörröppning löser vi det. Designmöbler kräver extra omsorg – läder, teak och lackerade ytor skyddas med ytterligare lager av filtar.",
        },
        {
          heading:
            "Hemleverans av silver, porslin och smycken från Uppsala Auktionskammare",
          content:
            "Uppsala Auktionskammares auktioner av silver, porslin och smycken lockar samlare från hela Mälardalen. Silverbestick, kandelabrar, brickor och dekorativa föremål i silver kräver varsam hantering för att undvika bucklor och repor. Porslinserviser och kristallglas är ömtåliga och behöver stötdämpande förpackning. Smycken och klockor är mindre till storleken men kan ha högt värde. Vi transporterar allt säkert i skåpbilen – silver och porslin packas individuellt med mjuk polstring, kristall skyddas med bubbelplast och smycken transporteras stötsäkert. Om du har vunnit en kombination av föremål – till exempel en byrå, ett par tavlor och en silverservis – hämtar vi allt på samma gång och anpassar skyddet för varje objektstyp. Du behöver inte oroa dig för att blanda ömtåliga föremål i samma last – vi separerar och skyddar varje del.",
        },
        {
          heading: "Transport Uppsala–Stockholm och övriga Mälardalen",
          content:
            "Många som köper på Uppsala Auktionskammare bor i Stockholm eller andra delar av Mälardalen. Sträckan Uppsala–Stockholm är cirka 70 km och tar runt en timme med skåpbil. Vi kör regelbundet mellan Uppsala och Stockholm och kan hämta på Kungsängsgatan 27 och leverera hem till dig i hela Stockholms- och Uppsalaområdet. Bor du i Enköping, Västerås, Sigtuna, Norrtälje eller någon annan kommun i Mälardalen? Vi kör dit också. Ange din hemadress vid bokning så ser du priset direkt – det inkluderar hela sträckan, skåpbil, bärhjälp och inbärning utan dolda avgifter.",
        },
        {
          heading: "Så fungerar leverans från Uppsala Auktionskammare",
          listItems: [
            {
              label: "Boka online",
              text: "Ange Kungsängsgatan 27 i Uppsala som hämtplats, din hemadress och beskriv objektet. Ange gärna objektnummer. Du ser priset direkt.",
            },
            {
              label: "Vi hämtar på auktionskammaren",
              text: "Vårt team kommer med skåpbil och rätt skyddsmaterial. Vi identifierar ditt objekt, skyddar det varsamt och lastar.",
            },
            {
              label: "Vi levererar hem",
              text: "Vi kör hem till dig – oavsett om det är i Uppsala, Stockholm eller Mälardalen. Vi bär in genom trapphus och dörrar och ställer ditt auktionsfynd på plats.",
            },
            {
              label: "Klart",
              text: "Ditt fynd från Uppsala Auktionskammare står hemma hos dig – varsamt inburet och uppställt.",
            },
          ],
          afterContent:
            "Hela processen tar normalt 1–4 timmar beroende på avstånd och objektets hanteringsbehov. Du kan följa statusen i realtid.",
        },
        {
          heading:
            "Varför boka leverans från Uppsala Auktionskammare med Ungdomskraft?",
          content:
            "Det som gör Ungdomskraft unikt är att vi anställer unga människor och ger dem riktig arbetslivserfarenhet. När du bokar hemleverans av ditt auktionsfynd från Uppsala Auktionskammare får du inte bara professionell och varsam hantering – du bidrar också till att unga människor kommer in på arbetsmarknaden och bygger sin framtid.\n\nVarje uppdrag bemannas av motiverade ungdomar som lär sig allt från kundservice och logistik till varsam hantering av värdefulla föremål. De lär sig att kommunicera med kunder, planera rutter, skydda ömtåliga ytor och arbeta effektivt i team. Det är arbetslivserfarenhet som inte går att få i ett klassrum – och varje leverans vi kör ger en ung person möjlighet att utvecklas.\n\nVi har funnits sedan 2014 och har genomfört tiotusentals transporter i Stockholm, Uppsala och Göteborg. Våra ungdomar har burit in allt från gustavianska möblemang och oljemålningar till ljuskronor, silversamlingar och designklassiker värda hundratusentals kronor. Vi vet vad som krävs: rätt skyddsmaterial, försiktig lastning, jämn körning och varsam inbärning.\n\nKvalitet genomsyrar allt vi gör. Våra ungdomar tar inga genvägar – de lägger extra filtar, kontrollerar att spännbanden sitter och kommunicerar med dig under hela uppdraget. Den energi och det engagemang som unga medarbetare har märks i varje leverans. De vill visa vad de kan och de tar uppdraget på allvar.\n\nHållbarhet är en annan grundpelare. Att köpa på auktion är i sig ett hållbart val – du ger föremål nytt liv istället för att köpa nytt. Vi är stolta över att vara en del av den kedjan genom att transportera föremålen hem till sin nya ägare. Vi optimerar rutter för att minimera utsläpp och använder återanvändbara skyddsmaterial.\n\nFör företag som köper konst eller möbler på Uppsala Auktionskammare – till kontoret, lobbyn eller showroom – erbjuder vi faktura och underlag för CSR-rapportering. Du kan visa att din transport genomfördes av ett företag som sysselsätter ungdomar och bidrar till social hållbarhet.\n\nVi erbjuder snabb hämtning, inbärning till rätt plats och ett konkurrenskraftigt pris utan dolda avgifter. Du ser priset direkt vid bokning, allt är försäkrat och vi har egna skåpbilar i flera storlekar.\n\nDet är varsam, professionell hjälp med mening.\n\nHemleverans från Uppsala Auktionskammare ger inte rätt till RUT-avdrag, men flera av våra andra tjänster gör det – exempelvis flytt, bärhjälp och möbelmontering med 50 % avdrag på arbetskostnaden. Kombinera gärna tjänster i en och samma bokning.",
        },
      ],
    },

    faq: [
      {
        question: "Vad kostar hemleverans från Uppsala Auktionskammare?",
        answer:
          "Priset beror på objektets storlek och avstånd. En tavla eller silverföremål kostar från 595 kr. Större möbler kostar från 995–2 495 kr. Du ser det exakta priset direkt när du bokar online.",
      },
      {
        question: "Kan jag få RUT-avdrag på hemleverans från Uppsala Auktionskammare?",
        answer:
          "Hemleverans från Uppsala Auktionskammare ger i sig inte rätt till RUT-avdrag. RUT gäller hushållsnära tjänster som utförs i din bostad, exempelvis flytt, bärhjälp och möbelmontering. Ungdomskraft erbjuder flera RUT-berättigade tjänster med 50 % avdrag på arbetskostnaden – kombinera enkelt i en bokning via vår hemsida.",
      },
      {
        question:
          "Ingår skåpbil i leveransen från Uppsala Auktionskammare?",
        answer:
          "Ja, skåpbil ingår alltid. Vi väljer rätt storlek utifrån vad som ska transporteras – du behöver inte hyra bil eller köra själv.",
      },
      {
        question:
          "Ingår inbärning i priset för leverans från Uppsala Auktionskammare?",
        answer:
          "Ja, inbärning ingår alltid. Vi bär in ditt objekt genom trapphus, hiss och dörrar och ställer på plats – oavsett våning. Inga extra avgifter per våningsplan.",
      },
      {
        question:
          "Hur skyddar ni värdefulla föremål från Uppsala Auktionskammare?",
        answer:
          "Vi använder möbelfiltar, bubbelplast, hörnskydd och spännband. Tavlor transporteras stående, skulpturer säkras individuellt och silver eller porslin packas med stötdämpande material. Allt är försäkrat.",
      },
      {
        question:
          "Kan ni leverera från Uppsala Auktionskammare till Stockholm?",
        answer:
          "Ja, vi kör regelbundet sträckan Uppsala–Stockholm. Vi hämtar på Kungsängsgatan 27 och levererar hem till dig i hela Stockholms- och Uppsalaområdet. Priset inkluderar hela sträckan.",
      },
      {
        question:
          "Kan ni hämta tunga antika möbler från Uppsala Auktionskammare?",
        answer:
          "Ja, våra ungdomar är vana vid att bära tunga föremål – byråer, skåp, matbord och andra möbler som väger 50–200 kg. Vi skyddar alla ytor med filtar och bär varsamt genom trapphus och trånga dörröppningar.",
      },
      {
        question: "Hur snabbt kan ni hämta från Uppsala Auktionskammare?",
        answer:
          "Vi kan ofta hämta redan samma dag eller nästa dag. Boka så snart du fått hämtbesked – det hjälper dig att undvika lagringsavgifter.",
      },
      {
        question:
          "Tar ni ansvar för objektets skick från Uppsala Auktionskammare?",
        answer:
          "Vi ansvarar för transporten – objektet är försäkrat mot transportskador. Objektets skick, autenticitet och proveniens är mellan dig och Uppsala Auktionskammare. Vi rekommenderar att du inspekterar objektet innan vi lastar.",
      },
      {
        question:
          "Behöver jag vara med vid hämtning på Uppsala Auktionskammare?",
        answer:
          "Nej, du behöver inte vara med. Se till att köpet är betalt och att vi har ditt kundnummer eller objektnummer. Du kan även ge oss fullmakt att hämta – ange det vid bokning. Du behöver bara vara hemma för att ta emot leveransen.",
      },
      {
        question:
          "Kan ni leverera konst och möbler från Uppsala Auktionskammare på helger?",
        answer:
          "Ja, vi erbjuder hemleverans på helger och vissa kvällar. Kontrollera att auktionskammaren har öppet för upphämtning den tid du bokar.",
      },
      {
        question:
          "Kan ni hämta flera objekt från samma auktion på Uppsala Auktionskammare?",
        answer:
          "Ja, vi hämtar alla dina vunna objekt i en tur. Beskriv samtliga föremål vid bokning – vi anpassar skåpbilsstorlek och skyddsmaterial efter hela lasten.",
      },
      {
        question:
          "Kan ni transportera silver och porslin från Uppsala Auktionskammare?",
        answer:
          "Ja, vi transporterar ömtåliga föremål som silver, porslin, kristall och smycken. Allt packas individuellt med stötdämpande material och försäkras under transporten.",
      },
      {
        question:
          "Är leveransen från Uppsala Auktionskammare försäkrad?",
        answer:
          "Ja, alla våra transporter är helförsäkrade. Om ditt objekt skadas under lastning, transport eller inbärning ersätter vi det. Du kan boka med trygghet.",
      },
      {
        question:
          "I vilka kommuner erbjuder ni hemleverans från Uppsala Auktionskammare?",
        answer:
          "Vi erbjuder hemleverans från Uppsala Auktionskammare i hela Stockholmsområdet: Botkyrka, Danderyd, Ekerö, Haninge, Huddinge, Järfälla, Lidingö, Nacka, Norrtälje, Nykvarn, Nynäshamn, Salem, Sigtuna, Sollentuna, Solna, Stockholm, Sundbyberg, Södertälje, Tyresö, Täby, Upplands Väsby, Upplands-Bro, Vallentuna, Vaxholm, Värmdö och Österåker. Vi finns även i Göteborgsområdet: Ale, Alingsås, Göteborg, Härryda, Kungälv, Kungsbacka, Lerum, Lilla Edet, Mölndal, Partille, Stenungsund, Tjörn och Öckerö.",
      },
    ],

    priceHint: PACKAGE_PRICE_HINT_SV.bud,

    pricingRows: getPackagePricingRows("bud", "sv"),

    dynamicHeadings: {
      threeSteps: "Leverans från Uppsala Auktionskammare i 3 enkla steg",
      step2Text:
        "Våra ungdomar hämtar ditt objekt på Kungsängsgatan i Uppsala, skyddar det varsamt med filtar och bubbelplast och lastar skåpbilen. Sedan kör vi hem till dig.",
      step3Title: "Ditt fynd är hemma",
      step3Desc:
        "Ditt auktionsfynd från Uppsala Auktionskammare står hemma hos dig – varsamt inburet och på plats. Klart.",
      ctaHeading: "Boka leverans från Uppsala Auktionskammare idag",
      ctaText:
        "Ange hämtplats och din hemadress, beskriv objektet och välj en tid. Du ser priset direkt.",
    },

    en: {
      slug: "uppsala-auktionskammare-delivery",

      meta: {
        title:
          "Uppsala Auktionskammare Delivery – We Pick Up & Deliver Home | Ungdomskraft",
        description:
          "Bought at Uppsala Auktionskammare? We pick up your item in Uppsala and deliver home with careful carrying. Van and help included. Book auction delivery today.",
        h1: "Uppsala Auktionskammare delivery",
      },

      hero: {
        heading:
          "We pick up your winning bid – Uppsala Auktionskammare delivery",
        intro:
          "Won a bid at Uppsala Auktionskammare? Congratulations – but how do you get that antique dresser, painting or design armchair home? Pickup deadlines are tight and the items are often heavy or fragile. Ungdomskraft picks up your item at Kungsängsgatan 27 in Uppsala, protects it carefully and delivers to your door – with carrying and placement inside.",
      },

      featureCards: {
        sinceText: "Auction delivery since 2014",
        storyText:
          "Since 2014, Ungdomskraft has helped auction buyers across the Mälardalen region get their finds home – from heavy antique furniture and delicate art to design classics, silver and porcelain. Our young workers know how to handle valuable items with the extra care they deserve.",
        bookYourText: "Book your auction delivery",
        bookDescText:
          "Enter Uppsala Auktionskammare's address and your home address, describe the item and choose a time. You see the price instantly.",
        vehicleTitleText: "Vans equipped for valuable auction finds",
        vehicleDescText:
          "We bring the right van size and protective materials – whether it's a small silver goblet or a full furniture set. Everything is protected with blankets, bubble wrap and straps.",
      },

      itemsSection: {
        heading: "Common items we collect from Uppsala Auktionskammare",
        items: [
          {
            name: "Antique furniture",
            description:
              "Gustavian dressers, cabinets, writing desks, tables, chairs and furniture from the 18th to early 20th century",
            image: "/wardrobe.webp",
          },
          {
            name: "Art & paintings",
            description:
              "Oil paintings, watercolours, prints, lithographs, sculptures and photographs – Swedish and international art",
            image: "/painting.webp",
          },
          {
            name: "Design furniture",
            description:
              "Armchairs, sofas, lamps, tables and lighting by renowned Scandinavian designers",
            image: "/armchair.webp",
          },
          {
            name: "Rugs & textiles",
            description:
              "Persian rugs, oriental rugs, Swedish rya rugs, tapestries and other textile collectibles",
            image: "/bedding.webp",
          },
          {
            name: "Silver, porcelain & glass",
            description:
              "Silverware, candelabras, dinnerware sets, crystal glass, vases and figurines",
            image: "/dishes.webp",
          },
          {
            name: "Jewellery, watches & other",
            description:
              "Wristwatches, pocket watches, rings, necklaces, chandeliers, mirrors and curiosities",
            image: "/box.webp",
          },
        ],
      },

      seoContent: {
        mainHeading:
          "Delivery from Uppsala Auktionskammare – we pick up, transport and carry in your auction find",
        mainText:
          "Uppsala Auktionskammare is one of central Sweden's most established auction houses, holding regular auctions of art, antique furniture, design, silver, porcelain, jewellery and watches. The auction house at Kungsängsgatan 27 in Uppsala attracts buyers from across the Mälardalen region – Uppsala, Stockholm, Enköping, Västerås and far beyond. Winning a bid at Uppsala Auktionskammare is exciting, but then comes the practical question: how do I get this home? The pickup deadline is limited and storage fees start accumulating after just a few days. Transporting valuable antique furniture, delicate art or heavy sculptures in your own car means significant risk of damage – and the distance from Uppsala can make the journey especially challenging with fragile items. Ungdomskraft offers home delivery from Uppsala Auktionskammare at Kungsängsgatan 27. We pick up your winning item, protect it carefully with blankets, bubble wrap and straps, and drive it home to you – whether you live in Uppsala, Stockholm or elsewhere in the Mälardalen region. We carry in through stairwells, lifts or narrow garages and place it exactly where you want it. Carrying is always included – you see the price when you book. Every week we collect items from auction houses in all price ranges and sizes – from a single painting or silver set to an entire estate lot with furniture, rugs and art. Our young workers are experienced with fragile and valuable items. We know that a Gustavian dresser needs soft blankets and smooth driving, that an oil painting must stand upright and be shielded from impacts, and that a crystal chandelier requires extra padding. Please note that we are responsible for the transport from Uppsala Auktionskammare to your home – the item's condition, authenticity and provenance are between you and the auction house.",
        preparationHeading:
          "Prepare for your delivery from Uppsala Auktionskammare",
        preparationText:
          "Make sure your purchase is fully paid for and ready for collection. Have your customer number or lot number ready so our team can identify the right item at pickup. Check the pickup deadline – Uppsala Auktionskammare always specifies the last collection date and storage fees apply after that. If the item is particularly fragile, heavy or unusually shaped, describe it clearly when booking. Measure doorways and stairwells at home if the item is large. Check that the van can park close to your entrance.",
        additionalSections: [
          {
            heading: "Delivering art from Uppsala Auktionskammare",
            content:
              "Uppsala Auktionskammare regularly auctions art by both Swedish and international artists – oil paintings, watercolours, prints, lithographs and sculptures. An oil painting with a gilded frame can weigh 20–40 kg and cannot tolerate impacts, moisture or pressure on the canvas. We protect art with bubble wrap, corner guards and soft blankets. Paintings are always transported standing – never lying – and sculptures are secured individually with padding around all exposed parts. We carry in and place at your home – on the wall, on the pedestal or in the display cabinet. Bought multiple works at the same auction? We collect everything in one trip and protect each piece separately. Whether it's a small watercolour or a large oil painting requiring special handling through narrow doorways, we have the experience and materials to handle it carefully.",
          },
          {
            heading:
              "Delivering antique furniture and design from Uppsala Auktionskammare",
            content:
              "Uppsala Auktionskammare offers furniture from the 18th century to modern Scandinavian design – Gustavian dressers, Rococo cabinets, Empire writing desks and design classics by renowned designers. These items are often heavy (50–200 kg), come without packaging and have surfaces sensitive to scratches and impacts. Transporting them in your own car or with a rented trailer means significant risk – especially over the longer distance from Uppsala. With Ungdomskraft, we pick up your furniture directly at Kungsängsgatan 27. We wrap every surface with soft blankets, tape down loose parts like drawers and handles, and secure everything with straps in the van. We drive carefully and avoid hard braking. At your home we carry in through stairwells and doors and place the piece exactly where you want it. Need to disassemble parts to pass through a narrow doorway? We handle it. Design furniture requires extra care – leather, teak and lacquered surfaces are protected with additional layers of blankets.",
          },
          {
            heading:
              "Delivering silver, porcelain and jewellery from Uppsala Auktionskammare",
            content:
              "Uppsala Auktionskammare's auctions of silver, porcelain and jewellery attract collectors from across the Mälardalen region. Silverware, candelabras, trays and decorative silver items require careful handling to avoid dents and scratches. Porcelain dinnerware sets and crystal glass are fragile and need shock-absorbing packaging. Jewellery and watches are smaller but can have high value. We transport everything securely in the van – silver and porcelain are packed individually with soft padding, crystal is protected with bubble wrap and jewellery is transported safely. If you have won a combination of items – for example a dresser, a couple of paintings and a silver set – we collect everything at once and adapt the protection for each item type. You don't need to worry about mixing fragile items in the same load – we separate and protect every piece.",
          },
          {
            heading: "Transport Uppsala–Stockholm and the Mälardalen region",
            content:
              "Many buyers at Uppsala Auktionskammare live in Stockholm or other parts of the Mälardalen region. The Uppsala–Stockholm distance is approximately 70 km and takes around one hour by van. We regularly drive between Uppsala and Stockholm and can pick up at Kungsängsgatan 27 and deliver to your home across the entire Stockholm and Uppsala areas. Live in Enköping, Västerås, Sigtuna, Norrtälje or another municipality in the Mälardalen region? We drive there too. Enter your home address when booking and you see the price instantly – it includes the entire distance, van, carrying help and placement with no hidden fees.",
          },
          {
            heading: "How delivery from Uppsala Auktionskammare works",
            listItems: [
              {
                label: "Book online",
                text: "Enter Kungsängsgatan 27 in Uppsala as the pickup location, your home address and describe the item. Include the lot number if possible. You see the price instantly.",
              },
              {
                label: "We pick up at the auction house",
                text: "Our crew arrives with a van and the right protective materials. We identify your item, protect it carefully and load it.",
              },
              {
                label: "We deliver home",
                text: "We drive to your home – whether in Uppsala, Stockholm or the Mälardalen region. We carry in through stairwells and doors and place your auction find where you want it.",
              },
              {
                label: "Done",
                text: "Your find from Uppsala Auktionskammare is at home – carefully carried in and in place.",
              },
            ],
            afterContent:
              "The whole process usually takes 1–4 hours depending on distance and handling requirements. You can track the status in real time.",
          },
          {
            heading:
              "Why book Uppsala Auktionskammare delivery with Ungdomskraft?",
            content:
              "What makes Ungdomskraft unique is that we employ young people and give them real work experience. When you book delivery of your find from Uppsala Auktionskammare, you don't just get professional, careful handling – you also help young people enter the job market and build their futures.\n\nEvery job is staffed by motivated young workers who learn everything from customer service and logistics to careful handling of valuable objects. They learn to communicate with customers, plan routes, protect delicate surfaces and work efficiently as a team. This is work experience you can't get in a classroom – and every delivery we make gives a young person the chance to grow.\n\nWe have been operating since 2014 and have completed tens of thousands of transports in Stockholm, Uppsala and Gothenburg. Our young workers have carried in everything from Gustavian furniture sets and oil paintings to chandeliers, silver collections and design classics worth hundreds of thousands of kronor. We know what it takes: the right protective materials, careful loading, smooth driving and gentle carrying.\n\nQuality runs through everything we do. Our workers don't take shortcuts – they add extra blankets, double-check the straps and communicate with you throughout the job. The energy and commitment that young workers bring shows in every delivery. They want to prove themselves and they take the job seriously.\n\nSustainability is another cornerstone. Buying at auction is inherently sustainable – you give items new life instead of buying new. We are proud to be part of that chain by transporting items home to their new owners. We optimise routes to minimise emissions and use reusable protective materials.\n\nFor businesses buying art or furniture at Uppsala Auktionskammare – for the office, lobby or showroom – we also offer invoicing and documentation for CSR reporting. You can show that your transport was carried out by a company that employs young people and contributes to social sustainability.\n\nWe offer fast pickup, carrying to the right spot and a competitive price with no hidden fees. You see the price when you book, everything is insured and we have our own vans in several sizes.\n\nIt is careful, professional help with purpose.\n\nDelivery from Uppsala Auktionskammare does not qualify for the Swedish RUT tax deduction, but several of our other services do – including moving help, carrying help and furniture assembly with a 50% deduction on the labour cost. Feel free to combine services in a single booking.",
          },
        ],
      },

      faq: [
        {
          question:
            "How much does delivery from Uppsala Auktionskammare cost?",
          answer:
            "The price depends on the item's size and distance. A painting or silver item starts from SEK 595. Larger furniture starts from SEK 995–1,699. You see the exact price when you book online.",
        },
        {
          question: "Can I get a RUT tax deduction on delivery from Uppsala Auktionskammare?",
          answer:
            "Delivery from Uppsala Auktionskammare does not in itself qualify for the Swedish RUT tax deduction. RUT applies to household services carried out in your home, such as moving help, carrying help and furniture assembly. Ungdomskraft offers several RUT-eligible services with a 50% deduction on the labour cost – easily combine them in one booking via our website.",
        },
        {
          question:
            "Is a van included in the Uppsala Auktionskammare delivery?",
          answer:
            "Yes, a van is always included. We choose the right size based on what needs to be transported – you don't need to rent a vehicle or drive yourself.",
        },
        {
          question:
            "Is carrying included in the Uppsala Auktionskammare delivery price?",
          answer:
            "Yes, carrying is always included. We carry your item through stairwells, lifts and doors and place it where you want it – regardless of floor. No extra fees per floor.",
        },
        {
          question:
            "How do you protect valuable items from Uppsala Auktionskammare?",
          answer:
            "We use furniture blankets, bubble wrap, corner guards and straps. Paintings are transported standing, sculptures are secured individually and silver or porcelain is packed with shock-absorbing material. Everything is insured.",
        },
        {
          question:
            "Can you deliver from Uppsala Auktionskammare to Stockholm?",
          answer:
            "Yes, we regularly drive the Uppsala–Stockholm route. We pick up at Kungsängsgatan 27 and deliver to your home across the entire Stockholm and Uppsala areas. The price includes the full distance.",
        },
        {
          question:
            "Can you carry heavy antique furniture from Uppsala Auktionskammare?",
          answer:
            "Yes, our workers are experienced with heavy items – dressers, cabinets, dining tables and other furniture weighing 50–200 kg. We protect all surfaces with blankets and carry carefully through stairwells and narrow doorways.",
        },
        {
          question:
            "How quickly can you pick up from Uppsala Auktionskammare?",
          answer:
            "We can often pick up the same day or next day. Book as soon as you receive the pickup notification – it helps you avoid storage fees.",
        },
        {
          question:
            "Are you responsible for the item's condition from Uppsala Auktionskammare?",
          answer:
            "We are responsible for transport – the item is insured against transport damage. The item's condition, authenticity and provenance are between you and Uppsala Auktionskammare. We recommend inspecting the item before we load it.",
        },
        {
          question:
            "Do I need to be at Uppsala Auktionskammare for pickup?",
          answer:
            "No, you don't need to be there. Make sure the purchase is paid for and that we have your customer number or lot number. You can also authorise us to collect on your behalf – specify this when booking. You just need to be home to receive the delivery.",
        },
        {
          question:
            "Can you pick up multiple items from the same auction at Uppsala Auktionskammare?",
          answer:
            "Yes, we pick up all your winning items in one trip. Describe all items when booking – we adjust van size and protective materials for the entire load.",
        },
        {
          question:
            "Can you transport silver and porcelain from Uppsala Auktionskammare?",
          answer:
            "Yes, we transport fragile items like silver, porcelain, crystal and jewellery. Everything is packed individually with shock-absorbing material and insured during transport.",
        },
        {
          question:
            "Do you offer delivery from Uppsala Auktionskammare on weekends?",
          answer:
            "Yes, we offer delivery on weekends and some evenings. Check that the auction house is open for pickup at the time you book.",
        },
        {
          question:
            "Is the delivery from Uppsala Auktionskammare insured?",
          answer:
            "Yes, all our transports are fully insured. If your item is damaged during loading, transport or carrying, we will compensate you. You can book with confidence.",
        },
        {
          question:
            "In which municipalities do you offer delivery from Uppsala Auktionskammare?",
          answer:
            "We offer delivery from Uppsala Auktionskammare across the entire Stockholm area: Botkyrka, Danderyd, Ekerö, Haninge, Huddinge, Järfälla, Lidingö, Nacka, Norrtälje, Nykvarn, Nynäshamn, Salem, Sigtuna, Sollentuna, Solna, Stockholm, Sundbyberg, Södertälje, Tyresö, Täby, Upplands Väsby, Upplands-Bro, Vallentuna, Vaxholm, Värmdö and Österåker. We also serve the Gothenburg area: Ale, Alingsås, Gothenburg, Härryda, Kungälv, Kungsbacka, Lerum, Lilla Edet, Mölndal, Partille, Stenungsund, Tjörn and Öckerö.",
        },
      ],

      priceHint: PACKAGE_PRICE_HINT_EN.bud,

      pricingRows: getPackagePricingRows("bud", "en"),

      dynamicHeadings: {
        threeSteps: "Uppsala Auktionskammare delivery in 3 easy steps",
        step2Text:
          "Our crew picks up your item at Kungsängsgatan in Uppsala, protects it carefully with blankets and bubble wrap and loads the van. Then we drive home to you.",
        step3Title: "Your find is home",
        step3Desc:
          "Your find from Uppsala Auktionskammare is at home – carefully carried in and in place. Done.",
        ctaHeading: "Book Uppsala Auktionskammare delivery today",
        ctaText:
          "Enter the pickup location and your home address, describe the item and choose a time. You see the price instantly.",
      },
    },
  },

  // ────────────────────────────────────────────────────────────────────
  // Hemleverans Auctionet
  // Vinkel: Hemleverans av auktionsfynd köpta via Auctionet – Sveriges
  // största onlineplattform för auktioner. Objekten finns hos partner-
  // auktionshus runt om i Sverige. Vi hämtar på rätt auktionshus och
  // levererar hem med varsam inbärning.
  // ────────────────────────────────────────────────────────────────────
  {
    slug: "hemleverans-auctionet",
    serviceCategory: "hemleverans",

    meta: {
      title:
        "Hemleverans Auctionet – Vi Hämtar Ditt Auktionsfynd | Ungdomskraft",
      description:
        "Köpt på Auctionet? Vi hämtar ditt objekt hos partnerauktionshuset och levererar hem med varsam inbärning. Skåpbil och bärhjälp ingår. Boka hemleverans idag.",
      h1: "Hemleverans Auctionet",
    },

    hero: {
      heading: "Vi hämtar ditt vunna objekt – Hemleverans Auctionet",
      intro:
        "Vunnit ett bud på Auctionet? Grattis – men hur får du hem din antika byrå, tavla eller designlampa? Objektet finns hos ett partnerauktionshus och hämtfristen är kort. Vi löser det. Ungdomskraft hämtar ditt objekt hos rätt auktionshus, skyddar det varsamt och levererar hem till dig – med inbärning och uppställning på plats.",
      heroImage: "/hero-hemleverans.png",
    },

    featureCards: {
      sinceText: "Auctionet-leverans sedan 2014",
      storyText:
        "Sedan 2014 har Ungdomskraft hjälpt köpare på Auctionet att få hem sina fynd – från tunga antika möbler och ömtålig konst till designklassiker, silver och porslin. Våra ungdomar vet hur man hanterar värdefulla föremål med den extra omsorg de förtjänar.",
      bookYourText: "Boka din Auctionet-leverans",
      bookDescText:
        "Ange auktionshusets adress och din hemadress, beskriv objektet och välj en tid. Du ser priset direkt.",
      vehicleTitleText: "Skåpbilar för värdefulla auktionsfynd",
      vehicleDescText:
        "Vi kommer med rätt storlek på skåpbil och rätt skyddsmaterial – oavsett om det gäller en liten silverbägare eller ett stort möblemang. Allt skyddas med filtar, bubbelplast och spännband.",
    },

    itemsSection: {
      heading: "Vanliga objekt vi hämtar från Auctionet",
      items: [
        {
          name: "Antika möbler",
          description:
            "Gustavianska byråer, skåp, sekretärer, bord, stolar och möbler från 1700-tal till tidigt 1900-tal",
          image: "/wardrobe.webp",
        },
        {
          name: "Konst & tavlor",
          description:
            "Oljemålningar, akvareller, grafik, litografier, skulpturer och fotografier – svensk och internationell konst",
          image: "/painting.webp",
        },
        {
          name: "Designmöbler & lampor",
          description:
            "Fåtöljer, soffor, bord, hyllsystem och belysning av kända skandinaviska och internationella formgivare",
          image: "/armchair.webp",
        },
        {
          name: "Mattor & textilier",
          description:
            "Persiska mattor, orientaliska mattor, svenska ryor, kelimmattor och andra textila föremål",
          image: "/bedding.webp",
        },
        {
          name: "Silver, porslin & glas",
          description:
            "Silverbestick, kandelabrar, porslinserviser, kristallglas, vaser och figuriner",
          image: "/dishes.webp",
        },
        {
          name: "Smycken, klockor & övrigt",
          description:
            "Armbandsur, fickklockor, ringar, halsband, ljuskronor, speglar och kuriosa",
          image: "/box.webp",
        },
      ],
    },

    seoContent: {
      mainHeading:
        "Hemleverans från Auctionet – vi hämtar hos auktionshuset och levererar hem till dig",
      mainText:
        "Auctionet är Sveriges största onlineplattform för auktioner och samlar hundratals auktionshus under ett tak. Varje vecka avslutas tusentals auktioner med allt från antika möbler och konst till designklassiker, silver, porslin, smycken och klockor. Du budar enkelt online, men sedan kommer den praktiska frågan: hur får jag hem det här? Objektet finns hos ett partnerauktionshus – ibland i din egen stad, ibland på andra sidan landet. Hämtfristen är begränsad och lagringsavgifter kan börja ticka efter bara några dagar. Att transportera värdefulla antika möbler, ömtålig konst eller tunga skulpturer i egen bil innebär stor risk för skador. Ungdomskraft erbjuder hemleverans av objekt köpta via Auctionet. Vi hämtar ditt vunna objekt hos partnerauktionshuset – oavsett om det ligger i Stockholm, Göteborg, Uppsala eller någon annan stad i Mälardalen. Vi skyddar objektet varsamt med filtar, bubbelplast och spännband och kör hem till dig. Vi bär in genom trapphus, hiss eller trångt garage och ställer på plats där du vill ha det. Inbärning ingår alltid – du ser priset direkt vid bokning. Varje vecka hämtar vi Auctionet-köp från auktionshus runt om i regionen – i alla prisklasser och storlekar. Från en enstaka tavla eller silverbägare till ett helt dödsbo med möbler, mattor och konst. Våra ungdomar är vana vid att hantera ömtåliga och värdefulla föremål. Vi vet att en gustaviansk byrå kräver mjuka filtar och jämn körning, att en oljemålning måste stå upprätt och skyddas mot stötar och att en kristallkrona behöver extra polstring. Vi tar med oss allt skyddsmaterial som behövs. Tänk på att vi ansvarar för transporten från auktionshuset hem till dig – objektets skick, autenticitet och proveniens är mellan dig och säljaren via Auctionet.",
      preparationHeading: "Förbered inför din leverans från Auctionet",
      preparationText:
        "Se till att ditt köp är fullt betalt via Auctionet och redo att hämtas. Kontrollera vilken adress objektet finns på – det står i din orderbekräftelse eller under Mina köp på Auctionet. Ha ditt ordernummer eller objektnummer redo så att vårt team kan identifiera rätt föremål vid hämtning. Kontrollera hämtfristen – auktionshuset anger alltid sista hämtdag och lagringsavgifter kan tillkomma efter det. Om objektet är särskilt ömtåligt, tungt eller har ovanlig form – beskriv det tydligt vid bokning. Mät dörröppningar och trapphus hemma om det rör sig om stora möbler. Kolla att skåpbilen kan parkera nära din port.",
      additionalSections: [
        {
          heading: "Hemleverans av konst köpt på Auctionet",
          content:
            "Auctionet har ett enormt utbud av konst – oljemålningar, akvareller, grafik, litografier, skulpturer och fotografier från hundratals auktionshus i hela Sverige. En oljemålning med förgylld ram kan väga 20–40 kg och tål inte stötar, fukt eller tryck mot duken. Vi skyddar konst med bubbelplast, hörnskydd och mjuka filtar. Tavlor transporteras alltid stående – aldrig liggande – och skulpturer säkras individuellt med polstring runt alla utsatta delar. Vi bär in och ställer på plats hemma hos dig – på väggen, på piedestalen eller i vitrinskåpet. Köpt flera verk på Auctionet från samma auktionshus? Vi hämtar allt i en tur och skyddar varje objekt separat. Oavsett om det är en liten akvarell eller en monumental oljemålning som kräver specialhantering genom trånga dörröppningar har vi erfarenheten och materialet för att hantera det varsamt.",
        },
        {
          heading: "Hemleverans av möbler och design köpt på Auctionet",
          content:
            "Möbler och design är bland de mest populära kategorierna på Auctionet – gustavianska byråer, rokokoskåp, empiresekreterärer, jugendmöbler och skandinaviska designklassiker av Josef Frank, Bruno Mathsson, Alvar Aalto, Hans Wegner och Arne Jacobsen. Dessa föremål är ofta tunga (50–200 kg), saknar originalförpackning och har ytor som är känsliga för repor och stötar. Att transportera dem i vanlig bil eller med ett hyrt släp innebär stor risk. Med Ungdomskraft hämtar vi dina möbler direkt hos auktionshuset. Vi sveper in varje yta med mjuka filtar, tejpar fast lösa delar som lådor och handtag och säkrar med spännband i skåpbilen. Vi kör försiktigt och undviker hårda inbromsningar. Hemma hos dig bär vi in genom trapphus och dörrar och ställer möbeln exakt där du vill ha den. Behöver vi demontera delar för att passera en trång dörröppning löser vi det. Designmöbler kräver extra omsorg – läder, teak och lackerade ytor skyddas med ytterligare lager av filtar.",
        },
        {
          heading:
            "Hemleverans av silver, porslin och smycken köpt på Auctionet",
          content:
            "Silver, porslin och smycken är populära kategorier på Auctionet med tusentals objekt varje vecka. Silverbestick, kandelabrar, brickor och dekorativa föremål i silver kräver varsam hantering för att undvika bucklor och repor. Porslinserviser och kristallglas är ömtåliga och behöver stötdämpande förpackning. Smycken och klockor är mindre till storleken men kan ha högt värde. Vi transporterar allt säkert i skåpbilen – silver och porslin packas individuellt med mjuk polstring, kristall skyddas med bubbelplast och smycken transporteras stötsäkert. Om du har vunnit en kombination av föremål från samma auktionshus – till exempel en byrå, ett par tavlor och en silverservis – hämtar vi allt på samma gång och anpassar skyddet för varje objektstyp.",
        },
        {
          heading: "Så fungerar hämtning av Auctionet-köp",
          content:
            "Auctionet fungerar som en plattform som samlar auktionshus från hela Sverige. När du vinner ett bud finns objektet hos det specifika partnerauktionshuset – inte hos Auctionet centralt. Det innebär att hämtadressen varierar beroende på vilken auktion du köpt från. Du hittar hämtadressen i din orderbekräftelse eller under Mina köp på auctionet.com. Ange den adressen som hämtplats vid bokning hos oss. Vi hämtar från auktionshus i hela Stockholms- och Göteborgsområdet samt övriga Mälardalen. Har du köpt från flera auktionshus i samma stad? Beskriv det vid bokning så kan vi planera hämtning från flera adresser i samma tur.",
        },
        {
          heading: "Så fungerar leverans från Auctionet",
          listItems: [
            {
              label: "Boka online",
              text: "Ange auktionshusets adress (från din Auctionet-order) och din hemadress. Beskriv objektet och ange gärna ordernummer. Du ser priset direkt.",
            },
            {
              label: "Vi hämtar hos auktionshuset",
              text: "Vårt team kommer med skåpbil och rätt skyddsmaterial. Vi identifierar ditt objekt, skyddar det varsamt och lastar.",
            },
            {
              label: "Vi levererar hem",
              text: "Vi kör hem till dig, bär in genom trapphus och dörrar och ställer ditt auktionsfynd på plats.",
            },
            {
              label: "Klart",
              text: "Ditt Auctionet-fynd står hemma hos dig – varsamt inburet och uppställt.",
            },
          ],
          afterContent:
            "Hela processen tar normalt 1–4 timmar beroende på avstånd och objektets hanteringsbehov. Du kan följa statusen i realtid.",
        },
        {
          heading: "Varför boka Auctionet-leverans med Ungdomskraft?",
          content:
            "Det som gör Ungdomskraft unikt är att vi anställer unga människor och ger dem riktig arbetslivserfarenhet. När du bokar hemleverans av ditt Auctionet-köp får du inte bara professionell och varsam hantering – du bidrar också till att unga människor kommer in på arbetsmarknaden och bygger sin framtid.\n\nVarje uppdrag bemannas av motiverade ungdomar som lär sig allt från kundservice och logistik till varsam hantering av värdefulla föremål. De lär sig att kommunicera med kunder, planera rutter, skydda ömtåliga ytor och arbeta effektivt i team. Det är arbetslivserfarenhet som inte går att få i ett klassrum – och varje leverans vi kör ger en ung person möjlighet att utvecklas.\n\nVi har funnits sedan 2014 och har genomfört tiotusentals transporter i Stockholm och Göteborg. Våra ungdomar har burit in allt från gustavianska möblemang och oljemålningar till ljuskronor, silversamlingar och designklassiker värda hundratusentals kronor. Vi vet vad som krävs: rätt skyddsmaterial, försiktig lastning, jämn körning och varsam inbärning.\n\nKvalitet genomsyrar allt vi gör. Våra ungdomar tar inga genvägar – de lägger extra filtar, kontrollerar att spännbanden sitter och kommunicerar med dig under hela uppdraget. Den energi och det engagemang som unga medarbetare har märks i varje leverans. De vill visa vad de kan och de tar uppdraget på allvar.\n\nHållbarhet är en annan grundpelare. Att köpa på auktion via Auctionet är i sig ett hållbart val – du ger föremål nytt liv istället för att köpa nytt. Vi är stolta över att vara en del av den kedjan genom att transportera föremålen hem till sin nya ägare. Vi optimerar rutter för att minimera utsläpp och använder återanvändbara skyddsmaterial.\n\nFör företag som köper konst eller design via Auctionet – till kontoret, lobbyn eller showroom – erbjuder vi faktura och underlag för CSR-rapportering. Du kan visa att din transport genomfördes av ett företag som sysselsätter ungdomar och bidrar till social hållbarhet.\n\nVi erbjuder snabb hämtning, inbärning till rätt plats och ett konkurrenskraftigt pris utan dolda avgifter. Du ser priset direkt vid bokning, allt är försäkrat och vi har egna skåpbilar i flera storlekar.\n\nDet är varsam, professionell hjälp med mening.\n\nAuctionet-leverans ger inte rätt till RUT-avdrag, men flera av våra andra tjänster gör det – exempelvis flytt, bärhjälp och möbelmontering med 50 % avdrag på arbetskostnaden. Kombinera gärna tjänster i en och samma bokning.",
        },
      ],
    },

    faq: [
      {
        question: "Vad kostar hemleverans av objekt köpta på Auctionet?",
        answer:
          "Priset beror på objektets storlek och avstånd från auktionshuset. En tavla eller silverföremål kostar från 595 kr. Större möbler kostar från 995–2 495 kr. Du ser det exakta priset direkt när du bokar online.",
      },
      {
        question: "Kan jag få RUT-avdrag på Auctionet-leverans?",
        answer:
          "Auctionet-leverans ger i sig inte rätt till RUT-avdrag. RUT gäller hushållsnära tjänster som utförs i din bostad, exempelvis flytt, bärhjälp och möbelmontering. Ungdomskraft erbjuder flera RUT-berättigade tjänster med 50 % avdrag på arbetskostnaden – kombinera enkelt i en bokning via vår hemsida.",
      },
      {
        question: "Ingår skåpbil i Auctionet-leveransen?",
        answer:
          "Ja, skåpbil ingår alltid. Vi väljer rätt storlek utifrån vad som ska transporteras – du behöver inte hyra bil eller köra själv.",
      },
      {
        question: "Ingår inbärning i priset för Auctionet-leverans?",
        answer:
          "Ja, inbärning ingår alltid. Vi bär in ditt objekt genom trapphus, hiss och dörrar och ställer på plats – oavsett våning. Inga extra avgifter per våningsplan.",
      },
      {
        question:
          "Hur skyddar ni värdefulla föremål köpta på Auctionet?",
        answer:
          "Vi använder möbelfiltar, bubbelplast, hörnskydd och spännband. Tavlor transporteras stående, skulpturer säkras individuellt och silver eller porslin packas med stötdämpande material. Allt är försäkrat.",
      },
      {
        question:
          "Var hämtar ni Auctionet-objekt – finns det en fast adress?",
        answer:
          "Auctionet är en plattform som samlar många auktionshus. Objektet finns hos det specifika partnerauktionshuset. Du hittar hämtadressen i din orderbekräftelse på Auctionet. Ange den adressen vid bokning hos oss.",
      },
      {
        question:
          "Kan ni hämta tunga antika möbler köpta på Auctionet?",
        answer:
          "Ja, våra ungdomar är vana vid att bära tunga föremål – byråer, skåp, matbord och andra möbler som väger 50–200 kg. Vi skyddar alla ytor med filtar och bär varsamt genom trapphus och trånga dörröppningar.",
      },
      {
        question: "Hur snabbt kan ni hämta mitt Auctionet-köp?",
        answer:
          "Vi kan ofta hämta redan samma dag eller nästa dag. Boka så snart du fått hämtbesked – det hjälper dig att undvika lagringsavgifter hos auktionshuset.",
      },
      {
        question:
          "Tar ni ansvar för objektets skick vid Auctionet-leverans?",
        answer:
          "Vi ansvarar för transporten – objektet är försäkrat mot transportskador. Objektets skick, autenticitet och proveniens är mellan dig och säljaren via Auctionet. Vi rekommenderar att du inspekterar objektet innan vi lastar.",
      },
      {
        question:
          "Behöver jag vara med vid hämtning hos auktionshuset?",
        answer:
          "Nej, du behöver inte vara med. Se till att köpet är betalt via Auctionet och att vi har ditt ordernummer. Du kan ge oss fullmakt att hämta – ange det vid bokning. Du behöver bara vara hemma för att ta emot leveransen.",
      },
      {
        question:
          "Kan ni leverera Auctionet-köp på helger?",
        answer:
          "Ja, vi erbjuder hemleverans på helger och vissa kvällar. Kontrollera att auktionshuset har öppet för upphämtning den tid du bokar.",
      },
      {
        question:
          "Kan ni hämta flera Auctionet-objekt från samma auktionshus?",
        answer:
          "Ja, vi hämtar alla dina vunna objekt i en tur om de finns hos samma auktionshus. Beskriv samtliga föremål vid bokning – vi anpassar skåpbilsstorlek och skyddsmaterial efter hela lasten.",
      },
      {
        question:
          "Kan ni hämta Auctionet-köp från olika auktionshus i samma tur?",
        answer:
          "Ja, om auktionshusen ligger i samma stad eller längs samma rutt kan vi planera hämtning från flera adresser. Beskriv alla hämtplatser vid bokning så optimerar vi rutten.",
      },
      {
        question: "Är Auctionet-leveransen försäkrad?",
        answer:
          "Ja, alla våra transporter är helförsäkrade. Om ditt objekt skadas under lastning, transport eller inbärning ersätter vi det. Du kan boka med trygghet.",
      },
      {
        question:
          "I vilka kommuner erbjuder ni hemleverans av Auctionet-köp?",
        answer:
          "Vi erbjuder hemleverans av Auctionet-köp i hela Stockholmsområdet: Botkyrka, Danderyd, Ekerö, Haninge, Huddinge, Järfälla, Lidingö, Nacka, Norrtälje, Nykvarn, Nynäshamn, Salem, Sigtuna, Sollentuna, Solna, Stockholm, Sundbyberg, Södertälje, Tyresö, Täby, Upplands Väsby, Upplands-Bro, Vallentuna, Vaxholm, Värmdö och Österåker. Vi finns även i Göteborgsområdet: Ale, Alingsås, Göteborg, Härryda, Kungälv, Kungsbacka, Lerum, Lilla Edet, Mölndal, Partille, Stenungsund, Tjörn och Öckerö.",
      },
    ],

    priceHint: PACKAGE_PRICE_HINT_SV.bud,

    pricingRows: getPackagePricingRows("bud", "sv"),

    dynamicHeadings: {
      threeSteps: "Auctionet-leverans i 3 enkla steg",
      step2Text:
        "Våra ungdomar hämtar ditt objekt hos partnerauktionshuset, skyddar det varsamt med filtar och bubbelplast och lastar skåpbilen. Sedan kör vi hem till dig.",
      step3Title: "Ditt fynd är hemma",
      step3Desc:
        "Ditt Auctionet-fynd står hemma hos dig – varsamt inburet och på plats. Klart.",
      ctaHeading: "Boka Auctionet-leverans idag",
      ctaText:
        "Ange auktionshusets adress och din hemadress, beskriv objektet och välj en tid. Du ser priset direkt.",
    },

    en: {
      slug: "auctionet-delivery",

      meta: {
        title:
          "Auctionet Delivery – We Pick Up & Deliver Your Auction Find Home | Ungdomskraft",
        description:
          "Bought on Auctionet? We pick up your item at the partner auction house and deliver home with careful carrying. Van and help included. Book delivery today.",
        h1: "Auctionet delivery",
      },

      hero: {
        heading: "We pick up your winning bid – Auctionet delivery",
        intro:
          "Won a bid on Auctionet? Congratulations – but how do you get that antique dresser, painting or design lamp home? The item is at a partner auction house and the pickup deadline is tight. Ungdomskraft picks up your item at the right auction house, protects it carefully and delivers to your door – with carrying and placement inside.",
      },

      featureCards: {
        sinceText: "Auctionet delivery since 2014",
        storyText:
          "Since 2014, Ungdomskraft has helped buyers on Auctionet get their finds home – from heavy antique furniture and delicate art to design classics, silver and porcelain. Our young workers know how to handle valuable items with the extra care they deserve.",
        bookYourText: "Book your Auctionet delivery",
        bookDescText:
          "Enter the auction house address and your home address, describe the item and choose a time. You see the price instantly.",
        vehicleTitleText: "Vans equipped for valuable auction finds",
        vehicleDescText:
          "We bring the right van size and protective materials – whether it's a small silver goblet or a full furniture set. Everything is protected with blankets, bubble wrap and straps.",
      },

      itemsSection: {
        heading: "Common items we collect from Auctionet",
        items: [
          {
            name: "Antique furniture",
            description:
              "Gustavian dressers, cabinets, writing desks, tables, chairs and furniture from the 18th to early 20th century",
            image: "/wardrobe.webp",
          },
          {
            name: "Art & paintings",
            description:
              "Oil paintings, watercolours, prints, lithographs, sculptures and photographs – Swedish and international art",
            image: "/painting.webp",
          },
          {
            name: "Design furniture & lamps",
            description:
              "Armchairs, sofas, tables, shelving systems and lighting by renowned Scandinavian and international designers",
            image: "/armchair.webp",
          },
          {
            name: "Rugs & textiles",
            description:
              "Persian rugs, oriental rugs, Swedish rya rugs, kilim rugs and other textile collectibles",
            image: "/bedding.webp",
          },
          {
            name: "Silver, porcelain & glass",
            description:
              "Silverware, candelabras, dinnerware sets, crystal glass, vases and figurines",
            image: "/dishes.webp",
          },
          {
            name: "Jewellery, watches & other",
            description:
              "Wristwatches, pocket watches, rings, necklaces, chandeliers, mirrors and curiosities",
            image: "/box.webp",
          },
        ],
      },

      seoContent: {
        mainHeading:
          "Delivery from Auctionet – we pick up at the auction house and deliver home to you",
        mainText:
          "Auctionet is Sweden's largest online auction platform, bringing together hundreds of auction houses under one roof. Every week thousands of auctions close with everything from antique furniture and art to design classics, silver, porcelain, jewellery and watches. Bidding online is easy, but then comes the practical question: how do I get this home? The item is at a partner auction house – sometimes in your own city, sometimes on the other side of the country. The pickup deadline is limited and storage fees can start accumulating after just a few days. Transporting valuable antique furniture, delicate art or heavy sculptures in your own car means significant risk of damage. Ungdomskraft offers home delivery of items bought on Auctionet. We pick up your winning item at the partner auction house – whether it's in Stockholm, Gothenburg, Uppsala or another city in the Mälardalen region. We protect the item carefully with blankets, bubble wrap and straps and drive it home to you. We carry in through stairwells, lifts or narrow garages and place it exactly where you want it. Carrying is always included – you see the price when you book. Every week we collect Auctionet purchases from auction houses across the region – in all price ranges and sizes. From a single painting or silver goblet to an entire estate lot with furniture, rugs and art. Our young workers are experienced with fragile and valuable items. We know that a Gustavian dresser needs soft blankets and smooth driving, that an oil painting must stand upright and be shielded from impacts, and that a crystal chandelier requires extra padding. Please note that we are responsible for the transport from the auction house to your home – the item's condition, authenticity and provenance are between you and the seller via Auctionet.",
        preparationHeading: "Prepare for your delivery from Auctionet",
        preparationText:
          "Make sure your purchase is fully paid for via Auctionet and ready for collection. Check which address the item is at – it's listed in your order confirmation or under My Purchases on auctionet.com. Have your order number or lot number ready so our team can identify the right item at pickup. Check the pickup deadline – the auction house always specifies the last collection date and storage fees may apply after that. If the item is particularly fragile, heavy or unusually shaped, describe it clearly when booking. Measure doorways and stairwells at home if the item is large. Check that the van can park close to your entrance.",
        additionalSections: [
          {
            heading: "Delivering art bought on Auctionet",
            content:
              "Auctionet has an enormous selection of art – oil paintings, watercolours, prints, lithographs, sculptures and photographs from hundreds of auction houses across Sweden. An oil painting with a gilded frame can weigh 20–40 kg and cannot tolerate impacts, moisture or pressure on the canvas. We protect art with bubble wrap, corner guards and soft blankets. Paintings are always transported standing – never lying – and sculptures are secured individually with padding around all exposed parts. We carry in and place at your home – on the wall, on the pedestal or in the display cabinet. Bought multiple works from the same auction house on Auctionet? We collect everything in one trip and protect each piece separately. Whether it's a small watercolour or a monumental oil painting requiring special handling through narrow doorways, we have the experience and materials to handle it carefully.",
          },
          {
            heading: "Delivering furniture and design bought on Auctionet",
            content:
              "Furniture and design are among the most popular categories on Auctionet – Gustavian dressers, Rococo cabinets, Empire writing desks, Art Nouveau furniture and Scandinavian design classics by Josef Frank, Bruno Mathsson, Alvar Aalto, Hans Wegner and Arne Jacobsen. These items are often heavy (50–200 kg), come without packaging and have surfaces sensitive to scratches and impacts. Transporting them in your own car or with a rented trailer means significant risk. With Ungdomskraft, we pick up your furniture directly at the auction house. We wrap every surface with soft blankets, tape down loose parts like drawers and handles, and secure everything with straps in the van. We drive carefully and avoid hard braking. At your home we carry in through stairwells and doors and place the piece exactly where you want it. Design furniture requires extra care – leather, teak and lacquered surfaces are protected with additional layers of blankets.",
          },
          {
            heading:
              "Delivering silver, porcelain and jewellery bought on Auctionet",
            content:
              "Silver, porcelain and jewellery are popular categories on Auctionet with thousands of items every week. Silverware, candelabras, trays and decorative silver require careful handling to avoid dents and scratches. Porcelain dinnerware and crystal glass are fragile and need shock-absorbing packaging. Jewellery and watches are smaller but can have high value. We transport everything securely – silver and porcelain are packed individually with soft padding, crystal is protected with bubble wrap and jewellery is transported safely. If you have won a combination of items from the same auction house – for example a dresser, a couple of paintings and a silver set – we collect everything at once and adapt the protection for each item type.",
          },
          {
            heading: "How Auctionet pickup works",
            content:
              "Auctionet is a platform that brings together auction houses from across Sweden. When you win a bid, the item is at the specific partner auction house – not at a central Auctionet location. This means the pickup address varies depending on which auction you bought from. You can find the pickup address in your order confirmation or under My Purchases at auctionet.com. Enter that address as the pickup location when booking with us. We pick up from auction houses across the Stockholm and Gothenburg areas and the wider Mälardalen region. Bought from multiple auction houses in the same city? Describe it when booking and we can plan pickups from several addresses in one trip.",
          },
          {
            heading: "How delivery from Auctionet works",
            listItems: [
              {
                label: "Book online",
                text: "Enter the auction house address (from your Auctionet order) and your home address. Describe the item and include the order number if possible. You see the price instantly.",
              },
              {
                label: "We pick up at the auction house",
                text: "Our crew arrives with a van and the right protective materials. We identify your item, protect it carefully and load it.",
              },
              {
                label: "We deliver home",
                text: "We drive to your home, carry in through stairwells and doors and place your auction find where you want it.",
              },
              {
                label: "Done",
                text: "Your Auctionet find is at home – carefully carried in and in place.",
              },
            ],
            afterContent:
              "The whole process usually takes 1–4 hours depending on distance and handling requirements. You can track the status in real time.",
          },
          {
            heading: "Why book Auctionet delivery with Ungdomskraft?",
            content:
              "What makes Ungdomskraft unique is that we employ young people and give them real work experience. When you book delivery of your Auctionet purchase, you don't just get professional, careful handling – you also help young people enter the job market and build their futures.\n\nEvery job is staffed by motivated young workers who learn everything from customer service and logistics to careful handling of valuable objects. They learn to communicate with customers, plan routes, protect delicate surfaces and work efficiently as a team. This is work experience you can't get in a classroom – and every delivery we make gives a young person the chance to grow.\n\nWe have been operating since 2014 and have completed tens of thousands of transports in Stockholm and Gothenburg. Our young workers have carried in everything from Gustavian furniture sets and oil paintings to chandeliers, silver collections and design classics worth hundreds of thousands of kronor. We know what it takes: the right protective materials, careful loading, smooth driving and gentle carrying.\n\nQuality runs through everything we do. Our workers don't take shortcuts – they add extra blankets, double-check the straps and communicate with you throughout the job. The energy and commitment that young workers bring shows in every delivery. They want to prove themselves and they take the job seriously.\n\nSustainability is another cornerstone. Buying at auction via Auctionet is inherently sustainable – you give items new life instead of buying new. We are proud to be part of that chain by transporting items home to their new owners. We optimise routes to minimise emissions and use reusable protective materials.\n\nFor businesses buying art or design on Auctionet – for the office, lobby or showroom – we also offer invoicing and documentation for CSR reporting. You can show that your transport was carried out by a company that employs young people and contributes to social sustainability.\n\nWe offer fast pickup, carrying to the right spot and a competitive price with no hidden fees. You see the price when you book, everything is insured and we have our own vans in several sizes.\n\nIt is careful, professional help with purpose.\n\nAuctionet delivery does not qualify for the Swedish RUT tax deduction, but several of our other services do – including moving help, carrying help and furniture assembly with a 50% deduction on the labour cost. Feel free to combine services in a single booking.",
          },
        ],
      },

      faq: [
        {
          question:
            "How much does delivery of items bought on Auctionet cost?",
          answer:
            "The price depends on the item's size and distance from the auction house. A painting or silver item starts from SEK 595. Larger furniture starts from SEK 995–1,699. You see the exact price when you book online.",
        },
        {
          question: "Can I get a RUT tax deduction on Auctionet delivery?",
          answer:
            "Auctionet delivery does not in itself qualify for the Swedish RUT tax deduction. RUT applies to household services carried out in your home, such as moving help, carrying help and furniture assembly. Ungdomskraft offers several RUT-eligible services with a 50% deduction on the labour cost – easily combine them in one booking via our website.",
        },
        {
          question: "Is a van included in the Auctionet delivery?",
          answer:
            "Yes, a van is always included. We choose the right size based on what needs to be transported – you don't need to rent a vehicle or drive yourself.",
        },
        {
          question: "Is carrying included in the Auctionet delivery price?",
          answer:
            "Yes, carrying is always included. We carry your item through stairwells, lifts and doors and place it where you want it – regardless of floor. No extra fees per floor.",
        },
        {
          question:
            "How do you protect valuable items bought on Auctionet?",
          answer:
            "We use furniture blankets, bubble wrap, corner guards and straps. Paintings are transported standing, sculptures are secured individually and silver or porcelain is packed with shock-absorbing material. Everything is insured.",
        },
        {
          question:
            "Where do you pick up Auctionet items – is there a fixed address?",
          answer:
            "Auctionet is a platform connecting many auction houses. The item is at the specific partner auction house. You can find the pickup address in your order confirmation on Auctionet. Enter that address when booking with us.",
        },
        {
          question:
            "Can you carry heavy antique furniture bought on Auctionet?",
          answer:
            "Yes, our workers are experienced with heavy items – dressers, cabinets, dining tables and other furniture weighing 50–200 kg. We protect all surfaces with blankets and carry carefully through stairwells and narrow doorways.",
        },
        {
          question: "How quickly can you pick up my Auctionet purchase?",
          answer:
            "We can often pick up the same day or next day. Book as soon as you receive the pickup notification – it helps you avoid storage fees at the auction house.",
        },
        {
          question:
            "Are you responsible for the item's condition from Auctionet?",
          answer:
            "We are responsible for transport – the item is insured against transport damage. The item's condition, authenticity and provenance are between you and the seller via Auctionet. We recommend inspecting the item before we load it.",
        },
        {
          question:
            "Do I need to be at the auction house for pickup?",
          answer:
            "No, you don't need to be there. Make sure the purchase is paid for via Auctionet and that we have your order number. You can authorise us to collect on your behalf – specify this when booking. You just need to be home to receive the delivery.",
        },
        {
          question:
            "Can you pick up multiple Auctionet items from the same auction house?",
          answer:
            "Yes, we pick up all your winning items in one trip if they're at the same auction house. Describe all items when booking – we adjust van size and protective materials for the entire load.",
        },
        {
          question:
            "Can you pick up from multiple auction houses in one trip?",
          answer:
            "Yes, if the auction houses are in the same city or along the same route, we can plan pickups from several addresses. Describe all pickup locations when booking and we'll optimise the route.",
        },
        {
          question: "Do you offer Auctionet delivery on weekends?",
          answer:
            "Yes, we offer delivery on weekends and some evenings. Check that the auction house is open for pickup at the time you book.",
        },
        {
          question: "Is the Auctionet delivery insured?",
          answer:
            "Yes, all our transports are fully insured. If your item is damaged during loading, transport or carrying, we will compensate you. You can book with confidence.",
        },
        {
          question:
            "In which municipalities do you offer Auctionet delivery?",
          answer:
            "We offer Auctionet delivery across the entire Stockholm area: Botkyrka, Danderyd, Ekerö, Haninge, Huddinge, Järfälla, Lidingö, Nacka, Norrtälje, Nykvarn, Nynäshamn, Salem, Sigtuna, Sollentuna, Solna, Stockholm, Sundbyberg, Södertälje, Tyresö, Täby, Upplands Väsby, Upplands-Bro, Vallentuna, Vaxholm, Värmdö and Österåker. We also serve the Gothenburg area: Ale, Alingsås, Gothenburg, Härryda, Kungälv, Kungsbacka, Lerum, Lilla Edet, Mölndal, Partille, Stenungsund, Tjörn and Öckerö.",
        },
      ],

      priceHint: PACKAGE_PRICE_HINT_EN.bud,

      pricingRows: getPackagePricingRows("bud", "en"),

      dynamicHeadings: {
        threeSteps: "Auctionet delivery in 3 easy steps",
        step2Text:
          "Our crew picks up your item at the partner auction house, protects it carefully with blankets and bubble wrap and loads the van. Then we drive home to you.",
        step3Title: "Your find is home",
        step3Desc:
          "Your Auctionet find is at home – carefully carried in and in place. Done.",
        ctaHeading: "Book Auctionet delivery today",
        ctaText:
          "Enter the auction house address and your home address, describe the item and choose a time. You see the price instantly.",
      },
    },
  },

  // ────────────────────────────────────────────────────────────────────
  // Hemleverans Klaravik
  // Vinkel: Hemleverans av objekt köpta på Klaravik – Sveriges ledande
  // nätauktion för verktyg, verkstadsutrustning, trädgårdsmaskiner,
  // elektronik, kontorsmöbler och heminredning. Objekten finns hos
  // säljaren eller på Klaraviks uppställningsplats. Vi hämtar och
  // levererar hem med skåpbil.
  // ────────────────────────────────────────────────────────────────────
  {
    slug: "hemleverans-klaravik",
    serviceCategory: "hemleverans",

    meta: {
      title:
        "Hemleverans Klaravik – Vi Hämtar Ditt Auktionsköp | Ungdomskraft",
      description:
        "Köpt på Klaravik? Vi hämtar ditt objekt på uppställningsplatsen och levererar hem med varsam inbärning. Skåpbil och bärhjälp ingår. Försäkrat. Boka idag!",
      h1: "Hemleverans Klaravik",
    },

    hero: {
      heading: "Vi hämtar ditt köp och kör hem till dig – Hemleverans Klaravik",
      intro:
        "Vunnit ett bud på Klaravik? Grattis – men hur får du hem din verkstadsutrustning, trädgårdsmaskin eller kontorsmöbel? Objektet finns hos säljaren eller på en uppställningsplats och hämtfristen är kort. Vi löser det. Ungdomskraft hämtar ditt Klaravik-köp på rätt adress, lastar skåpbilen och levererar hem till dig – med inbärning på plats.",
      heroImage: "/hero-hemleverans.png",
    },

    featureCards: {
      sinceText: "Klaravik-leverans sedan 2014",
      storyText:
        "Sedan 2014 har Ungdomskraft hjälpt köpare på nätauktioner att få hem sina fynd – från tunga verktyg och verkstadsutrustning till trädgårdsmaskiner, elektronik och kontorsmöbler. Våra ungdomar vet hur man hanterar tunga och skrymmande föremål med den omsorg de kräver.",
      bookYourText: "Boka din Klaravik-leverans",
      bookDescText:
        "Ange hämtadressen (från din Klaravik-order) och din hemadress, beskriv objektet och välj en tid. Du ser priset direkt.",
      vehicleTitleText: "Skåpbilar för Klaravik-köp",
      vehicleDescText:
        "Vi kommer med rätt storlek på skåpbil – oavsett om det gäller en verktygslåda eller en tung bänkslip. Allt säkras med spännband och skyddas under transporten.",
    },

    itemsSection: {
      heading: "Vanliga objekt vi hämtar från Klaravik",
      items: [
        {
          name: "Verktyg & verkstad",
          description:
            "Bänkslipar, borrmaskiner, svetsaggregat, kompressorer, verktygsskåp och handverktyg",
          image: "/toolbox.webp",
        },
        {
          name: "Trädgård & utomhus",
          description:
            "Åkgräsklippare, robotgräsklippare, trimmers, lövblåsare, högtryckstvättar och kedjasågar",
          image: "/patio-furniture.webp",
        },
        {
          name: "Elektronik & IT",
          description:
            "Datorer, skärmar, servrar, skrivare, projektorer och annan IT-utrustning",
          image: "/tv.webp",
        },
        {
          name: "Kontorsmöbler",
          description:
            "Skrivbord, kontorsstolar, hyllsystem, konferensbord, arkivskåp och förvaringslösningar",
          image: "/office-desk.webp",
        },
        {
          name: "Hem & inredning",
          description:
            "Möbler, vitvaror, belysning, mattor, speglar och andra inredningsföremål",
          image: "/sofa.webp",
        },
        {
          name: "Gym & fritid",
          description:
            "Löpband, crosstrainers, träningsbänkar, motionscyklar och annan träningsutrustning",
          image: "/treadmill.webp",
        },
      ],
    },

    seoContent: {
      mainHeading:
        "Hemleverans från Klaravik – vi hämtar på uppställningsplatsen och kör hem till dig",
      mainText:
        "Klaravik är en av Sveriges största nätauktionsplattformar med tusentals objekt varje vecka – verktyg, verkstadsutrustning, trädgårdsmaskiner, elektronik, kontorsmöbler, heminredning, träningsutrustning och mycket mer. Du budar enkelt online, men sedan kommer den praktiska frågan: hur får jag hem det här? Objektet finns hos säljaren eller på en uppställningsplats, ibland i din stad och ibland långt bort. Hämtfristen är kort och föremålen ofta tunga eller skrymmande. Att försöka få in en bänkslip på 80 kg, en åkgräsklippare eller ett komplett verktygsskåp i vanlig bil fungerar sällan. Ungdomskraft erbjuder hemleverans av objekt köpta på Klaravik. Vi hämtar ditt köp på rätt adress – oavsett om det finns hos säljaren, på en uppställningsplats eller ett lager i Stockholms- eller Göteborgsområdet. Vi lastar skåpbilen, säkrar allt med spännband och filtar och kör hem till dig. Vi bär in genom garage, verkstad, källare eller trapphus och ställer på plats. Inbärning ingår alltid – du ser priset direkt vid bokning. Varje vecka hämtar vi Klaravik-köp i alla storlekar – från en enstaka verktygslåda till komplett verkstadsutrustning eller ett helt kontors möblemang. Våra ungdomar är vana vid att lasta tunga och otympliga föremål. Vi vet hur man bär en 80-kilos bänkslip nedför en trappa, hur man säkrar en åkgräsklippare i skåpbilen och hur man manövrerar ett stort verktygsskåp genom en smal garageport. Vi tar med oss bärremmar, spännband och skydd för golv och väggar. Tänk på att vi ansvarar för transporten från hämtplatsen hem till dig – objektets skick och funktion är mellan dig och säljaren via Klaravik.",
      preparationHeading: "Förbered inför din leverans från Klaravik",
      preparationText:
        "Se till att ditt köp är fullt betalt via Klaravik och redo att hämtas. Kontrollera vilken adress objektet finns på – det står i din orderbekräftelse eller under Mina köp på klaravik.se. Ha ditt ordernummer redo så att vårt team kan identifiera rätt föremål. Kontrollera hämtfristen – Klaravik anger alltid sista hämtdag. Om objektet är särskilt tungt eller otympligt – beskriv det tydligt vid bokning, inklusive ungefärlig vikt. Kolla att skåpbilen kan parkera nära hämtplatsen och nära din port hemma.",
      additionalSections: [
        {
          heading: "Hemleverans av verktyg och verkstadsutrustning från Klaravik",
          content:
            "Verktyg och verkstadsutrustning är bland de mest populära kategorierna på Klaravik. Bänkslipar, pelarborrsmaskiner, svetsaggregat, kompressorer, metallsvarvar och kompletta verktygsvagnar – föremål som ofta väger 50–150 kg och är besvärliga att transportera på egen hand. Vi hämtar dina verktyg och din utrustning på angiven adress, lastar försiktigt i skåpbilen och säkrar allt med spännband. Hemma hos dig bär vi in till verkstaden, garaget eller källaren och ställer på plats. Tunga maskiner som bänkslipar och borrmaskiner kräver ofta två personers lyft – vårt team hanterar det. Har du köpt flera verktyg från samma säljare på Klaravik? Vi hämtar allt i en tur och säkrar varje föremål separat.",
        },
        {
          heading: "Hemleverans av trädgårdsmaskiner från Klaravik",
          content:
            "Klaravik har ett brett utbud av trädgårds- och utomhusutrustning – åkgräsklippare, robotgräsklippare, trimmers, häcksaxar, lövblåsare, högtryckstvättar, motorsågar och snöslungor. Åkgräsklippare väger ofta 150–250 kg och kräver ramper eller lyftteknik för att lastas. Mindre maskiner som trimmers och häcksaxar kan vara känsliga och behöver skydd under transport. Vi hämtar din trädgårdsmaskin på uppställningsplatsen, lastar med hjälp av ramper vid behov och säkrar i skåpbilen. Hemma hos dig kör vi av maskinen och ställer den på plats i trädgården, garaget eller förrådet. Vi hanterar också bensinmotorer varsamt – inga läckage, inget stök.",
        },
        {
          heading: "Hemleverans av kontorsmöbler och elektronik från Klaravik",
          content:
            "Klaravik auktionerar regelbundet kontorsinredning och IT-utrustning – skrivbord, kontorsstolar, konferensbord, hyllsystem, arkivskåp, datorer, skärmar, servrar och skrivare. Det kan vara utrustning från företag som avvecklar, uppgraderar eller flyttar. Vi hämtar dina kontorsmöbler och din elektronik på angiven adress, skyddar skärmar och ömtålig elektronik med filtar och bubbelplast och lastar i skåpbilen. Hemma – eller på ditt kontor – bär vi in och ställer på plats. Skrivbord och konferensbord som inte går genom dörren demonterar vi vid behov. Elektronik hanteras varsamt för att undvika stötar.",
        },
        {
          heading: "Så fungerar hämtning av Klaravik-köp",
          content:
            "Klaravik är en nätauktionsplattform där objekten finns hos säljaren eller på en uppställningsplats – inte hos Klaravik centralt. Det innebär att hämtadressen varierar beroende på vad du köpt. Du hittar hämtadressen i din orderbekräftelse eller under Mina köp på klaravik.se. Ange den adressen som hämtplats vid bokning hos oss. Vi hämtar från adresser i hela Stockholms- och Göteborgsområdet samt övriga Mälardalen. Har du köpt flera objekt från samma säljare? Vi hämtar allt i en tur.",
        },
        {
          heading: "Så fungerar leverans från Klaravik",
          listItems: [
            {
              label: "Boka online",
              text: "Ange hämtadressen (från din Klaravik-order) och din hemadress. Beskriv objektet – vikt, mått och typ. Du ser priset direkt.",
            },
            {
              label: "Vi hämtar på plats",
              text: "Vårt team kommer med skåpbil och rätt utrustning. Vi identifierar ditt objekt, lastar och säkrar allt med spännband och filtar.",
            },
            {
              label: "Vi levererar hem",
              text: "Vi kör hem till dig, bär in genom garage, verkstad, källare eller trapphus och ställer ditt Klaravik-köp på plats.",
            },
            {
              label: "Klart",
              text: "Ditt Klaravik-köp står hemma hos dig – inburet och på plats. Klart.",
            },
          ],
          afterContent:
            "Hela processen tar normalt 1–4 timmar beroende på avstånd och objektets storlek. Du kan följa statusen i realtid.",
        },
        {
          heading: "Varför boka Klaravik-leverans med Ungdomskraft?",
          content:
            "Det som gör Ungdomskraft unikt är att vi anställer unga människor och ger dem riktig arbetslivserfarenhet. När du bokar hemleverans av ditt Klaravik-köp får du inte bara professionell hantering av tunga och skrymmande föremål – du bidrar också till att unga människor kommer in på arbetsmarknaden och bygger sin framtid.\n\nVarje uppdrag bemannas av motiverade ungdomar som lär sig allt från kundservice och logistik till säker lastning och ergonomiskt lyft. De lär sig att kommunicera med kunder, planera rutter, säkra last i en skåpbil och arbeta effektivt i team. Det är arbetslivserfarenhet som inte går att få i ett klassrum – och varje leverans vi kör ger en ung person möjlighet att utvecklas.\n\nVi har funnits sedan 2014 och har genomfört tiotusentals transporter i Stockholm och Göteborg. Våra ungdomar har burit in allt från tunga bänkslipar och verkstadsutrustning till åkgräsklippare, kontorsmöbler och komplett IT-utrustning. Vi vet vad som krävs: rätt lastteknik, bärremmar, spännband och skydd för golv och väggar.\n\nKvalitet genomsyrar allt vi gör. Våra ungdomar tar inga genvägar – de kontrollerar att spännbanden sitter, skyddar känsliga ytor och kommunicerar med dig under hela uppdraget. Den energi och det engagemang som unga medarbetare har märks i varje leverans. De vill visa vad de kan och de tar uppdraget på allvar.\n\nHållbarhet är en annan grundpelare. Att köpa begagnat på Klaravik är i sig ett hållbart val – du ger verktyg, maskiner och möbler nytt liv istället för att köpa nytt. Vi är stolta över att vara en del av den kedjan genom att transportera föremålen hem till sin nya ägare. Vi optimerar rutter för att minimera utsläpp och använder återanvändbara skyddsmaterial.\n\nFör företag som köper utrustning på Klaravik – till verkstaden, kontoret eller lagret – erbjuder vi faktura och underlag för CSR-rapportering. Du kan visa att din transport genomfördes av ett företag som sysselsätter ungdomar och bidrar till social hållbarhet.\n\nVi erbjuder snabb hämtning, inbärning till rätt plats och ett konkurrenskraftigt pris utan dolda avgifter. Du ser priset direkt vid bokning, allt är försäkrat och vi har egna skåpbilar i flera storlekar.\n\nDet är stark, pålitlig hjälp med mening.\n\nKlaravik-leverans ger inte rätt till RUT-avdrag, men flera av våra andra tjänster gör det – exempelvis flytt, bärhjälp och möbelmontering med 50 % avdrag på arbetskostnaden. Kombinera gärna tjänster i en och samma bokning.",
        },
      ],
    },

    faq: [
      {
        question: "Vad kostar hemleverans av objekt köpta på Klaravik?",
        answer:
          "Priset beror på objektets storlek, vikt och avstånd. Ett mindre verktyg kostar från 595 kr. Tyngre utrustning kostar från 995–2 495 kr. Du ser det exakta priset direkt när du bokar online.",
      },
      {
        question: "Kan jag få RUT-avdrag på Klaravik-leverans?",
        answer:
          "Klaravik-leverans ger i sig inte rätt till RUT-avdrag. RUT gäller hushållsnära tjänster som utförs i din bostad, exempelvis flytt, bärhjälp och möbelmontering. Ungdomskraft erbjuder flera RUT-berättigade tjänster med 50 % avdrag på arbetskostnaden – kombinera enkelt i en bokning via vår hemsida.",
      },
      {
        question: "Ingår skåpbil i Klaravik-leveransen?",
        answer:
          "Ja, skåpbil ingår alltid. Vi väljer rätt storlek utifrån vad som ska transporteras – du behöver inte hyra bil, släp eller köra själv.",
      },
      {
        question: "Ingår inbärning i priset för Klaravik-leverans?",
        answer:
          "Ja, inbärning ingår alltid. Vi bär in ditt köp i garage, verkstad, källare eller genom trapphus och ställer på plats. Inga extra avgifter.",
      },
      {
        question: "Kan ni hämta tunga verktyg och maskiner från Klaravik?",
        answer:
          "Ja, våra ungdomar är vana vid tunga lyft. Vi hanterar bänkslipar, kompressorer, verkstadsutrustning och andra föremål upp till 200–300 kg med hjälp av bärremmar och rätt lyftteknik.",
      },
      {
        question: "Var hämtar ni Klaravik-objekt – finns det en fast adress?",
        answer:
          "Klaravik är en nätauktionsplattform där objekten finns hos säljaren eller på en uppställningsplats. Du hittar hämtadressen i din orderbekräftelse på klaravik.se. Ange den adressen vid bokning hos oss.",
      },
      {
        question: "Kan ni transportera en åkgräsklippare köpt på Klaravik?",
        answer:
          "Ja, vi transporterar åkgräsklippare regelbundet. Vi lastar med ramper vid behov, säkrar maskinen i skåpbilen och kör av den hemma hos dig – i trädgården, garaget eller förrådet.",
      },
      {
        question: "Hur snabbt kan ni hämta mitt Klaravik-köp?",
        answer:
          "Vi kan ofta hämta redan samma dag eller nästa dag. Boka så snart du fått hämtbesked – det hjälper dig att hålla hämtfristen.",
      },
      {
        question: "Tar ni ansvar för objektets skick vid Klaravik-leverans?",
        answer:
          "Vi ansvarar för transporten – objektet är försäkrat mot transportskador. Objektets skick och funktion är mellan dig och säljaren via Klaravik. Vi rekommenderar att du inspekterar objektet innan vi lastar.",
      },
      {
        question: "Behöver jag vara med vid hämtning av mitt Klaravik-köp?",
        answer:
          "Nej, du behöver inte vara med vid hämtningen om du ordnat det med säljaren. Ha ditt ordernummer redo och beskriv objektet vid bokning. Du behöver bara vara hemma för att ta emot leveransen.",
      },
      {
        question: "Kan ni leverera Klaravik-köp på helger?",
        answer:
          "Ja, vi erbjuder hemleverans på helger och vissa kvällar. Kontrollera att hämtplatsen är tillgänglig den tid du bokar.",
      },
      {
        question: "Kan ni hämta flera Klaravik-objekt från samma säljare?",
        answer:
          "Ja, vi hämtar alla dina köp i en tur om de finns på samma adress. Beskriv samtliga föremål vid bokning – vi anpassar skåpbilsstorlek och utrustning efter hela lasten.",
      },
      {
        question: "Kan ni leverera kontorsmöbler och IT-utrustning från Klaravik?",
        answer:
          "Ja, vi transporterar skrivbord, kontorsstolar, skärmar, datorer och annan kontorsutrustning. Elektronik skyddas med filtar och bubbelplast. Vi bär in och ställer på plats på kontoret eller hemma.",
      },
      {
        question: "Är Klaravik-leveransen försäkrad?",
        answer:
          "Ja, alla våra transporter är helförsäkrade. Om ditt objekt skadas under lastning, transport eller inbärning ersätter vi det. Du kan boka med trygghet.",
      },
      {
        question:
          "I vilka kommuner erbjuder ni hemleverans av Klaravik-köp?",
        answer:
          "Vi erbjuder hemleverans av Klaravik-köp i hela Stockholmsområdet: Botkyrka, Danderyd, Ekerö, Haninge, Huddinge, Järfälla, Lidingö, Nacka, Norrtälje, Nykvarn, Nynäshamn, Salem, Sigtuna, Sollentuna, Solna, Stockholm, Sundbyberg, Södertälje, Tyresö, Täby, Upplands Väsby, Upplands-Bro, Vallentuna, Vaxholm, Värmdö och Österåker. Vi finns även i Göteborgsområdet: Ale, Alingsås, Göteborg, Härryda, Kungälv, Kungsbacka, Lerum, Lilla Edet, Mölndal, Partille, Stenungsund, Tjörn och Öckerö.",
      },
    ],

    priceHint: PACKAGE_PRICE_HINT_SV.bud,

    pricingRows: getPackagePricingRows("bud", "sv"),

    dynamicHeadings: {
      threeSteps: "Klaravik-leverans i 3 enkla steg",
      step2Text:
        "Våra ungdomar hämtar ditt objekt på uppställningsplatsen, lastar skåpbilen och säkrar allt med spännband och filtar. Sedan kör vi hem till dig.",
      step3Title: "Ditt köp är hemma",
      step3Desc:
        "Ditt Klaravik-köp står hemma hos dig – inburet och på plats. Klart.",
      ctaHeading: "Boka Klaravik-leverans idag",
      ctaText:
        "Ange hämtadressen och din hemadress, beskriv objektet och välj en tid. Du ser priset direkt.",
    },

    en: {
      slug: "klaravik-delivery",

      meta: {
        title:
          "Klaravik Delivery – We Pick Up & Deliver Your Auction Purchase Home | Ungdomskraft",
        description:
          "Bought on Klaravik? We pick up your item at the seller's location and deliver home. Van, carrying and placement included. Book delivery today.",
        h1: "Klaravik delivery",
      },

      hero: {
        heading: "We pick up your purchase and deliver home – Klaravik delivery",
        intro:
          "Won a bid on Klaravik? Congratulations – but how do you get that workshop equipment, garden machine or office furniture home? The item is at the seller's location or a storage yard and the pickup deadline is tight. Ungdomskraft picks up your Klaravik purchase at the right address, loads the van and delivers to your door – with carrying and placement inside.",
      },

      featureCards: {
        sinceText: "Klaravik delivery since 2014",
        storyText:
          "Since 2014, Ungdomskraft has helped online auction buyers get their purchases home – from heavy tools and workshop equipment to garden machines, electronics and office furniture. Our young workers know how to handle heavy and bulky items with the care they require.",
        bookYourText: "Book your Klaravik delivery",
        bookDescText:
          "Enter the pickup address (from your Klaravik order) and your home address, describe the item and choose a time. You see the price instantly.",
        vehicleTitleText: "Vans ready for Klaravik purchases",
        vehicleDescText:
          "We bring the right van size – whether it's a toolbox or a heavy bench grinder. Everything is secured with straps and protected during transport.",
      },

      itemsSection: {
        heading: "Common items we collect from Klaravik",
        items: [
          {
            name: "Tools & workshop",
            description:
              "Bench grinders, drill presses, welding machines, compressors, tool cabinets and hand tools",
            image: "/toolbox.webp",
          },
          {
            name: "Garden & outdoor",
            description:
              "Ride-on mowers, robot mowers, trimmers, leaf blowers, pressure washers and chainsaws",
            image: "/patio-furniture.webp",
          },
          {
            name: "Electronics & IT",
            description:
              "Computers, monitors, servers, printers, projectors and other IT equipment",
            image: "/tv.webp",
          },
          {
            name: "Office furniture",
            description:
              "Desks, office chairs, shelving systems, conference tables, filing cabinets and storage solutions",
            image: "/office-desk.webp",
          },
          {
            name: "Home & interiors",
            description:
              "Furniture, appliances, lighting, rugs, mirrors and other interior items",
            image: "/sofa.webp",
          },
          {
            name: "Gym & leisure",
            description:
              "Treadmills, cross trainers, weight benches, exercise bikes and other fitness equipment",
            image: "/treadmill.webp",
          },
        ],
      },

      seoContent: {
        mainHeading:
          "Delivery from Klaravik – we pick up at the seller's location and deliver home to you",
        mainText:
          "Klaravik is one of Sweden's largest online auction platforms with thousands of items every week – tools, workshop equipment, garden machines, electronics, office furniture, home interiors, fitness equipment and much more. Bidding online is easy, but then comes the practical question: how do I get this home? The item is at the seller's location or a storage yard, sometimes in your city and sometimes far away. The pickup deadline is short and the items are often heavy or bulky. Trying to fit an 80 kg bench grinder, a ride-on mower or a complete tool cabinet in a regular car rarely works. Ungdomskraft offers home delivery of items bought on Klaravik. We pick up your purchase at the right address – whether it's at the seller's premises, a storage yard or a warehouse in the Stockholm or Gothenburg area. We load the van, secure everything with straps and blankets and drive it home to you. We carry in through garages, workshops, basements or stairwells and place it where you want it. Carrying is always included – you see the price when you book. Every week we collect Klaravik purchases in all sizes – from a single toolbox to complete workshop equipment or a full office furniture set. Our young workers are used to loading heavy and awkward items. We know how to carry an 80 kg bench grinder down stairs, how to secure a ride-on mower in the van and how to manoeuvre a large tool cabinet through a narrow garage door. We bring carrying straps, ratchet straps and protection for floors and walls. Please note that we are responsible for the transport from the pickup location to your home – the item's condition and function are between you and the seller via Klaravik.",
        preparationHeading: "Prepare for your delivery from Klaravik",
        preparationText:
          "Make sure your purchase is fully paid for via Klaravik and ready for collection. Check which address the item is at – it's listed in your order confirmation or under My Purchases on klaravik.se. Have your order number ready so our team can identify the right item. Check the pickup deadline – Klaravik always specifies the last collection date. If the item is particularly heavy or bulky, describe it clearly when booking including approximate weight. Check that the van can park near the pickup location and near your entrance at home.",
        additionalSections: [
          {
            heading: "Delivering tools and workshop equipment from Klaravik",
            content:
              "Tools and workshop equipment are among the most popular categories on Klaravik. Bench grinders, drill presses, welding machines, compressors, metal lathes and complete tool wagons – items that often weigh 50–150 kg and are difficult to transport on your own. We pick up your tools and equipment at the specified address, load carefully into the van and secure everything with straps. At your home we carry in to the workshop, garage or basement and place where you want it. Heavy machines like bench grinders and drill presses often require two-person lifts – our team handles it. Bought multiple tools from the same seller on Klaravik? We collect everything in one trip and secure each item separately.",
          },
          {
            heading: "Delivering garden machines from Klaravik",
            content:
              "Klaravik has a wide range of garden and outdoor equipment – ride-on mowers, robot mowers, trimmers, hedge cutters, leaf blowers, pressure washers, chainsaws and snow blowers. Ride-on mowers often weigh 150–250 kg and require ramps or lifting technique to load. Smaller machines like trimmers and hedge cutters can be sensitive and need protection during transport. We pick up your garden machine at the storage location, load with ramps when needed and secure in the van. At your home we unload and place the machine in the garden, garage or storage shed. We also handle petrol engines carefully – no leaks, no mess.",
          },
          {
            heading: "Delivering office furniture and electronics from Klaravik",
            content:
              "Klaravik regularly auctions office interiors and IT equipment – desks, office chairs, conference tables, shelving systems, filing cabinets, computers, monitors, servers and printers. This may be equipment from businesses that are closing, upgrading or relocating. We pick up your office furniture and electronics at the specified address, protect screens and fragile electronics with blankets and bubble wrap and load into the van. At your home – or at your office – we carry in and place where you want it. Desks and conference tables that don't fit through the door can be disassembled if needed. Electronics are handled gently to avoid impacts.",
          },
          {
            heading: "How Klaravik pickup works",
            content:
              "Klaravik is an online auction platform where items are at the seller's location or a storage yard – not at a central Klaravik facility. This means the pickup address varies depending on what you bought. You can find the pickup address in your order confirmation or under My Purchases on klaravik.se. Enter that address as the pickup location when booking with us. We pick up from addresses across the Stockholm and Gothenburg areas and the wider Mälardalen region. Bought multiple items from the same seller? We collect everything in one trip.",
          },
          {
            heading: "How delivery from Klaravik works",
            listItems: [
              {
                label: "Book online",
                text: "Enter the pickup address (from your Klaravik order) and your home address. Describe the item – weight, dimensions and type. You see the price instantly.",
              },
              {
                label: "We pick up on site",
                text: "Our crew arrives with a van and the right equipment. We identify your item, load and secure everything with straps and blankets.",
              },
              {
                label: "We deliver home",
                text: "We drive to your home, carry in through garage, workshop, basement or stairwell and place your Klaravik purchase where you want it.",
              },
              {
                label: "Done",
                text: "Your Klaravik purchase is at home – carried in and in place. Done.",
              },
            ],
            afterContent:
              "The whole process usually takes 1–4 hours depending on distance and item size. You can track the status in real time.",
          },
          {
            heading: "Why book Klaravik delivery with Ungdomskraft?",
            content:
              "What makes Ungdomskraft unique is that we employ young people and give them real work experience. When you book delivery of your Klaravik purchase, you don't just get professional handling of heavy and bulky items – you also help young people enter the job market and build their futures.\n\nEvery job is staffed by motivated young workers who learn everything from customer service and logistics to safe loading and ergonomic lifting. They learn to communicate with customers, plan routes, secure loads in a van and work efficiently as a team. This is work experience you can't get in a classroom – and every delivery we make gives a young person the chance to grow.\n\nWe have been operating since 2014 and have completed tens of thousands of transports in Stockholm and Gothenburg. Our young workers have carried in everything from heavy bench grinders and workshop equipment to ride-on mowers, office furniture and complete IT setups. We know what it takes: the right loading technique, carrying straps, ratchet straps and protection for floors and walls.\n\nQuality runs through everything we do. Our workers don't take shortcuts – they check that the straps are secure, protect sensitive surfaces and communicate with you throughout the job. The energy and commitment that young workers bring shows in every delivery. They want to prove themselves and they take the job seriously.\n\nSustainability is another cornerstone. Buying second-hand on Klaravik is inherently sustainable – you give tools, machines and furniture new life instead of buying new. We are proud to be part of that chain by transporting items home to their new owners. We optimise routes to minimise emissions and use reusable protective materials.\n\nFor businesses buying equipment on Klaravik – for the workshop, office or warehouse – we also offer invoicing and documentation for CSR reporting. You can show that your transport was carried out by a company that employs young people and contributes to social sustainability.\n\nWe offer fast pickup, carrying to the right spot and a competitive price with no hidden fees. You see the price when you book, everything is insured and we have our own vans in several sizes.\n\nIt is strong, reliable help with purpose.\n\nKlaravik delivery does not qualify for the Swedish RUT tax deduction, but several of our other services do – including moving help, carrying help and furniture assembly with a 50% deduction on the labour cost. Feel free to combine services in a single booking.",
          },
        ],
      },

      faq: [
        {
          question: "How much does delivery of items bought on Klaravik cost?",
          answer:
            "The price depends on the item's size, weight and distance. A smaller tool starts from SEK 595. Heavier equipment starts from SEK 995–1,699. You see the exact price when you book online.",
        },
        {
          question: "Can I get a RUT tax deduction on Klaravik delivery?",
          answer:
            "Klaravik delivery does not in itself qualify for the Swedish RUT tax deduction. RUT applies to household services carried out in your home, such as moving help, carrying help and furniture assembly. Ungdomskraft offers several RUT-eligible services with a 50% deduction on the labour cost – easily combine them in one booking via our website.",
        },
        {
          question: "Is a van included in the Klaravik delivery?",
          answer:
            "Yes, a van is always included. We choose the right size based on what needs to be transported – you don't need to rent a vehicle, trailer or drive yourself.",
        },
        {
          question: "Is carrying included in the Klaravik delivery price?",
          answer:
            "Yes, carrying is always included. We carry your purchase into the garage, workshop, basement or through stairwells and place it where you want it. No extra fees.",
        },
        {
          question: "Can you transport heavy tools and machines from Klaravik?",
          answer:
            "Yes, our workers are experienced with heavy lifting. We handle bench grinders, compressors, workshop equipment and other items up to 200–300 kg using carrying straps and proper lifting technique.",
        },
        {
          question:
            "Where do you pick up Klaravik items – is there a fixed address?",
          answer:
            "Klaravik is an online auction platform where items are at the seller's location or a storage yard. You can find the pickup address in your order confirmation on klaravik.se. Enter that address when booking with us.",
        },
        {
          question: "Can you transport a ride-on mower bought on Klaravik?",
          answer:
            "Yes, we transport ride-on mowers regularly. We load with ramps when needed, secure the machine in the van and unload at your home – in the garden, garage or storage shed.",
        },
        {
          question: "How quickly can you pick up my Klaravik purchase?",
          answer:
            "We can often pick up the same day or next day. Book as soon as you receive the pickup notification – it helps you meet the pickup deadline.",
        },
        {
          question: "Are you responsible for the item's condition from Klaravik?",
          answer:
            "We are responsible for transport – the item is insured against transport damage. The item's condition and function are between you and the seller via Klaravik. We recommend inspecting the item before we load it.",
        },
        {
          question: "Do I need to be at the pickup location?",
          answer:
            "No, you don't need to be at the pickup if you've arranged it with the seller. Have your order number ready and describe the item when booking. You just need to be home to receive the delivery.",
        },
        {
          question: "Can you deliver Klaravik purchases on weekends?",
          answer:
            "Yes, we offer delivery on weekends and some evenings. Check that the pickup location is accessible at the time you book.",
        },
        {
          question: "Can you pick up multiple Klaravik items from the same seller?",
          answer:
            "Yes, we pick up all your purchases in one trip if they're at the same address. Describe all items when booking – we adjust van size and equipment for the entire load.",
        },
        {
          question: "Can you deliver office furniture and IT equipment from Klaravik?",
          answer:
            "Yes, we transport desks, office chairs, monitors, computers and other office equipment. Electronics are protected with blankets and bubble wrap. We carry in and place at your office or home.",
        },
        {
          question: "Is the Klaravik delivery insured?",
          answer:
            "Yes, all our transports are fully insured. If your item is damaged during loading, transport or carrying, we will compensate you. You can book with confidence.",
        },
        {
          question: "In which municipalities do you offer Klaravik delivery?",
          answer:
            "We offer Klaravik delivery across the entire Stockholm area: Botkyrka, Danderyd, Ekerö, Haninge, Huddinge, Järfälla, Lidingö, Nacka, Norrtälje, Nykvarn, Nynäshamn, Salem, Sigtuna, Sollentuna, Solna, Stockholm, Sundbyberg, Södertälje, Tyresö, Täby, Upplands Väsby, Upplands-Bro, Vallentuna, Vaxholm, Värmdö and Österåker. We also serve the Gothenburg area: Ale, Alingsås, Gothenburg, Härryda, Kungälv, Kungsbacka, Lerum, Lilla Edet, Mölndal, Partille, Stenungsund, Tjörn and Öckerö.",
        },
      ],

      priceHint: PACKAGE_PRICE_HINT_EN.bud,

      pricingRows: getPackagePricingRows("bud", "en"),

      dynamicHeadings: {
        threeSteps: "Klaravik delivery in 3 easy steps",
        step2Text:
          "Our crew picks up your item at the seller's location, loads the van and secures everything with straps and blankets. Then we drive home to you.",
        step3Title: "Your purchase is home",
        step3Desc:
          "Your Klaravik purchase is at home – carried in and in place. Done.",
        ctaHeading: "Book Klaravik delivery today",
        ctaText:
          "Enter the pickup address and your home address, describe the item and choose a time. You see the price instantly.",
      },
    },
  },
];